'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Globe2 } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';

export const SUPPORTED_LOCALES = ['en', 'fr', 'es', 'uk', 'ru'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_OPTIONS: { code: AppLocale; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'uk', label: 'Ukrainian', nativeLabel: 'Українська' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский' },
];

const DEFAULT_LOCALE: AppLocale = 'en';
const ACCOUNT_STORAGE_KEY = 'bcl_locale';
const PUBLIC_STORAGE_KEY = 'bcl_public_locale';
const ACCOUNT_LOCALE_CHANGE_EVENT = 'bcl_account_locale_change';
const PUBLIC_LOCALE_CHANGE_EVENT = 'bcl_public_locale_change';

const en = {
  'common.loading': 'Loading...',
  'common.loadingEllipsis': 'Loading…',
  'common.loadingMore': 'Loading more…',
  'common.loadMore': 'Load more',
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.saving': 'Saving…',
  'common.saved': 'Saved',
  'common.edit': 'Edit',
  'common.cancel': 'Cancel',
  'common.discard': 'Discard',
  'common.delete': 'Delete',
  'common.unsavedChanges': 'You have unsaved changes',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.custom': 'Custom',
  'common.and': 'and',
  'common.all': 'All',
  'common.any': 'Any',
  'common.add': 'Add',
  'common.clear': 'Clear',
  'common.clearFilters': 'Clear filters',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.decrease': 'Decrease',
  'common.dismiss': 'Dismiss',
  'common.done': 'Done',
  'common.emailChannel': 'email',
  'common.filters': 'Filters',
  'common.from': 'From',
  'common.homeAria': 'Bevelure home',
  'common.increase': 'Increase',
  'common.km': 'km',
  'common.max': 'Max',
  'common.min': 'Min',
  'common.minutesShort': '{count} min',
  'common.next': 'Next',
  'common.noMatches': 'No matches',
  'common.no': 'No',
  'common.pageOf': 'Page {page} of {total}',
  'common.prev': 'Prev',
  'common.price': 'Price',
  'common.remove': 'Remove',
  'common.scrollRight': 'Scroll right',
  'common.search': 'Search',
  'common.sort': 'Sort',
  'common.sortBy': 'Sort by: {label}',
  'common.to': 'To',
  'common.unknown': 'Unknown',
  'common.user': 'User',
  'common.viewAll': 'View All',
  'common.viewFullDetails': 'View full details',
  'common.viewHistory': 'View History',
  'common.viewProfile': 'View Profile',
  'common.yes': 'Yes',

  'imageCrop.adjustLogo': 'Adjust logo',
  'imageCrop.adjustPhoto': 'Adjust photo',
  'imageCrop.description': 'Move and zoom the image until the part you want sits inside the circle.',
  'imageCrop.dragHint': 'Drag the image to reposition it.',
  'imageCrop.squareDescription': 'Move and zoom the image until the part you want sits inside the square.',
  'imageCrop.useImage': 'Use image',
  'imageCrop.zoom': 'Zoom',

  'greeting.afternoon': 'Good afternoon',
  'greeting.evening': 'Good evening',
  'greeting.morning': 'Good morning',
  'greeting.night': 'Good night',

  'status.awaitingProvider': 'Awaiting provider',
  'status.cancelled': 'Cancelled',
  'status.completed': 'Completed',
  'status.confirmed': 'Confirmed',
  'status.inProgress': 'In progress',
  'status.pending': 'Pending',

  'date.daysAgo': '{count}d ago',
  'date.afternoon': 'Afternoon',
  'date.evening': 'Evening',
  'date.morning': 'Morning',
  'date.noon': 'Noon',
  'date.selectDate': 'Select a date',
  'date.selectDateTime': 'Select date & time',
  'date.time': 'Time',
  'date.today': 'Today',
  'date.tomorrow': 'Tomorrow',
  'date.yesterday': 'Yesterday',

  'field.addCustomValue': 'Add “{value}”',
  'field.addLanguage': 'Add a language…',
  'field.anyDate': 'Any date',
  'field.anyLanguage': 'Any language',
  'field.anyProvider': 'Any provider',
  'field.anyService': 'Any service',
  'field.age': 'Age',
  'field.ageMaxAria': 'Maximum age',
  'field.ageMinAria': 'Minimum age',
  'field.ageUpTo': 'Up to {age}',
  'field.cityPlaceholder': 'Los Angeles',
  'field.city': 'City',
  'field.firstNamePlaceholder': 'Jane',
  'field.hidePassword': 'Hide password',
  'field.invalidPhone': 'Enter a valid phone number for the selected country.',
  'field.language': 'Language',
  'field.languageHelp': 'Pick from the list or type your own and press Enter.',
  'field.lastNamePlaceholder': 'Doe',
  'field.noCountriesFound': 'No countries found',
  'field.phoneNumber': 'Phone number',
  'field.priceMaxAria': 'Maximum price',
  'field.priceMinAria': 'Minimum price',
  'field.providerNamePlaceholder': 'Alex Morgan',
  'field.rating': 'Rating',
  'field.searchCountry': 'Search country…',
  'field.searchCurrency': 'Search currency…',
  'field.selectCountry': 'Select a country',
  'field.service': 'Service',
  'field.serviceExamplePlaceholder': 'e.g., Manicure',
  'field.shareProcedurePlaceholder': 'e.g. Men Haircut, Facial…',
  'field.showPassword': 'Show password',
  'field.socialPlaceholder': '@username or profile URL',
  'field.startTypingAddress': 'Start typing an address…',
  'field.startTypingCity': 'Start typing a city…',
  'field.serviceType': 'Service type',
  'field.sex': 'Sex',
  'field.useCustomLanguage': 'Use “{value}”',

  'sex.female': 'Female',
  'sex.male': 'Male',
  'sex.other': 'Other',
  'sex.preferNotToSay': 'Prefer not to say',

  'rating.any': 'Any',
  'sort.distance': 'Distance',
  'sort.name': 'Name',
  'sort.rating': 'Rating',

  'customerHome.bookingConfirmedBody': 'Saved to your history. Open it for the full details.',
  'customerHome.bookingPendingBody': "We'll let you know once the provider confirms. You can cancel any time.",
  'customerHome.bookingRequestSent': 'Booking request sent',
  'customerHome.appointmentBooked': 'Appointment booked',
  'customerHome.inProgressSubtitle': 'Your appointment is in progress',
  'customerHome.lastVisit': 'Last visit',
  'customerHome.nextAppointment': 'Your next appointment {when}',
  'customerHome.noAppointmentsThisDay': 'No appointments this day',
  'customerHome.noUpcoming': 'No upcoming appointments',
  'customerHome.nothingHereYet': 'Nothing here yet',
  'customerHome.recent': 'Recent',
  'customerHome.upcoming': 'Upcoming',

  'providerHome.allProviders': 'All providers',
  'providerHome.appointmentToday': 'Appointment today',
  'providerHome.appointmentsToday': 'Appointments today',
  'providerHome.awaitingYou': 'Awaiting you',
  'providerHome.pendingRequests': '{count} booking request(s) awaiting you',
  'providerHome.pendingRequestsBody': 'Confirm or decline so customers know what to expect.',
  'providerHome.reviewConfirm': 'Review & confirm',
  'providerHome.searchTeam': 'Search team…',

  'customerSearch.anyCity': 'Any city',
  'customerSearch.detectingLocation': 'Detecting your location…',
  'customerSearch.findingProviders': 'Finding providers…',
  'customerSearch.inCity': ' in {city}',
  'customerSearch.locationCity': 'Showing providers in {city}.',
  'customerSearch.locationNearest': 'Showing providers nearest to you{city}.',
  'customerSearch.noProvidersFound': 'No providers found',
  'customerSearch.priceHintAny': 'Enter a service type above to price a specific procedure.',
  'customerSearch.priceHintService': 'Price of services matching “{serviceType}”.',
  'customerSearch.searchPlaceholder': 'Search by name…',
  'customerSearch.servicePlaceholder': 'Any (e.g. Manicure, Barber…)',
  'customerSearch.showingAll': 'Showing all providers.',
  'customerSearch.title': 'Find Providers',
  'customerSearch.useMyLocation': 'Use my location',

  'careHub.completedVisits': 'Completed visits: {count}',
  'careHub.loadingMore': 'Loading more…',
  'careHub.noCompletedFor': 'No completed visits for {serviceType} yet.',
  'careHub.noCompletedYet': 'No completed visits yet.',
  'careHub.noProcedureDetails': 'No procedure details recorded yet.',
  'careHub.procedurePhotos': 'Procedure photos',
  'careHub.procedureTypes': 'Procedure types: {count}',
  'careHub.quickShare': 'Quick share',
  'careHub.quickShareBody': 'Generate a QR code or link for one of your procedure types.',
  'careHub.shareHistory': 'Share history',
  'careHub.title': 'My Care Hub',
  'careHub.titleAccent': 'My Care *Hub*',
  'careHub.detailNotes': 'Notes',
  'careHub.detailProducts': 'Products',
  'careHub.detailRecommendations': 'Recommendations',

  'appointments.cancelled': 'Cancelled',
  'appointments.careProvider': 'Care provider',
  'appointments.history': 'History',
  'appointments.loadOlder': 'Load older appointments',
  'appointments.loadingOlder': 'Loading older appointments…',
  'appointments.noCancelled': 'No cancelled appointments',
  'appointments.noFilterMatches': 'No appointments match your filters.',
  'appointments.noHistory': 'Nothing here yet',
  'appointments.noUpcoming': 'No upcoming appointments',
  'appointments.rateThisVisit': 'Rate this visit',
  'appointments.upcoming': 'Upcoming',
  'appointments.yourRating': 'Your rating',

  'providerAppointments.anyCustomer': 'Any customer',
  'providerAppointments.noPending': 'No pending requests — you’re all caught up.',
  'providerAppointments.rateClient': 'Rate this client',

  'providerServices.addService': 'Add service',
  'providerServices.addon': 'Add-on',
  'providerServices.addonName': 'Add-on name',
  'providerServices.addonPlaceholder': 'e.g., Gel removal',
  'providerServices.deactivate': 'Deactivate',
  'providerServices.deactivateAddon': 'Deactivate add-on',
  'providerServices.deactivateAddonDescription': 'The service itself stays available — only this add-on becomes unbookable. You can reactivate it anytime.',
  'providerServices.deactivateAddonTitle': 'Deactivate add-on “{name}”?',
  'providerServices.deactivateService': 'Deactivate service',
  'providerServices.deactivateServiceDescription': 'Customers won’t be able to book this service for new appointments. Past bookings stay in history. You can reactivate this service anytime.',
  'providerServices.deactivateServiceTitle': 'Deactivate “{name}”?',
  'providerServices.durationMinutes': 'Duration (minutes)',
  'providerServices.editAddon': 'Edit add-on',
  'providerServices.extraMinutes': 'Extra minutes',
  'providerServices.extraPrice': 'Extra price ({currency})',
  'providerServices.failedCreate': 'Failed to create service',
  'providerServices.failedSaveAddon': 'Failed to save add-on',
  'providerServices.inactive': 'Inactive',
  'providerServices.noAddons': 'No add-ons yet.',
  'providerServices.priceOptional': 'Price ({currency}, optional)',
  'providerServices.reactivate': 'Reactivate',
  'providerServices.reactivateService': 'Reactivate service',
  'providerServices.renameDisabled': 'Renaming is not supported — past bookings reference this name. Deactivate and create a new one instead.',
  'chat.blockedTitle': 'Messaging unavailable',
  'chat.blocked': 'You can no longer message each other.',
  'pub.noSlotsMonth': 'No openings this month — use the arrows to check other months.',
  'pub.publicPage': 'Public booking page',
  'pub.username': 'Username',
  'pub.usernameHelp': 'Your public link — share it on social media so clients can book you.',
  'pub.copy': 'Copy',
  'pub.copied': 'Copied',
  'common.back': 'Back',
  'pub.close': 'Close',
  'pub.bookWith': 'Book with {name}',
  'pub.bookCta': 'Book an appointment',
  'pub.selectService': 'Select a service',
  'pub.chooseTime': 'Choose a time',
  'pub.yourDetails': 'Your details',
  'pub.pickDay': 'Pick a day with openings.',
  'pub.continue': 'Continue',
  'pub.confirmBooking': 'Confirm booking',
  'pub.successTitle': 'Booking received',
  'pub.successGuest': 'Your request is in! We emailed the appointment details to {email} — the provider will confirm shortly.',
  'pub.successCustomer': 'Booked! {provider} will confirm your appointment shortly.',
  'pub.manageCancel': 'Manage or cancel this booking',
  'pub.signedInAs': 'Booking as {name}',
  'pub.tooMany': 'Too many booking attempts. Please try again later.',
  'pub.bookingFailed': 'Could not complete the booking. Please try again.',
  'pub.about': 'About',
  'pub.services': 'Services',
  'pub.portfolio': 'Portfolio',
  'pub.speaks': 'Speaks',
  'pub.ratings': '{count} ratings',
  'pub.atSalon': 'at {salon}',
  'pub.team': 'Team',
  'pub.noTeam': 'No team members yet.',
  'pub.createYourOwn': 'Create your own booking page on Bevelure →',
  'pub.agree': 'I agree to the',
  'pub.terms': 'Terms',
  'pub.privacy': 'Privacy Policy',
  'common.showMore': 'Show more',
  'common.showLess': 'Show less',
  'providerServices.descriptionOptional': 'Description (optional)',
  'providerServices.serviceDescriptionPlaceholder': 'What’s included, what to expect, prep & aftercare…',
  'providerServices.addonDescriptionPlaceholder': 'What this add-on includes…',
  'providerServices.serviceName': 'Service name',
  'providerServices.servicePlaceholder': 'e.g., Manicure',
  'providerServices.title': 'My Services',

  'schedule.adding': 'Adding…',
  'schedule.addTimeOff': 'Add time off',
  'schedule.addTimeOffHelp': 'Block out dates for vacation, sick days, breaks. Customers won’t be able to book during these periods.',
  'schedule.addTimeOffImpactHelp': 'Customers won’t be able to book new appointments in this window.',
  'schedule.addTimeOffTitle': 'Add this time off?',
  'schedule.addWorkingHours': 'Add working hours',
  'schedule.dayOfWeek': 'Day of week',
  'schedule.dayOff': 'Day off',
  'schedule.end': 'End',
  'schedule.errorEndAfterStart': 'End must be after start',
  'schedule.errorEndAfterStartTime': 'End time must be after start time',
  'schedule.errorOverlap': 'These working hours overlap an existing rule for this day',
  'schedule.errorPickStartEnd': 'Pick both start and end times',
  'schedule.failedAddRule': 'Failed to add schedule rule',
  'schedule.friShort': 'Fri',
  'schedule.friday': 'Friday',
  'schedule.monShort': 'Mon',
  'schedule.monday': 'Monday',
  'schedule.noneScheduled': 'None scheduled',
  'schedule.pastTimeOff': 'Past time off ({count})',
  'schedule.pickEnd': 'Pick end',
  'schedule.pickStart': 'Pick start',
  'schedule.reasonPlaceholder': 'Reason (optional) — vacation, training, etc.',
  'schedule.removeWorkingHoursDescription': 'will be removed from your weekly schedule. New bookings inside this window won’t be possible.',
  'schedule.removeWorkingHoursTitle': 'Remove working hours?',
  'schedule.satShort': 'Sat',
  'schedule.saturday': 'Saturday',
  'schedule.start': 'Start',
  'schedule.sunShort': 'Sun',
  'schedule.sunday': 'Sunday',
  'schedule.thuShort': 'Thu',
  'schedule.thursday': 'Thursday',
  'schedule.title': 'Schedule',
  'schedule.tueShort': 'Tue',
  'schedule.tuesday': 'Tuesday',
  'schedule.upcomingTimeOff': 'Upcoming time off',
  'schedule.wedShort': 'Wed',
  'schedule.wednesday': 'Wednesday',
  'schedule.weeklySchedule': 'Weekly schedule',

  'providers.all': 'All',
  'providers.blocked': 'Blocked',
  'providers.blockedBody': "Providers you block won't show up in search or your lists. You can unblock anytime.",
  'providers.blockedOn': 'Blocked {date}',
  'providers.browseProviders': 'Browse providers',
  'providers.countSaved': '{count} saved',
  'providers.countVisited': "{count} provider{plural} you've visited",
  'providers.emptyFavoritesBody': "Tap the heart on a provider's profile to save them here for quick access.",
  'providers.emptyVisitedBody': 'Providers you book will appear here after your first appointment.',
  'providers.favorites': 'Favorites',
  'providers.lastVisit': 'last {when}',
  'providers.nextVisit': 'Next {when}',
  'providers.nextVisitSort': 'Next visit',
  'providers.noBlocked': 'No blocked providers',
  'providers.noFavorites': 'No favorites yet',
  'providers.noFilterMatches': 'No providers match your filters.',
  'providers.noProviders': 'No providers yet',
  'providers.overallRating': 'Overall rating',
  'providers.keepTyping': 'Showing {shown} of {total} — keep typing to narrow.',
  'providers.pickServiceForPrice': 'Pick a service to price a specific procedure.',
  'providers.priceOfService': 'Price of “{service}”.',
  'providers.searchPlaceholder': 'Search by name…',
  'providers.sortMostVisits': 'Most visits',
  'providers.title': 'My Providers',
  'providers.unblock': 'Unblock',
  'providers.visits': '{count} visit{plural}',
  'providers.yearsShort': '{count} yr{plural}',
  'providers.youRated': 'You rated {rating}',

  'providerCustomers.count': '{count} customer(s)',
  'providerCustomers.last': 'last {when}',
  'providerCustomers.noBlockedCustomers': 'No blocked customers',
  'providerCustomers.noBlockedCustomersBody': 'Block a customer from their profile to stop them booking new appointments with you.',
  'providerCustomers.noCustomers': 'No customers yet',
  'providerCustomers.noCustomersBody': 'Your customers will appear here after their first appointment.',
  'providerCustomers.noSearchMatches': 'No customers match your search.',

  'messages.attachImage': 'Attach image',
  'messages.careProvider': 'Care Provider',
  'messages.customer': 'Customer',
  'messages.salonAdministrator': 'Salon administrator',
  'messages.emptyCustomer': 'Open a provider profile and tap Message to start chatting.',
  'messages.emptyProvider': 'Open a customer profile and tap Message, or wait for them to reach out.',
  'messages.failedUpload': 'Failed to upload image',
  'messages.loadOlder': 'Load older messages',
  'messages.loadingOlder': 'Loading older messages…',
  'messages.noConversations': 'No conversations yet',
  'messages.noMessagesYet': 'No messages yet. Say hi!',
  'messages.noMessagesPreview': 'No messages yet',
  'messages.photo': 'Photo',
  'messages.removeAttachment': 'Remove attachment',
  'messages.reply': 'Reply',
  'messages.replyingTo': 'Replying to {name}',
  'messages.cancelReply': 'Cancel reply',
  'messages.heart': 'Send heart',
  'messages.unheart': 'Remove heart',
  'messages.messagingUnavailable': 'Messaging is unavailable for this conversation.',
  'messages.title': 'Messages',
  'messages.typeMessage': 'Type a message…',
  'messages.viewProfile': 'View profile',

  'finance.customerCaption': "Approximate, based on the prices recorded on your appointments. Past months are realised; the current month splits done vs. still upcoming; future months are estimated from what you've already booked.",
  'finance.customerEmpty': 'Once you book or complete appointments with a price, your monthly spending will appear here.',
  'finance.customerTitle': 'Your Spending',
  'finance.done': 'done',
  'finance.explainNumbersAria': 'What do these numbers mean?',
  'finance.hideBreakdown': 'Hide breakdown',
  'finance.hidePastMonths': 'Hide past {count} month{plural}',
  'finance.noActivity': 'No activity',
  'finance.nothingToShow': 'Nothing to show yet',
  'finance.pendingConfirmation': 'Includes {amount} pending confirmation',
  'finance.providerEmpty': 'Once you have appointments with a price, your monthly earnings will appear here.',
  'finance.providerCaption': 'Approximate, based on the snapshot prices on your appointments. Past months are realised; the current month splits done vs. still upcoming; future months are estimated from already-booked appointments. Cancelled bookings are excluded.',
  'finance.providerTitle': 'Your Earnings',
  'finance.showBreakdown': 'Show breakdown',
  'finance.showPastMonths': 'Show past {count} month{plural}',
  'finance.statusCurrent': 'Current',
  'finance.statusPast': 'Past',
  'finance.statusUpcoming': 'Upcoming',
  'finance.thisMonth': 'This month',
  'finance.upcoming': 'upcoming',

  'appointmentNew.addons': 'Add-ons',
  'appointmentNew.addonsHelp': 'Optional extras — tap to add.',
  'appointmentNew.addressOptional': 'Address (optional)',
  'appointmentNew.availableDaysHelp': 'Days with available slots are marked with a dot.',
  'appointmentNew.bookingAtSalon': 'Booking at salon',
  'appointmentNew.confirmAppointment': 'Confirm Appointment',
  'appointmentNew.create': 'Create',
  'appointmentNew.createError': 'Failed to create appointment',
  'appointmentNew.creating': 'Creating…',
  'appointmentNew.careProviderName': 'Care Provider Name',
  'appointmentNew.dateTime': 'Date & Time',
  'appointmentNew.email': 'Email',
  'appointmentNew.emailPhoneSocialHelp': "Enter one of these — provider's email, phone or social link. We'll look them up.",
  'appointmentNew.enterProviderContact': "Enter the provider's email, phone or social link",
  'appointmentNew.enterProviderName': "Enter the provider's name",
  'appointmentNew.enterService': 'Enter the service (e.g. Manicure)',
  'appointmentNew.externalProviderRecord': 'Add their details to keep this visit in your own record.',
  'appointmentNew.externalProviderNotify': "Add their details and we'll notify them by {channel}.",
  'appointmentNew.findProvider': 'Find your provider',
  'appointmentNew.lookupFailed': 'Lookup failed',
  'appointmentNew.newProvider': 'New provider',
  'appointmentNew.newProviderIntro': "We couldn't find this provider on Bevelure.",
  'appointmentNew.noMatchingProviders': 'No matching providers.',
  'appointmentNew.noServices': 'No services available for this provider',
  'appointmentNew.noSlots': 'No available slots in the next 30 days',
  'appointmentNew.or': 'or',
  'appointmentNew.orFindNew': 'or find a new one',
  'appointmentNew.pickDateTime': 'Pick a date and time',
  'appointmentNew.pickDateTimeShort': 'Pick date & time',
  'appointmentNew.pickDateWithSlots': 'Pick a date with available slots above.',
  'appointmentNew.phone': 'Phone',
  'appointmentNew.priceOptional': 'Price (optional)',
  'appointmentNew.providerFound': 'Provider Found!',
  'appointmentNew.providerFoundText': 'We found {name} in our system. Please select the service.',
  'appointmentNew.providerZone': "Times shown in the provider's zone — {zone}.",
  'appointmentNew.selectService': 'Select Service',
  'appointmentNew.selectServiceHelp': 'Choose a service for this appointment',
  'appointmentNew.shareAllDesc': 'All your matching visits.',
  'appointmentNew.shareAllDescService': 'All your {service} visits.',
  'appointmentNew.shareAllTitle': 'Full history',
  'appointmentNew.shareConsent': 'I understand I am sharing this treatment history with {provider} — procedure notes, products, recommendations and result/procedure photos. My private notes and private photos are never shared.',
  'appointmentNew.shareDontDesc': 'Keep your history private.',
  'appointmentNew.shareDontTitle': "Don't share",
  'appointmentNew.shareHistory': 'Share History',
  'appointmentNew.shareHistoryHelp': 'Choose what this provider can see about your past visits.',
  'appointmentNew.shareLast5Desc': 'Your 5 most recent matching visits.',
  'appointmentNew.shareLast5DescService': 'Your 5 most recent {service} visits.',
  'appointmentNew.shareLast5Title': 'Last 5 visits',
  'appointmentNew.shareProcedureHint': 'Other providers may name the same service differently — add those names to include those visits too.',
  'appointmentNew.shareProcedures': 'Which procedures to share',
  'appointmentNew.socialMedia': 'Social media',
  'appointmentNew.stepOf': 'Step {step} of {total}',
  'appointmentNew.thisProvider': 'this provider',
  'appointmentNew.title': 'Create Appointment',
  'appointmentNew.total': 'Total',
  'appointmentNew.whereToGo': 'Where to go',
  'appointmentNew.yourProviders': 'Your providers',

  'providerAppointmentNew.adminBookTeam': 'As a salon admin you can book for your team.',
  'providerAppointmentNew.adminBookTeamOnly': 'As a salon admin you book on behalf of your team.',
  'providerAppointmentNew.bookFor': 'Book for',
  'providerAppointmentNew.bookingFor': 'Booking for',
  'providerAppointmentNew.change': 'Change',
  'providerAppointmentNew.checkWorkingHours': "Check the provider's working hours and time off.",
  'providerAppointmentNew.chooseOneService': 'Choose one of your services',
  'providerAppointmentNew.confirmDetails': 'Confirm details',
  'providerAppointmentNew.createAppointment': 'Create Appointment',
  'providerAppointmentNew.customer': 'Customer',
  'providerAppointmentNew.customerPreselected': 'Customer pre-selected. Continue to choose a service.',
  'providerAppointmentNew.enterOneContact': "Enter one of the customer's contacts — email, phone or social link.",
  'providerAppointmentNew.inviteProviderFirst': 'Invite a provider to your salon before creating an appointment.',
  'providerAppointmentNew.matchInviteHelp': "We'll match an existing Bevelure account. If they're not a member yet, we'll email them the details and an invite to join (no notification for social links).",
  'providerAppointmentNew.myself': 'Myself',
  'providerAppointmentNew.noActiveServices': 'You have no active services yet.',
  'providerAppointmentNew.noBookableProviders': 'No bookable providers in your salon yet. Invite providers from My Salon.',
  'providerAppointmentNew.noMatchingCustomers': 'No matching customers.',
  'providerAppointmentNew.noSlots': 'No available slots in the next 30 days.',
  'providerAppointmentNew.noTeamMembersMatch': 'No team members match.',
  'providerAppointmentNew.orEnterContact': 'or enter their contact',
  'providerAppointmentNew.pickDateWithSlots': 'Pick a date with available slots above.',
  'providerAppointmentNew.pickTeamMember': 'Pick which team member will perform this appointment.',
  'providerAppointmentNew.pickTeamMemberError': 'Pick which team member this appointment is for.',
  'providerAppointmentNew.pleaseSelectService': 'Please select a service',
  'providerAppointmentNew.providerFallback': 'Provider',
  'providerAppointmentNew.selected': 'Selected',
  'providerAppointmentNew.teamMemberFallback': 'team member',
  'providerAppointmentNew.title': 'New Appointment',
  'providerAppointmentNew.yourCustomers': 'Your customers',

  'providerProfile.about': 'About',
  'providerProfile.addFavorite': 'Add to favorites',
  'providerProfile.addons': 'Add-ons',
  'providerProfile.blockProvider': 'Block this provider',
  'providerProfile.bookAppointment': 'Book appointment',
  'providerProfile.certificates': 'Certificates',
  'providerProfile.fromClients': 'from clients',
  'providerProfile.message': 'Message',
  'providerProfile.noServicesListed': 'No services listed yet',
  'providerProfile.pastAppointments': 'Past appointments',
  'providerProfile.rating': 'Rating',
  'providerProfile.removeConfirm': 'Remove',
  'providerProfile.removeDescription': "They'll lose access to this salon. Past appointments stay in your salon's history. New bookings they take after this point won't be tagged with this salon anymore.",
  'providerProfile.removeFavorite': 'Remove from favorites',
  'providerProfile.removeFromSalon': 'Remove from salon',
  'providerProfile.removeTitle': 'Remove {name} from the salon?',
  'providerProfile.services': 'Services',
  'providerProfile.title': 'Provider Profile',
  'providerProfile.unblockProvider': 'Unblock this provider',
  'providerProfile.unblocking': 'Unblocking…',
  'providerProfile.unavailableTitle': 'Profile unavailable',
  'providerProfile.unavailableBody': 'This profile is no longer available. Past appointments and records remain in your history.',
  'providerProfile.visits': 'Visits',
  'providerProfile.worksAt': 'Works at',
  'providerProfile.yearsExperience': '{count} year{plural} of experience',

  'customerProfile.allergies': 'Allergies',
  'customerProfile.allergiesNoDetails': 'Has allergies (no details provided)',
  'customerProfile.blockCustomer': 'Block this customer',
  'customerProfile.book': 'Book',
  'customerProfile.careRoutine': 'Care routine',
  'customerProfile.goBack': 'Go back',
  'customerProfile.noKnownAllergies': 'No known allergies',
  'customerProfile.notFound': 'Customer not found or no shared appointments.',
  'customerProfile.unavailableTitle': 'Profile unavailable',
  'customerProfile.unavailableBody': 'This profile is no longer available, or you no longer have access to view it. Past appointments remain in your history.',
  'customerProfile.ratings': '{count} ratings',
  'customerProfile.title': 'Customer',
  'customerProfile.unblockCustomer': 'Unblock this customer',

  'externalCancel.alreadyCancelled': 'This appointment is already cancelled.',
  'externalCancel.alreadyCompleted': "This appointment is already completed — can't cancel a past visit.",
  'externalCancel.cancelledBody': 'We notified the other side. The slot is now open for someone else.',
  'externalCancel.cancelledTitle': 'Appointment cancelled',
  'externalCancel.cancelling': 'Cancelling…',
  'externalCancel.confirmHint': 'Changed your mind? Just close this page — nothing happens until you tap above.',
  'externalCancel.errorInvalid': 'Cancel link is invalid or expired.',
  'externalCancel.errorMissing': 'Cancel link is missing the token.',
  'externalCancel.errorRetry': 'Could not cancel — try again or contact the other side directly.',
  'externalCancel.intro': "You're about to cancel:",
  'externalCancel.titlePrefix': 'Cancel',
  'externalCancel.titleEmphasis': 'appointment',

  'externalFill.addDetails': 'Add the treatment details for this appointment.',
  'externalFill.errorInvalidTitle': 'Link not valid',
  'externalFill.errorInvalidBody': 'This link is invalid or has expired. Please ask the customer to resend it.',
  'externalFill.lastUpdated': 'Last updated {date}',
  'externalFill.productsPlaceholder': 'Brands, colours, materials…',
  'externalFill.saveError': "Couldn't save. Please try again.",

  'share.allergiesOnFile': 'Allergies on file',
  'share.customerAllergyFallback': 'Customer reports allergies (no specific notes).',
  'share.expired': 'This link has expired or is no longer valid.',
  'share.expires': 'Link expires {date}',
  'share.failedLoad': 'Failed to load shared history',
  'share.footer': 'Shared via {brand}. The customer can revoke this link at any time.',
  'share.fullHistory': 'Full history',
  'share.last5Visits': 'Last 5 visits',
  'share.linkUnavailable': 'Link unavailable',
  'share.noKnownAllergies': 'No known allergies on file.',
  'share.noSharedHistory': 'Could not load this shared history.',
  'share.readOnly': 'Read-only',
  'share.sharedTreatmentHistory': 'Shared treatment history',

  'appointmentDetail.add': 'Add',
  'appointmentDetail.addPhoto': 'Add photo',
  'appointmentDetail.addSelected': 'Add ({count})',
  'appointmentDetail.addToPortfolio': 'Add to portfolio',
  'appointmentDetail.addToPortfolioBody': 'Pick which photos to show on your public profile.',
  'appointmentDetail.addons': 'Add-ons',
  'appointmentDetail.allergiesOnFile': 'Allergies on file',
  'appointmentDetail.allergyFallback': 'Customer reports allergies (no specific notes).',
  'appointmentDetail.atSalon': 'at {salon}',
  'appointmentDetail.bookingRequestBody': 'Confirm to lock the slot, or decline so the customer can pick another time.',
  'appointmentDetail.bookingRequestTitle': 'Booking request — waiting on you',
  'appointmentDetail.byProvider': 'by {provider}',
  'appointmentDetail.cancelAppointment': 'Cancel appointment',
  'appointmentDetail.cancelAppointmentMessage': 'This will free up the slot and notify the other side.',
  'appointmentDetail.cancelAppointmentTitle': 'Cancel appointment?',
  'appointmentDetail.cancelRequest': 'Cancel request',
  'appointmentDetail.cancelRequestMessage': 'This will withdraw the request.',
  'appointmentDetail.cancelRequestTitle': 'Cancel booking request?',
  'appointmentDetail.captionOptional': 'Caption (optional)',
  'appointmentDetail.category': 'Category',
  'appointmentDetail.categoryPlaceholder': 'e.g. Manicure, Botox',
  'appointmentDetail.confirm': 'Confirm',
  'appointmentDetail.confirming': 'Confirming…',
  'appointmentDetail.customerAllergyInfo': 'Customer allergy info',
  'appointmentDetail.customerAllergyInfoPrefix': "See the customer's allergy notes from",
  'appointmentDetail.decline': 'Decline',
  'appointmentDetail.declineBookingMessage': 'The slot will open up for others.',
  'appointmentDetail.declineBookingTitle': 'Decline booking?',
  'appointmentDetail.declining': 'Declining…',
  'appointmentDetail.defaultProvider': 'the provider',
  'appointmentDetail.detailsSaved': 'Details saved',
  'appointmentDetail.failedConfirm': 'Failed to confirm',
  'appointmentDetail.failedDecline': 'Failed to decline',
  'appointmentDetail.fullHistory': 'Full history',
  'appointmentDetail.inProgressNow': 'In progress now',
  'appointmentDetail.keepIt': 'Keep it',
  'appointmentDetail.last5Visits': 'Last 5 visits',
  'appointmentDetail.minutes': '{count} min',
  'appointmentDetail.myPersonalNotes': 'My personal notes',
  'appointmentDetail.myPersonalNotesHelp': 'Only you can see this. Useful for jotting down how you felt, side effects, etc.',
  'appointmentDetail.myPersonalNotesPlaceholder': 'Your private notes about this appointment…',
  'appointmentDetail.noCompletedShared': 'No completed appointments of this type yet.',
  'appointmentDetail.noPhotosYet': 'No photos yet.',
  'appointmentDetail.noProcedureDetails': 'No procedure details recorded.',
  'appointmentDetail.notesSaved': 'Notes saved',
  'appointmentDetail.photos': 'Photos',
  'appointmentDetail.private': 'Private',
  'appointmentDetail.privatePhotosHelp': 'only you can see these',
  'appointmentDetail.privatePortfolioHelp': 'Saved to your portfolio but hidden until you toggle it on.',
  'appointmentDetail.procedureNotes': 'Procedure notes',
  'appointmentDetail.procedureNotesPlaceholder': 'What was done during the procedure…',
  'appointmentDetail.procedurePhotosHelp': 'receipts, products, what was applied',
  'appointmentDetail.productsUsed': 'Products used',
  'appointmentDetail.providerOnlyNotes': 'Provider-only notes',
  'appointmentDetail.providerOnlyNotesHelp': 'Visible only to providers working on the appointment, not to the client.',
  'appointmentDetail.providerOnlyNotesPlaceholder': 'Internal appointment context, special considerations, provider follow-up…',
  'appointmentDetail.public': 'Public',
  'appointmentDetail.publicPortfolioHelp': 'Visible on your public profile to clients.',
  'appointmentDetail.rateCustomer': 'Rate the customer',
  'appointmentDetail.rateProvider': 'Rate the provider',
  'appointmentDetail.readOnly': 'Read-only',
  'appointmentDetail.recommendationsPlaceholder': 'Recommendations / aftercare',
  'appointmentDetail.recordCount': '{count} record{plural}',
  'appointmentDetail.removePhoto': 'Remove',
  'appointmentDetail.removePhotoMessage': 'This photo will be permanently deleted.',
  'appointmentDetail.removePhotoTitle': 'Remove photo?',
  'appointmentDetail.resultPhotos': 'Result photos',
  'appointmentDetail.saveDetails': 'Save details',
  'appointmentDetail.saveNotes': 'Save notes',
  'appointmentDetail.sharedHistoryTitle': '{name} shared their history',
  'appointmentDetail.shortDescription': 'Short description',
  'appointmentDetail.tapStar': 'Tap a star to submit your rating.',
  'appointmentDetail.theirProfile': 'their profile',
  'appointmentDetail.title': 'Appointment',
  'appointmentDetail.total': 'Total',
  'appointmentDetail.waitingForProviderBody': "You'll get a notification once they accept or decline. You can cancel this request anytime.",
  'appointmentDetail.waitingForProviderTitle': 'Waiting for {provider} to confirm',
  'appointmentDetail.yourTime': '{time} your time ({zone})',

  'auth.alreadyHaveAccount': 'Already have an account?',
  'auth.backToBevelure': 'Back to Bevelure',
  'auth.backToLogin': 'Back to log in',
  'auth.checkInbox': 'Check your inbox',
  'auth.confirmEmail': 'Confirm *email*',
  'auth.confirmEmailLoading': 'Confirming your email…',
  'auth.createAccount': 'Create account',
  'auth.createAccountTitle': 'Create *account*',
  'auth.email': 'Email',
  'auth.emailConfirmed': 'Email confirmed',
  'auth.emailConfirmedBody': "Thanks — your email is verified. You're all set.",
  'auth.enterEmailPassword': 'Enter your email and password',
  'auth.forgotPassword': 'Forgot *password*',
  'auth.forgotPasswordCta': 'Forgot password?',
  'auth.forgotPasswordSubtitle': "We'll email you a link to set a new one.",
  'auth.goToLogin': 'Go to log in',
  'auth.invalidLogin': 'Invalid email or password',
  'auth.login': 'Log in',
  'auth.loginButton': 'Log In',
  'auth.loginSubtitle': 'Log in to continue your beauty journey',
  'auth.loggingIn': 'Logging in…',
  'auth.newPassword': 'New password',
  'auth.newToBevelure': 'New to Bevelure?',
  'auth.password': 'Password',
  'auth.passwordMismatch': "Passwords don't match",
  'auth.passwordMin': 'Password must be at least 8 characters',
  'auth.passwordUpdated': 'Password updated',
  'auth.passwordUpdatedBody': 'Log in with your new password. Other devices will need to log in again too.',
  'auth.rememberNow': 'Remember it now?',
  'auth.repeatPassword': 'Repeat the new password',
  'auth.resetInvalid': 'Reset link is invalid or expired',
  'auth.resetLinkMissing': 'Reset link is missing — request a new one.',
  'auth.resetPassword': 'Reset *password*',
  'auth.resetPasswordSubtitle': 'Pick something strong — at least 8 characters.',
  'auth.sendResetLink': 'Send reset link',
  'auth.sending': 'Sending…',
  'auth.sentResetBody': 'If an account exists for {email}, we just sent a reset link. It is good for 1 hour.',
  'auth.signupChooseType': 'Choose your account type to get started',
  'auth.signupCustomerBody': 'Track your beauty appointments and procedures',
  'auth.signupCustomerTitle': "I'm a Customer",
  'auth.signupProviderBody': 'Manage your clients and appointments',
  'auth.signupProviderTitle': "I'm a Care Provider",
  'auth.signupSalonBody': 'Register your business and invite your team',
  'auth.signupSalonTitle': "We're a Salon / Studio",
  'signup.acceptLegal': 'Please confirm that you are at least 18 and accept the Terms and Privacy Policy.',
  'signup.aboutYou': 'About you',
  'signup.aboutYouSubtitle': 'Basic personal information',
  'signup.accountSubtitle': "How you'll sign in",
  'signup.allergies': 'Allergies',
  'signup.allergiesQuestion': 'Do you have any allergic reactions?',
  'signup.allergiesSubtitle': 'This helps your care providers keep you safe. You can update this anytime from your profile.',
  'signup.allergyPlaceholder': 'Describe your allergies...',
  'signup.birthDatePlaceholder': 'Select your birth date',
  'signup.certificates': 'Certificates',
  'signup.certificatesFileHint': 'PDF, JPG, PNG — up to 10MB each',
  'signup.certificatesLater': 'No worries — you can upload them later from your profile.',
  'signup.certificatesQuestion': 'Do you have any certificates to share?',
  'signup.certificatesRequired': 'Please answer the certificates question.',
  'signup.certificatesSubtitle': 'Show clients your professional credentials',
  'signup.certificatesUpload': 'Click to upload certificates',
  'signup.completeRegistration': 'Complete registration',
  'signup.confirmPassword': 'Confirm password',
  'signup.contact': 'Contact',
  'signup.createSalon': 'Create salon',
  'signup.creatingAccount': 'Creating account…',
  'signup.creatingSalon': 'Creating salon…',
  'signup.customerTitle': 'Customer *sign up*',
  'signup.dateOfBirth': 'Date of birth',
  'signup.firstName': 'First name',
  'signup.invalidEmail': 'Please enter a valid email address.',
  'signup.invalidSalonEmail': 'Please enter a valid salon email or leave it blank.',
  'signup.languagesYouSpeak': 'Languages you speak',
  'signup.lastName': 'Last name',
  'signup.legalPrefix': "I confirm that I am at least 18 and agree to Bevelure's",
  'signup.missingCity': 'Please provide your city.',
  'signup.missingCountry': 'Please provide your country.',
  'signup.missingAddress': 'Please provide your address.',
  'signup.missingDateOfBirth': 'Please provide your date of birth.',
  'signup.minimumAge': 'You must be at least 18 years old to create an account.',
  'signup.missingEmail': 'Please provide your email.',
  'signup.missingFirstName': 'Please provide your first name.',
  'signup.missingLastName': 'Please provide your last name.',
  'signup.missingPassword': 'Please provide a password.',
  'signup.missingServiceType': 'Please provide your service type.',
  'signup.missingSex': 'Please provide your sex.',
  'signup.missingYearsInService': 'Please provide your years in service.',
  'signup.passwordPlaceholder': 'Minimum 8 characters',
  'signup.profession': 'Profession',
  'signup.professionSubtitle': 'What you offer',
  'signup.providerTitle': 'Care provider *sign up*',
  'signup.repeatPasswordPlaceholder': 'Repeat password',
  'signup.required': 'Required',
  'signup.salonAdminSubtitle': 'The salon administrator account',
  'signup.salonContactSubtitle': 'Optional — shown on the salon profile',
  'signup.salonMissingName': 'Please give your salon a name.',
  'signup.salonNamePlaceholder': 'e.g., Velvet Beauty Studio',
  'signup.salonTitle': 'Salon *sign up*',
  'signup.selectOption': 'Please select an option.',
  'signup.serviceTypePlaceholder': 'e.g., nail technician, esthetician',
  'signup.sexRequired': 'Please select your sex',
  'signup.signupFailed': 'Signup failed',
  'signup.stepOf2': 'Step {step} of 2',
  'signup.whereYouAre': 'Where you are',
  'signup.whereYouAreSubtitle': 'Used to find local providers',
  'signup.whereYouWork': 'Where you work',
  'signup.whereYouWorkSubtitle': 'Helps clients find you',
  'signup.yearsInService': 'Years in service',
  'signup.yearsPlaceholder': 'e.g., 5',
  'signup.yourSalon': 'Your salon',
  'signup.yourSalonSubtitle': 'This is what clients and your team will see',

  'landing.nav.platform': 'Platform',
  'landing.nav.features': 'Features',
  'landing.nav.story': 'Story',
  'landing.nav.trust': 'Trust',
  'landing.nav.startNow': 'Start now',
  'landing.nav.openMenu': 'Open menu',
  'landing.cta.startJourney': 'Start your journey',
  'landing.stores.appStoreEyebrow': 'Download on the',
  'landing.stores.appStoreName': 'App Store',
  'landing.stores.googlePlayEyebrow': 'Get it on',
  'landing.stores.googlePlayName': 'Google Play',
  'landing.appBanner.subtitle': 'Open in the Bevelure app',
  'landing.appBanner.action': 'OPEN',
  'landing.appBanner.close': 'Dismiss app banner',
  'landing.panel.title': 'Beauty record',
  'landing.panel.visits': 'visits',
  'landing.panel.providers': 'providers',
  'landing.panel.rating': 'rating',
  'landing.panel.row1Label': 'Allergies & care routine',
  'landing.panel.row1Meta': 'Shared before you arrive',
  'landing.panel.row2Label': 'Hydrafacial treatment',
  'landing.panel.row2Meta': 'Completed · photos attached',
  'landing.panel.row3Label': 'Message from Maya',
  'landing.panel.row3Meta': 'Aftercare for your Hydrafacial · 1h ago',
  'landing.hero.eyebrow': 'Your beauty history, finally yours',
  'landing.hero.body': 'One quietly beautiful record that belongs to you and follows you from provider to provider — your story, ready to share with whoever you see next.',
  'landing.hero.tile1Title': 'Yours',
  'landing.hero.tile1Body': 'a record that travels with you',
  'landing.hero.tile2Title': 'Prepared',
  'landing.hero.tile2Body': 'context before every client',
  'landing.hero.tile3Title': 'In sync',
  'landing.hero.tile3Body': 'your whole team, one calendar',
  'landing.trustBar.item1': 'A history that’s yours',
  'landing.trustBar.item2': 'Context before every client',
  'landing.trustBar.item3': 'One calendar for the whole team',
  'landing.three.eyebrow': 'One record, three sides',
  'landing.three.title': 'The same record. Seen three ways.',
  'landing.three.body': 'Beauty care connects three people who rarely share the same notes. Bevelure gives them one source of truth — and lets each see exactly the part that matters to them.',
  'landing.three.side1Tag': 'For you',
  'landing.three.side1Title': 'Your full history — yours to keep, yours to share.',
  'landing.three.side1Desc': 'Every visit, product, and photo in one place that belongs to you and follows you anywhere.',
  'landing.three.side2Tag': 'For your provider',
  'landing.three.side2Title': 'Everything they need to know, before you sit down.',
  'landing.three.side2Desc': 'The context you choose to share — allergies, past treatments, what worked — ready the moment you arrive.',
  'landing.three.side3Tag': 'For the salon',
  'landing.three.side3Title': 'Every booking and every member, in one calm place.',
  'landing.three.side3Desc': 'The whole team, the calendar, and the bookings under one roof — coordinated, never chaotic.',
  'landing.audiences.eyebrow': 'One system, every side of care',
  'landing.audiences.title': 'What each side actually gets.',
  'landing.audiences.clientTag': 'For clients',
  'landing.audiences.clientTitle': 'Your history, finally yours.',
  'landing.audiences.clientLead': 'Every treatment you’ve ever had, in one place that travels with you. No more starting over with each new provider, no more trying to remember what worked.',
  'landing.audiences.clientPoint1': 'Own a complete record — visits, products, photos, prices.',
  'landing.audiences.clientPoint2': 'Your allergies and sensitivities, surfaced to every provider automatically.',
  'landing.audiences.clientPoint3': 'Share your last five visits or one treatment type — and revoke it anytime.',
  'landing.audiences.clientPoint4': 'See what you actually spend on yourself, month by month.',
  'landing.audiences.clientCta': 'Start your record',
  'landing.audiences.providerTag': 'For providers',
  'landing.audiences.providerTitle': 'Know your client before they sit down.',
  'landing.audiences.providerLead': 'Open an appointment and the context is already there — what you did last time, what they reacted to, what they use at home. Less guessing, safer work, and a client book that’s truly yours.',
  'landing.audiences.providerPoint1': 'See the history a client chose to share — before they arrive.',
  'landing.audiences.providerPoint2': 'Your notes, products, and result photos persist between every visit.',
  'landing.audiences.providerPoint3': 'Set your services, add-ons, hours, and prices once.',
  'landing.audiences.providerPoint4': 'Message clients without handing over your number.',
  'landing.audiences.providerCta': 'Open a provider account',
  'landing.audiences.salonTag': 'For salons',
  'landing.audiences.salonTitle': 'Your whole salon, in sync.',
  'landing.audiences.salonLead': 'Bring the team, the calendar, and every booking under one roof — with oversight where you need it and no one stepping on anyone’s craft.',
  'landing.audiences.salonPoint1': 'Invite and manage your whole team in minutes.',
  'landing.audiences.salonPoint2': 'See every booking under your roof, across all providers.',
  'landing.audiences.salonPoint3': 'Coordinate without overreach — admins never touch a provider’s services.',
  'landing.audiences.salonPoint4': 'No double-bookings, no missed handoffs.',
  'landing.audiences.salonCta': 'Register your salon',
  'landing.features.eyebrow': 'Everything, in one place',
  'landing.features.title': 'The things you wish someone had been keeping track of all along.',
  'landing.features.body': 'Your history comes first — but Bevelure quietly handles everything around it too: booking, the conversation with your provider, the prices, the photos, and the visits you logged somewhere else.',
  'landing.features.item1Title': 'A record that’s truly yours',
  'landing.features.item1Desc': 'Every visit, product, note, and result photo in one timeline that moves with you — so changing providers never means starting from zero.',
  'landing.features.item2Title': 'Booking, the easy way',
  'landing.features.item2Desc': 'See real prices and add-ons, pick a slot. Or log a visit with a salon that isn’t on Bevelure yet — it still becomes part of your history.',
  'landing.features.item3Title': 'You decide who sees what',
  'landing.features.item3Desc': 'Share your last five visits or one type of treatment with a new provider — then take that access back the moment you want to.',
  'landing.features.item4Title': 'Before, after, and just for you',
  'landing.features.item4Desc': 'Result photos to look back on, procedure photos for your provider, and private photos only you can see — never mixed up.',
  'landing.features.item5Title': 'Wherever you book',
  'landing.features.item5Desc': 'Provider in Miami, you in Berlin — the salon’s time and yours, side by side. Any city, any currency, no missed appointments.',
  'landing.features.item6Title': 'Talk it through',
  'landing.features.item6Desc': 'Message before and after a visit — clarify a detail, ask about aftercare, send a photo — without ever sharing your phone number.',
  'landing.features.item7Title': 'Honest prices, kept honest',
  'landing.features.item7Desc': 'Real prices fixed to each visit, in the currency your provider charges — your history never quietly rewrites itself later.',
  'landing.features.item8Title': 'Private, two-way ratings',
  'landing.features.item8Desc': 'Rate a visit for your own memory — and providers can too. No public leaderboard, no pressure, just an honest note to yourself.',
  'landing.founder.eyebrow': 'Founder story',
  'landing.founder.title': 'Why I believe in Bevelure.',
  'landing.founder.p1': 'I built Bevelure because I could never remember my own treatments — what was done, when, and which products were used. Every time I went to see a new specialist, the questions were the same: *what did you have done last time, which products were used, and did you have any allergic reactions?* I would try to remember, and I could never give a solid answer.',
  'landing.founder.p2': 'What usually came out was something like: *“I remember the result, but I don’t remember the exact treatment name, the products, or whether anything irritated my skin.”* Not very helpful. I always wished I had it all written down somewhere — the exact products, the details of every appointment — instead of carrying it around in my head.',
  'landing.founder.p3': 'That is exactly what Bevelure is: you keep the record of everything in one place, and you can share it with any provider you choose.',
  'landing.founder.p4': 'My beauty appointments were also a beautiful mess in my calendar. As time went on, I had to scroll forever to find when I did what — and when I was due to book again. Now you can see them all in one place: your beauty history.',
  'landing.founder.p5': 'I travel a lot, so most of the time I have to see new providers just to keep up with my treatments — and it is always the same story. I go in for a facial, and the question never changes: *what home care do you use, and what are the main ingredients in your creams?* With Bevelure you simply don’t have to think about it: upload photos of your home-care routine with labels and ingredients, then share it when you need to.',
  'landing.founder.p6': 'I think so many people experience exactly these situations and find themselves in the same spot — so I truly want to help them. That is why I built Bevelure.',
  'landing.founder.quote': '“For every beauty story that deserves to be remembered. With care.”',
  'landing.founder.signature': 'Ievgeniia Andrieieva, Founder & CEO',
  'landing.trust.eyebrow': 'Trust layer',
  'landing.trust.title': 'Built for the things that matter most — and are hardest to get back.',
  'landing.trust.body': 'Sensitive information, real appointments, relationships that last for years. It feels effortless on the surface because the careful boundaries underneath are quietly holding everything together.',
  'landing.trust.item1Title': 'Privacy that’s built in',
  'landing.trust.item1Desc': 'Your private notes, your provider’s internal notes, and what you choose to share are all kept apart — each visible only to the right person.',
  'landing.trust.item2Title': 'Stay in touch, simply',
  'landing.trust.item2Desc': 'Chat, confirmations, and gentle nudges keep you and your provider on the same page — so nothing important slips through.',
  'landing.trust.item3Title': 'No public scoreboard',
  'landing.trust.item3Desc': 'Rate a visit privately, for your own memory. Beauty care should never turn into a leaderboard everyone can see.',
  'landing.final.title': 'Your beauty story, finally in one place.',
  'landing.final.body': 'Whether you’re booking, doing the work, or running the room — it all lives in one beautiful, private record.',
  'landing.footer.tagline': 'Your beauty story, in one place.',
  'landing.footer.contact': 'Contact us',
  'landing.footer.site': 'Site',

  'block.title': 'Block {name}?',
  'block.body': 'They will not be able to book or accept new appointments with you. Past appointments and ratings stay visible to both sides.',
  'block.reasonLabel': 'Reason (only you can see this)',
  'block.reasonPlaceholder': 'Optional — helpful if you ever review your blocks later',
  'block.reportUser': 'Also report this user',
  'block.reportHelp': 'Sends the account and selected reason to Bevelure for review. The other user will not see your report.',
  'block.reportReasonLabel': 'Reason for report',
  'block.reportReasonPlaceholder': 'Choose a reason',
  'block.reportReasonRequired': 'Choose a reason before reporting.',
  'block.reportReasonSpam': 'Spam or scam',
  'block.reportReasonHarassment': 'Harassment or threats',
  'block.reportReasonContent': 'Inappropriate content',
  'block.reportReasonSafety': 'Unsafe behavior',
  'block.reportReasonImpersonation': 'Impersonation',
  'block.reportReasonOther': 'Other',
  'block.blockAndReport': 'Block and report',
  'block.cancelFutureOne': 'Also cancel {count} upcoming appointment between you.',
  'block.cancelFutureMany': 'Also cancel {count} upcoming appointments between you.',
  'block.keepFutureHint': 'Leaving this off keeps them on both calendars — the block only stops new bookings.',
  'block.failed': 'Failed to block',
  'block.blocking': 'Blocking…',
  'block.block': 'Block',

  'shareModal.titlePrefix': 'Share',
  'shareModal.titleEmphasis': 'history',
  'shareModal.subtitle': 'Generate a read-only link for a care provider.',
  'shareModal.procedureTypes': 'Procedure types',
  'shareModal.procedurePlaceholder': 'e.g. Men Haircut, Manicure…',
  'shareModal.procedureHelp': 'Pick from your procedures or type your own. Only completed appointments matching these will be visible.',
  'shareModal.whatToShare': 'What to share',
  'shareModal.recipientEmail': 'Recipient email',
  'shareModal.optional': 'optional',
  'shareModal.emailHelp': 'If set, the link is sent by email. Otherwise just generate a QR code / link.',
  'shareModal.consent': 'I understand that anyone with this link can view the treatment history I am sharing for the selected procedures — procedure notes, products, recommendations and result/procedure photos. My private notes and private photos are never shared.',
  'shareModal.createError': 'Failed to create share link. Try again.',
  'shareModal.generateLink': 'Generate link',
  'shareModal.emailIt': 'Email it',
  'shareModal.scanHint': 'Scan with a phone camera to open the shared history.',
  'shareModal.link': 'Link',
  'shareModal.copyLink': 'Copy link',
  'shareModal.procedure': 'Procedure',
  'shareModal.mode': 'Mode',
  'shareModal.expires': 'Expires',
  'shareModal.sentTo': 'Sent to',
  'shareModal.generateAnother': 'Generate another',

  'contactSetup.title': 'Make yourself easier to find',
  'contactSetup.bodyPrefix': 'Add your',
  'contactSetup.socialLink': 'social link',
  'contactSetup.bodySuffix': 'so people can find you by them, not just your email.',
  'contactSetup.addContactInfo': 'Add contact info',

  'errorBoundary.titlePrefix': 'Something went',
  'errorBoundary.titleEmphasis': 'wrong',
  'errorBoundary.body': 'We hit an unexpected error rendering this page. Refresh to try again — if it keeps happening, please let us know.',
  'errorBoundary.refresh': 'Refresh page',
  'errorBoundary.dismiss': 'Dismiss and continue',

  'confirm.defaultTitle': 'Are you sure?',
  'impact.futureBookingOne': '{count} future booking will be affected.',
  'impact.futureBookingMany': '{count} future bookings will be affected.',
  'impact.cancelAffectedOn': 'All of them will be cancelled and customers will be notified.',
  'impact.cancelAffectedOff': 'Leave this off to keep them as scheduled — only new bookings are blocked.',
  'impact.actionFailed': 'Action failed',
  'impact.working': 'Working…',

  'desktopHome.settings': 'Home settings',
  'desktopHome.layoutTitle': 'Home layout',
  'desktopHome.layout1': 'Split — Upcoming & Recent next to calendar',
  'desktopHome.layout2': 'Compact greeting with stacked lists',
  'desktopHome.layout3': 'Stats column · calendar · lists below',
  'desktopHome.layout4': 'Scroll lists fill calendar height',
  'auth.tryAgain': 'Something went wrong — try again',
  'auth.updatePassword': 'Update password',
  'auth.updating': 'Updating…',
  'auth.verificationInvalid': 'Verification link is invalid or expired.',
  'auth.verificationMissing': 'Verification link is missing the token.',
  'auth.verifyErrorTitle': "Couldn't verify",
  'auth.verifyResendHint': 'Sign in to your account and tap Resend on the verification banner.',
  'auth.welcomeBack': 'Welcome *back*',
  'notif.providerRequest.title': 'New booking request',
  'notif.providerRequest.body': 'A customer requested an appointment. Confirm or decline.',
  'notif.providerScheduled.title': 'New appointment',
  'notif.providerScheduled.body': 'A new appointment was scheduled for you.',
  'notif.customerCreated.title': 'Appointment scheduled for you',
  'notif.customerCreated.body': '{provider} booked {service} for you.',
  'notif.customerConfirmed.title': 'Appointment confirmed',
  'notif.customerConfirmed.body': '{provider} confirmed your {service}.',
  'notif.customerRejected.title': 'Booking request declined',
  'notif.customerRejected.body': '{provider} couldn’t accept your {service}. Pick another time.',
  'notif.cancelled.title': 'Appointment cancelled',
  'notif.cancelled.body': 'Your {service} with {other} was cancelled.',
  'notif.customerExpired.title': 'Booking request expired',
  'notif.customerExpired.body': 'Your {service} request expired — not confirmed in time. Pick another slot.',
  'notif.detailsUpdated.title': 'Treatment details updated',
  'notif.detailsUpdated.body': '{provider} added new notes or photos to your {service}.',
  'notif.reminderCustomer.title': 'Appointment in 48 hours',
  'notif.reminderCustomer.body': 'Your {service} with {other} is in about 48 hours.',
  'notif.reminderProvider.title': 'Appointment in 48 hours',
  'notif.reminderProvider.body': '{service} with {other} is in about 48 hours.',
  'notif.salonInvite.title': 'Salon invitation',
  'notif.salonInvite.body': '{salon} invited you to join their team.',
  'notif.memberRemoved.title': 'Removed from salon',
  'notif.memberRemoved.body': 'The admin removed you from {salon}.',
  'notif.memberLeft.title': 'Provider left your salon',
  'notif.memberLeft.body': '{provider} left the team.',
  'notif.inviteAccepted.title': 'Provider joined your salon',
  'notif.inviteAccepted.body': '{provider} accepted your invite and joined the team.',
  'notif.inviteDeclined.title': 'Invite declined',
  'notif.inviteDeclined.body': '{provider} declined your salon invite.',
  'notif.salonBookingRequested.title': 'New booking request in your salon',
  'notif.salonBookingRequested.body': '{customer} requested {service} with {provider} — confirmation pending.',
  'notif.salonBooking.title': 'New booking in your salon',
  'notif.salonBooking.body': '{provider} has a new appointment with {customer}.',
  'notif.salonBookingCancelled.title': 'Booking cancelled in your salon',
  'notif.salonBookingCancelled.body': '{provider}’s appointment with {customer} was cancelled.',
  'auth.yourPassword': 'Your password',

  'nav.navigation': 'Navigation',
  'nav.account': 'Account',
  'nav.menu': 'Menu',
  'nav.home': 'Home',
  'nav.search': 'Search',
  'nav.searchProviders': 'Search providers',
  'nav.favorites': 'Favorites',
  'nav.messages': 'Messages',
  'nav.myCareHub': 'My Care Hub',
  'nav.spending': 'Spending',
  'nav.profile': 'Profile',
  'nav.history': 'History',
  'nav.upcoming': 'Upcoming',
  'nav.customers': 'Customers',
  'nav.team': 'Team',
  'nav.schedule': 'Schedule',
  'nav.services': 'Services',
  'nav.portfolio': 'Portfolio',
  'nav.mySalon': 'My Salon',
  'nav.earnings': 'Earnings',
  'nav.pending': 'Pending',
  'nav.signOut': 'Sign Out',
  'nav.terms': 'Terms',
  'nav.privacy': 'Privacy',
  'nav.notifications': 'Notifications',
  'nav.salonInviteTitle': '{count} pending salon invite{plural}',

  'notifications.allCaughtUp': "You're all caught up",
  'notifications.allLoaded': 'That’s all {count} notifications.',
  'notifications.empty': 'No notifications yet',
  'notifications.markAsRead': 'Mark as read',
  'notifications.title': 'Notifications',

  'salonTeam.inviteFromPrefix': 'Invite providers from',
  'salonTeam.manageInvitesPrefix': 'Manage invites from',
  'salonTeam.noProviders': 'No providers yet',
  'salonTeam.noSearchMatches': 'No team members match your search.',
  'salonTeam.providerCount': '{count} provider(s)',
  'salonTeam.searchPlaceholder': 'Search by name or service…',
  'salonTeam.sortName': 'Name (A-Z)',
  'salonTeam.sortRecentlyJoined': 'Recently joined',

  'salon.accept': 'Accept',
  'salon.activeProviders': '{count} active provider{plural}',
  'salon.address': 'Address',
  'salon.addressPlaceholder': 'Start typing your address…',
  'salon.admin': 'Admin',
  'salon.city': 'City',
  'salon.close': 'Close',
  'salon.country': 'Country',
  'salon.decline': 'Decline',
  'salon.description': 'Description',
  'salon.descriptionPlaceholder': 'Tell clients what makes your salon special',
  'salon.desktopTitleEmphasis': 'salon',
  'salon.desktopTitlePrefix': 'My',
  'salon.editDetails': 'Edit details',
  'salon.invitation': 'Salon invitation',
  'salon.invitationSent': 'Invitation sent.',
  'salon.inviteProvider': 'Invite a provider',
  'salon.inviteProviderHelp': "They must already have a Bevelure care-provider account. We'll notify them to accept.",
  'salon.invitedAs': 'Invited as {role}',
  'salon.leaveDescription': "You'll stop being part of this salon. New bookings you take after this point won't be tagged with this salon. Past appointments stay in your history.",
  'salon.leaveSalon': 'Leave salon',
  'salon.leaveTitle': 'Leave {name}?',
  'salon.name': 'Salon name',
  'salon.noPendingInvites': 'No invitations are waiting. Active team members live on {team}.',
  'salon.notPartBody': 'Salons invite providers by email. If a salon invites you, the invitation will appear here.',
  'salon.notPartTitle': "You're not part of a salon",
  'salon.pendingInvites': 'Pending invites',
  'salon.phone': 'Phone',
  'salon.phonePlaceholder': 'Salon phone number',
  'salon.postalCode': 'Postal code',
  'salon.providerEmail': 'Provider email',
  'salon.providerFallback': 'Provider',
  'salon.publicEmail': 'Public email',
  'salon.removeMember': 'Remove member',
  'salon.revokeInvite': 'Revoke invite',
  'salon.roleAdmin': 'admin',
  'salon.roleMember': 'member',
  'salon.saveDetailsError': 'Could not save salon details',
  'salon.sendInvite': 'Send invite',
  'salon.sending': 'Sending…',
  'salon.socialLink': 'Website / social link',
  'salon.subtitle': 'Manage your salon and team.',
  'salon.teamLink': 'Team',
  'salon.title': 'My Salon',
  'salon.uploadLogo': 'Upload logo',
  'salon.view': 'View',
  'salon.viewProfile': 'View salon profile',
  'salon.youWorkAt': 'You work at',

  'teamBookings.all': 'All',
  'teamBookings.history': 'History',
  'teamBookings.noBookings': 'No bookings here yet.',
  'teamBookings.subtitle': "Every appointment across your salon's providers.",
  'teamBookings.title': 'Team bookings',
  'teamBookings.titleEmphasis': 'bookings',
  'teamBookings.titlePrefix': 'Team',
  'teamBookings.upcoming': 'Upcoming',
  'salonAppointments.cancelReason': 'Cancelled by salon admin',

  'salonProfile.messageSalon': 'Message salon',
  'salonProfile.noProviders': 'No providers listed yet.',
  'salonProfile.openingChat': 'Opening chat…',
  'salonProfile.ourTeam': 'Our team',
  'salonProfile.provider': 'provider',
  'salonProfile.providers': 'providers',
  'salonProfile.title': 'Salon',

  'portfolio.addMore': 'Add more',
  'portfolio.addPhoto': 'Add photo',
  'portfolio.addToPortfolio': 'Add to portfolio',
  'portfolio.bulkApplyHint': 'Category & caption apply to all {count} photos.',
  'portfolio.caption': 'Caption',
  'portfolio.captionOptional': 'Caption (optional)',
  'portfolio.captionPlaceholder': 'Short description shown under the photo',
  'portfolio.category': 'Category',
  'portfolio.categoryCount': '{count} categor{plural}',
  'portfolio.categoryPlaceholder': 'e.g. Manicure, Botox, Hydrafacial',
  'portfolio.choosePhotos': 'Tap to choose photos',
  'portfolio.deletePhotoMessage': 'It will be removed from your public portfolio.',
  'portfolio.deletePhotoTitle': 'Delete photo?',
  'portfolio.desktopTitleEmphasis': 'portfolio',
  'portfolio.desktopTitlePrefix': 'My',
  'portfolio.editTitle': 'Edit portfolio item',
  'portfolio.fileHint': 'JPG, PNG up to 10MB · pick several',
  'portfolio.hide': 'Hide',
  'portfolio.noFiles': 'No files',
  'portfolio.noItemsBody': 'Show off your work — clients see this on your public profile. Upload directly or promote photos from completed appointments.',
  'portfolio.noItemsTitle': 'No portfolio items yet',
  'portfolio.noItemsYet': 'No items yet',
  'portfolio.private': 'Private',
  'portfolio.privateDescription': 'Saved to your portfolio but hidden from clients.',
  'portfolio.public': 'Public',
  'portfolio.publicDescription': 'Visible on your public profile to anyone who opens it.',
  'portfolio.remove': 'Remove',
  'portfolio.shortDescription': 'Short description',
  'portfolio.show': 'Show',
  'portfolio.title': 'Portfolio',
  'portfolio.uncategorized': 'Uncategorized',
  'portfolio.upload': 'Upload',
  'portfolio.uploadCount': 'Upload ({count})',
  'portfolio.uploading': 'Uploading…',

  'emailVerify.resendError': 'Could not resend — try again in a moment.',
  'emailVerify.sent': 'Email sent — check your inbox.',
  'emailVerify.message': 'Confirm your email so booking notifications reach you.',
  'emailVerify.sending': 'Sending…',
  'emailVerify.resend': 'Resend',

  'providerSetup.title': 'Finish setting up your profile',
  'providerSetup.bodyPrefix': "Clients can't book you until you add",
  'providerSetup.oneService': 'at least one service',
  'providerSetup.workingHours': 'your working hours',
  'providerSetup.addService': 'Add a service',
  'providerSetup.setHours': 'Set working hours',

  'profile.title': 'Profile',
  'profile.preferences': 'Preferences',
  'profile.language': 'Language',
  'profile.pushNotifications': 'Push notifications',
  'profile.languageAria': 'Interface language',
  'profile.providerLanguages': 'Languages you speak',
  'profile.providerLanguagesLabel': 'Languages',
  'profile.currencyLabel': 'Currency',
  'profile.failedUpdate': 'Failed to update profile',
  'profile.roleCustomer': 'Customer',
  'profile.roleCareProvider': 'Care Provider',
  'profile.roleSalonAdmin': 'Salon Administrator',
  'profile.uploadPhoto': 'Upload photo',
  'profile.rating': 'Rating',
  'profile.ratingSingular': 'rating',
  'profile.ratingPlural': 'ratings',
  'profile.fromClients': 'from clients',
  'profile.fromProviders': 'from providers',
  'profile.byService': 'By service',
  'profile.noRatingsCustomer': 'No ratings yet. Providers can rate you after a completed appointment.',
  'profile.noRatingsProvider': 'No ratings yet. Clients can rate you after a completed appointment.',
  'profile.personalInformation': 'Personal information',
  'profile.dateOfBirth': 'Date of birth',
  'profile.sex': 'Sex',
  'profile.location': 'Location',
  'profile.address': 'Address',
  'profile.addressPlaceholder': 'Start typing your address...',
  'profile.timeZone': 'Time zone',
  'profile.timeZonePrefix': 'Time zone: {zone}',
  'profile.timeZoneHelp': 'Your working hours are in this zone. Clients booking from elsewhere also see their own time.',
  'profile.contact': 'Contact',
  'profile.phone': 'Phone',
  'profile.social': 'Social',
  'profile.socialMediaLink': 'Social media link',
  'profile.socialPlaceholder': 'https://instagram.com/your-handle',
  'profile.contactHelp': 'So clients can find and book you by phone or social link. Phone is saved in a standard international format — include your country code.',
  'profile.noContactDetails': 'No contact details yet.',
  'profile.allergies': 'Allergies',
  'profile.noAllergies': 'No allergies',
  'profile.hasAllergies': 'I have allergies',
  'profile.notes': 'Notes',
  'profile.allergyPlaceholder': 'Latex, fragrances, etc.',
  'profile.allergiesNoDetails': 'Has allergies (no details)',
  'profile.noKnownAllergies': 'No known allergies',
  'profile.careRoutine': 'Care routine',
  'profile.careRoutineHelp': 'Describe or photograph your at-home routine (products, steps). Shared with your care providers so they have context.',
  'profile.careRoutinePlaceholder': 'e.g. Morning: gentle cleanser, vitamin C, SPF. Evening: retinol 2x/week...',
  'profile.addPhoto': 'Add photo',
  'profile.removePhotoTitle': 'Remove photo?',
  'profile.removePhotoBody': 'This photo will be permanently deleted.',
  'profile.removePhoto': 'Remove',
  'profile.professionalDetails': 'Professional details',
  'profile.yearExperience': '{service} · {count} year of experience',
  'profile.yearsExperience': '{service} · {count} years of experience',
  'profile.bio': 'Bio',
  'profile.bioPlaceholder': 'A short introduction shown on your public profile...',
  'profile.about': 'About',
  'profile.noBio': 'No bio yet — add one to help clients trust you.',
  'profile.currencyHelp': 'Used for all your services and add-ons going forward. Past appointments keep the currency they were booked in.',
  'profile.certificates': 'Certificates',
  'profile.addCertificate': 'Add',
  'profile.certificatesHelp': 'Shown on your public profile. Images or PDF.',
  'profile.noCertificates': 'No certificates yet.',
  'profile.removeCertificateTitle': 'Remove certificate?',
  'profile.removeCertificateBody': 'This certificate will be permanently deleted.',
  'profile.removeCertificate': 'Remove',
  'profile.password': 'Password',
  'profile.changePassword': 'Change',
  'profile.currentPassword': 'Current password',
  'profile.newPassword': 'New password',
  'profile.confirmNewPassword': 'Confirm new password',
  'profile.newPasswordMin': 'New password must be at least 8 characters',
  'profile.newPasswordsMismatch': "New passwords don't match",
  'profile.passwordUpdated': 'Password updated. Other devices will need to log in again.',
  'profile.passwordChangeFailed': 'Could not change password',
  'profile.newPasswordPlaceholder': 'At least 8 characters',
  'profile.confirmNewPasswordPlaceholder': 'Repeat the new password',
  'profile.updatePassword': 'Update password',
  'profile.deleteAccountTitle': 'Delete account',
  'profile.deleteAccountBody': 'Deleting your account permanently removes your profile and the personal data associated with your account. Upcoming and in-progress appointments will be cancelled. This action cannot be undone.',
  'profile.deleteAccount': 'Delete account',
  'profile.deletingAccount': 'Deleting…',
  'profile.deleteAccountConfirmTitle': 'Delete your account?',
  'profile.deleteAccountImpactNone': 'No upcoming or in-progress appointments will be cancelled.',
  'profile.deleteAccountImpactOne': '1 upcoming or in-progress appointment will be cancelled automatically.',
  'profile.deleteAccountImpactMany': '{count} upcoming or in-progress appointments will be cancelled automatically.',
  'profile.deleteAccountConfirmBody': 'Your account and associated personal data will be permanently deleted. This action cannot be undone.',
  'profile.deleteAccountConfirm': 'Delete account',
  'profile.deleteAccountError': 'Could not delete account',
  'profile.switchCurrencyTitle': 'Switch currency to {currency}?',
  'profile.serviceSingular': 'service',
  'profile.servicePlural': 'services',
  'profile.currencyWarningBody': 'You have {count} active {services} priced in {from}. Switching to {to} will not convert those numbers — they will keep their digits but display with the new currency symbol. Recheck and update the prices on your My Services page after the switch.',
  'profile.currencyWarningPast': 'Past appointments stay in their booked currency — they are not affected.',
  'profile.switchToCurrency': 'Switch to {currency}',
};

type Messages = typeof en;
export type TranslationKey = keyof Messages;

const dictionaries: Record<AppLocale, Messages> = {
  en,
  fr: {
    ...en,
    'common.loading': 'Chargement...',
    'common.loadingEllipsis': 'Chargement…',
    'common.loadingMore': 'Chargement…',
    'common.loadMore': 'Charger plus',
    'common.save': 'Enregistrer',
    'common.saveChanges': 'Enregistrer',
    'common.saving': 'Enregistrement…',
    'common.saved': 'Enregistré',
    'common.edit': 'Modifier',
    'common.cancel': 'Annuler',
    'common.discard': 'Ignorer',
    'common.delete': 'Supprimer',
    'common.unsavedChanges': 'Vous avez des modifications non enregistrées',
    'common.enabled': 'Activé',
    'common.disabled': 'Désactivé',
    'common.custom': 'Personnalisé',
    'common.and': 'et',
    'common.all': 'Tout',
    'common.any': 'Tous',
    'common.add': 'Ajouter',
    'common.clear': 'Effacer',
    'common.clearFilters': 'Effacer les filtres',
    'common.close': 'Fermer',
    'common.confirm': 'Confirmer',
    'common.decrease': 'Diminuer',
    'common.dismiss': 'Fermer',
    'common.done': 'Terminé',
    'common.emailChannel': 'e-mail',
    'common.filters': 'Filtres',
    'common.from': 'De',
    'common.homeAria': 'Accueil Bevelure',
    'common.increase': 'Augmenter',
    'common.km': 'km',
    'common.max': 'Max',
    'common.min': 'Min',
    'common.minutesShort': '{count} min',
    'common.next': 'Suivant',
    'common.noMatches': 'Aucun résultat',
    'common.no': 'Non',
    'common.pageOf': 'Page {page} sur {total}',
    'common.prev': 'Précédent',
    'common.price': 'Prix',
    'common.remove': 'Retirer',
    'common.scrollRight': 'Faire défiler à droite',
    'common.search': 'Rechercher',
    'common.sort': 'Trier',
    'common.sortBy': 'Trier par : {label}',
    'common.to': 'À',
    'common.unknown': 'Inconnu',
    'common.user': 'Utilisateur',
    'common.viewAll': 'Tout voir',
    'common.viewFullDetails': 'Voir tous les détails',
    'common.viewHistory': 'Voir l’historique',
    'common.viewProfile': 'Voir le profil',
    'common.yes': 'Oui',

    'imageCrop.adjustLogo': 'Ajuster le logo',
    'imageCrop.adjustPhoto': 'Ajuster la photo',
    'imageCrop.description': 'Déplacez et zoomez l’image jusqu’à ce que la partie souhaitée soit dans le cercle.',
    'imageCrop.dragHint': 'Faites glisser l’image pour la repositionner.',
    'imageCrop.squareDescription': 'Déplacez et zoomez l’image jusqu’à ce que la partie souhaitée soit dans le carré.',
    'imageCrop.useImage': 'Utiliser l’image',
    'imageCrop.zoom': 'Zoom',

    'greeting.afternoon': 'Bon après-midi',
    'greeting.evening': 'Bonsoir',
    'greeting.morning': 'Bonjour',
    'greeting.night': 'Bonne nuit',

    'status.awaitingProvider': 'En attente du prestataire',
    'status.cancelled': 'Annulé',
    'status.completed': 'Terminé',
    'status.confirmed': 'Confirmé',
    'status.inProgress': 'En cours',
    'status.pending': 'En attente',

    'date.daysAgo': 'il y a {count} j',
    'date.afternoon': 'Après-midi',
    'date.evening': 'Soir',
    'date.morning': 'Matin',
    'date.noon': 'Midi',
    'date.selectDate': 'Sélectionner une date',
    'date.selectDateTime': 'Sélectionner date et heure',
    'date.time': 'Heure',
    'date.today': 'Aujourd’hui',
    'date.tomorrow': 'Demain',
    'date.yesterday': 'Hier',

    'field.addCustomValue': 'Ajouter « {value} »',
    'field.addLanguage': 'Ajouter une langue…',
    'field.anyDate': 'Toute date',
    'field.anyLanguage': 'Toute langue',
    'field.anyProvider': 'Tout prestataire',
    'field.anyService': 'Tout service',
    'field.age': 'Âge',
    'field.ageMaxAria': 'Âge maximum',
    'field.ageMinAria': 'Âge minimum',
    'field.ageUpTo': 'Jusqu’à {age}',
    'field.cityPlaceholder': 'Montréal',
    'field.city': 'Ville',
    'field.firstNamePlaceholder': 'Camille',
    'field.hidePassword': 'Masquer le mot de passe',
    'field.invalidPhone': 'Entrez un numéro valide pour le pays sélectionné.',
    'field.language': 'Langue',
    'field.languageHelp': 'Choisissez dans la liste ou saisissez votre langue puis appuyez sur Entrée.',
    'field.lastNamePlaceholder': 'Martin',
    'field.noCountriesFound': 'Aucun pays trouvé',
    'field.phoneNumber': 'Numéro de téléphone',
    'field.priceMaxAria': 'Prix maximum',
    'field.priceMinAria': 'Prix minimum',
    'field.providerNamePlaceholder': 'Alex Morgan',
    'field.rating': 'Note',
    'field.searchCountry': 'Rechercher un pays…',
    'field.searchCurrency': 'Rechercher une devise…',
    'field.selectCountry': 'Sélectionner un pays',
    'field.service': 'Service',
    'field.serviceExamplePlaceholder': 'ex. manucure',
    'field.shareProcedurePlaceholder': 'ex. coupe homme, soin visage…',
    'field.showPassword': 'Afficher le mot de passe',
    'field.socialPlaceholder': '@pseudo ou URL du profil',
    'field.startTypingAddress': 'Commencez à saisir une adresse…',
    'field.startTypingCity': 'Commencez à saisir une ville…',
    'field.serviceType': 'Type de service',
    'field.sex': 'Sexe',
    'field.useCustomLanguage': 'Utiliser « {value} »',

    'sex.female': 'Femme',
    'sex.male': 'Homme',
    'sex.other': 'Autre',
    'sex.preferNotToSay': 'Préfère ne pas dire',

    'rating.any': 'Toutes',
    'sort.distance': 'Distance',
    'sort.name': 'Nom',
    'sort.rating': 'Note',

    'customerHome.bookingConfirmedBody': 'Enregistré dans votre historique. Ouvrez-le pour voir tous les détails.',
    'customerHome.bookingPendingBody': 'Nous vous préviendrons dès que le prestataire confirme. Vous pouvez annuler à tout moment.',
    'customerHome.bookingRequestSent': 'Demande de réservation envoyée',
    'customerHome.appointmentBooked': 'Rendez-vous réservé',
    'customerHome.inProgressSubtitle': 'Votre rendez-vous est en cours',
    'customerHome.lastVisit': 'Dernière visite',
    'customerHome.nextAppointment': 'Votre prochain rendez-vous {when}',
    'customerHome.noAppointmentsThisDay': 'Aucun rendez-vous ce jour-là',
    'customerHome.noUpcoming': 'Aucun rendez-vous à venir',
    'customerHome.nothingHereYet': 'Rien pour le moment',
    'customerHome.recent': 'Récent',
    'customerHome.upcoming': 'À venir',

    'providerHome.allProviders': 'Tous les prestataires',
    'providerHome.appointmentToday': 'Rendez-vous aujourd’hui',
    'providerHome.appointmentsToday': 'Rendez-vous aujourd’hui',
    'providerHome.awaitingYou': 'En attente de vous',
    'providerHome.pendingRequests': '{count} demande(s) de réservation en attente',
    'providerHome.pendingRequestsBody': 'Confirmez ou refusez pour que les clients sachent à quoi s’attendre.',
    'providerHome.reviewConfirm': 'Voir et confirmer',
    'providerHome.searchTeam': 'Rechercher dans l’équipe…',

    'customerSearch.anyCity': 'Toute ville',
    'customerSearch.detectingLocation': 'Détection de votre position…',
    'customerSearch.findingProviders': 'Recherche de prestataires…',
    'customerSearch.inCity': ' à {city}',
    'customerSearch.locationCity': 'Prestataires affichés à {city}.',
    'customerSearch.locationNearest': 'Prestataires les plus proches{city}.',
    'customerSearch.noProvidersFound': 'Aucun prestataire trouvé',
    'customerSearch.priceHintAny': 'Entrez un type de service ci-dessus pour filtrer une procédure précise par prix.',
    'customerSearch.priceHintService': 'Prix des services correspondant à « {serviceType} ».',
    'customerSearch.searchPlaceholder': 'Rechercher par nom…',
    'customerSearch.servicePlaceholder': 'Tout (ex. manucure, barbier…)',
    'customerSearch.showingAll': 'Tous les prestataires sont affichés.',
    'customerSearch.title': 'Trouver des prestataires',
    'customerSearch.useMyLocation': 'Utiliser ma position',

    'careHub.completedVisits': 'Visites terminées : {count}',
    'careHub.loadingMore': 'Chargement…',
    'careHub.noCompletedFor': 'Aucune visite terminée pour {serviceType} pour le moment.',
    'careHub.noCompletedYet': 'Aucune visite terminée pour le moment.',
    'careHub.noProcedureDetails': 'Aucun détail de procédure enregistré pour le moment.',
    'careHub.procedurePhotos': 'Photos de la procédure',
    'careHub.procedureTypes': 'Types de procédure : {count}',
    'careHub.quickShare': 'Partage rapide',
    'careHub.quickShareBody': 'Générez un QR code ou un lien pour l’un de vos types de procédure.',
    'careHub.shareHistory': 'Partager l’historique',
    'careHub.title': 'Mon Care Hub',
    'careHub.titleAccent': 'Mon Care *Hub*',
    'careHub.detailNotes': 'Notes',
    'careHub.detailProducts': 'Produits',
    'careHub.detailRecommendations': 'Recommandations',

    'appointments.cancelled': 'Annulé',
    'appointments.careProvider': 'Prestataire',
    'appointments.history': 'Historique',
    'appointments.loadOlder': 'Charger les anciens rendez-vous',
    'appointments.loadingOlder': 'Chargement des anciens rendez-vous…',
    'appointments.noCancelled': 'Aucun rendez-vous annulé',
    'appointments.noFilterMatches': 'Aucun rendez-vous ne correspond à vos filtres.',
    'appointments.noHistory': 'Rien pour le moment',
    'appointments.noUpcoming': 'Aucun rendez-vous à venir',
    'appointments.rateThisVisit': 'Noter cette visite',
    'appointments.upcoming': 'À venir',
    'appointments.yourRating': 'Votre note',

    'providerAppointments.anyCustomer': 'Tout client',
    'providerAppointments.noPending': 'Aucune demande en attente — tout est à jour.',
    'providerAppointments.rateClient': 'Noter ce client',

    'providerServices.addService': 'Ajouter un service',
    'providerServices.addon': 'Option',
    'providerServices.addonName': 'Nom de l’option',
    'providerServices.addonPlaceholder': 'ex. retrait de gel',
    'providerServices.deactivate': 'Désactiver',
    'providerServices.deactivateAddon': 'Désactiver l’option',
    'providerServices.deactivateAddonDescription': 'Le service reste disponible — seule cette option ne pourra plus être réservée. Vous pourrez la réactiver à tout moment.',
    'providerServices.deactivateAddonTitle': 'Désactiver l’option « {name} » ?',
    'providerServices.deactivateService': 'Désactiver le service',
    'providerServices.deactivateServiceDescription': 'Les clients ne pourront plus réserver ce service pour de nouveaux rendez-vous. Les anciennes réservations restent dans l’historique. Vous pourrez le réactiver à tout moment.',
    'providerServices.deactivateServiceTitle': 'Désactiver « {name} » ?',
    'providerServices.durationMinutes': 'Durée (minutes)',
    'providerServices.editAddon': 'Modifier l’option',
    'providerServices.extraMinutes': 'Minutes en plus',
    'providerServices.extraPrice': 'Prix en plus ({currency})',
    'providerServices.failedCreate': 'Impossible de créer le service',
    'providerServices.failedSaveAddon': 'Impossible d’enregistrer l’option',
    'providerServices.inactive': 'Inactif',
    'providerServices.noAddons': 'Aucune option pour le moment.',
    'providerServices.priceOptional': 'Prix ({currency}, facultatif)',
    'providerServices.reactivate': 'Réactiver',
    'providerServices.reactivateService': 'Réactiver le service',
    'providerServices.renameDisabled': 'Le renommage n’est pas pris en charge — les anciennes réservations utilisent ce nom. Désactivez-le et créez-en un nouveau.',
    'chat.blockedTitle': 'Messagerie indisponible',
    'chat.blocked': 'Vous ne pouvez plus vous envoyer de messages.',
    'pub.noSlotsMonth': 'Aucune disponibilité ce mois-ci — utilisez les flèches pour voir d’autres mois.',
    'pub.publicPage': 'Page de réservation publique',
    'pub.username': 'Nom d’utilisateur',
    'pub.usernameHelp': 'Votre lien public — partagez-le sur les réseaux pour que vos clients réservent.',
    'pub.copy': 'Copier',
    'pub.copied': 'Copié',
    'common.back': 'Retour',
    'pub.close': 'Fermer',
    'pub.bookWith': 'Réserver avec {name}',
    'pub.bookCta': 'Prendre rendez-vous',
    'pub.selectService': 'Choisissez un service',
    'pub.chooseTime': 'Choisissez un horaire',
    'pub.yourDetails': 'Vos informations',
    'pub.pickDay': 'Choisissez un jour disponible.',
    'pub.continue': 'Continuer',
    'pub.confirmBooking': 'Confirmer la réservation',
    'pub.successTitle': 'Demande envoyée',
    'pub.successGuest': 'Votre demande est envoyée ! Les détails du rendez-vous ont été envoyés à {email} — le prestataire confirmera bientôt.',
    'pub.successCustomer': 'Réservé ! {provider} confirmera votre rendez-vous sous peu.',
    'pub.manageCancel': 'Gérer ou annuler cette réservation',
    'pub.signedInAs': 'Réservation en tant que {name}',
    'pub.tooMany': 'Trop de tentatives de réservation. Réessayez plus tard.',
    'pub.bookingFailed': 'Impossible de finaliser la réservation. Réessayez.',
    'pub.about': 'À propos',
    'pub.services': 'Services',
    'pub.portfolio': 'Portfolio',
    'pub.speaks': 'Parle',
    'pub.ratings': '{count} avis',
    'pub.atSalon': 'chez {salon}',
    'pub.team': 'Équipe',
    'pub.noTeam': 'Aucun membre pour le moment.',
    'pub.createYourOwn': 'Créez votre page de réservation sur Bevelure →',
    'pub.agree': 'J’accepte les',
    'pub.terms': 'Conditions',
    'pub.privacy': 'Politique de confidentialité',
    'common.showMore': 'Voir plus',
    'common.showLess': 'Voir moins',
    'providerServices.descriptionOptional': 'Description (facultatif)',
    'providerServices.serviceDescriptionPlaceholder': 'Ce qui est inclus, à quoi s’attendre, préparation et soins après…',
    'providerServices.addonDescriptionPlaceholder': 'Ce que comprend cette option…',
    'providerServices.serviceName': 'Nom du service',
    'providerServices.servicePlaceholder': 'ex. manucure',
    'providerServices.title': 'Mes services',

    'schedule.adding': 'Ajout…',
    'schedule.addTimeOff': 'Ajouter une absence',
    'schedule.addTimeOffHelp': 'Bloquez des dates pour vacances, maladie ou pauses. Les clients ne pourront pas réserver pendant ces périodes.',
    'schedule.addTimeOffImpactHelp': 'Les clients ne pourront pas réserver de nouveaux rendez-vous dans cette plage.',
    'schedule.addTimeOffTitle': 'Ajouter cette absence ?',
    'schedule.addWorkingHours': 'Ajouter des heures de travail',
    'schedule.dayOfWeek': 'Jour de la semaine',
    'schedule.dayOff': 'Jour off',
    'schedule.end': 'Fin',
    'schedule.errorEndAfterStart': 'La fin doit être après le début',
    'schedule.errorEndAfterStartTime': 'L’heure de fin doit être après l’heure de début',
    'schedule.errorOverlap': 'Ces heures de travail chevauchent une règle existante pour ce jour',
    'schedule.errorPickStartEnd': 'Choisissez le début et la fin',
    'schedule.failedAddRule': 'Impossible d’ajouter la règle d’horaire',
    'schedule.friShort': 'Ven',
    'schedule.friday': 'Vendredi',
    'schedule.monShort': 'Lun',
    'schedule.monday': 'Lundi',
    'schedule.noneScheduled': 'Rien de prévu',
    'schedule.pastTimeOff': 'Absences passées ({count})',
    'schedule.pickEnd': 'Choisir la fin',
    'schedule.pickStart': 'Choisir le début',
    'schedule.reasonPlaceholder': 'Raison (facultatif) — vacances, formation, etc.',
    'schedule.removeWorkingHoursDescription': 'sera retiré de votre planning hebdomadaire. Les nouvelles réservations dans cette plage ne seront plus possibles.',
    'schedule.removeWorkingHoursTitle': 'Supprimer les heures de travail ?',
    'schedule.satShort': 'Sam',
    'schedule.saturday': 'Samedi',
    'schedule.start': 'Début',
    'schedule.sunShort': 'Dim',
    'schedule.sunday': 'Dimanche',
    'schedule.thuShort': 'Jeu',
    'schedule.thursday': 'Jeudi',
    'schedule.title': 'Planning',
    'schedule.tueShort': 'Mar',
    'schedule.tuesday': 'Mardi',
    'schedule.upcomingTimeOff': 'Absences à venir',
    'schedule.wedShort': 'Mer',
    'schedule.wednesday': 'Mercredi',
    'schedule.weeklySchedule': 'Planning hebdomadaire',

    'providers.all': 'Tous',
    'providers.blocked': 'Bloqués',
    'providers.blockedBody': 'Les prestataires bloqués n’apparaîtront pas dans la recherche ni dans vos listes. Vous pouvez les débloquer à tout moment.',
    'providers.blockedOn': 'Bloqué le {date}',
    'providers.browseProviders': 'Parcourir les prestataires',
    'providers.countSaved': '{count} enregistrés',
    'providers.countVisited': '{count} prestataire{plural} consulté{plural}',
    'providers.emptyFavoritesBody': 'Touchez le cœur sur le profil d’un prestataire pour l’enregistrer ici.',
    'providers.emptyVisitedBody': 'Les prestataires réservés apparaîtront ici après votre premier rendez-vous.',
    'providers.favorites': 'Favoris',
    'providers.lastVisit': 'dernière visite {when}',
    'providers.nextVisit': 'Prochain {when}',
    'providers.nextVisitSort': 'Prochaine visite',
    'providers.noBlocked': 'Aucun prestataire bloqué',
    'providers.noFavorites': 'Aucun favori pour le moment',
    'providers.noFilterMatches': 'Aucun prestataire ne correspond à vos filtres.',
    'providers.noProviders': 'Aucun prestataire pour le moment',
    'providers.overallRating': 'Note globale',
    'providers.keepTyping': '{shown} sur {total} affichés — continuez à taper pour affiner.',
    'providers.pickServiceForPrice': 'Choisissez un service pour filtrer une procédure précise par prix.',
    'providers.priceOfService': 'Prix de « {service} ».',
    'providers.searchPlaceholder': 'Rechercher par nom…',
    'providers.sortMostVisits': 'Le plus de visites',
    'providers.title': 'Mes prestataires',
    'providers.unblock': 'Débloquer',
    'providers.visits': '{count} visite{plural}',
    'providers.yearsShort': '{count} an{plural}',
    'providers.youRated': 'Votre note {rating}',

    'providerCustomers.count': '{count} client(s)',
    'providerCustomers.last': 'dernière visite {when}',
    'providerCustomers.noBlockedCustomers': 'Aucun client bloqué',
    'providerCustomers.noBlockedCustomersBody': 'Bloquez un client depuis son profil pour l’empêcher de réserver de nouveaux rendez-vous avec vous.',
    'providerCustomers.noCustomers': 'Aucun client pour le moment',
    'providerCustomers.noCustomersBody': 'Vos clients apparaîtront ici après leur premier rendez-vous.',
    'providerCustomers.noSearchMatches': 'Aucun client ne correspond à votre recherche.',

    'messages.attachImage': 'Joindre une image',
    'messages.careProvider': 'Prestataire',
    'messages.customer': 'Client',
    'messages.salonAdministrator': 'Administration du salon',
    'messages.emptyCustomer': 'Ouvrez le profil d’un prestataire et touchez Message pour commencer.',
    'messages.emptyProvider': 'Ouvrez le profil d’un client et touchez Message, ou attendez qu’il vous écrive.',
    'messages.failedUpload': 'Impossible de téléverser l’image',
    'messages.loadOlder': 'Charger les anciens messages',
    'messages.loadingOlder': 'Chargement des anciens messages…',
    'messages.noConversations': 'Aucune conversation pour le moment',
    'messages.noMessagesYet': 'Aucun message pour le moment. Dites bonjour !',
    'messages.noMessagesPreview': 'Aucun message pour le moment',
    'messages.photo': 'Photo',
    'messages.removeAttachment': 'Supprimer la pièce jointe',
    'messages.reply': 'Répondre',
    'messages.replyingTo': 'Réponse à {name}',
    'messages.cancelReply': 'Annuler la réponse',
    'messages.heart': 'Envoyer un cœur',
    'messages.unheart': 'Retirer le cœur',
    'messages.messagingUnavailable': 'La messagerie est indisponible pour cette conversation.',
    'messages.title': 'Messages',
    'messages.typeMessage': 'Écrire un message…',
    'messages.viewProfile': 'Voir le profil',

    'finance.customerCaption': 'Estimation basée sur les prix enregistrés dans vos rendez-vous. Les mois passés sont réalisés ; le mois en cours sépare le terminé et l’à venir ; les mois futurs sont estimés selon vos réservations.',
    'finance.customerEmpty': 'Quand vous réservez ou terminez des rendez-vous avec un prix, vos dépenses mensuelles apparaîtront ici.',
    'finance.customerTitle': 'Vos dépenses',
    'finance.done': 'terminé',
    'finance.explainNumbersAria': 'Que signifient ces chiffres ?',
    'finance.hideBreakdown': 'Masquer le détail',
    'finance.hidePastMonths': 'Masquer les {count} mois passés',
    'finance.noActivity': 'Aucune activité',
    'finance.nothingToShow': 'Rien à afficher pour le moment',
    'finance.pendingConfirmation': 'Inclut {amount} en attente de confirmation',
    'finance.providerEmpty': 'Quand vous avez des rendez-vous avec un prix, vos revenus mensuels apparaîtront ici.',
    'finance.providerCaption': 'Estimation basée sur les prix figés dans vos rendez-vous. Les mois passés sont réalisés ; le mois en cours sépare terminé et à venir ; les mois futurs sont estimés selon les rendez-vous déjà réservés. Les réservations annulées sont exclues.',
    'finance.providerTitle': 'Vos revenus',
    'finance.showBreakdown': 'Afficher le détail',
    'finance.showPastMonths': 'Afficher les {count} mois passés',
    'finance.statusCurrent': 'Actuel',
    'finance.statusPast': 'Passé',
    'finance.statusUpcoming': 'À venir',
    'finance.thisMonth': 'Ce mois-ci',
    'finance.upcoming': 'à venir',

    'appointmentNew.addons': 'Options',
    'appointmentNew.addonsHelp': 'Extras facultatifs — touchez pour ajouter.',
    'appointmentNew.addressOptional': 'Adresse (facultatif)',
    'appointmentNew.availableDaysHelp': 'Les jours avec créneaux disponibles sont marqués d’un point.',
    'appointmentNew.bookingAtSalon': 'Réservation au salon',
    'appointmentNew.confirmAppointment': 'Confirmer le rendez-vous',
    'appointmentNew.create': 'Créer',
    'appointmentNew.createError': 'Impossible de créer le rendez-vous',
    'appointmentNew.creating': 'Création…',
    'appointmentNew.careProviderName': 'Nom du prestataire',
    'appointmentNew.dateTime': 'Date et heure',
    'appointmentNew.email': 'E-mail',
    'appointmentNew.emailPhoneSocialHelp': 'Entrez un seul élément : e-mail, téléphone ou lien social du prestataire. Nous le rechercherons.',
    'appointmentNew.enterProviderContact': 'Entrez l’e-mail, le téléphone ou le lien social du prestataire',
    'appointmentNew.enterProviderName': 'Entrez le nom du prestataire',
    'appointmentNew.enterService': 'Entrez le service (ex. manucure)',
    'appointmentNew.externalProviderRecord': 'Ajoutez ses détails pour conserver cette visite dans votre historique.',
    'appointmentNew.externalProviderNotify': 'Ajoutez ses détails et nous le notifierons par {channel}.',
    'appointmentNew.findProvider': 'Trouver votre prestataire',
    'appointmentNew.lookupFailed': 'Recherche échouée',
    'appointmentNew.newProvider': 'Nouveau prestataire',
    'appointmentNew.newProviderIntro': 'Nous n’avons pas trouvé ce prestataire sur Bevelure.',
    'appointmentNew.noMatchingProviders': 'Aucun prestataire correspondant.',
    'appointmentNew.noServices': 'Aucun service disponible pour ce prestataire',
    'appointmentNew.noSlots': 'Aucun créneau disponible dans les 30 prochains jours',
    'appointmentNew.or': 'ou',
    'appointmentNew.orFindNew': 'ou trouvez-en un nouveau',
    'appointmentNew.pickDateTime': 'Choisir une date et une heure',
    'appointmentNew.pickDateTimeShort': 'Choisir date et heure',
    'appointmentNew.pickDateWithSlots': 'Choisissez une date avec des créneaux disponibles ci-dessus.',
    'appointmentNew.phone': 'Téléphone',
    'appointmentNew.priceOptional': 'Prix (facultatif)',
    'appointmentNew.providerFound': 'Prestataire trouvé !',
    'appointmentNew.providerFoundText': 'Nous avons trouvé {name} dans notre système. Veuillez sélectionner le service.',
    'appointmentNew.providerZone': 'Heures affichées dans le fuseau du prestataire — {zone}.',
    'appointmentNew.selectService': 'Sélectionner un service',
    'appointmentNew.selectServiceHelp': 'Choisissez un service pour ce rendez-vous',
    'appointmentNew.shareAllDesc': 'Toutes vos visites correspondantes.',
    'appointmentNew.shareAllDescService': 'Toutes vos visites {service}.',
    'appointmentNew.shareAllTitle': 'Historique complet',
    'appointmentNew.shareConsent': 'Je comprends que je partage cet historique de soins avec {provider} — notes de procédure, produits, recommandations et photos de résultat/procédure. Mes notes et photos privées ne sont jamais partagées.',
    'appointmentNew.shareDontDesc': 'Gardez votre historique privé.',
    'appointmentNew.shareDontTitle': 'Ne pas partager',
    'appointmentNew.shareHistory': 'Partager l’historique',
    'appointmentNew.shareHistoryHelp': 'Choisissez ce que ce prestataire peut voir de vos visites passées.',
    'appointmentNew.shareLast5Desc': 'Vos 5 visites correspondantes les plus récentes.',
    'appointmentNew.shareLast5DescService': 'Vos 5 visites {service} les plus récentes.',
    'appointmentNew.shareLast5Title': '5 dernières visites',
    'appointmentNew.shareProcedureHint': 'D’autres prestataires peuvent nommer le même service différemment — ajoutez ces noms pour inclure ces visites aussi.',
    'appointmentNew.shareProcedures': 'Procédures à partager',
    'appointmentNew.socialMedia': 'Réseaux sociaux',
    'appointmentNew.stepOf': 'Étape {step} sur {total}',
    'appointmentNew.thisProvider': 'ce prestataire',
    'appointmentNew.title': 'Créer un rendez-vous',
    'appointmentNew.total': 'Total',
    'appointmentNew.whereToGo': 'Où aller',
    'appointmentNew.yourProviders': 'Vos prestataires',

    'providerAppointmentNew.adminBookTeam': 'En tant qu’admin du salon, vous pouvez réserver pour votre équipe.',
    'providerAppointmentNew.adminBookTeamOnly': 'En tant qu’admin du salon, vous réservez au nom de votre équipe.',
    'providerAppointmentNew.bookFor': 'Réserver pour',
    'providerAppointmentNew.bookingFor': 'Réservation pour',
    'providerAppointmentNew.change': 'Changer',
    'providerAppointmentNew.checkWorkingHours': 'Vérifiez les heures de travail et absences du prestataire.',
    'providerAppointmentNew.chooseOneService': 'Choisissez l’un de vos services',
    'providerAppointmentNew.confirmDetails': 'Confirmer les détails',
    'providerAppointmentNew.createAppointment': 'Créer le rendez-vous',
    'providerAppointmentNew.customer': 'Client',
    'providerAppointmentNew.customerPreselected': 'Client pré-sélectionné. Continuez pour choisir un service.',
    'providerAppointmentNew.enterOneContact': 'Entrez un contact du client — e-mail, téléphone ou lien social.',
    'providerAppointmentNew.inviteProviderFirst': 'Invitez un prestataire dans votre salon avant de créer un rendez-vous.',
    'providerAppointmentNew.matchInviteHelp': 'Nous rechercherons un compte Bevelure existant. S’il n’est pas encore membre, nous lui enverrons les détails et une invitation (pas de notification pour les liens sociaux).',
    'providerAppointmentNew.myself': 'Moi-même',
    'providerAppointmentNew.noActiveServices': 'Vous n’avez pas encore de services actifs.',
    'providerAppointmentNew.noBookableProviders': 'Aucun prestataire réservable dans votre salon pour le moment. Invitez des prestataires depuis Mon salon.',
    'providerAppointmentNew.noMatchingCustomers': 'Aucun client correspondant.',
    'providerAppointmentNew.noSlots': 'Aucun créneau disponible dans les 30 prochains jours.',
    'providerAppointmentNew.noTeamMembersMatch': 'Aucun membre de l’équipe ne correspond.',
    'providerAppointmentNew.orEnterContact': 'ou entrez son contact',
    'providerAppointmentNew.pickDateWithSlots': 'Choisissez une date avec des créneaux disponibles ci-dessus.',
    'providerAppointmentNew.pickTeamMember': 'Choisissez le membre de l’équipe qui réalisera ce rendez-vous.',
    'providerAppointmentNew.pickTeamMemberError': 'Choisissez pour quel membre de l’équipe est ce rendez-vous.',
    'providerAppointmentNew.pleaseSelectService': 'Veuillez sélectionner un service',
    'providerAppointmentNew.providerFallback': 'Prestataire',
    'providerAppointmentNew.selected': 'Sélectionné',
    'providerAppointmentNew.teamMemberFallback': 'membre de l’équipe',
    'providerAppointmentNew.title': 'Nouveau rendez-vous',
    'providerAppointmentNew.yourCustomers': 'Vos clients',

    'providerProfile.about': 'À propos',
    'providerProfile.addFavorite': 'Ajouter aux favoris',
    'providerProfile.addons': 'Options',
    'providerProfile.blockProvider': 'Bloquer ce prestataire',
    'providerProfile.bookAppointment': 'Réserver',
    'providerProfile.certificates': 'Certificats',
    'providerProfile.fromClients': 'de clients',
    'providerProfile.message': 'Message',
    'providerProfile.noServicesListed': 'Aucun service listé pour le moment',
    'providerProfile.pastAppointments': 'Rendez-vous passés',
    'providerProfile.rating': 'Note',
    'providerProfile.removeConfirm': 'Retirer',
    'providerProfile.removeDescription': 'Il perdra l’accès à ce salon. Les rendez-vous passés restent dans l’historique du salon. Les nouvelles réservations ne seront plus rattachées à ce salon.',
    'providerProfile.removeFavorite': 'Retirer des favoris',
    'providerProfile.removeFromSalon': 'Retirer du salon',
    'providerProfile.removeTitle': 'Retirer {name} du salon ?',
    'providerProfile.services': 'Services',
    'providerProfile.title': 'Profil du prestataire',
    'providerProfile.unblockProvider': 'Débloquer ce prestataire',
    'providerProfile.unblocking': 'Déblocage…',
    'providerProfile.unavailableTitle': 'Profil indisponible',
    'providerProfile.unavailableBody': 'Ce profil n’est plus disponible. Les anciens rendez-vous et dossiers restent dans votre historique.',
    'providerProfile.visits': 'Visites',
    'providerProfile.worksAt': 'Travaille chez',
    'providerProfile.yearsExperience': '{count} an(s) d’expérience',

    'customerProfile.allergies': 'Allergies',
    'customerProfile.allergiesNoDetails': 'A des allergies (aucun détail fourni)',
    'customerProfile.blockCustomer': 'Bloquer ce client',
    'customerProfile.book': 'Réserver',
    'customerProfile.careRoutine': 'Routine de soin',
    'customerProfile.goBack': 'Retour',
    'customerProfile.noKnownAllergies': 'Aucune allergie connue',
    'customerProfile.notFound': 'Client introuvable ou aucun rendez-vous partagé.',
    'customerProfile.unavailableTitle': 'Profil indisponible',
    'customerProfile.unavailableBody': 'Ce profil n’est plus disponible, ou vous n’avez plus accès à celui-ci. Les anciens rendez-vous restent dans votre historique.',
    'customerProfile.ratings': '{count} notes',
    'customerProfile.title': 'Client',
    'customerProfile.unblockCustomer': 'Débloquer ce client',

    'externalCancel.alreadyCancelled': 'Ce rendez-vous est déjà annulé.',
    'externalCancel.alreadyCompleted': 'Ce rendez-vous est déjà terminé — impossible d’annuler une visite passée.',
    'externalCancel.cancelledBody': 'Nous avons prévenu l’autre partie. Le créneau est maintenant disponible pour quelqu’un d’autre.',
    'externalCancel.cancelledTitle': 'Rendez-vous annulé',
    'externalCancel.cancelling': 'Annulation…',
    'externalCancel.confirmHint': 'Vous avez changé d’avis ? Fermez simplement cette page — rien ne se passe tant que vous ne touchez pas le bouton ci-dessus.',
    'externalCancel.errorInvalid': 'Le lien d’annulation est invalide ou expiré.',
    'externalCancel.errorMissing': 'Le lien d’annulation ne contient pas le token.',
    'externalCancel.errorRetry': 'Impossible d’annuler — réessayez ou contactez directement l’autre partie.',
    'externalCancel.intro': 'Vous êtes sur le point d’annuler :',
    'externalCancel.titlePrefix': 'Annuler',
    'externalCancel.titleEmphasis': 'le rendez-vous',

    'externalFill.addDetails': 'Ajoutez les détails du traitement pour ce rendez-vous.',
    'externalFill.errorInvalidTitle': 'Lien invalide',
    'externalFill.errorInvalidBody': 'Ce lien est invalide ou a expiré. Demandez au client de le renvoyer.',
    'externalFill.lastUpdated': 'Dernière mise à jour {date}',
    'externalFill.productsPlaceholder': 'Marques, couleurs, matériaux…',
    'externalFill.saveError': 'Impossible d’enregistrer. Veuillez réessayer.',

    'share.allergiesOnFile': 'Allergies enregistrées',
    'share.customerAllergyFallback': 'Le client indique des allergies (sans notes précises).',
    'share.expired': 'Ce lien a expiré ou n’est plus valide.',
    'share.expires': 'Le lien expire le {date}',
    'share.failedLoad': 'Impossible de charger l’historique partagé',
    'share.footer': 'Partagé via {brand}. Le client peut révoquer ce lien à tout moment.',
    'share.fullHistory': 'Historique complet',
    'share.last5Visits': '5 dernières visites',
    'share.linkUnavailable': 'Lien indisponible',
    'share.noKnownAllergies': 'Aucune allergie connue enregistrée.',
    'share.noSharedHistory': 'Impossible de charger cet historique partagé.',
    'share.readOnly': 'Lecture seule',
    'share.sharedTreatmentHistory': 'Historique de traitement partagé',

    'appointmentDetail.add': 'Ajouter',
    'appointmentDetail.addPhoto': 'Ajouter une photo',
    'appointmentDetail.addSelected': 'Ajouter ({count})',
    'appointmentDetail.addToPortfolio': 'Ajouter au portfolio',
    'appointmentDetail.addToPortfolioBody': 'Choisissez les photos à afficher sur votre profil public.',
    'appointmentDetail.addons': 'Options',
    'appointmentDetail.allergiesOnFile': 'Allergies enregistrées',
    'appointmentDetail.allergyFallback': 'Le client indique des allergies (sans notes précises).',
    'appointmentDetail.atSalon': 'chez {salon}',
    'appointmentDetail.bookingRequestBody': 'Confirmez pour bloquer le créneau, ou refusez pour que le client choisisse un autre horaire.',
    'appointmentDetail.bookingRequestTitle': 'Demande de réservation — en attente de vous',
    'appointmentDetail.byProvider': 'par {provider}',
    'appointmentDetail.cancelAppointment': 'Annuler le rendez-vous',
    'appointmentDetail.cancelAppointmentMessage': 'Le créneau sera libéré et l’autre partie sera notifiée.',
    'appointmentDetail.cancelAppointmentTitle': 'Annuler le rendez-vous ?',
    'appointmentDetail.cancelRequest': 'Annuler la demande',
    'appointmentDetail.cancelRequestMessage': 'Cette demande sera retirée.',
    'appointmentDetail.cancelRequestTitle': 'Annuler la demande de réservation ?',
    'appointmentDetail.captionOptional': 'Légende (facultatif)',
    'appointmentDetail.category': 'Catégorie',
    'appointmentDetail.categoryPlaceholder': 'ex. manucure, Botox',
    'appointmentDetail.confirm': 'Confirmer',
    'appointmentDetail.confirming': 'Confirmation…',
    'appointmentDetail.customerAllergyInfo': 'Informations allergies du client',
    'appointmentDetail.customerAllergyInfoPrefix': 'Voir les notes d’allergies du client dans',
    'appointmentDetail.decline': 'Refuser',
    'appointmentDetail.declineBookingMessage': 'Le créneau sera à nouveau disponible.',
    'appointmentDetail.declineBookingTitle': 'Refuser la réservation ?',
    'appointmentDetail.declining': 'Refus…',
    'appointmentDetail.defaultProvider': 'le prestataire',
    'appointmentDetail.detailsSaved': 'Détails enregistrés',
    'appointmentDetail.failedConfirm': 'Impossible de confirmer',
    'appointmentDetail.failedDecline': 'Impossible de refuser',
    'appointmentDetail.fullHistory': 'Historique complet',
    'appointmentDetail.inProgressNow': 'En cours maintenant',
    'appointmentDetail.keepIt': 'Conserver',
    'appointmentDetail.last5Visits': '5 dernières visites',
    'appointmentDetail.minutes': '{count} min',
    'appointmentDetail.myPersonalNotes': 'Mes notes personnelles',
    'appointmentDetail.myPersonalNotesHelp': 'Vous seul pouvez les voir. Pratique pour noter vos ressentis, réactions, etc.',
    'appointmentDetail.myPersonalNotesPlaceholder': 'Vos notes privées sur ce rendez-vous…',
    'appointmentDetail.noCompletedShared': 'Aucun rendez-vous terminé de ce type pour le moment.',
    'appointmentDetail.noPhotosYet': 'Aucune photo pour le moment.',
    'appointmentDetail.noProcedureDetails': 'Aucun détail de procédure enregistré.',
    'appointmentDetail.notesSaved': 'Notes enregistrées',
    'appointmentDetail.photos': 'Photos',
    'appointmentDetail.private': 'Privé',
    'appointmentDetail.privatePhotosHelp': 'vous seul pouvez les voir',
    'appointmentDetail.privatePortfolioHelp': 'Enregistré dans votre portfolio, mais masqué tant que vous ne l’activez pas.',
    'appointmentDetail.procedureNotes': 'Notes de procédure',
    'appointmentDetail.procedureNotesPlaceholder': 'Ce qui a été fait pendant la procédure…',
    'appointmentDetail.procedurePhotosHelp': 'reçus, produits, ce qui a été appliqué',
    'appointmentDetail.productsUsed': 'Produits utilisés',
    'appointmentDetail.providerOnlyNotes': 'Notes internes prestataire',
    'appointmentDetail.providerOnlyNotesHelp': 'Visible uniquement par les prestataires travaillant sur ce rendez-vous, pas par le client.',
    'appointmentDetail.providerOnlyNotesPlaceholder': 'Contexte interne, points particuliers, suivi prestataire…',
    'appointmentDetail.public': 'Public',
    'appointmentDetail.publicPortfolioHelp': 'Visible sur votre profil public pour les clients.',
    'appointmentDetail.rateCustomer': 'Noter le client',
    'appointmentDetail.rateProvider': 'Noter le prestataire',
    'appointmentDetail.readOnly': 'Lecture seule',
    'appointmentDetail.recommendationsPlaceholder': 'Recommandations / soins après visite',
    'appointmentDetail.recordCount': '{count} dossier{plural}',
    'appointmentDetail.removePhoto': 'Supprimer',
    'appointmentDetail.removePhotoMessage': 'Cette photo sera supprimée définitivement.',
    'appointmentDetail.removePhotoTitle': 'Supprimer la photo ?',
    'appointmentDetail.resultPhotos': 'Photos du résultat',
    'appointmentDetail.saveDetails': 'Enregistrer les détails',
    'appointmentDetail.saveNotes': 'Enregistrer les notes',
    'appointmentDetail.sharedHistoryTitle': '{name} a partagé son historique',
    'appointmentDetail.shortDescription': 'Courte description',
    'appointmentDetail.tapStar': 'Touchez une étoile pour envoyer votre note.',
    'appointmentDetail.theirProfile': 'son profil',
    'appointmentDetail.title': 'Rendez-vous',
    'appointmentDetail.total': 'Total',
    'appointmentDetail.waitingForProviderBody': 'Vous recevrez une notification quand il accepte ou refuse. Vous pouvez annuler cette demande à tout moment.',
    'appointmentDetail.waitingForProviderTitle': 'En attente de confirmation par {provider}',
    'appointmentDetail.yourTime': '{time} votre heure ({zone})',

    'auth.alreadyHaveAccount': 'Vous avez déjà un compte ?',
    'auth.backToBevelure': 'Retour à Bevelure',
    'auth.backToLogin': 'Retour à la connexion',
    'auth.checkInbox': 'Vérifiez votre boîte de réception',
    'auth.confirmEmail': 'Confirmer *l’e-mail*',
    'auth.confirmEmailLoading': 'Confirmation de votre e-mail…',
    'auth.createAccount': 'Créer un compte',
    'auth.createAccountTitle': 'Créer un *compte*',
    'auth.email': 'E-mail',
    'auth.emailConfirmed': 'E-mail confirmé',
    'auth.emailConfirmedBody': 'Merci — votre e-mail est vérifié. Tout est prêt.',
    'auth.enterEmailPassword': 'Entrez votre e-mail et votre mot de passe',
    'auth.forgotPassword': 'Mot de passe *oublié*',
    'auth.forgotPasswordCta': 'Mot de passe oublié ?',
    'auth.forgotPasswordSubtitle': 'Nous vous enverrons un lien pour en créer un nouveau.',
    'auth.goToLogin': 'Aller à la connexion',
    'auth.invalidLogin': 'E-mail ou mot de passe invalide',
    'auth.login': 'Se connecter',
    'auth.loginButton': 'Se connecter',
    'auth.loginSubtitle': 'Connectez-vous pour continuer votre parcours beauté',
    'auth.loggingIn': 'Connexion…',
    'auth.newPassword': 'Nouveau mot de passe',
    'auth.newToBevelure': 'Nouveau sur Bevelure ?',
    'auth.password': 'Mot de passe',
    'auth.passwordMismatch': 'Les mots de passe ne correspondent pas',
    'auth.passwordMin': 'Le mot de passe doit contenir au moins 8 caractères',
    'auth.passwordUpdated': 'Mot de passe mis à jour',
    'auth.passwordUpdatedBody': 'Connectez-vous avec votre nouveau mot de passe. Les autres appareils devront aussi se reconnecter.',
    'auth.rememberNow': 'Vous vous en souvenez ?',
    'auth.repeatPassword': 'Répétez le nouveau mot de passe',
    'auth.resetInvalid': 'Le lien de réinitialisation est invalide ou expiré',
    'auth.resetLinkMissing': 'Le lien de réinitialisation est manquant — demandez-en un nouveau.',
    'auth.resetPassword': 'Réinitialiser le *mot de passe*',
    'auth.resetPasswordSubtitle': 'Choisissez quelque chose de solide — au moins 8 caractères.',
    'auth.sendResetLink': 'Envoyer le lien',
    'auth.sending': 'Envoi…',
    'auth.sentResetBody': 'Si un compte existe pour {email}, nous venons d’envoyer un lien de réinitialisation. Il est valide pendant 1 heure.',
    'auth.signupChooseType': 'Choisissez votre type de compte pour commencer',
    'auth.signupCustomerBody': 'Suivez vos rendez-vous beauté et procédures',
    'auth.signupCustomerTitle': 'Je suis client',
    'auth.signupProviderBody': 'Gérez vos clients et rendez-vous',
    'auth.signupProviderTitle': 'Je suis professionnel',
    'auth.signupSalonBody': 'Inscrivez votre entreprise et invitez votre équipe',
    'auth.signupSalonTitle': 'Nous sommes un salon / studio',
    'signup.acceptLegal': 'Veuillez confirmer que vous avez au moins 18 ans et accepter les Conditions et la Politique de confidentialité.',
    'signup.aboutYou': 'À propos de vous',
    'signup.aboutYouSubtitle': 'Informations personnelles de base',
    'signup.accountSubtitle': 'Comment vous vous connecterez',
    'signup.allergies': 'Allergies',
    'signup.allergiesQuestion': 'Avez-vous des réactions allergiques ?',
    'signup.allergiesSubtitle': 'Cela aide vos professionnels à prendre soin de vous en toute sécurité. Vous pouvez le modifier à tout moment depuis votre profil.',
    'signup.allergyPlaceholder': 'Décrivez vos allergies...',
    'signup.birthDatePlaceholder': 'Sélectionnez votre date de naissance',
    'signup.certificates': 'Certificats',
    'signup.certificatesFileHint': 'PDF, JPG, PNG — jusqu’à 10 Mo chacun',
    'signup.certificatesLater': 'Aucun souci — vous pourrez les téléverser plus tard depuis votre profil.',
    'signup.certificatesQuestion': 'Avez-vous des certificats à partager ?',
    'signup.certificatesRequired': 'Veuillez répondre à la question sur les certificats.',
    'signup.certificatesSubtitle': 'Montrez vos qualifications professionnelles aux clients',
    'signup.certificatesUpload': 'Cliquez pour téléverser des certificats',
    'signup.completeRegistration': 'Terminer l’inscription',
    'signup.confirmPassword': 'Confirmer le mot de passe',
    'signup.contact': 'Contact',
    'signup.createSalon': 'Créer le salon',
    'signup.creatingAccount': 'Création du compte…',
    'signup.creatingSalon': 'Création du salon…',
    'signup.customerTitle': 'Inscription *client*',
    'signup.dateOfBirth': 'Date de naissance',
    'signup.firstName': 'Prénom',
    'signup.invalidEmail': 'Veuillez entrer une adresse e-mail valide.',
    'signup.invalidSalonEmail': 'Veuillez entrer un e-mail de salon valide ou laisser le champ vide.',
    'signup.languagesYouSpeak': 'Langues parlées',
    'signup.lastName': 'Nom',
    'signup.legalPrefix': 'Je confirme avoir au moins 18 ans et j’accepte les',
    'signup.missingCity': 'Veuillez indiquer votre ville.',
    'signup.missingCountry': 'Veuillez indiquer votre pays.',
    'signup.missingAddress': 'Veuillez indiquer votre adresse.',
    'signup.missingDateOfBirth': 'Veuillez indiquer votre date de naissance.',
    'signup.minimumAge': 'Vous devez avoir au moins 18 ans pour créer un compte.',
    'signup.missingEmail': 'Veuillez indiquer votre e-mail.',
    'signup.missingFirstName': 'Veuillez indiquer votre prénom.',
    'signup.missingLastName': 'Veuillez indiquer votre nom.',
    'signup.missingPassword': 'Veuillez saisir un mot de passe.',
    'signup.missingServiceType': 'Veuillez indiquer votre type de service.',
    'signup.missingSex': 'Veuillez indiquer votre sexe.',
    'signup.missingYearsInService': 'Veuillez indiquer vos années d’expérience.',
    'signup.passwordPlaceholder': 'Minimum 8 caractères',
    'signup.profession': 'Profession',
    'signup.professionSubtitle': 'Ce que vous proposez',
    'signup.providerTitle': 'Inscription *professionnel*',
    'signup.repeatPasswordPlaceholder': 'Répéter le mot de passe',
    'signup.required': 'Obligatoire',
    'signup.salonAdminSubtitle': 'Le compte administrateur du salon',
    'signup.salonContactSubtitle': 'Facultatif — affiché sur le profil du salon',
    'signup.salonMissingName': 'Veuillez donner un nom à votre salon.',
    'signup.salonNamePlaceholder': 'ex. Velvet Beauty Studio',
    'signup.salonTitle': 'Inscription *salon*',
    'signup.selectOption': 'Veuillez sélectionner une option.',
    'signup.serviceTypePlaceholder': 'p. ex., prothésiste ongulaire, esthéticienne',
    'signup.sexRequired': 'Veuillez sélectionner votre sexe',
    'signup.signupFailed': 'Échec de l’inscription',
    'signup.stepOf2': 'Étape {step} sur 2',
    'signup.whereYouAre': 'Où vous êtes',
    'signup.whereYouAreSubtitle': 'Utilisé pour trouver des professionnels locaux',
    'signup.whereYouWork': 'Où vous travaillez',
    'signup.whereYouWorkSubtitle': 'Aide les clients à vous trouver',
    'signup.yearsInService': 'Années d’expérience',
    'signup.yearsPlaceholder': 'ex. 5',
    'signup.yourSalon': 'Votre salon',
    'signup.yourSalonSubtitle': 'Ce que les clients et votre équipe verront',

    'landing.nav.platform': 'Plateforme',
    'landing.nav.features': 'Fonctionnalités',
    'landing.nav.story': 'Histoire',
    'landing.nav.trust': 'Confiance',
    'landing.nav.startNow': 'Commencer',
    'landing.nav.openMenu': 'Ouvrir le menu',
    'landing.cta.startJourney': 'Commencer votre parcours',
    'landing.stores.appStoreEyebrow': 'Télécharger dans',
    'landing.stores.appStoreName': 'App Store',
    'landing.stores.googlePlayEyebrow': 'Disponible sur',
    'landing.stores.googlePlayName': 'Google Play',
    'landing.appBanner.subtitle': 'Ouvrir dans l’app Bevelure',
    'landing.appBanner.action': 'OUVRIR',
    'landing.appBanner.close': 'Fermer la bannière de l’application',
    'landing.panel.title': 'Carnet beauté',
    'landing.panel.visits': 'visites',
    'landing.panel.providers': 'prestataires',
    'landing.panel.rating': 'note',
    'landing.panel.row1Label': 'Allergies et routine de soins',
    'landing.panel.row1Meta': 'Partagé avant votre arrivée',
    'landing.panel.row2Label': 'Soin Hydrafacial',
    'landing.panel.row2Meta': 'Terminé · photos ajoutées',
    'landing.panel.row3Label': 'Message de Maya',
    'landing.panel.row3Meta': 'Soins après Hydrafacial · il y a 1 h',
    'landing.hero.eyebrow': 'Votre historique beauté, enfin à vous',
    'landing.hero.body': 'Un carnet élégant et discret qui vous appartient et vous suit de prestataire en prestataire — votre histoire, prête à être partagée avec la prochaine personne qui prendra soin de vous.',
    'landing.hero.tile1Title': 'À vous',
    'landing.hero.tile1Body': 'un carnet qui voyage avec vous',
    'landing.hero.tile2Title': 'Prêt',
    'landing.hero.tile2Body': 'le contexte avant chaque client',
    'landing.hero.tile3Title': 'En phase',
    'landing.hero.tile3Body': 'toute l’équipe, un seul calendrier',
    'landing.trustBar.item1': 'Un historique qui vous appartient',
    'landing.trustBar.item2': 'Le contexte avant chaque client',
    'landing.trustBar.item3': 'Un calendrier pour toute l’équipe',
    'landing.three.eyebrow': 'Un carnet, trois points de vue',
    'landing.three.title': 'Le même carnet. Trois lectures.',
    'landing.three.body': 'Les soins beauté relient des personnes qui partagent rarement les mêmes notes. Bevelure leur donne une source de vérité — et montre à chacun uniquement ce qui compte pour lui.',
    'landing.three.side1Tag': 'Pour vous',
    'landing.three.side1Title': 'Votre historique complet — à garder, à partager.',
    'landing.three.side1Desc': 'Chaque visite, produit et photo au même endroit, qui vous suit partout.',
    'landing.three.side2Tag': 'Pour votre prestataire',
    'landing.three.side2Title': 'Tout ce qu’il faut savoir avant de commencer.',
    'landing.three.side2Desc': 'Le contexte que vous choisissez de partager — allergies, soins passés, ce qui a fonctionné — prêt dès votre arrivée.',
    'landing.three.side3Tag': 'Pour le salon',
    'landing.three.side3Title': 'Chaque réservation et chaque membre dans un espace calme.',
    'landing.three.side3Desc': 'L’équipe, le calendrier et les réservations sous un même toit — coordonnés, jamais chaotiques.',
    'landing.audiences.eyebrow': 'Un système pour chaque acteur du soin',
    'landing.audiences.title': 'Ce que chaque acteur reçoit vraiment.',
    'landing.audiences.clientTag': 'Pour les clients',
    'landing.audiences.clientTitle': 'Votre historique, enfin à vous.',
    'landing.audiences.clientLead': 'Tous vos soins, dans un endroit qui voyage avec vous. Plus besoin de tout recommencer avec un nouveau prestataire ni de deviner ce qui avait fonctionné.',
    'landing.audiences.clientPoint1': 'Gardez un dossier complet — visites, produits, photos, prix.',
    'landing.audiences.clientPoint2': 'Vos allergies et sensibilités sont visibles pour chaque prestataire.',
    'landing.audiences.clientPoint3': 'Partagez vos cinq dernières visites ou un type de soin — puis révoquez l’accès à tout moment.',
    'landing.audiences.clientPoint4': 'Voyez ce que vous dépensez réellement pour vous, mois par mois.',
    'landing.audiences.clientCta': 'Créer mon historique',
    'landing.audiences.providerTag': 'Pour les prestataires',
    'landing.audiences.providerTitle': 'Connaissez votre client avant qu’il ne s’installe.',
    'landing.audiences.providerLead': 'Ouvrez un rendez-vous et le contexte est déjà là — ce qui a été fait, les réactions, les soins à domicile. Moins de suppositions, plus de sécurité, un vrai carnet client.',
    'landing.audiences.providerPoint1': 'Voyez l’historique que le client a choisi de partager avant son arrivée.',
    'landing.audiences.providerPoint2': 'Vos notes, produits et photos de résultats restent disponibles entre les visites.',
    'landing.audiences.providerPoint3': 'Configurez vos services, options, horaires et prix une seule fois.',
    'landing.audiences.providerPoint4': 'Échangez avec les clients sans donner votre numéro.',
    'landing.audiences.providerCta': 'Ouvrir un compte prestataire',
    'landing.audiences.salonTag': 'Pour les salons',
    'landing.audiences.salonTitle': 'Tout votre salon, synchronisé.',
    'landing.audiences.salonLead': 'Rassemblez l’équipe, le calendrier et chaque réservation sous un même toit — avec la visibilité nécessaire, sans empiéter sur le travail de chacun.',
    'landing.audiences.salonPoint1': 'Invitez et gérez toute votre équipe en quelques minutes.',
    'landing.audiences.salonPoint2': 'Voyez toutes les réservations du salon, tous prestataires confondus.',
    'landing.audiences.salonPoint3': 'Coordonnez sans excès — les admins ne modifient jamais les services des prestataires.',
    'landing.audiences.salonPoint4': 'Pas de double réservation, pas de relais manqués.',
    'landing.audiences.salonCta': 'Inscrire votre salon',
    'landing.features.eyebrow': 'Tout au même endroit',
    'landing.features.title': 'Tout ce que vous auriez aimé voir suivi depuis le début.',
    'landing.features.body': 'Votre historique passe avant tout — mais Bevelure gère aussi ce qui l’entoure : réservation, conversation, prix, photos et visites ajoutées ailleurs.',
    'landing.features.item1Title': 'Un carnet vraiment à vous',
    'landing.features.item1Desc': 'Chaque visite, produit, note et photo dans une chronologie qui vous suit — changer de prestataire ne veut plus dire repartir de zéro.',
    'landing.features.item2Title': 'Réserver simplement',
    'landing.features.item2Desc': 'Voyez les prix et les options, choisissez un créneau. Ou ajoutez une visite avec un salon qui n’est pas encore sur Bevelure.',
    'landing.features.item3Title': 'Vous décidez qui voit quoi',
    'landing.features.item3Desc': 'Partagez vos cinq dernières visites ou un type de soin avec un nouveau prestataire — puis retirez l’accès quand vous voulez.',
    'landing.features.item4Title': 'Avant, après, et rien qu’à vous',
    'landing.features.item4Desc': 'Photos de résultats, photos de procédure pour le prestataire, et photos privées visibles seulement par vous — sans mélange.',
    'landing.features.item5Title': 'Où que vous réserviez',
    'landing.features.item5Desc': 'Prestataire à Miami, vous à Berlin — l’heure du salon et la vôtre côte à côte. Toute ville, toute devise, aucun rendez-vous manqué.',
    'landing.features.item6Title': 'Parlez-en simplement',
    'landing.features.item6Desc': 'Échangez avant et après une visite — détail à clarifier, conseils après-soin, photo — sans partager votre numéro.',
    'landing.features.item7Title': 'Des prix honnêtes, conservés',
    'landing.features.item7Desc': 'Les prix réels sont figés pour chaque visite, dans la devise du prestataire — votre historique ne se réécrit pas plus tard.',
    'landing.features.item8Title': 'Notes privées dans les deux sens',
    'landing.features.item8Desc': 'Notez une visite pour votre mémoire — les prestataires aussi. Pas de classement public, juste une note honnête.',
    'landing.founder.eyebrow': 'Histoire de la fondatrice',
    'landing.founder.title': 'Pourquoi je crois en Bevelure.',
    'landing.founder.p1': 'J’ai créé Bevelure parce que je n’arrivais jamais à me souvenir de mes propres soins — ce qui avait été fait, quand, et quels produits avaient été utilisés. À chaque nouveau spécialiste, les questions revenaient : *qu’avez-vous fait la dernière fois, quels produits ont été utilisés, avez-vous eu une réaction allergique ?* Je cherchais dans ma mémoire, sans pouvoir donner une réponse solide.',
    'landing.founder.p2': 'Ce qui sortait ressemblait souvent à : *“Je me souviens du résultat, mais pas du nom exact du soin, des produits, ni de ce qui avait irrité ma peau.”* Pas très utile. Je voulais tout avoir quelque part — produits exacts, détails de chaque rendez-vous — plutôt que dans ma tête.',
    'landing.founder.p3': 'C’est exactement Bevelure : garder le dossier de tout au même endroit et le partager avec le prestataire de votre choix.',
    'landing.founder.p4': 'Mes rendez-vous beauté étaient aussi un joli désordre dans mon calendrier. Je devais faire défiler longtemps pour retrouver ce que j’avais fait et quand réserver à nouveau. Maintenant tout est au même endroit : votre historique beauté.',
    'landing.founder.p5': 'Je voyage beaucoup, donc je vois souvent de nouveaux prestataires pour continuer mes soins — et l’histoire se répète. Pour un facial, on me demande toujours *quels soins maison j’utilise et quels ingrédients contiennent mes crèmes*. Avec Bevelure, vous n’avez plus à y penser : ajoutez les photos de votre routine avec les étiquettes et ingrédients, puis partagez-les quand il faut.',
    'landing.founder.p6': 'Je pense que beaucoup de personnes vivent exactement ces situations. Je veux vraiment les aider. C’est pour cela que j’ai créé Bevelure.',
    'landing.founder.quote': '« Pour chaque histoire beauté qui mérite d’être gardée en mémoire. Avec soin. »',
    'landing.founder.signature': 'Ievgeniia Andrieieva, Fondatrice et CEO',
    'landing.trust.eyebrow': 'Couche de confiance',
    'landing.trust.title': 'Conçu pour ce qui compte le plus — et ce qui est le plus difficile à retrouver.',
    'landing.trust.body': 'Informations sensibles, vrais rendez-vous, relations qui durent des années. L’expérience paraît simple parce que les limites importantes tiennent discrètement tout ensemble.',
    'landing.trust.item1Title': 'Confidentialité intégrée',
    'landing.trust.item1Desc': 'Vos notes privées, les notes internes du prestataire et ce que vous choisissez de partager restent séparés — visibles seulement par la bonne personne.',
    'landing.trust.item2Title': 'Rester en contact, simplement',
    'landing.trust.item2Desc': 'Chat, confirmations et rappels doux gardent tout le monde aligné, sans laisser passer l’essentiel.',
    'landing.trust.item3Title': 'Pas de classement public',
    'landing.trust.item3Desc': 'Notez une visite en privé, pour votre mémoire. Les soins beauté ne devraient pas devenir un tableau public.',
    'landing.final.title': 'Votre histoire beauté, enfin au même endroit.',
    'landing.final.body': 'Que vous réserviez, réalisiez le soin ou dirigiez le salon — tout vit dans un seul carnet, beau et privé.',
    'landing.footer.tagline': 'Votre histoire beauté, au même endroit.',
    'landing.footer.contact': 'Contact',
    'landing.footer.site': 'Site',

    'block.title': 'Bloquer {name} ?',
    'block.body': 'Cette personne ne pourra plus réserver ni accepter de nouveaux rendez-vous avec vous. Les anciens rendez-vous et notes restent visibles des deux côtés.',
    'block.reasonLabel': 'Raison (vous seul pouvez la voir)',
    'block.reasonPlaceholder': 'Facultatif — utile si vous relisez vos blocages plus tard',
    'block.reportUser': 'Signaler également cet utilisateur',
    'block.reportHelp': 'Le compte et le motif sélectionné seront transmis à Bevelure pour examen. L’autre utilisateur ne verra pas votre signalement.',
    'block.reportReasonLabel': 'Motif du signalement',
    'block.reportReasonPlaceholder': 'Choisissez un motif',
    'block.reportReasonRequired': 'Choisissez un motif avant de signaler.',
    'block.reportReasonSpam': 'Spam ou arnaque',
    'block.reportReasonHarassment': 'Harcèlement ou menaces',
    'block.reportReasonContent': 'Contenu inapproprié',
    'block.reportReasonSafety': 'Comportement dangereux',
    'block.reportReasonImpersonation': 'Usurpation d’identité',
    'block.reportReasonOther': 'Autre',
    'block.blockAndReport': 'Bloquer et signaler',
    'block.cancelFutureOne': 'Annuler aussi {count} rendez-vous à venir entre vous.',
    'block.cancelFutureMany': 'Annuler aussi {count} rendez-vous à venir entre vous.',
    'block.keepFutureHint': 'Si vous laissez cette option désactivée, ils restent dans les deux calendriers — le blocage empêche seulement les nouvelles réservations.',
    'block.failed': 'Impossible de bloquer',
    'block.blocking': 'Blocage…',
    'block.block': 'Bloquer',

    'shareModal.titlePrefix': 'Partager',
    'shareModal.titleEmphasis': 'l’historique',
    'shareModal.subtitle': 'Générez un lien en lecture seule pour un prestataire.',
    'shareModal.procedureTypes': 'Types de procédure',
    'shareModal.procedurePlaceholder': 'ex. coupe homme, manucure…',
    'shareModal.procedureHelp': 'Choisissez dans vos procédures ou saisissez la vôtre. Seuls les rendez-vous terminés correspondants seront visibles.',
    'shareModal.whatToShare': 'Que partager',
    'shareModal.recipientEmail': 'E-mail du destinataire',
    'shareModal.optional': 'facultatif',
    'shareModal.emailHelp': 'Si renseigné, le lien est envoyé par e-mail. Sinon, un QR code / lien est simplement généré.',
    'shareModal.consent': 'Je comprends que toute personne ayant ce lien peut voir l’historique que je partage pour les procédures sélectionnées — notes, produits, recommandations et photos de résultat/procédure. Mes notes privées et photos privées ne sont jamais partagées.',
    'shareModal.createError': 'Impossible de créer le lien de partage. Réessayez.',
    'shareModal.generateLink': 'Générer le lien',
    'shareModal.emailIt': 'Envoyer par e-mail',
    'shareModal.scanHint': 'Scannez avec l’appareil photo du téléphone pour ouvrir l’historique partagé.',
    'shareModal.link': 'Lien',
    'shareModal.copyLink': 'Copier le lien',
    'shareModal.procedure': 'Procédure',
    'shareModal.mode': 'Mode',
    'shareModal.expires': 'Expire',
    'shareModal.sentTo': 'Envoyé à',
    'shareModal.generateAnother': 'Générer un autre',

    'contactSetup.title': 'Rendez-vous plus facile à trouver',
    'contactSetup.bodyPrefix': 'Ajoutez votre',
    'contactSetup.socialLink': 'lien social',
    'contactSetup.bodySuffix': 'pour que les gens puissent vous trouver autrement que par e-mail.',
    'contactSetup.addContactInfo': 'Ajouter des coordonnées',

    'errorBoundary.titlePrefix': 'Quelque chose s’est',
    'errorBoundary.titleEmphasis': 'mal passé',
    'errorBoundary.body': 'Une erreur inattendue est survenue pendant l’affichage de cette page. Actualisez pour réessayer — si cela continue, dites-le-nous.',
    'errorBoundary.refresh': 'Actualiser la page',
    'errorBoundary.dismiss': 'Fermer et continuer',

    'confirm.defaultTitle': 'Êtes-vous sûr ?',
    'impact.futureBookingOne': '{count} réservation future sera affectée.',
    'impact.futureBookingMany': '{count} réservations futures seront affectées.',
    'impact.cancelAffectedOn': 'Elles seront toutes annulées et les clients seront notifiés.',
    'impact.cancelAffectedOff': 'Laissez désactivé pour les garder comme prévues — seules les nouvelles réservations sont bloquées.',
    'impact.actionFailed': 'Action échouée',
    'impact.working': 'Traitement…',

    'desktopHome.settings': 'Paramètres de l’accueil',
    'desktopHome.layoutTitle': 'Mise en page',
    'desktopHome.layout1': 'Séparé — À venir et Récent près du calendrier',
    'desktopHome.layout2': 'Accueil compact avec listes empilées',
    'desktopHome.layout3': 'Colonne stats · calendrier · listes dessous',
    'desktopHome.layout4': 'Listes défilantes à la hauteur du calendrier',
    'auth.tryAgain': 'Une erreur est survenue — réessayez',
    'auth.updatePassword': 'Mettre à jour le mot de passe',
    'auth.updating': 'Mise à jour…',
    'auth.verificationInvalid': 'Le lien de vérification est invalide ou expiré.',
    'auth.verificationMissing': 'Le lien de vérification ne contient pas de token.',
    'auth.verifyErrorTitle': 'Impossible de vérifier',
    'auth.verifyResendHint': 'Connectez-vous à votre compte et touchez Renvoyer dans la bannière de vérification.',
    'auth.welcomeBack': 'Bon *retour*',
    'notif.providerRequest.title': 'Nouvelle demande de rendez-vous',
    'notif.providerRequest.body': 'Un client souhaite réserver. Confirmez ou refusez.',
    'notif.providerScheduled.title': 'Nouveau rendez-vous',
    'notif.providerScheduled.body': 'Un nouveau rendez-vous a été programmé pour vous.',
    'notif.customerCreated.title': 'Rendez-vous programmé',
    'notif.customerCreated.body': '{provider} vous a réservé {service}.',
    'notif.customerConfirmed.title': 'Rendez-vous confirmé',
    'notif.customerConfirmed.body': '{provider} a confirmé votre {service}.',
    'notif.customerRejected.title': 'Demande refusée',
    'notif.customerRejected.body': '{provider} n’a pas pu accepter votre {service}. Choisissez un autre horaire.',
    'notif.cancelled.title': 'Rendez-vous annulé',
    'notif.cancelled.body': 'Votre {service} avec {other} a été annulé.',
    'notif.customerExpired.title': 'Demande expirée',
    'notif.customerExpired.body': 'Votre demande de {service} a expiré — non confirmée à temps. Choisissez un autre créneau.',
    'notif.detailsUpdated.title': 'Détails du soin mis à jour',
    'notif.detailsUpdated.body': '{provider} a ajouté des notes ou des photos à votre {service}.',
    'notif.reminderCustomer.title': 'Rendez-vous dans 48 heures',
    'notif.reminderCustomer.body': 'Votre {service} avec {other} est dans environ 48 heures.',
    'notif.reminderProvider.title': 'Rendez-vous dans 48 heures',
    'notif.reminderProvider.body': '{service} avec {other} est dans environ 48 heures.',
    'notif.salonInvite.title': 'Invitation au salon',
    'notif.salonInvite.body': '{salon} vous invite à rejoindre son équipe.',
    'notif.memberRemoved.title': 'Retiré du salon',
    'notif.memberRemoved.body': 'L’administrateur vous a retiré de {salon}.',
    'notif.memberLeft.title': 'Un prestataire a quitté votre salon',
    'notif.memberLeft.body': '{provider} a quitté l’équipe.',
    'notif.inviteAccepted.title': 'Un prestataire a rejoint votre salon',
    'notif.inviteAccepted.body': '{provider} a accepté l’invitation et rejoint l’équipe.',
    'notif.inviteDeclined.title': 'Invitation refusée',
    'notif.inviteDeclined.body': '{provider} a refusé votre invitation au salon.',
    'notif.salonBookingRequested.title': 'Nouvelle demande dans votre salon',
    'notif.salonBookingRequested.body': '{customer} souhaite réserver {service} avec {provider} — en attente de confirmation.',
    'notif.salonBooking.title': 'Nouvelle réservation dans votre salon',
    'notif.salonBooking.body': '{provider} a un nouveau rendez-vous avec {customer}.',
    'notif.salonBookingCancelled.title': 'Réservation annulée dans votre salon',
    'notif.salonBookingCancelled.body': 'Le rendez-vous de {provider} avec {customer} a été annulé.',
    'auth.yourPassword': 'Votre mot de passe',

    'nav.navigation': 'Navigation',
    'nav.account': 'Compte',
    'nav.menu': 'Menu',
    'nav.home': 'Accueil',
    'nav.search': 'Recherche',
    'nav.searchProviders': 'Rechercher des prestataires',
    'nav.favorites': 'Favoris',
    'nav.messages': 'Messages',
    'nav.myCareHub': 'Mon espace soins',
    'nav.spending': 'Dépenses',
    'nav.profile': 'Profil',
    'nav.history': 'Historique',
    'nav.upcoming': 'À venir',
    'nav.customers': 'Clients',
    'nav.team': 'Équipe',
    'nav.schedule': 'Planning',
    'nav.services': 'Services',
    'nav.portfolio': 'Portfolio',
    'nav.mySalon': 'Mon salon',
    'nav.earnings': 'Revenus',
    'nav.pending': 'En attente',
    'nav.signOut': 'Se déconnecter',
    'nav.terms': 'Conditions',
    'nav.privacy': 'Confidentialité',
    'nav.notifications': 'Notifications',
    'nav.salonInviteTitle': '{count} invitation{plural} de salon en attente',

    'notifications.allCaughtUp': 'Vous êtes à jour',
    'notifications.allLoaded': 'C’est tout : {count} notifications.',
    'notifications.empty': 'Aucune notification pour le moment',
    'notifications.markAsRead': 'Marquer comme lu',
    'notifications.title': 'Notifications',

    'salonTeam.inviteFromPrefix': 'Invitez des prestataires depuis',
    'salonTeam.manageInvitesPrefix': 'Gérez les invitations depuis',
    'salonTeam.noProviders': 'Aucun prestataire pour le moment',
    'salonTeam.noSearchMatches': 'Aucun membre ne correspond à votre recherche.',
    'salonTeam.providerCount': '{count} prestataire(s)',
    'salonTeam.searchPlaceholder': 'Rechercher par nom ou service…',
    'salonTeam.sortName': 'Nom (A-Z)',
    'salonTeam.sortRecentlyJoined': 'Récemment arrivé',

    'salon.accept': 'Accepter',
    'salon.activeProviders': '{count} prestataire(s) actif(s)',
    'salon.address': 'Adresse',
    'salon.addressPlaceholder': 'Commencez à saisir votre adresse…',
    'salon.admin': 'Admin',
    'salon.city': 'Ville',
    'salon.close': 'Fermer',
    'salon.country': 'Pays',
    'salon.decline': 'Refuser',
    'salon.description': 'Description',
    'salon.descriptionPlaceholder': 'Dites aux clients ce qui rend votre salon spécial',
    'salon.desktopTitleEmphasis': 'salon',
    'salon.desktopTitlePrefix': 'Mon',
    'salon.editDetails': 'Modifier les détails',
    'salon.invitation': 'Invitation salon',
    'salon.invitationSent': 'Invitation envoyée.',
    'salon.inviteProvider': 'Inviter un prestataire',
    'salon.inviteProviderHelp': 'Il doit déjà avoir un compte prestataire Bevelure. Nous le notifierons pour accepter.',
    'salon.invitedAs': 'Invité comme {role}',
    'salon.leaveDescription': 'Vous ne ferez plus partie de ce salon. Les nouvelles réservations prises après cela ne seront plus rattachées à ce salon. Les rendez-vous passés restent dans votre historique.',
    'salon.leaveSalon': 'Quitter le salon',
    'salon.leaveTitle': 'Quitter {name} ?',
    'salon.name': 'Nom du salon',
    'salon.noPendingInvites': 'Aucune invitation en attente. Les membres actifs sont dans {team}.',
    'salon.notPartBody': 'Les salons invitent les prestataires par e-mail. Si un salon vous invite, l’invitation apparaîtra ici.',
    'salon.notPartTitle': 'Vous ne faites partie d’aucun salon',
    'salon.pendingInvites': 'Invitations en attente',
    'salon.phone': 'Téléphone',
    'salon.phonePlaceholder': 'Numéro du salon',
    'salon.postalCode': 'Code postal',
    'salon.providerEmail': 'E-mail du prestataire',
    'salon.providerFallback': 'Prestataire',
    'salon.publicEmail': 'E-mail public',
    'salon.removeMember': 'Retirer le membre',
    'salon.revokeInvite': 'Révoquer l’invitation',
    'salon.roleAdmin': 'admin',
    'salon.roleMember': 'membre',
    'salon.saveDetailsError': 'Impossible d’enregistrer les détails du salon',
    'salon.sendInvite': 'Envoyer l’invitation',
    'salon.sending': 'Envoi…',
    'salon.socialLink': 'Site / lien social',
    'salon.subtitle': 'Gérez votre salon et votre équipe.',
    'salon.teamLink': 'Équipe',
    'salon.title': 'Mon salon',
    'salon.uploadLogo': 'Téléverser le logo',
    'salon.view': 'Voir',
    'salon.viewProfile': 'Voir le profil du salon',
    'salon.youWorkAt': 'Vous travaillez chez',

    'teamBookings.all': 'Tout',
    'teamBookings.history': 'Historique',
    'teamBookings.noBookings': 'Aucune réservation ici pour le moment.',
    'teamBookings.subtitle': 'Tous les rendez-vous des prestataires de votre salon.',
    'teamBookings.title': 'Réservations équipe',
    'teamBookings.titleEmphasis': 'équipe',
    'teamBookings.titlePrefix': 'Réservations',
    'teamBookings.upcoming': 'À venir',
    'salonAppointments.cancelReason': 'Annulé par l’administrateur du salon',

    'salonProfile.messageSalon': 'Message au salon',
    'salonProfile.noProviders': 'Aucun prestataire listé pour le moment.',
    'salonProfile.openingChat': 'Ouverture du chat…',
    'salonProfile.ourTeam': 'Notre équipe',
    'salonProfile.provider': 'prestataire',
    'salonProfile.providers': 'prestataires',
    'salonProfile.title': 'Salon',

    'portfolio.addMore': 'Ajouter plus',
    'portfolio.addPhoto': 'Ajouter une photo',
    'portfolio.addToPortfolio': 'Ajouter au portfolio',
    'portfolio.bulkApplyHint': 'La catégorie et la légende s’appliquent aux {count} photos.',
    'portfolio.caption': 'Légende',
    'portfolio.captionOptional': 'Légende (facultatif)',
    'portfolio.captionPlaceholder': 'Courte description affichée sous la photo',
    'portfolio.category': 'Catégorie',
    'portfolio.categoryCount': '{count} catégorie(s)',
    'portfolio.categoryPlaceholder': 'ex. manucure, Botox, Hydrafacial',
    'portfolio.choosePhotos': 'Touchez pour choisir des photos',
    'portfolio.deletePhotoMessage': 'Elle sera retirée de votre portfolio public.',
    'portfolio.deletePhotoTitle': 'Supprimer la photo ?',
    'portfolio.desktopTitleEmphasis': 'portfolio',
    'portfolio.desktopTitlePrefix': 'Mon',
    'portfolio.editTitle': 'Modifier l’élément du portfolio',
    'portfolio.fileHint': 'JPG, PNG jusqu’à 10 Mo · sélection multiple',
    'portfolio.hide': 'Masquer',
    'portfolio.noFiles': 'Aucun fichier',
    'portfolio.noItemsBody': 'Montrez votre travail — les clients le voient sur votre profil public. Téléversez directement ou promouvez des photos de rendez-vous terminés.',
    'portfolio.noItemsTitle': 'Aucun élément dans le portfolio',
    'portfolio.noItemsYet': 'Aucun élément',
    'portfolio.private': 'Privé',
    'portfolio.privateDescription': 'Enregistré dans votre portfolio mais masqué aux clients.',
    'portfolio.public': 'Public',
    'portfolio.publicDescription': 'Visible sur votre profil public par toute personne qui l’ouvre.',
    'portfolio.remove': 'Supprimer',
    'portfolio.shortDescription': 'Courte description',
    'portfolio.show': 'Afficher',
    'portfolio.title': 'Portfolio',
    'portfolio.uncategorized': 'Sans catégorie',
    'portfolio.upload': 'Téléverser',
    'portfolio.uploadCount': 'Téléverser ({count})',
    'portfolio.uploading': 'Téléversement…',

    'emailVerify.resendError': 'Impossible de renvoyer — réessayez dans un instant.',
    'emailVerify.sent': 'E-mail envoyé — vérifiez votre boîte de réception.',
    'emailVerify.message': 'Confirmez votre e-mail pour recevoir les notifications de réservation.',
    'emailVerify.sending': 'Envoi…',
    'emailVerify.resend': 'Renvoyer',

    'providerSetup.title': 'Terminez la configuration de votre profil',
    'providerSetup.bodyPrefix': 'Les clients ne peuvent pas réserver tant que vous n’ajoutez pas',
    'providerSetup.oneService': 'au moins un service',
    'providerSetup.workingHours': 'vos heures de travail',
    'providerSetup.addService': 'Ajouter un service',
    'providerSetup.setHours': 'Définir les heures',

    'profile.title': 'Profil',
    'profile.preferences': 'Préférences',
    'profile.language': 'Langue',
    'profile.pushNotifications': 'Notifications push',
    'profile.languageAria': 'Langue de l’interface',
    'profile.providerLanguages': 'Langues parlées',
    'profile.providerLanguagesLabel': 'Langues',
    'profile.currencyLabel': 'Devise',
    'profile.failedUpdate': 'Impossible de mettre à jour le profil',
    'profile.roleCustomer': 'Client',
    'profile.roleCareProvider': 'Prestataire',
    'profile.roleSalonAdmin': 'Administrateur du salon',
    'profile.uploadPhoto': 'Importer une photo',
    'profile.rating': 'Note',
    'profile.ratingSingular': 'note',
    'profile.ratingPlural': 'notes',
    'profile.fromClients': 'de clients',
    'profile.fromProviders': 'de prestataires',
    'profile.byService': 'Par service',
    'profile.noRatingsCustomer': 'Aucune note pour le moment. Les prestataires pourront vous noter après un rendez-vous terminé.',
    'profile.noRatingsProvider': 'Aucune note pour le moment. Les clients pourront vous noter après un rendez-vous terminé.',
    'profile.personalInformation': 'Informations personnelles',
    'profile.dateOfBirth': 'Date de naissance',
    'profile.sex': 'Sexe',
    'profile.location': 'Adresse',
    'profile.address': 'Adresse',
    'profile.addressPlaceholder': 'Commencez à saisir votre adresse...',
    'profile.timeZone': 'Fuseau horaire',
    'profile.timeZonePrefix': 'Fuseau horaire : {zone}',
    'profile.timeZoneHelp': 'Vos horaires de travail utilisent ce fuseau. Les clients ailleurs voient aussi leur propre heure.',
    'profile.contact': 'Contact',
    'profile.phone': 'Téléphone',
    'profile.social': 'Réseaux sociaux',
    'profile.socialMediaLink': 'Lien social',
    'profile.socialPlaceholder': 'https://instagram.com/votre-compte',
    'profile.contactHelp': 'Pour que les clients puissent vous trouver et réserver par téléphone ou lien social. Le téléphone est enregistré au format international standard — ajoutez l’indicatif pays.',
    'profile.noContactDetails': 'Aucune coordonnée pour le moment.',
    'profile.allergies': 'Allergies',
    'profile.noAllergies': 'Aucune allergie',
    'profile.hasAllergies': 'J’ai des allergies',
    'profile.notes': 'Notes',
    'profile.allergyPlaceholder': 'Latex, parfums, etc.',
    'profile.allergiesNoDetails': 'Allergies indiquées (sans détails)',
    'profile.noKnownAllergies': 'Aucune allergie connue',
    'profile.careRoutine': 'Routine de soins',
    'profile.careRoutineHelp': 'Décrivez ou photographiez votre routine à domicile (produits, étapes). Elle est partagée avec vos prestataires pour leur donner du contexte.',
    'profile.careRoutinePlaceholder': 'ex. Matin : nettoyant doux, vitamine C, SPF. Soir : rétinol 2x/semaine...',
    'profile.addPhoto': 'Ajouter une photo',
    'profile.removePhotoTitle': 'Supprimer la photo ?',
    'profile.removePhotoBody': 'Cette photo sera supprimée définitivement.',
    'profile.removePhoto': 'Supprimer',
    'profile.professionalDetails': 'Détails professionnels',
    'profile.yearExperience': '{service} · {count} an d’expérience',
    'profile.yearsExperience': '{service} · {count} ans d’expérience',
    'profile.bio': 'Bio',
    'profile.bioPlaceholder': 'Une courte présentation visible sur votre profil public...',
    'profile.about': 'À propos',
    'profile.noBio': 'Aucune bio pour le moment — ajoutez-en une pour aider les clients à vous faire confiance.',
    'profile.currencyHelp': 'Utilisée pour tous vos services et options à venir. Les anciens rendez-vous gardent la devise dans laquelle ils ont été réservés.',
    'profile.certificates': 'Certificats',
    'profile.addCertificate': 'Ajouter',
    'profile.certificatesHelp': 'Affichés sur votre profil public. Images ou PDF.',
    'profile.noCertificates': 'Aucun certificat pour le moment.',
    'profile.removeCertificateTitle': 'Supprimer le certificat ?',
    'profile.removeCertificateBody': 'Ce certificat sera supprimé définitivement.',
    'profile.removeCertificate': 'Supprimer',
    'profile.password': 'Mot de passe',
    'profile.changePassword': 'Modifier',
    'profile.currentPassword': 'Mot de passe actuel',
    'profile.newPassword': 'Nouveau mot de passe',
    'profile.confirmNewPassword': 'Confirmer le nouveau mot de passe',
    'profile.newPasswordMin': 'Le nouveau mot de passe doit contenir au moins 8 caractères',
    'profile.newPasswordsMismatch': 'Les nouveaux mots de passe ne correspondent pas',
    'profile.passwordUpdated': 'Mot de passe mis à jour. Les autres appareils devront se reconnecter.',
    'profile.passwordChangeFailed': 'Impossible de modifier le mot de passe',
    'profile.newPasswordPlaceholder': 'Au moins 8 caractères',
    'profile.confirmNewPasswordPlaceholder': 'Répétez le nouveau mot de passe',
    'profile.updatePassword': 'Mettre à jour le mot de passe',
    'profile.deleteAccountTitle': 'Supprimer le compte',
    'profile.deleteAccountBody': 'La suppression de votre compte efface définitivement votre profil et les données personnelles qui y sont associées. Les rendez-vous à venir ou en cours seront annulés. Cette action est irréversible.',
    'profile.deleteAccount': 'Supprimer le compte',
    'profile.deletingAccount': 'Suppression…',
    'profile.deleteAccountConfirmTitle': 'Supprimer votre compte ?',
    'profile.deleteAccountImpactNone': 'Aucun rendez-vous à venir ou en cours ne sera annulé.',
    'profile.deleteAccountImpactOne': '1 rendez-vous à venir ou en cours sera annulé automatiquement.',
    'profile.deleteAccountImpactMany': '{count} rendez-vous à venir ou en cours seront annulés automatiquement.',
    'profile.deleteAccountConfirmBody': 'Votre compte et les données personnelles associées seront définitivement supprimés. Cette action est irréversible.',
    'profile.deleteAccountConfirm': 'Supprimer le compte',
    'profile.deleteAccountError': 'Impossible de supprimer le compte',
    'profile.switchCurrencyTitle': 'Passer à {currency} ?',
    'profile.serviceSingular': 'service',
    'profile.servicePlural': 'services',
    'profile.currencyWarningBody': 'Vous avez {count} {services} actif(s) avec des prix en {from}. Passer à {to} ne convertira pas ces montants — les chiffres resteront identiques, mais s’afficheront avec le nouveau symbole. Vérifiez et mettez à jour vos prix dans My Services après le changement.',
    'profile.currencyWarningPast': 'Les rendez-vous passés gardent leur devise de réservation — ils ne sont pas affectés.',
    'profile.switchToCurrency': 'Passer à {currency}',
  },
  es: {
    ...en,
    'common.loading': 'Cargando...',
    'common.loadingEllipsis': 'Cargando…',
    'common.loadingMore': 'Cargando más…',
    'common.loadMore': 'Cargar más',
    'common.save': 'Guardar',
    'common.saveChanges': 'Guardar cambios',
    'common.saving': 'Guardando…',
    'common.saved': 'Guardado',
    'common.edit': 'Editar',
    'common.cancel': 'Cancelar',
    'common.discard': 'Descartar',
    'common.delete': 'Eliminar',
    'common.unsavedChanges': 'Tienes cambios sin guardar',
    'common.enabled': 'Activadas',
    'common.disabled': 'Desactivadas',
    'common.custom': 'Personalizado',
    'common.and': 'y',
    'common.all': 'Todo',
    'common.any': 'Cualquiera',
    'common.add': 'Agregar',
    'common.clear': 'Borrar',
    'common.clearFilters': 'Borrar filtros',
    'common.close': 'Cerrar',
    'common.confirm': 'Confirmar',
    'common.decrease': 'Disminuir',
    'common.dismiss': 'Cerrar',
    'common.done': 'Listo',
    'common.emailChannel': 'correo electrónico',
    'common.filters': 'Filtros',
    'common.from': 'Desde',
    'common.homeAria': 'Inicio de Bevelure',
    'common.increase': 'Aumentar',
    'common.km': 'km',
    'common.max': 'Máx.',
    'common.min': 'Mín.',
    'common.minutesShort': '{count} min',
    'common.next': 'Siguiente',
    'common.noMatches': 'Sin resultados',
    'common.no': 'No',
    'common.pageOf': 'Página {page} de {total}',
    'common.prev': 'Anterior',
    'common.price': 'Precio',
    'common.remove': 'Eliminar',
    'common.scrollRight': 'Desplazar a la derecha',
    'common.search': 'Buscar',
    'common.sort': 'Ordenar',
    'common.sortBy': 'Ordenar por: {label}',
    'common.to': 'Hasta',
    'common.unknown': 'Desconocido',
    'common.user': 'Usuario',
    'common.viewAll': 'Ver todo',
    'common.viewFullDetails': 'Ver todos los detalles',
    'common.viewHistory': 'Ver historial',
    'common.viewProfile': 'Ver perfil',
    'common.yes': 'Sí',

    'imageCrop.adjustLogo': 'Ajustar logo',
    'imageCrop.adjustPhoto': 'Ajustar foto',
    'imageCrop.description': 'Mueve y amplía la imagen hasta que la parte que quieres quede dentro del círculo.',
    'imageCrop.dragHint': 'Arrastra la imagen para recolocarla.',
    'imageCrop.squareDescription': 'Mueve y amplía la imagen hasta que la parte que quieres quede dentro del cuadrado.',
    'imageCrop.useImage': 'Usar imagen',
    'imageCrop.zoom': 'Zoom',

    'greeting.afternoon': 'Buenas tardes',
    'greeting.evening': 'Buenas noches',
    'greeting.morning': 'Buenos días',
    'greeting.night': 'Buenas noches',

    'status.awaitingProvider': 'Esperando al proveedor',
    'status.cancelled': 'Cancelada',
    'status.completed': 'Completada',
    'status.confirmed': 'Confirmada',
    'status.inProgress': 'En curso',
    'status.pending': 'Pendiente',

    'date.daysAgo': 'hace {count} d',
    'date.afternoon': 'Tarde',
    'date.evening': 'Noche',
    'date.morning': 'Mañana',
    'date.noon': 'Mediodía',
    'date.selectDate': 'Seleccionar una fecha',
    'date.selectDateTime': 'Seleccionar fecha y hora',
    'date.time': 'Hora',
    'date.today': 'Hoy',
    'date.tomorrow': 'Mañana',
    'date.yesterday': 'Ayer',

    'field.addCustomValue': 'Agregar “{value}”',
    'field.addLanguage': 'Agregar un idioma…',
    'field.anyDate': 'Cualquier fecha',
    'field.anyLanguage': 'Cualquier idioma',
    'field.anyProvider': 'Cualquier proveedor',
    'field.anyService': 'Cualquier servicio',
    'field.age': 'Edad',
    'field.ageMaxAria': 'Edad máxima',
    'field.ageMinAria': 'Edad mínima',
    'field.ageUpTo': 'Hasta {age}',
    'field.cityPlaceholder': 'Miami',
    'field.city': 'Ciudad',
    'field.firstNamePlaceholder': 'Sofía',
    'field.hidePassword': 'Ocultar contraseña',
    'field.invalidPhone': 'Ingresa un número válido para el país seleccionado.',
    'field.language': 'Idioma',
    'field.languageHelp': 'Elige de la lista o escribe uno propio y presiona Enter.',
    'field.lastNamePlaceholder': 'García',
    'field.noCountriesFound': 'No se encontraron países',
    'field.phoneNumber': 'Número de teléfono',
    'field.priceMaxAria': 'Precio máximo',
    'field.priceMinAria': 'Precio mínimo',
    'field.providerNamePlaceholder': 'Alex Morgan',
    'field.rating': 'Calificación',
    'field.searchCountry': 'Buscar país…',
    'field.searchCurrency': 'Buscar moneda…',
    'field.selectCountry': 'Seleccionar país',
    'field.service': 'Servicio',
    'field.serviceExamplePlaceholder': 'p. ej., manicura',
    'field.shareProcedurePlaceholder': 'p. ej. corte masculino, facial…',
    'field.showPassword': 'Mostrar contraseña',
    'field.socialPlaceholder': '@usuario o URL del perfil',
    'field.startTypingAddress': 'Empieza a escribir una dirección…',
    'field.startTypingCity': 'Empieza a escribir una ciudad…',
    'field.serviceType': 'Tipo de servicio',
    'field.sex': 'Sexo',
    'field.useCustomLanguage': 'Usar “{value}”',

    'sex.female': 'Mujer',
    'sex.male': 'Hombre',
    'sex.other': 'Otro',
    'sex.preferNotToSay': 'Prefiero no decirlo',

    'rating.any': 'Cualquiera',
    'sort.distance': 'Distancia',
    'sort.name': 'Nombre',
    'sort.rating': 'Calificación',

    'customerHome.bookingConfirmedBody': 'Guardado en tu historial. Ábrelo para ver todos los detalles.',
    'customerHome.bookingPendingBody': 'Te avisaremos cuando el proveedor confirme. Puedes cancelar en cualquier momento.',
    'customerHome.bookingRequestSent': 'Solicitud de reserva enviada',
    'customerHome.appointmentBooked': 'Cita reservada',
    'customerHome.inProgressSubtitle': 'Tu cita está en curso',
    'customerHome.lastVisit': 'Última visita',
    'customerHome.nextAppointment': 'Tu próxima cita {when}',
    'customerHome.noAppointmentsThisDay': 'No hay citas este día',
    'customerHome.noUpcoming': 'No hay citas próximas',
    'customerHome.nothingHereYet': 'Nada por ahora',
    'customerHome.recent': 'Recientes',
    'customerHome.upcoming': 'Próximas',

    'providerHome.allProviders': 'Todos los proveedores',
    'providerHome.appointmentToday': 'Cita hoy',
    'providerHome.appointmentsToday': 'Citas hoy',
    'providerHome.awaitingYou': 'Esperando tu respuesta',
    'providerHome.pendingRequests': '{count} solicitud(es) de reserva esperando',
    'providerHome.pendingRequestsBody': 'Confirma o rechaza para que los clientes sepan qué esperar.',
    'providerHome.reviewConfirm': 'Revisar y confirmar',
    'providerHome.searchTeam': 'Buscar equipo…',

    'customerSearch.anyCity': 'Cualquier ciudad',
    'customerSearch.detectingLocation': 'Detectando tu ubicación…',
    'customerSearch.findingProviders': 'Buscando proveedores…',
    'customerSearch.inCity': ' en {city}',
    'customerSearch.locationCity': 'Mostrando proveedores en {city}.',
    'customerSearch.locationNearest': 'Mostrando proveedores cerca de ti{city}.',
    'customerSearch.noProvidersFound': 'No se encontraron proveedores',
    'customerSearch.priceHintAny': 'Ingresa un tipo de servicio arriba para filtrar una procedure específica por precio.',
    'customerSearch.priceHintService': 'Precio de servicios que coinciden con “{serviceType}”.',
    'customerSearch.searchPlaceholder': 'Buscar por nombre…',
    'customerSearch.servicePlaceholder': 'Cualquiera (ej. manicura, barbería…)',
    'customerSearch.showingAll': 'Mostrando todos los proveedores.',
    'customerSearch.title': 'Encontrar proveedores',
    'customerSearch.useMyLocation': 'Usar mi ubicación',

    'careHub.completedVisits': 'Visitas completadas: {count}',
    'careHub.loadingMore': 'Cargando más…',
    'careHub.noCompletedFor': 'Aún no hay visitas completadas para {serviceType}.',
    'careHub.noCompletedYet': 'Aún no hay visitas completadas.',
    'careHub.noProcedureDetails': 'Aún no hay detalles de procedimiento registrados.',
    'careHub.procedurePhotos': 'Fotos del procedimiento',
    'careHub.procedureTypes': 'Tipos de procedimiento: {count}',
    'careHub.quickShare': 'Compartir rápido',
    'careHub.quickShareBody': 'Genera un código QR o enlace para uno de tus tipos de procedimiento.',
    'careHub.shareHistory': 'Compartir historial',
    'careHub.title': 'Mi Care Hub',
    'careHub.titleAccent': 'Mi Care *Hub*',
    'careHub.detailNotes': 'Notas',
    'careHub.detailProducts': 'Productos',
    'careHub.detailRecommendations': 'Recomendaciones',

    'appointments.cancelled': 'Canceladas',
    'appointments.careProvider': 'Proveedor',
    'appointments.history': 'Historial',
    'appointments.loadOlder': 'Cargar citas anteriores',
    'appointments.loadingOlder': 'Cargando citas anteriores…',
    'appointments.noCancelled': 'No hay citas canceladas',
    'appointments.noFilterMatches': 'Ninguna cita coincide con tus filtros.',
    'appointments.noHistory': 'Nada por ahora',
    'appointments.noUpcoming': 'No hay citas próximas',
    'appointments.rateThisVisit': 'Calificar esta visita',
    'appointments.upcoming': 'Próximas',
    'appointments.yourRating': 'Tu calificación',

    'providerAppointments.anyCustomer': 'Cualquier cliente',
    'providerAppointments.noPending': 'No hay solicitudes pendientes — todo al día.',
    'providerAppointments.rateClient': 'Calificar a este cliente',

    'providerServices.addService': 'Agregar servicio',
    'providerServices.addon': 'Extra',
    'providerServices.addonName': 'Nombre del extra',
    'providerServices.addonPlaceholder': 'ej. retiro de gel',
    'providerServices.deactivate': 'Desactivar',
    'providerServices.deactivateAddon': 'Desactivar extra',
    'providerServices.deactivateAddonDescription': 'El servicio sigue disponible; solo este extra deja de poder reservarse. Puedes reactivarlo cuando quieras.',
    'providerServices.deactivateAddonTitle': '¿Desactivar extra “{name}”?',
    'providerServices.deactivateService': 'Desactivar servicio',
    'providerServices.deactivateServiceDescription': 'Los clientes no podrán reservar este servicio para nuevas citas. Las reservas pasadas permanecen en el historial. Puedes reactivarlo cuando quieras.',
    'providerServices.deactivateServiceTitle': '¿Desactivar “{name}”?',
    'providerServices.durationMinutes': 'Duración (minutos)',
    'providerServices.editAddon': 'Editar extra',
    'providerServices.extraMinutes': 'Minutos extra',
    'providerServices.extraPrice': 'Precio extra ({currency})',
    'providerServices.failedCreate': 'No se pudo crear el servicio',
    'providerServices.failedSaveAddon': 'No se pudo guardar el extra',
    'providerServices.inactive': 'Inactivo',
    'providerServices.noAddons': 'Aún no hay extras.',
    'providerServices.priceOptional': 'Precio ({currency}, opcional)',
    'providerServices.reactivate': 'Reactivar',
    'providerServices.reactivateService': 'Reactivar servicio',
    'providerServices.renameDisabled': 'Cambiar el nombre no está disponible: las reservas pasadas usan este nombre. Desactívalo y crea uno nuevo.',
    'chat.blockedTitle': 'Mensajería no disponible',
    'chat.blocked': 'Ya no pueden enviarse mensajes.',
    'pub.noSlotsMonth': 'Sin horarios este mes: usa las flechas para ver otros meses.',
    'pub.publicPage': 'Página de reservas pública',
    'pub.username': 'Nombre de usuario',
    'pub.usernameHelp': 'Tu enlace público: compártelo en redes para que te reserven.',
    'pub.copy': 'Copiar',
    'pub.copied': 'Copiado',
    'common.back': 'Atrás',
    'pub.close': 'Cerrar',
    'pub.bookWith': 'Reservar con {name}',
    'pub.bookCta': 'Reservar una cita',
    'pub.selectService': 'Elige un servicio',
    'pub.chooseTime': 'Elige un horario',
    'pub.yourDetails': 'Tus datos',
    'pub.pickDay': 'Elige un día con horarios disponibles.',
    'pub.continue': 'Continuar',
    'pub.confirmBooking': 'Confirmar reserva',
    'pub.successTitle': 'Reserva recibida',
    'pub.successGuest': '¡Tu solicitud se envió! Enviamos los detalles de la cita a {email}: el profesional la confirmará pronto.',
    'pub.successCustomer': '¡Reservado! {provider} confirmará tu cita en breve.',
    'pub.manageCancel': 'Gestionar o cancelar esta reserva',
    'pub.signedInAs': 'Reservando como {name}',
    'pub.tooMany': 'Demasiados intentos de reserva. Inténtalo más tarde.',
    'pub.bookingFailed': 'No se pudo completar la reserva. Inténtalo de nuevo.',
    'pub.about': 'Acerca de',
    'pub.services': 'Servicios',
    'pub.portfolio': 'Portafolio',
    'pub.speaks': 'Habla',
    'pub.ratings': '{count} valoraciones',
    'pub.atSalon': 'en {salon}',
    'pub.team': 'Equipo',
    'pub.noTeam': 'Aún no hay miembros.',
    'pub.createYourOwn': 'Crea tu página de reservas en Bevelure →',
    'pub.agree': 'Acepto los',
    'pub.terms': 'Términos',
    'pub.privacy': 'Política de privacidad',
    'common.showMore': 'Ver más',
    'common.showLess': 'Ver menos',
    'providerServices.descriptionOptional': 'Descripción (opcional)',
    'providerServices.serviceDescriptionPlaceholder': 'Qué incluye, qué esperar, preparación y cuidados posteriores…',
    'providerServices.addonDescriptionPlaceholder': 'Qué incluye este extra…',
    'providerServices.serviceName': 'Nombre del servicio',
    'providerServices.servicePlaceholder': 'ej. manicura',
    'providerServices.title': 'Mis servicios',

    'schedule.adding': 'Agregando…',
    'schedule.addTimeOff': 'Agregar tiempo libre',
    'schedule.addTimeOffHelp': 'Bloquea fechas por vacaciones, días de enfermedad o pausas. Los clientes no podrán reservar durante esos períodos.',
    'schedule.addTimeOffImpactHelp': 'Los clientes no podrán reservar nuevas citas en esta ventana.',
    'schedule.addTimeOffTitle': '¿Agregar este tiempo libre?',
    'schedule.addWorkingHours': 'Agregar horario de trabajo',
    'schedule.dayOfWeek': 'Día de la semana',
    'schedule.dayOff': 'Día libre',
    'schedule.end': 'Fin',
    'schedule.errorEndAfterStart': 'El fin debe ser después del inicio',
    'schedule.errorEndAfterStartTime': 'La hora de fin debe ser después de la hora de inicio',
    'schedule.errorOverlap': 'Este horario se superpone con una regla existente para este día',
    'schedule.errorPickStartEnd': 'Elige inicio y fin',
    'schedule.failedAddRule': 'No se pudo agregar la regla de horario',
    'schedule.friShort': 'Vie',
    'schedule.friday': 'Viernes',
    'schedule.monShort': 'Lun',
    'schedule.monday': 'Lunes',
    'schedule.noneScheduled': 'Nada programado',
    'schedule.pastTimeOff': 'Tiempo libre pasado ({count})',
    'schedule.pickEnd': 'Elegir fin',
    'schedule.pickStart': 'Elegir inicio',
    'schedule.reasonPlaceholder': 'Motivo (opcional) — vacaciones, formación, etc.',
    'schedule.removeWorkingHoursDescription': 'se eliminará de tu horario semanal. No se podrán hacer nuevas reservas en esta ventana.',
    'schedule.removeWorkingHoursTitle': '¿Eliminar horario de trabajo?',
    'schedule.satShort': 'Sáb',
    'schedule.saturday': 'Sábado',
    'schedule.start': 'Inicio',
    'schedule.sunShort': 'Dom',
    'schedule.sunday': 'Domingo',
    'schedule.thuShort': 'Jue',
    'schedule.thursday': 'Jueves',
    'schedule.title': 'Agenda',
    'schedule.tueShort': 'Mar',
    'schedule.tuesday': 'Martes',
    'schedule.upcomingTimeOff': 'Tiempo libre próximo',
    'schedule.wedShort': 'Mié',
    'schedule.wednesday': 'Miércoles',
    'schedule.weeklySchedule': 'Horario semanal',

    'providers.all': 'Todos',
    'providers.blocked': 'Bloqueados',
    'providers.blockedBody': 'Los proveedores que bloquees no aparecerán en búsqueda ni en tus listas. Puedes desbloquearlos cuando quieras.',
    'providers.blockedOn': 'Bloqueado el {date}',
    'providers.browseProviders': 'Explorar proveedores',
    'providers.countSaved': '{count} guardados',
    'providers.countVisited': '{count} proveedor{plural} visitado{plural}',
    'providers.emptyFavoritesBody': 'Toca el corazón en el perfil de un proveedor para guardarlo aquí.',
    'providers.emptyVisitedBody': 'Los proveedores que reserves aparecerán aquí después de tu primera cita.',
    'providers.favorites': 'Favoritos',
    'providers.lastVisit': 'última visita {when}',
    'providers.nextVisit': 'Próxima {when}',
    'providers.nextVisitSort': 'Próxima visita',
    'providers.noBlocked': 'No hay proveedores bloqueados',
    'providers.noFavorites': 'Aún no hay favoritos',
    'providers.noFilterMatches': 'Ningún proveedor coincide con tus filtros.',
    'providers.noProviders': 'Aún no hay proveedores',
    'providers.overallRating': 'Calificación general',
    'providers.keepTyping': 'Mostrando {shown} de {total}; sigue escribiendo para ajustar.',
    'providers.pickServiceForPrice': 'Elige un servicio para filtrar una procedure específica por precio.',
    'providers.priceOfService': 'Precio de “{service}”.',
    'providers.searchPlaceholder': 'Buscar por nombre…',
    'providers.sortMostVisits': 'Más visitas',
    'providers.title': 'Mis proveedores',
    'providers.unblock': 'Desbloquear',
    'providers.visits': '{count} visita{plural}',
    'providers.yearsShort': '{count} año{plural}',
    'providers.youRated': 'Tu calificación {rating}',

    'providerCustomers.count': '{count} cliente(s)',
    'providerCustomers.last': 'última {when}',
    'providerCustomers.noBlockedCustomers': 'No hay clientes bloqueados',
    'providerCustomers.noBlockedCustomersBody': 'Bloquea a un cliente desde su perfil para impedir que reserve nuevas citas contigo.',
    'providerCustomers.noCustomers': 'Aún no hay clientes',
    'providerCustomers.noCustomersBody': 'Tus clientes aparecerán aquí después de su primera cita.',
    'providerCustomers.noSearchMatches': 'Ningún cliente coincide con tu búsqueda.',

    'messages.attachImage': 'Adjuntar imagen',
    'messages.careProvider': 'Proveedor',
    'messages.customer': 'Cliente',
    'messages.salonAdministrator': 'Administrador del salón',
    'messages.emptyCustomer': 'Abre el perfil de un proveedor y toca Mensaje para empezar.',
    'messages.emptyProvider': 'Abre el perfil de un cliente y toca Mensaje, o espera a que escriba.',
    'messages.failedUpload': 'No se pudo subir la imagen',
    'messages.loadOlder': 'Cargar mensajes anteriores',
    'messages.loadingOlder': 'Cargando mensajes anteriores…',
    'messages.noConversations': 'Aún no hay conversaciones',
    'messages.noMessagesYet': 'Aún no hay mensajes. ¡Saluda!',
    'messages.noMessagesPreview': 'Aún no hay mensajes',
    'messages.photo': 'Foto',
    'messages.removeAttachment': 'Eliminar adjunto',
    'messages.reply': 'Responder',
    'messages.replyingTo': 'Respondiendo a {name}',
    'messages.cancelReply': 'Cancelar respuesta',
    'messages.heart': 'Enviar corazón',
    'messages.unheart': 'Quitar corazón',
    'messages.messagingUnavailable': 'La mensajería no está disponible para esta conversación.',
    'messages.title': 'Mensajes',
    'messages.typeMessage': 'Escribe un mensaje…',
    'messages.viewProfile': 'Ver perfil',

    'finance.customerCaption': 'Estimado según los precios registrados en tus citas. Los meses pasados están realizados; el mes actual separa terminado y próximo; los meses futuros se estiman según lo que ya reservaste.',
    'finance.customerEmpty': 'Cuando reserves o completes citas con precio, tu gasto mensual aparecerá aquí.',
    'finance.customerTitle': 'Tus gastos',
    'finance.done': 'terminado',
    'finance.explainNumbersAria': '¿Qué significan estos números?',
    'finance.hideBreakdown': 'Ocultar desglose',
    'finance.hidePastMonths': 'Ocultar últimos {count} mes{plural}',
    'finance.noActivity': 'Sin actividad',
    'finance.nothingToShow': 'Nada para mostrar todavía',
    'finance.pendingConfirmation': 'Incluye {amount} pendiente de confirmación',
    'finance.providerEmpty': 'Cuando tengas citas con precio, tus ingresos mensuales aparecerán aquí.',
    'finance.providerCaption': 'Estimado según los precios guardados en tus citas. Los meses pasados están realizados; el mes actual separa terminado y próximo; los meses futuros se estiman según citas ya reservadas. Las reservas canceladas se excluyen.',
    'finance.providerTitle': 'Tus ingresos',
    'finance.showBreakdown': 'Mostrar desglose',
    'finance.showPastMonths': 'Mostrar últimos {count} mes{plural}',
    'finance.statusCurrent': 'Actual',
    'finance.statusPast': 'Pasado',
    'finance.statusUpcoming': 'Próximo',
    'finance.thisMonth': 'Este mes',
    'finance.upcoming': 'próximo',

    'appointmentNew.addons': 'Extras',
    'appointmentNew.addonsHelp': 'Extras opcionales — toca para agregar.',
    'appointmentNew.addressOptional': 'Dirección (opcional)',
    'appointmentNew.availableDaysHelp': 'Los días con horarios disponibles están marcados con un punto.',
    'appointmentNew.bookingAtSalon': 'Reserva en salón',
    'appointmentNew.confirmAppointment': 'Confirmar cita',
    'appointmentNew.create': 'Crear',
    'appointmentNew.createError': 'No se pudo crear la cita',
    'appointmentNew.creating': 'Creando…',
    'appointmentNew.careProviderName': 'Nombre del proveedor',
    'appointmentNew.dateTime': 'Fecha y hora',
    'appointmentNew.email': 'Correo',
    'appointmentNew.emailPhoneSocialHelp': 'Ingresa uno de estos datos: correo, teléfono o enlace social del proveedor. Lo buscaremos.',
    'appointmentNew.enterProviderContact': 'Ingresa el correo, teléfono o enlace social del proveedor',
    'appointmentNew.enterProviderName': 'Ingresa el nombre del proveedor',
    'appointmentNew.enterService': 'Ingresa el servicio (ej. manicura)',
    'appointmentNew.externalProviderRecord': 'Agrega sus datos para guardar esta visita en tu historial.',
    'appointmentNew.externalProviderNotify': 'Agrega sus datos y le notificaremos por {channel}.',
    'appointmentNew.findProvider': 'Encuentra tu proveedor',
    'appointmentNew.lookupFailed': 'La búsqueda falló',
    'appointmentNew.newProvider': 'Nuevo proveedor',
    'appointmentNew.newProviderIntro': 'No pudimos encontrar este proveedor en Bevelure.',
    'appointmentNew.noMatchingProviders': 'No hay proveedores coincidentes.',
    'appointmentNew.noServices': 'No hay servicios disponibles para este proveedor',
    'appointmentNew.noSlots': 'No hay horarios disponibles en los próximos 30 días',
    'appointmentNew.or': 'o',
    'appointmentNew.orFindNew': 'o busca uno nuevo',
    'appointmentNew.pickDateTime': 'Elige fecha y hora',
    'appointmentNew.pickDateTimeShort': 'Elige fecha y hora',
    'appointmentNew.pickDateWithSlots': 'Elige arriba una fecha con horarios disponibles.',
    'appointmentNew.phone': 'Teléfono',
    'appointmentNew.priceOptional': 'Precio (opcional)',
    'appointmentNew.providerFound': '¡Proveedor encontrado!',
    'appointmentNew.providerFoundText': 'Encontramos a {name} en nuestro sistema. Selecciona el servicio.',
    'appointmentNew.providerZone': 'Los horarios se muestran en la zona del proveedor — {zone}.',
    'appointmentNew.selectService': 'Seleccionar servicio',
    'appointmentNew.selectServiceHelp': 'Elige un servicio para esta cita',
    'appointmentNew.shareAllDesc': 'Todas tus visitas coincidentes.',
    'appointmentNew.shareAllDescService': 'Todas tus visitas de {service}.',
    'appointmentNew.shareAllTitle': 'Historial completo',
    'appointmentNew.shareConsent': 'Entiendo que comparto este historial de tratamientos con {provider}: notas del procedimiento, productos, recomendaciones y fotos de resultado/procedimiento. Mis notas y fotos privadas nunca se comparten.',
    'appointmentNew.shareDontDesc': 'Mantén tu historial privado.',
    'appointmentNew.shareDontTitle': 'No compartir',
    'appointmentNew.shareHistory': 'Compartir historial',
    'appointmentNew.shareHistoryHelp': 'Elige qué puede ver este proveedor sobre tus visitas pasadas.',
    'appointmentNew.shareLast5Desc': 'Tus 5 visitas coincidentes más recientes.',
    'appointmentNew.shareLast5DescService': 'Tus 5 visitas de {service} más recientes.',
    'appointmentNew.shareLast5Title': 'Últimas 5 visitas',
    'appointmentNew.shareProcedureHint': 'Otros proveedores pueden nombrar el mismo servicio de otra forma; agrega esos nombres para incluir esas visitas.',
    'appointmentNew.shareProcedures': 'Qué procedimientos compartir',
    'appointmentNew.socialMedia': 'Red social',
    'appointmentNew.stepOf': 'Paso {step} de {total}',
    'appointmentNew.thisProvider': 'este proveedor',
    'appointmentNew.title': 'Crear cita',
    'appointmentNew.total': 'Total',
    'appointmentNew.whereToGo': 'A dónde ir',
    'appointmentNew.yourProviders': 'Tus proveedores',

    'providerAppointmentNew.adminBookTeam': 'Como admin del salón puedes reservar para tu equipo.',
    'providerAppointmentNew.adminBookTeamOnly': 'Como admin del salón reservas en nombre de tu equipo.',
    'providerAppointmentNew.bookFor': 'Reservar para',
    'providerAppointmentNew.bookingFor': 'Reserva para',
    'providerAppointmentNew.change': 'Cambiar',
    'providerAppointmentNew.checkWorkingHours': 'Revisa el horario y ausencias del proveedor.',
    'providerAppointmentNew.chooseOneService': 'Elige uno de tus servicios',
    'providerAppointmentNew.confirmDetails': 'Confirmar detalles',
    'providerAppointmentNew.createAppointment': 'Crear cita',
    'providerAppointmentNew.customer': 'Cliente',
    'providerAppointmentNew.customerPreselected': 'Cliente preseleccionado. Continúa para elegir un servicio.',
    'providerAppointmentNew.enterOneContact': 'Introduce un contacto del cliente: email, teléfono o enlace social.',
    'providerAppointmentNew.inviteProviderFirst': 'Invita a un proveedor a tu salón antes de crear una cita.',
    'providerAppointmentNew.matchInviteHelp': 'Buscaremos una cuenta Bevelure existente. Si aún no es miembro, le enviaremos los detalles y una invitación (sin notificación para enlaces sociales).',
    'providerAppointmentNew.myself': 'Yo',
    'providerAppointmentNew.noActiveServices': 'Aún no tienes servicios activos.',
    'providerAppointmentNew.noBookableProviders': 'Aún no hay proveedores reservables en tu salón. Invítalos desde Mi salón.',
    'providerAppointmentNew.noMatchingCustomers': 'No hay clientes coincidentes.',
    'providerAppointmentNew.noSlots': 'No hay horarios disponibles en los próximos 30 días.',
    'providerAppointmentNew.noTeamMembersMatch': 'Ningún miembro del equipo coincide.',
    'providerAppointmentNew.orEnterContact': 'o introduce su contacto',
    'providerAppointmentNew.pickDateWithSlots': 'Elige una fecha con horarios disponibles arriba.',
    'providerAppointmentNew.pickTeamMember': 'Elige qué miembro del equipo realizará esta cita.',
    'providerAppointmentNew.pickTeamMemberError': 'Elige para qué miembro del equipo es esta cita.',
    'providerAppointmentNew.pleaseSelectService': 'Selecciona un servicio',
    'providerAppointmentNew.providerFallback': 'Proveedor',
    'providerAppointmentNew.selected': 'Seleccionado',
    'providerAppointmentNew.teamMemberFallback': 'miembro del equipo',
    'providerAppointmentNew.title': 'Nueva cita',
    'providerAppointmentNew.yourCustomers': 'Tus clientes',

    'providerProfile.about': 'Acerca de',
    'providerProfile.addFavorite': 'Agregar a favoritos',
    'providerProfile.addons': 'Extras',
    'providerProfile.blockProvider': 'Bloquear este proveedor',
    'providerProfile.bookAppointment': 'Reservar cita',
    'providerProfile.certificates': 'Certificados',
    'providerProfile.fromClients': 'de clientes',
    'providerProfile.message': 'Mensaje',
    'providerProfile.noServicesListed': 'Aún no hay servicios listados',
    'providerProfile.pastAppointments': 'Citas pasadas',
    'providerProfile.rating': 'Calificación',
    'providerProfile.removeConfirm': 'Eliminar',
    'providerProfile.removeDescription': 'Perderá acceso a este salón. Las citas pasadas siguen en el historial del salón. Las nuevas reservas ya no se asociarán a este salón.',
    'providerProfile.removeFavorite': 'Quitar de favoritos',
    'providerProfile.removeFromSalon': 'Eliminar del salón',
    'providerProfile.removeTitle': '¿Eliminar a {name} del salón?',
    'providerProfile.services': 'Servicios',
    'providerProfile.title': 'Perfil del proveedor',
    'providerProfile.unblockProvider': 'Desbloquear este proveedor',
    'providerProfile.unblocking': 'Desbloqueando…',
    'providerProfile.unavailableTitle': 'Perfil no disponible',
    'providerProfile.unavailableBody': 'Este perfil ya no está disponible. Las citas y registros anteriores permanecen en tu historial.',
    'providerProfile.visits': 'Visitas',
    'providerProfile.worksAt': 'Trabaja en',
    'providerProfile.yearsExperience': '{count} año(s) de experiencia',

    'customerProfile.allergies': 'Alergias',
    'customerProfile.allergiesNoDetails': 'Tiene alergias (sin detalles)',
    'customerProfile.blockCustomer': 'Bloquear este cliente',
    'customerProfile.book': 'Reservar',
    'customerProfile.careRoutine': 'Rutina de cuidado',
    'customerProfile.goBack': 'Volver',
    'customerProfile.noKnownAllergies': 'Sin alergias conocidas',
    'customerProfile.notFound': 'Cliente no encontrado o sin citas compartidas.',
    'customerProfile.unavailableTitle': 'Perfil no disponible',
    'customerProfile.unavailableBody': 'Este perfil ya no está disponible, o ya no tienes acceso para verlo. Las citas anteriores permanecen en tu historial.',
    'customerProfile.ratings': '{count} calificaciones',
    'customerProfile.title': 'Cliente',
    'customerProfile.unblockCustomer': 'Desbloquear este cliente',

    'externalCancel.alreadyCancelled': 'Esta cita ya está cancelada.',
    'externalCancel.alreadyCompleted': 'Esta cita ya está completada; no se puede cancelar una visita pasada.',
    'externalCancel.cancelledBody': 'Avisamos a la otra parte. El horario vuelve a estar disponible.',
    'externalCancel.cancelledTitle': 'Cita cancelada',
    'externalCancel.cancelling': 'Cancelando…',
    'externalCancel.confirmHint': '¿Cambiaste de opinión? Solo cierra esta página: no pasa nada hasta que pulses el botón de arriba.',
    'externalCancel.errorInvalid': 'El enlace de cancelación no es válido o expiró.',
    'externalCancel.errorMissing': 'Al enlace de cancelación le falta el token.',
    'externalCancel.errorRetry': 'No se pudo cancelar; inténtalo de nuevo o contacta directamente a la otra parte.',
    'externalCancel.intro': 'Estás por cancelar:',
    'externalCancel.titlePrefix': 'Cancelar',
    'externalCancel.titleEmphasis': 'cita',

    'externalFill.addDetails': 'Agrega los detalles del tratamiento para esta cita.',
    'externalFill.errorInvalidTitle': 'Enlace no válido',
    'externalFill.errorInvalidBody': 'Este enlace no es válido o ha expirado. Pídele al cliente que lo reenvíe.',
    'externalFill.lastUpdated': 'Última actualización {date}',
    'externalFill.productsPlaceholder': 'Marcas, colores, materiales…',
    'externalFill.saveError': 'No se pudo guardar. Inténtalo de nuevo.',

    'share.allergiesOnFile': 'Alergias registradas',
    'share.customerAllergyFallback': 'El cliente reporta alergias (sin notas específicas).',
    'share.expired': 'Este enlace expiró o ya no es válido.',
    'share.expires': 'El enlace expira {date}',
    'share.failedLoad': 'No se pudo cargar el historial compartido',
    'share.footer': 'Compartido vía {brand}. El cliente puede revocar este enlace en cualquier momento.',
    'share.fullHistory': 'Historial completo',
    'share.last5Visits': 'Últimas 5 visitas',
    'share.linkUnavailable': 'Enlace no disponible',
    'share.noKnownAllergies': 'No hay alergias conocidas registradas.',
    'share.noSharedHistory': 'No se pudo cargar este historial compartido.',
    'share.readOnly': 'Solo lectura',
    'share.sharedTreatmentHistory': 'Historial de tratamientos compartido',

    'appointmentDetail.add': 'Agregar',
    'appointmentDetail.addPhoto': 'Agregar foto',
    'appointmentDetail.addSelected': 'Agregar ({count})',
    'appointmentDetail.addToPortfolio': 'Agregar al portafolio',
    'appointmentDetail.addToPortfolioBody': 'Elige qué fotos mostrar en tu perfil público.',
    'appointmentDetail.addons': 'Extras',
    'appointmentDetail.allergiesOnFile': 'Alergias registradas',
    'appointmentDetail.allergyFallback': 'El cliente reporta alergias (sin notas específicas).',
    'appointmentDetail.atSalon': 'en {salon}',
    'appointmentDetail.bookingRequestBody': 'Confirma para bloquear el horario o rechaza para que el cliente elija otro.',
    'appointmentDetail.bookingRequestTitle': 'Solicitud de reserva — esperando tu respuesta',
    'appointmentDetail.byProvider': 'por {provider}',
    'appointmentDetail.cancelAppointment': 'Cancelar cita',
    'appointmentDetail.cancelAppointmentMessage': 'Se liberará el horario y se notificará a la otra parte.',
    'appointmentDetail.cancelAppointmentTitle': '¿Cancelar la cita?',
    'appointmentDetail.cancelRequest': 'Cancelar solicitud',
    'appointmentDetail.cancelRequestMessage': 'Esto retirará la solicitud.',
    'appointmentDetail.cancelRequestTitle': '¿Cancelar solicitud de reserva?',
    'appointmentDetail.captionOptional': 'Descripción (opcional)',
    'appointmentDetail.category': 'Categoría',
    'appointmentDetail.categoryPlaceholder': 'ej. manicura, Botox',
    'appointmentDetail.confirm': 'Confirmar',
    'appointmentDetail.confirming': 'Confirmando…',
    'appointmentDetail.customerAllergyInfo': 'Información de alergias del cliente',
    'appointmentDetail.customerAllergyInfoPrefix': 'Ver las notas de alergias del cliente en',
    'appointmentDetail.decline': 'Rechazar',
    'appointmentDetail.declineBookingMessage': 'El horario quedará disponible para otros.',
    'appointmentDetail.declineBookingTitle': '¿Rechazar reserva?',
    'appointmentDetail.declining': 'Rechazando…',
    'appointmentDetail.defaultProvider': 'el proveedor',
    'appointmentDetail.detailsSaved': 'Detalles guardados',
    'appointmentDetail.failedConfirm': 'No se pudo confirmar',
    'appointmentDetail.failedDecline': 'No se pudo rechazar',
    'appointmentDetail.fullHistory': 'Historial completo',
    'appointmentDetail.inProgressNow': 'En curso ahora',
    'appointmentDetail.keepIt': 'Mantener',
    'appointmentDetail.last5Visits': 'Últimas 5 visitas',
    'appointmentDetail.minutes': '{count} min',
    'appointmentDetail.myPersonalNotes': 'Mis notas personales',
    'appointmentDetail.myPersonalNotesHelp': 'Solo tú puedes ver esto. Útil para anotar cómo te sentiste, reacciones, etc.',
    'appointmentDetail.myPersonalNotesPlaceholder': 'Tus notas privadas sobre esta cita…',
    'appointmentDetail.noCompletedShared': 'Aún no hay citas completadas de este tipo.',
    'appointmentDetail.noPhotosYet': 'Aún no hay fotos.',
    'appointmentDetail.noProcedureDetails': 'No hay detalles del procedimiento registrados.',
    'appointmentDetail.notesSaved': 'Notas guardadas',
    'appointmentDetail.photos': 'Fotos',
    'appointmentDetail.private': 'Privado',
    'appointmentDetail.privatePhotosHelp': 'solo tú puedes verlas',
    'appointmentDetail.privatePortfolioHelp': 'Guardado en tu portafolio, pero oculto hasta que lo actives.',
    'appointmentDetail.procedureNotes': 'Notas del procedimiento',
    'appointmentDetail.procedureNotesPlaceholder': 'Qué se hizo durante el procedimiento…',
    'appointmentDetail.procedurePhotosHelp': 'recibos, productos, qué se aplicó',
    'appointmentDetail.productsUsed': 'Productos usados',
    'appointmentDetail.providerOnlyNotes': 'Notas solo para el proveedor',
    'appointmentDetail.providerOnlyNotesHelp': 'Visible solo para proveedores que trabajan en la cita, no para el cliente.',
    'appointmentDetail.providerOnlyNotesPlaceholder': 'Contexto interno, consideraciones especiales, seguimiento…',
    'appointmentDetail.public': 'Público',
    'appointmentDetail.publicPortfolioHelp': 'Visible para clientes en tu perfil público.',
    'appointmentDetail.rateCustomer': 'Calificar al cliente',
    'appointmentDetail.rateProvider': 'Calificar al proveedor',
    'appointmentDetail.readOnly': 'Solo lectura',
    'appointmentDetail.recommendationsPlaceholder': 'Recomendaciones / cuidados posteriores',
    'appointmentDetail.recordCount': '{count} registro{plural}',
    'appointmentDetail.removePhoto': 'Eliminar',
    'appointmentDetail.removePhotoMessage': 'Esta foto se eliminará permanentemente.',
    'appointmentDetail.removePhotoTitle': '¿Eliminar foto?',
    'appointmentDetail.resultPhotos': 'Fotos del resultado',
    'appointmentDetail.saveDetails': 'Guardar detalles',
    'appointmentDetail.saveNotes': 'Guardar notas',
    'appointmentDetail.sharedHistoryTitle': '{name} compartió su historial',
    'appointmentDetail.shortDescription': 'Descripción breve',
    'appointmentDetail.tapStar': 'Toca una estrella para enviar tu calificación.',
    'appointmentDetail.theirProfile': 'su perfil',
    'appointmentDetail.title': 'Cita',
    'appointmentDetail.total': 'Total',
    'appointmentDetail.waitingForProviderBody': 'Recibirás una notificación cuando acepte o rechace. Puedes cancelar esta solicitud cuando quieras.',
    'appointmentDetail.waitingForProviderTitle': 'Esperando confirmación de {provider}',
    'appointmentDetail.yourTime': '{time} tu hora ({zone})',

    'auth.alreadyHaveAccount': '¿Ya tienes una cuenta?',
    'auth.backToBevelure': 'Volver a Bevelure',
    'auth.backToLogin': 'Volver a iniciar sesión',
    'auth.checkInbox': 'Revisa tu bandeja',
    'auth.confirmEmail': 'Confirmar *correo*',
    'auth.confirmEmailLoading': 'Confirmando tu correo…',
    'auth.createAccount': 'Crear cuenta',
    'auth.createAccountTitle': 'Crear *cuenta*',
    'auth.email': 'Correo',
    'auth.emailConfirmed': 'Correo confirmado',
    'auth.emailConfirmedBody': 'Gracias — tu correo está verificado. Todo listo.',
    'auth.enterEmailPassword': 'Ingresa tu correo y contraseña',
    'auth.forgotPassword': 'Olvidé mi *contraseña*',
    'auth.forgotPasswordCta': '¿Olvidaste tu contraseña?',
    'auth.forgotPasswordSubtitle': 'Te enviaremos un enlace para crear una nueva.',
    'auth.goToLogin': 'Ir a iniciar sesión',
    'auth.invalidLogin': 'Correo o contraseña inválidos',
    'auth.login': 'Iniciar sesión',
    'auth.loginButton': 'Iniciar sesión',
    'auth.loginSubtitle': 'Inicia sesión para continuar tu recorrido beauty',
    'auth.loggingIn': 'Iniciando sesión…',
    'auth.newPassword': 'Nueva contraseña',
    'auth.newToBevelure': '¿Nuevo en Bevelure?',
    'auth.password': 'Contraseña',
    'auth.passwordMismatch': 'Las contraseñas no coinciden',
    'auth.passwordMin': 'La contraseña debe tener al menos 8 caracteres',
    'auth.passwordUpdated': 'Contraseña actualizada',
    'auth.passwordUpdatedBody': 'Inicia sesión con tu nueva contraseña. Otros dispositivos también tendrán que iniciar sesión de nuevo.',
    'auth.rememberNow': '¿Ya la recuerdas?',
    'auth.repeatPassword': 'Repite la nueva contraseña',
    'auth.resetInvalid': 'El enlace de restablecimiento no es válido o expiró',
    'auth.resetLinkMissing': 'Falta el enlace de restablecimiento — solicita uno nuevo.',
    'auth.resetPassword': 'Restablecer *contraseña*',
    'auth.resetPasswordSubtitle': 'Elige algo seguro — al menos 8 caracteres.',
    'auth.sendResetLink': 'Enviar enlace',
    'auth.sending': 'Enviando…',
    'auth.sentResetBody': 'Si existe una cuenta para {email}, acabamos de enviar un enlace de restablecimiento. Es válido por 1 hora.',
    'auth.signupChooseType': 'Elige tu tipo de cuenta para empezar',
    'auth.signupCustomerBody': 'Haz seguimiento de tus citas y procedimientos beauty',
    'auth.signupCustomerTitle': 'Soy cliente',
    'auth.signupProviderBody': 'Gestiona tus clientes y citas',
    'auth.signupProviderTitle': 'Soy profesional',
    'auth.signupSalonBody': 'Registra tu negocio e invita a tu equipo',
    'auth.signupSalonTitle': 'Somos un salón / estudio',
    'signup.acceptLegal': 'Confirma que tienes al menos 18 años y acepta los Términos y la Política de privacidad.',
    'signup.aboutYou': 'Sobre ti',
    'signup.aboutYouSubtitle': 'Información personal básica',
    'signup.accountSubtitle': 'Cómo iniciarás sesión',
    'signup.allergies': 'Alergias',
    'signup.allergiesQuestion': '¿Tienes alguna reacción alérgica?',
    'signup.allergiesSubtitle': 'Esto ayuda a tus profesionales a cuidarte con seguridad. Puedes actualizarlo cuando quieras desde tu perfil.',
    'signup.allergyPlaceholder': 'Describe tus alergias...',
    'signup.birthDatePlaceholder': 'Selecciona tu fecha de nacimiento',
    'signup.certificates': 'Certificados',
    'signup.certificatesFileHint': 'PDF, JPG, PNG — hasta 10 MB cada uno',
    'signup.certificatesLater': 'No pasa nada — puedes subirlos más tarde desde tu perfil.',
    'signup.certificatesQuestion': '¿Tienes certificados para compartir?',
    'signup.certificatesRequired': 'Responde la pregunta sobre certificados.',
    'signup.certificatesSubtitle': 'Muestra tus credenciales profesionales a los clientes',
    'signup.certificatesUpload': 'Haz clic para subir certificados',
    'signup.completeRegistration': 'Completar registro',
    'signup.confirmPassword': 'Confirmar contraseña',
    'signup.contact': 'Contacto',
    'signup.createSalon': 'Crear salón',
    'signup.creatingAccount': 'Creando cuenta…',
    'signup.creatingSalon': 'Creando salón…',
    'signup.customerTitle': 'Registro de *cliente*',
    'signup.dateOfBirth': 'Fecha de nacimiento',
    'signup.firstName': 'Nombre',
    'signup.invalidEmail': 'Introduce una dirección de email válida.',
    'signup.invalidSalonEmail': 'Introduce un email de salón válido o déjalo vacío.',
    'signup.languagesYouSpeak': 'Idiomas que hablas',
    'signup.lastName': 'Apellido',
    'signup.legalPrefix': 'Confirmo que tengo al menos 18 años y acepto los',
    'signup.missingCity': 'Indica tu ciudad.',
    'signup.missingCountry': 'Indica tu país.',
    'signup.missingAddress': 'Indica tu dirección.',
    'signup.missingDateOfBirth': 'Indica tu fecha de nacimiento.',
    'signup.minimumAge': 'Debes tener al menos 18 años para crear una cuenta.',
    'signup.missingEmail': 'Indica tu email.',
    'signup.missingFirstName': 'Indica tu nombre.',
    'signup.missingLastName': 'Indica tu apellido.',
    'signup.missingPassword': 'Introduce una contraseña.',
    'signup.missingServiceType': 'Indica tu tipo de servicio.',
    'signup.missingSex': 'Indica tu sexo.',
    'signup.missingYearsInService': 'Indica tus años de experiencia.',
    'signup.passwordPlaceholder': 'Mínimo 8 caracteres',
    'signup.profession': 'Profesión',
    'signup.professionSubtitle': 'Lo que ofreces',
    'signup.providerTitle': 'Registro de *profesional*',
    'signup.repeatPasswordPlaceholder': 'Repite la contraseña',
    'signup.required': 'Obligatorio',
    'signup.salonAdminSubtitle': 'La cuenta de administrador del salón',
    'signup.salonContactSubtitle': 'Opcional — se muestra en el perfil del salón',
    'signup.salonMissingName': 'Dale un nombre a tu salón.',
    'signup.salonNamePlaceholder': 'ej., Velvet Beauty Studio',
    'signup.salonTitle': 'Registro de *salón*',
    'signup.selectOption': 'Selecciona una opción.',
    'signup.serviceTypePlaceholder': 'p. ej., manicurista, esteticista',
    'signup.sexRequired': 'Selecciona tu sexo',
    'signup.signupFailed': 'No se pudo crear la cuenta',
    'signup.stepOf2': 'Paso {step} de 2',
    'signup.whereYouAre': 'Dónde estás',
    'signup.whereYouAreSubtitle': 'Se usa para encontrar profesionales locales',
    'signup.whereYouWork': 'Dónde trabajas',
    'signup.whereYouWorkSubtitle': 'Ayuda a los clientes a encontrarte',
    'signup.yearsInService': 'Años de experiencia',
    'signup.yearsPlaceholder': 'ej., 5',
    'signup.yourSalon': 'Tu salón',
    'signup.yourSalonSubtitle': 'Esto es lo que verán los clientes y tu equipo',

    'landing.nav.platform': 'Plataforma',
    'landing.nav.features': 'Funciones',
    'landing.nav.story': 'Historia',
    'landing.nav.trust': 'Confianza',
    'landing.nav.startNow': 'Empezar',
    'landing.nav.openMenu': 'Abrir menú',
    'landing.cta.startJourney': 'Empezar tu recorrido',
    'landing.stores.appStoreEyebrow': 'Descárgalo en el',
    'landing.stores.appStoreName': 'App Store',
    'landing.stores.googlePlayEyebrow': 'Disponible en',
    'landing.stores.googlePlayName': 'Google Play',
    'landing.appBanner.subtitle': 'Abrir en la app Bevelure',
    'landing.appBanner.action': 'ABRIR',
    'landing.appBanner.close': 'Cerrar el banner de la app',
    'landing.panel.title': 'Historial de belleza',
    'landing.panel.visits': 'visitas',
    'landing.panel.providers': 'profesionales',
    'landing.panel.rating': 'valoración',
    'landing.panel.row1Label': 'Alergias y rutina de cuidado',
    'landing.panel.row1Meta': 'Compartido antes de llegar',
    'landing.panel.row2Label': 'Tratamiento Hydrafacial',
    'landing.panel.row2Meta': 'Completado · fotos adjuntas',
    'landing.panel.row3Label': 'Mensaje de Maya',
    'landing.panel.row3Meta': 'Cuidado posterior de tu Hydrafacial · hace 1 h',
    'landing.hero.eyebrow': 'Tu historial de belleza, por fin tuyo',
    'landing.hero.body': 'Un registro elegante y privado que te pertenece y te acompaña de un profesional a otro: tu historia, lista para compartir con quien te atienda después.',
    'landing.hero.tile1Title': 'Tuyo',
    'landing.hero.tile1Body': 'un registro que viaja contigo',
    'landing.hero.tile2Title': 'Listo',
    'landing.hero.tile2Body': 'contexto antes de cada cliente',
    'landing.hero.tile3Title': 'En sintonía',
    'landing.hero.tile3Body': 'todo el equipo, un calendario',
    'landing.trustBar.item1': 'Un historial que es tuyo',
    'landing.trustBar.item2': 'Contexto antes de cada cliente',
    'landing.trustBar.item3': 'Un calendario para todo el equipo',
    'landing.three.eyebrow': 'Un registro, tres lados',
    'landing.three.title': 'El mismo registro. Visto de tres maneras.',
    'landing.three.body': 'El cuidado de belleza conecta a personas que rara vez comparten las mismas notas. Bevelure les da una sola fuente fiable y deja que cada una vea justo lo que le importa.',
    'landing.three.side1Tag': 'Para ti',
    'landing.three.side1Title': 'Tu historial completo: tuyo para guardar, tuyo para compartir.',
    'landing.three.side1Desc': 'Cada visita, producto y foto en un solo lugar que te acompaña a cualquier parte.',
    'landing.three.side2Tag': 'Para tu profesional',
    'landing.three.side2Title': 'Todo lo que necesita saber antes de empezar.',
    'landing.three.side2Desc': 'El contexto que eliges compartir — alergias, tratamientos previos, qué funcionó — listo cuando llegas.',
    'landing.three.side3Tag': 'Para el salón',
    'landing.three.side3Title': 'Cada reserva y cada miembro en un lugar tranquilo.',
    'landing.three.side3Desc': 'El equipo, el calendario y las reservas bajo un mismo techo: coordinado, nunca caótico.',
    'landing.audiences.eyebrow': 'Un sistema para cada lado del cuidado',
    'landing.audiences.title': 'Lo que realmente recibe cada lado.',
    'landing.audiences.clientTag': 'Para clientes',
    'landing.audiences.clientTitle': 'Tu historial, por fin tuyo.',
    'landing.audiences.clientLead': 'Todos tus tratamientos en un lugar que viaja contigo. Sin empezar de cero con cada profesional nuevo y sin intentar recordar qué funcionó.',
    'landing.audiences.clientPoint1': 'Ten un registro completo: visitas, productos, fotos y precios.',
    'landing.audiences.clientPoint2': 'Tus alergias y sensibilidades, visibles para cada profesional.',
    'landing.audiences.clientPoint3': 'Comparte tus últimas cinco visitas o un tipo de tratamiento y revoca el acceso cuando quieras.',
    'landing.audiences.clientPoint4': 'Ve cuánto gastas realmente en ti, mes a mes.',
    'landing.audiences.clientCta': 'Empezar mi historial',
    'landing.audiences.providerTag': 'Para profesionales',
    'landing.audiences.providerTitle': 'Conoce a tu cliente antes de que se siente.',
    'landing.audiences.providerLead': 'Abre una cita y el contexto ya está ahí: qué se hizo, reacciones, qué usa en casa. Menos suposiciones, trabajo más seguro y una libreta de clientes realmente tuya.',
    'landing.audiences.providerPoint1': 'Ve el historial que el cliente eligió compartir antes de que llegue.',
    'landing.audiences.providerPoint2': 'Tus notas, productos y fotos de resultado permanecen entre visitas.',
    'landing.audiences.providerPoint3': 'Configura servicios, complementos, horarios y precios una sola vez.',
    'landing.audiences.providerPoint4': 'Escribe a clientes sin entregar tu número.',
    'landing.audiences.providerCta': 'Abrir cuenta de profesional',
    'landing.audiences.salonTag': 'Para salones',
    'landing.audiences.salonTitle': 'Todo tu salón, sincronizado.',
    'landing.audiences.salonLead': 'Reúne equipo, calendario y reservas bajo un mismo techo, con supervisión donde hace falta y sin invadir el trabajo de nadie.',
    'landing.audiences.salonPoint1': 'Invita y gestiona a todo tu equipo en minutos.',
    'landing.audiences.salonPoint2': 'Ve cada reserva de tu salón, de todos los profesionales.',
    'landing.audiences.salonPoint3': 'Coordina sin exceso: los admins nunca tocan los servicios del profesional.',
    'landing.audiences.salonPoint4': 'Sin dobles reservas, sin traspasos perdidos.',
    'landing.audiences.salonCta': 'Registrar tu salón',
    'landing.features.eyebrow': 'Todo en un lugar',
    'landing.features.title': 'Las cosas que ojalá alguien hubiera estado registrando desde el principio.',
    'landing.features.body': 'Tu historial va primero, pero Bevelure también gestiona lo que lo rodea: reservas, conversación, precios, fotos y visitas registradas fuera.',
    'landing.features.item1Title': 'Un registro realmente tuyo',
    'landing.features.item1Desc': 'Cada visita, producto, nota y foto de resultado en una línea de tiempo que se mueve contigo.',
    'landing.features.item2Title': 'Reservas fáciles',
    'landing.features.item2Desc': 'Ve precios y complementos reales, elige un horario. O registra una visita con un salón que aún no está en Bevelure.',
    'landing.features.item3Title': 'Tú decides quién ve qué',
    'landing.features.item3Desc': 'Comparte tus últimas cinco visitas o un tipo de tratamiento con un profesional nuevo, y retira el acceso cuando quieras.',
    'landing.features.item4Title': 'Antes, después y solo para ti',
    'landing.features.item4Desc': 'Fotos de resultados, fotos de procedimiento para tu proveedor y fotos privadas solo para ti, sin mezclarse.',
    'landing.features.item5Title': 'Donde sea que reserves',
    'landing.features.item5Desc': 'Proveedor en Miami, tú en Berlín: la hora del salón y la tuya lado a lado. Cualquier ciudad, cualquier moneda.',
    'landing.features.item6Title': 'Habla de los detalles',
    'landing.features.item6Desc': 'Mensajea antes y después de una visita: aclara algo, pregunta por el cuidado posterior, manda una foto, sin compartir tu teléfono.',
    'landing.features.item7Title': 'Precios honestos, bien guardados',
    'landing.features.item7Desc': 'Los precios reales quedan fijados en cada visita, en la moneda del profesional.',
    'landing.features.item8Title': 'Valoraciones privadas de dos lados',
    'landing.features.item8Desc': 'Valora una visita para tu memoria, y los profesionales también. Sin ranking público, solo una nota honesta.',
    'landing.founder.eyebrow': 'Historia de la fundadora',
    'landing.founder.title': 'Por qué creo en Bevelure.',
    'landing.founder.p1': 'Construí Bevelure porque nunca podía recordar mis propios tratamientos: qué se hizo, cuándo y qué productos se usaron. Cada vez que veía a un especialista nuevo, las preguntas eran las mismas: *qué te hiciste la vez pasada, qué productos usaron, tuviste alguna reacción alérgica*. Intentaba recordar, pero nunca daba una respuesta sólida.',
    'landing.founder.p2': 'Normalmente decía algo como: *“Recuerdo el resultado, pero no el nombre exacto del tratamiento, los productos o si algo irritó mi piel.”* No era muy útil. Siempre quise tenerlo escrito en algún lugar: productos exactos y detalles de cada cita.',
    'landing.founder.p3': 'Eso es Bevelure: mantienes el registro de todo en un lugar y puedes compartirlo con el proveedor que elijas.',
    'landing.founder.p4': 'Mis citas de belleza también eran un hermoso desorden en mi calendario. Tenía que desplazarme un buen rato para encontrar qué me hice y cuándo debía reservar otra vez. Ahora todo está en un solo lugar: tu historial de belleza.',
    'landing.founder.p5': 'Viajo mucho, así que a menudo veo proveedores nuevos para mantener mis tratamientos. Y siempre es la misma historia: para un facial me preguntan *qué cuidado uso en casa y qué ingredientes tienen mis cremas*. Con Bevelure no tienes que pensarlo: subes fotos de tu rutina con etiquetas e ingredientes y la compartes cuando la necesitas.',
    'landing.founder.p6': 'Creo que muchas personas viven exactamente estas situaciones. Quiero ayudarlas de verdad. Por eso construí Bevelure.',
    'landing.founder.quote': '“Para cada historia beauty que merece ser recordada. Con cuidado.”',
    'landing.founder.signature': 'Ievgeniia Andrieieva, Fundadora y CEO',
    'landing.trust.eyebrow': 'Capa de confianza',
    'landing.trust.title': 'Construido para lo que más importa y lo más difícil de recuperar.',
    'landing.trust.body': 'Información sensible, citas reales, relaciones que duran años. Se siente simple porque los límites importantes sostienen todo en silencio.',
    'landing.trust.item1Title': 'Privacidad integrada',
    'landing.trust.item1Desc': 'Tus notas privadas, las notas internas del profesional y lo que decides compartir se mantienen separados.',
    'landing.trust.item2Title': 'Mantente en contacto, simple',
    'landing.trust.item2Desc': 'Chat, confirmaciones y recordatorios suaves mantienen a todos alineados.',
    'landing.trust.item3Title': 'Sin ranking público',
    'landing.trust.item3Desc': 'Valora una visita en privado, para tu memoria. El cuidado de belleza no debería ser un ranking público.',
    'landing.final.title': 'Tu historia de belleza, por fin en un solo lugar.',
    'landing.final.body': 'Ya sea que reserves, hagas el trabajo o dirijas el salón, todo vive en un registro privado y hermoso.',
    'landing.footer.tagline': 'Tu historia de belleza, en un solo lugar.',
    'landing.footer.contact': 'Contacto',
    'landing.footer.site': 'Sitio',

    'block.title': '¿Bloquear a {name}?',
    'block.body': 'No podrá reservar ni aceptar nuevas citas contigo. Las citas pasadas y ratings seguirán visibles para ambos lados.',
    'block.reasonLabel': 'Razón (solo tú puedes verla)',
    'block.reasonPlaceholder': 'Opcional — útil si revisas tus bloqueos más tarde',
    'block.reportUser': 'También denunciar a este usuario',
    'block.reportHelp': 'La cuenta y el motivo seleccionado se enviarán a Bevelure para su revisión. El otro usuario no verá tu denuncia.',
    'block.reportReasonLabel': 'Motivo de la denuncia',
    'block.reportReasonPlaceholder': 'Elige un motivo',
    'block.reportReasonRequired': 'Elige un motivo antes de denunciar.',
    'block.reportReasonSpam': 'Spam o estafa',
    'block.reportReasonHarassment': 'Acoso o amenazas',
    'block.reportReasonContent': 'Contenido inapropiado',
    'block.reportReasonSafety': 'Comportamiento inseguro',
    'block.reportReasonImpersonation': 'Suplantación de identidad',
    'block.reportReasonOther': 'Otro',
    'block.blockAndReport': 'Bloquear y denunciar',
    'block.cancelFutureOne': 'Cancelar también {count} cita próxima entre ustedes.',
    'block.cancelFutureMany': 'Cancelar también {count} citas próximas entre ustedes.',
    'block.keepFutureHint': 'Si lo desactivas, seguirán en ambos calendarios; el bloqueo solo impide nuevas reservas.',
    'block.failed': 'No se pudo bloquear',
    'block.blocking': 'Bloqueando…',
    'block.block': 'Bloquear',

    'shareModal.titlePrefix': 'Compartir',
    'shareModal.titleEmphasis': 'historial',
    'shareModal.subtitle': 'Genera un enlace de solo lectura para un proveedor.',
    'shareModal.procedureTypes': 'Tipos de procedimiento',
    'shareModal.procedurePlaceholder': 'ej. corte hombre, manicura…',
    'shareModal.procedureHelp': 'Elige de tus procedimientos o escribe uno. Solo serán visibles las citas completadas que coincidan.',
    'shareModal.whatToShare': 'Qué compartir',
    'shareModal.recipientEmail': 'Email del destinatario',
    'shareModal.optional': 'opcional',
    'shareModal.emailHelp': 'Si lo indicas, el enlace se envía por email. Si no, solo se genera un QR / enlace.',
    'shareModal.consent': 'Entiendo que cualquier persona con este enlace puede ver el historial que comparto para los procedimientos seleccionados: notas, productos, recomendaciones y fotos de resultado/procedimiento. Mis notas privadas y fotos privadas nunca se comparten.',
    'shareModal.createError': 'No se pudo crear el enlace. Inténtalo de nuevo.',
    'shareModal.generateLink': 'Generar enlace',
    'shareModal.emailIt': 'Enviar por email',
    'shareModal.scanHint': 'Escanea con la cámara del teléfono para abrir el historial compartido.',
    'shareModal.link': 'Enlace',
    'shareModal.copyLink': 'Copiar enlace',
    'shareModal.procedure': 'Procedimiento',
    'shareModal.mode': 'Modo',
    'shareModal.expires': 'Expira',
    'shareModal.sentTo': 'Enviado a',
    'shareModal.generateAnother': 'Generar otro',

    'contactSetup.title': 'Haz que sea más fácil encontrarte',
    'contactSetup.bodyPrefix': 'Añade tu',
    'contactSetup.socialLink': 'enlace social',
    'contactSetup.bodySuffix': 'para que puedan encontrarte por eso, no solo por tu email.',
    'contactSetup.addContactInfo': 'Añadir contacto',

    'errorBoundary.titlePrefix': 'Algo salió',
    'errorBoundary.titleEmphasis': 'mal',
    'errorBoundary.body': 'Encontramos un error inesperado al mostrar esta página. Actualiza para intentarlo de nuevo; si sigue pasando, avísanos.',
    'errorBoundary.refresh': 'Actualizar página',
    'errorBoundary.dismiss': 'Cerrar y continuar',

    'confirm.defaultTitle': '¿Seguro?',
    'impact.futureBookingOne': '{count} reserva futura se verá afectada.',
    'impact.futureBookingMany': '{count} reservas futuras se verán afectadas.',
    'impact.cancelAffectedOn': 'Todas serán canceladas y los clientes serán notificados.',
    'impact.cancelAffectedOff': 'Déjalo desactivado para mantenerlas programadas; solo se bloquean nuevas reservas.',
    'impact.actionFailed': 'La acción falló',
    'impact.working': 'Trabajando…',

    'desktopHome.settings': 'Ajustes de inicio',
    'desktopHome.layoutTitle': 'Diseño de inicio',
    'desktopHome.layout1': 'Dividido — Próximas y Recientes junto al calendario',
    'desktopHome.layout2': 'Saludo compacto con listas apiladas',
    'desktopHome.layout3': 'Columna de stats · calendario · listas abajo',
    'desktopHome.layout4': 'Listas con scroll a la altura del calendario',
    'auth.tryAgain': 'Algo salió mal — inténtalo de nuevo',
    'auth.updatePassword': 'Actualizar contraseña',
    'auth.updating': 'Actualizando…',
    'auth.verificationInvalid': 'El enlace de verificación no es válido o expiró.',
    'auth.verificationMissing': 'Al enlace de verificación le falta el token.',
    'auth.verifyErrorTitle': 'No se pudo verificar',
    'auth.verifyResendHint': 'Inicia sesión y toca Reenviar en el banner de verificación.',
    'auth.welcomeBack': 'Bienvenido *de nuevo*',
    'notif.providerRequest.title': 'Nueva solicitud de cita',
    'notif.providerRequest.body': 'Un cliente quiere reservar. Confirma o rechaza.',
    'notif.providerScheduled.title': 'Nueva cita',
    'notif.providerScheduled.body': 'Se ha programado una nueva cita para ti.',
    'notif.customerCreated.title': 'Cita programada',
    'notif.customerCreated.body': '{provider} te reservó {service}.',
    'notif.customerConfirmed.title': 'Cita confirmada',
    'notif.customerConfirmed.body': '{provider} confirmó tu {service}.',
    'notif.customerRejected.title': 'Solicitud rechazada',
    'notif.customerRejected.body': '{provider} no pudo aceptar tu {service}. Elige otro horario.',
    'notif.cancelled.title': 'Cita cancelada',
    'notif.cancelled.body': 'Tu {service} con {other} fue cancelada.',
    'notif.customerExpired.title': 'Solicitud caducada',
    'notif.customerExpired.body': 'Tu solicitud de {service} caducó — no se confirmó a tiempo. Elige otro horario.',
    'notif.detailsUpdated.title': 'Detalles del tratamiento actualizados',
    'notif.detailsUpdated.body': '{provider} añadió notas o fotos a tu {service}.',
    'notif.reminderCustomer.title': 'Cita en 48 horas',
    'notif.reminderCustomer.body': 'Tu {service} con {other} es en unas 48 horas.',
    'notif.reminderProvider.title': 'Cita en 48 horas',
    'notif.reminderProvider.body': '{service} con {other} es en unas 48 horas.',
    'notif.salonInvite.title': 'Invitación al salón',
    'notif.salonInvite.body': '{salon} te invita a unirte a su equipo.',
    'notif.memberRemoved.title': 'Eliminado del salón',
    'notif.memberRemoved.body': 'El administrador te eliminó de {salon}.',
    'notif.memberLeft.title': 'Un profesional dejó tu salón',
    'notif.memberLeft.body': '{provider} dejó el equipo.',
    'notif.inviteAccepted.title': 'Un profesional se unió a tu salón',
    'notif.inviteAccepted.body': '{provider} aceptó la invitación y se unió al equipo.',
    'notif.inviteDeclined.title': 'Invitación rechazada',
    'notif.inviteDeclined.body': '{provider} rechazó tu invitación al salón.',
    'notif.salonBookingRequested.title': 'Nueva solicitud en tu salón',
    'notif.salonBookingRequested.body': '{customer} quiere reservar {service} con {provider} — pendiente de confirmación.',
    'notif.salonBooking.title': 'Nueva reserva en tu salón',
    'notif.salonBooking.body': '{provider} tiene una nueva cita con {customer}.',
    'notif.salonBookingCancelled.title': 'Reserva cancelada en tu salón',
    'notif.salonBookingCancelled.body': 'La cita de {provider} con {customer} fue cancelada.',
    'auth.yourPassword': 'Tu contraseña',

    'nav.navigation': 'Navegación',
    'nav.account': 'Cuenta',
    'nav.menu': 'Menú',
    'nav.home': 'Inicio',
    'nav.search': 'Buscar',
    'nav.searchProviders': 'Buscar proveedores',
    'nav.favorites': 'Favoritos',
    'nav.messages': 'Mensajes',
    'nav.myCareHub': 'Mi Care Hub',
    'nav.spending': 'Gastos',
    'nav.profile': 'Perfil',
    'nav.history': 'Historial',
    'nav.upcoming': 'Próximas',
    'nav.customers': 'Clientes',
    'nav.team': 'Equipo',
    'nav.schedule': 'Agenda',
    'nav.services': 'Servicios',
    'nav.portfolio': 'Portafolio',
    'nav.mySalon': 'Mi salón',
    'nav.earnings': 'Ingresos',
    'nav.pending': 'Pendientes',
    'nav.signOut': 'Cerrar sesión',
    'nav.terms': 'Términos',
    'nav.privacy': 'Privacidad',
    'nav.notifications': 'Notificaciones',
    'nav.salonInviteTitle': '{count} invitación{plural} de salón pendiente',

    'notifications.allCaughtUp': 'Todo al día',
    'notifications.allLoaded': 'Eso es todo: {count} notificaciones.',
    'notifications.empty': 'Aún no hay notificaciones',
    'notifications.markAsRead': 'Marcar como leída',
    'notifications.title': 'Notificaciones',

    'salonTeam.inviteFromPrefix': 'Invita proveedores desde',
    'salonTeam.manageInvitesPrefix': 'Gestiona invitaciones desde',
    'salonTeam.noProviders': 'Aún no hay proveedores',
    'salonTeam.noSearchMatches': 'Ningún miembro coincide con tu búsqueda.',
    'salonTeam.providerCount': '{count} proveedor(es)',
    'salonTeam.searchPlaceholder': 'Buscar por nombre o servicio…',
    'salonTeam.sortName': 'Nombre (A-Z)',
    'salonTeam.sortRecentlyJoined': 'Recientes',

    'salon.accept': 'Aceptar',
    'salon.activeProviders': '{count} proveedor(es) activo(s)',
    'salon.address': 'Dirección',
    'salon.addressPlaceholder': 'Empieza a escribir tu dirección…',
    'salon.admin': 'Admin',
    'salon.city': 'Ciudad',
    'salon.close': 'Cerrar',
    'salon.country': 'País',
    'salon.decline': 'Rechazar',
    'salon.description': 'Descripción',
    'salon.descriptionPlaceholder': 'Cuéntales a los clientes qué hace especial a tu salón',
    'salon.desktopTitleEmphasis': 'salón',
    'salon.desktopTitlePrefix': 'Mi',
    'salon.editDetails': 'Editar detalles',
    'salon.invitation': 'Invitación del salón',
    'salon.invitationSent': 'Invitación enviada.',
    'salon.inviteProvider': 'Invitar a un proveedor',
    'salon.inviteProviderHelp': 'Debe tener una cuenta de proveedor en Bevelure. Le avisaremos para que acepte.',
    'salon.invitedAs': 'Invitado como {role}',
    'salon.leaveDescription': 'Dejarás de formar parte de este salón. Las nuevas reservas que tomes después no se asociarán al salón. Las citas pasadas siguen en tu historial.',
    'salon.leaveSalon': 'Salir del salón',
    'salon.leaveTitle': '¿Salir de {name}?',
    'salon.name': 'Nombre del salón',
    'salon.noPendingInvites': 'No hay invitaciones pendientes. Los miembros activos están en {team}.',
    'salon.notPartBody': 'Los salones invitan proveedores por email. Si un salón te invita, la invitación aparecerá aquí.',
    'salon.notPartTitle': 'No formas parte de ningún salón',
    'salon.pendingInvites': 'Invitaciones pendientes',
    'salon.phone': 'Teléfono',
    'salon.phonePlaceholder': 'Número del salón',
    'salon.postalCode': 'Código postal',
    'salon.providerEmail': 'Email del proveedor',
    'salon.providerFallback': 'Proveedor',
    'salon.publicEmail': 'Email público',
    'salon.removeMember': 'Eliminar miembro',
    'salon.revokeInvite': 'Revocar invitación',
    'salon.roleAdmin': 'admin',
    'salon.roleMember': 'miembro',
    'salon.saveDetailsError': 'No se pudieron guardar los detalles del salón',
    'salon.sendInvite': 'Enviar invitación',
    'salon.sending': 'Enviando…',
    'salon.socialLink': 'Sitio / enlace social',
    'salon.subtitle': 'Gestiona tu salón y tu equipo.',
    'salon.teamLink': 'Equipo',
    'salon.title': 'Mi salón',
    'salon.uploadLogo': 'Subir logo',
    'salon.view': 'Ver',
    'salon.viewProfile': 'Ver perfil del salón',
    'salon.youWorkAt': 'Trabajas en',

    'teamBookings.all': 'Todo',
    'teamBookings.history': 'Historial',
    'teamBookings.noBookings': 'Aún no hay reservas aquí.',
    'teamBookings.subtitle': 'Todas las citas de los proveedores de tu salón.',
    'teamBookings.title': 'Reservas del equipo',
    'teamBookings.titleEmphasis': 'equipo',
    'teamBookings.titlePrefix': 'Reservas',
    'teamBookings.upcoming': 'Próximas',
    'salonAppointments.cancelReason': 'Cancelada por el administrador del salón',

    'salonProfile.messageSalon': 'Mensaje al salón',
    'salonProfile.noProviders': 'Aún no hay proveedores listados.',
    'salonProfile.openingChat': 'Abriendo chat…',
    'salonProfile.ourTeam': 'Nuestro equipo',
    'salonProfile.provider': 'proveedor',
    'salonProfile.providers': 'proveedores',
    'salonProfile.title': 'Salón',

    'portfolio.addMore': 'Agregar más',
    'portfolio.addPhoto': 'Agregar foto',
    'portfolio.addToPortfolio': 'Agregar al portfolio',
    'portfolio.bulkApplyHint': 'La categoría y el texto se aplican a las {count} fotos.',
    'portfolio.caption': 'Texto',
    'portfolio.captionOptional': 'Texto (opcional)',
    'portfolio.captionPlaceholder': 'Descripción corta bajo la foto',
    'portfolio.category': 'Categoría',
    'portfolio.categoryCount': '{count} categoría(s)',
    'portfolio.categoryPlaceholder': 'ej. Manicura, Botox, Hydrafacial',
    'portfolio.choosePhotos': 'Toca para elegir fotos',
    'portfolio.deletePhotoMessage': 'Se eliminará de tu portfolio público.',
    'portfolio.deletePhotoTitle': '¿Eliminar foto?',
    'portfolio.desktopTitleEmphasis': 'portfolio',
    'portfolio.desktopTitlePrefix': 'Mi',
    'portfolio.editTitle': 'Editar elemento del portfolio',
    'portfolio.fileHint': 'JPG, PNG hasta 10MB · puedes elegir varias',
    'portfolio.hide': 'Ocultar',
    'portfolio.noFiles': 'Sin archivos',
    'portfolio.noItemsBody': 'Muestra tu trabajo: los clientes lo ven en tu perfil público. Sube fotos directamente o promociona fotos de citas completadas.',
    'portfolio.noItemsTitle': 'Aún no hay elementos en el portfolio',
    'portfolio.noItemsYet': 'Aún no hay elementos',
    'portfolio.private': 'Privado',
    'portfolio.privateDescription': 'Guardado en tu portfolio pero oculto para clientes.',
    'portfolio.public': 'Público',
    'portfolio.publicDescription': 'Visible en tu perfil público para quien lo abra.',
    'portfolio.remove': 'Quitar',
    'portfolio.shortDescription': 'Descripción corta',
    'portfolio.show': 'Mostrar',
    'portfolio.title': 'Portfolio',
    'portfolio.uncategorized': 'Sin categoría',
    'portfolio.upload': 'Subir',
    'portfolio.uploadCount': 'Subir ({count})',
    'portfolio.uploading': 'Subiendo…',

    'emailVerify.resendError': 'No se pudo reenviar — inténtalo de nuevo en un momento.',
    'emailVerify.sent': 'Correo enviado — revisa tu bandeja.',
    'emailVerify.message': 'Confirma tu correo para recibir notificaciones de reservas.',
    'emailVerify.sending': 'Enviando…',
    'emailVerify.resend': 'Reenviar',

    'providerSetup.title': 'Termina de configurar tu perfil',
    'providerSetup.bodyPrefix': 'Los clientes no pueden reservar hasta que agregues',
    'providerSetup.oneService': 'al menos un servicio',
    'providerSetup.workingHours': 'tu horario de trabajo',
    'providerSetup.addService': 'Agregar un servicio',
    'providerSetup.setHours': 'Definir horario',

    'profile.title': 'Perfil',
    'profile.preferences': 'Preferencias',
    'profile.language': 'Idioma',
    'profile.pushNotifications': 'Notificaciones push',
    'profile.languageAria': 'Idioma de la interfaz',
    'profile.providerLanguages': 'Idiomas que hablas',
    'profile.providerLanguagesLabel': 'Idiomas',
    'profile.currencyLabel': 'Moneda',
    'profile.failedUpdate': 'No se pudo actualizar el perfil',
    'profile.roleCustomer': 'Cliente',
    'profile.roleCareProvider': 'Proveedor de cuidado',
    'profile.roleSalonAdmin': 'Administrador del salón',
    'profile.uploadPhoto': 'Subir foto',
    'profile.rating': 'Calificación',
    'profile.ratingSingular': 'calificación',
    'profile.ratingPlural': 'calificaciones',
    'profile.fromClients': 'de clientes',
    'profile.fromProviders': 'de proveedores',
    'profile.byService': 'Por servicio',
    'profile.noRatingsCustomer': 'Aún no hay calificaciones. Los proveedores podrán calificarte después de una cita completada.',
    'profile.noRatingsProvider': 'Aún no hay calificaciones. Los clientes podrán calificarte después de una cita completada.',
    'profile.personalInformation': 'Información personal',
    'profile.dateOfBirth': 'Fecha de nacimiento',
    'profile.sex': 'Sexo',
    'profile.location': 'Ubicación',
    'profile.address': 'Dirección',
    'profile.addressPlaceholder': 'Empieza a escribir tu dirección...',
    'profile.timeZone': 'Zona horaria',
    'profile.timeZonePrefix': 'Zona horaria: {zone}',
    'profile.timeZoneHelp': 'Tu horario de trabajo usa esta zona. Los clientes de otros lugares también ven su propia hora.',
    'profile.contact': 'Contacto',
    'profile.phone': 'Teléfono',
    'profile.social': 'Red social',
    'profile.socialMediaLink': 'Enlace social',
    'profile.socialPlaceholder': 'https://instagram.com/tu-usuario',
    'profile.contactHelp': 'Para que los clientes puedan encontrarte y reservar por teléfono o enlace social. El teléfono se guarda en formato internacional estándar — incluye tu código de país.',
    'profile.noContactDetails': 'Aún no hay datos de contacto.',
    'profile.allergies': 'Alergias',
    'profile.noAllergies': 'Sin alergias',
    'profile.hasAllergies': 'Tengo alergias',
    'profile.notes': 'Notas',
    'profile.allergyPlaceholder': 'Látex, fragancias, etc.',
    'profile.allergiesNoDetails': 'Tiene alergias (sin detalles)',
    'profile.noKnownAllergies': 'No hay alergias conocidas',
    'profile.careRoutine': 'Rutina de cuidado',
    'profile.careRoutineHelp': 'Describe o fotografía tu rutina en casa (productos, pasos). Se comparte con tus proveedores para darles contexto.',
    'profile.careRoutinePlaceholder': 'p. ej. Mañana: limpiador suave, vitamina C, SPF. Noche: retinol 2x/semana...',
    'profile.addPhoto': 'Agregar foto',
    'profile.removePhotoTitle': '¿Eliminar foto?',
    'profile.removePhotoBody': 'Esta foto se eliminará permanentemente.',
    'profile.removePhoto': 'Eliminar',
    'profile.professionalDetails': 'Detalles profesionales',
    'profile.yearExperience': '{service} · {count} año de experiencia',
    'profile.yearsExperience': '{service} · {count} años de experiencia',
    'profile.bio': 'Bio',
    'profile.bioPlaceholder': 'Una breve presentación para tu perfil público...',
    'profile.about': 'Acerca de',
    'profile.noBio': 'Aún no hay bio — agrega una para ayudar a los clientes a confiar en ti.',
    'profile.currencyHelp': 'Se usará para todos tus servicios y extras en adelante. Las citas pasadas conservan la moneda en la que se reservaron.',
    'profile.certificates': 'Certificados',
    'profile.addCertificate': 'Agregar',
    'profile.certificatesHelp': 'Se muestran en tu perfil público. Imágenes o PDF.',
    'profile.noCertificates': 'Aún no hay certificados.',
    'profile.removeCertificateTitle': '¿Eliminar certificado?',
    'profile.removeCertificateBody': 'Este certificado se eliminará permanentemente.',
    'profile.removeCertificate': 'Eliminar',
    'profile.password': 'Contraseña',
    'profile.changePassword': 'Cambiar',
    'profile.currentPassword': 'Contraseña actual',
    'profile.newPassword': 'Nueva contraseña',
    'profile.confirmNewPassword': 'Confirmar nueva contraseña',
    'profile.newPasswordMin': 'La nueva contraseña debe tener al menos 8 caracteres',
    'profile.newPasswordsMismatch': 'Las nuevas contraseñas no coinciden',
    'profile.passwordUpdated': 'Contraseña actualizada. Otros dispositivos deberán iniciar sesión de nuevo.',
    'profile.passwordChangeFailed': 'No se pudo cambiar la contraseña',
    'profile.newPasswordPlaceholder': 'Al menos 8 caracteres',
    'profile.confirmNewPasswordPlaceholder': 'Repite la nueva contraseña',
    'profile.updatePassword': 'Actualizar contraseña',
    'profile.deleteAccountTitle': 'Eliminar cuenta',
    'profile.deleteAccountBody': 'Al eliminar tu cuenta, se borrarán permanentemente tu perfil y los datos personales asociados. Las citas futuras o en curso se cancelarán. Esta acción no se puede deshacer.',
    'profile.deleteAccount': 'Eliminar cuenta',
    'profile.deletingAccount': 'Eliminando…',
    'profile.deleteAccountConfirmTitle': '¿Eliminar tu cuenta?',
    'profile.deleteAccountImpactNone': 'No se cancelará ninguna cita próxima o en curso.',
    'profile.deleteAccountImpactOne': '1 cita próxima o en curso se cancelará automáticamente.',
    'profile.deleteAccountImpactMany': '{count} citas próximas o en curso se cancelarán automáticamente.',
    'profile.deleteAccountConfirmBody': 'Tu cuenta y los datos personales asociados se eliminarán permanentemente. Esta acción no se puede deshacer.',
    'profile.deleteAccountConfirm': 'Eliminar cuenta',
    'profile.deleteAccountError': 'No se pudo eliminar la cuenta',
    'profile.switchCurrencyTitle': '¿Cambiar moneda a {currency}?',
    'profile.serviceSingular': 'servicio',
    'profile.servicePlural': 'servicios',
    'profile.currencyWarningBody': 'Tienes {count} {services} activos con precios en {from}. Cambiar a {to} no convertirá esos números: conservarán sus dígitos, pero se mostrarán con el nuevo símbolo. Revisa y actualiza los precios en My Services después del cambio.',
    'profile.currencyWarningPast': 'Las citas pasadas conservan la moneda con la que se reservaron; no se ven afectadas.',
    'profile.switchToCurrency': 'Cambiar a {currency}',
  },
  uk: {
    ...en,
    'common.loading': 'Завантаження...',
    'common.loadingEllipsis': 'Завантаження…',
    'common.loadingMore': 'Завантажуємо ще…',
    'common.loadMore': 'Завантажити ще',
    'common.save': 'Зберегти',
    'common.saveChanges': 'Зберегти зміни',
    'common.saving': 'Збереження…',
    'common.saved': 'Збережено',
    'common.edit': 'Редагувати',
    'common.cancel': 'Скасувати',
    'common.discard': 'Відхилити',
    'common.delete': 'Видалити',
    'common.unsavedChanges': 'У вас є незбережені зміни',
    'common.enabled': 'Увімкнено',
    'common.disabled': 'Вимкнено',
    'common.custom': 'Власне значення',
    'common.and': 'і',
    'common.all': 'Усі',
    'common.any': 'Будь-який',
    'common.add': 'Додати',
    'common.clear': 'Очистити',
    'common.clearFilters': 'Очистити фільтри',
    'common.close': 'Закрити',
    'common.confirm': 'Підтвердити',
    'common.decrease': 'Зменшити',
    'common.dismiss': 'Закрити',
    'common.done': 'Готово',
    'common.emailChannel': 'електронною поштою',
    'common.filters': 'Фільтри',
    'common.from': 'Від',
    'common.homeAria': 'Головна Bevelure',
    'common.increase': 'Збільшити',
    'common.km': 'км',
    'common.max': 'Макс.',
    'common.min': 'Мін.',
    'common.minutesShort': '{count} хв',
    'common.next': 'Далі',
    'common.noMatches': 'Нічого не знайдено',
    'common.no': 'Ні',
    'common.pageOf': 'Сторінка {page} з {total}',
    'common.prev': 'Назад',
    'common.price': 'Ціна',
    'common.remove': 'Видалити',
    'common.scrollRight': 'Прокрутити вправо',
    'common.search': 'Пошук',
    'common.sort': 'Сортувати',
    'common.sortBy': 'Сортувати за: {label}',
    'common.to': 'До',
    'common.unknown': 'Невідомо',
    'common.user': 'Користувач',
    'common.viewAll': 'Переглянути всі',
    'common.viewFullDetails': 'Переглянути деталі',
    'common.viewHistory': 'Переглянути історію',
    'common.viewProfile': 'Переглянути профіль',
    'common.yes': 'Так',

    'imageCrop.adjustLogo': 'Налаштувати логотип',
    'imageCrop.adjustPhoto': 'Налаштувати фото',
    'imageCrop.description': 'Перемістіть і наблизьте зображення так, щоб потрібна частина була всередині кола.',
    'imageCrop.dragHint': 'Перетягніть зображення, щоб змінити позицію.',
    'imageCrop.squareDescription': 'Перемістіть і наблизьте зображення так, щоб потрібна частина була всередині квадрата.',
    'imageCrop.useImage': 'Використати зображення',
    'imageCrop.zoom': 'Масштаб',

    'greeting.afternoon': 'Добрий день',
    'greeting.evening': 'Добрий вечір',
    'greeting.morning': 'Доброго ранку',
    'greeting.night': 'Доброї ночі',

    'status.awaitingProvider': 'Очікує спеціаліста',
    'status.cancelled': 'Скасовано',
    'status.completed': 'Завершено',
    'status.confirmed': 'Підтверджено',
    'status.inProgress': 'Триває',
    'status.pending': 'Очікує',

    'date.daysAgo': '{count} дн тому',
    'date.afternoon': 'День',
    'date.evening': 'Вечір',
    'date.morning': 'Ранок',
    'date.noon': 'Полудень',
    'date.selectDate': 'Оберіть дату',
    'date.selectDateTime': 'Оберіть дату й час',
    'date.time': 'Час',
    'date.today': 'Сьогодні',
    'date.tomorrow': 'Завтра',
    'date.yesterday': 'Вчора',

    'field.addCustomValue': 'Додати “{value}”',
    'field.addLanguage': 'Додати мову…',
    'field.anyDate': 'Будь-яка дата',
    'field.anyLanguage': 'Будь-яка мова',
    'field.anyProvider': 'Будь-який спеціаліст',
    'field.anyService': 'Будь-яка послуга',
    'field.age': 'Вік',
    'field.ageMaxAria': 'Максимальний вік',
    'field.ageMinAria': 'Мінімальний вік',
    'field.ageUpTo': 'До {age}',
    'field.cityPlaceholder': 'Маямі',
    'field.city': 'Місто',
    'field.firstNamePlaceholder': 'Анна',
    'field.hidePassword': 'Сховати пароль',
    'field.invalidPhone': 'Введіть дійсний номер телефону для вибраної країни.',
    'field.language': 'Мова',
    'field.languageHelp': 'Оберіть зі списку або введіть власну мову й натисніть Enter.',
    'field.lastNamePlaceholder': 'Коваленко',
    'field.noCountriesFound': 'Країн не знайдено',
    'field.phoneNumber': 'Номер телефону',
    'field.priceMaxAria': 'Максимальна ціна',
    'field.priceMinAria': 'Мінімальна ціна',
    'field.providerNamePlaceholder': 'Алекс Морган',
    'field.rating': 'Рейтинг',
    'field.searchCountry': 'Пошук країни…',
    'field.searchCurrency': 'Пошук валюти…',
    'field.selectCountry': 'Оберіть країну',
    'field.service': 'Послуга',
    'field.serviceExamplePlaceholder': 'наприклад, манікюр',
    'field.shareProcedurePlaceholder': 'наприклад, чоловіча стрижка, догляд за обличчям…',
    'field.showPassword': 'Показати пароль',
    'field.socialPlaceholder': '@ім’я або URL профілю',
    'field.startTypingAddress': 'Почніть вводити адресу…',
    'field.startTypingCity': 'Почніть вводити місто…',
    'field.serviceType': 'Тип послуги',
    'field.sex': 'Стать',
    'field.useCustomLanguage': 'Використати “{value}”',

    'sex.female': 'Жінка',
    'sex.male': 'Чоловік',
    'sex.other': 'Інше',
    'sex.preferNotToSay': 'Не хочу вказувати',

    'rating.any': 'Будь-який',
    'sort.distance': 'Відстань',
    'sort.name': 'Ім’я',
    'sort.rating': 'Рейтинг',

    'customerHome.bookingConfirmedBody': 'Збережено у вашу історію. Відкрийте запис, щоб побачити всі деталі.',
    'customerHome.bookingPendingBody': 'Ми повідомимо вас, щойно спеціаліст підтвердить. Ви можете скасувати будь-коли.',
    'customerHome.bookingRequestSent': 'Запит на бронювання надіслано',
    'customerHome.appointmentBooked': 'Запис заброньовано',
    'customerHome.inProgressSubtitle': 'Ваш запис зараз триває',
    'customerHome.lastVisit': 'Останній візит',
    'customerHome.nextAppointment': 'Ваш наступний запис {when}',
    'customerHome.noAppointmentsThisDay': 'На цей день немає записів',
    'customerHome.noUpcoming': 'Немає майбутніх записів',
    'customerHome.nothingHereYet': 'Поки що нічого немає',
    'customerHome.recent': 'Нещодавні',
    'customerHome.upcoming': 'Майбутні',

    'providerHome.allProviders': 'Усі спеціалісти',
    'providerHome.appointmentToday': 'Запис сьогодні',
    'providerHome.appointmentsToday': 'Записи сьогодні',
    'providerHome.awaitingYou': 'Очікує на вас',
    'providerHome.pendingRequests': 'Запитів на бронювання очікує: {count}',
    'providerHome.pendingRequestsBody': 'Підтвердьте або відхиліть, щоб клієнти знали, чого очікувати.',
    'providerHome.reviewConfirm': 'Переглянути й підтвердити',
    'providerHome.searchTeam': 'Пошук у команді…',

    'customerSearch.anyCity': 'Будь-яке місто',
    'customerSearch.detectingLocation': 'Визначаємо вашу локацію…',
    'customerSearch.findingProviders': 'Шукаємо спеціалістів…',
    'customerSearch.inCity': ' у {city}',
    'customerSearch.locationCity': 'Показуємо спеціалістів у {city}.',
    'customerSearch.locationNearest': 'Показуємо спеціалістів поблизу{city}.',
    'customerSearch.noProvidersFound': 'Спеціалістів не знайдено',
    'customerSearch.priceHintAny': 'Вкажіть тип послуги вище, щоб фільтрувати конкретну процедуру за ціною.',
    'customerSearch.priceHintService': 'Ціна послуг, що відповідають “{serviceType}”.',
    'customerSearch.searchPlaceholder': 'Пошук за ім’ям…',
    'customerSearch.servicePlaceholder': 'Будь-який (наприклад, манікюр, барбер…)',
    'customerSearch.showingAll': 'Показуємо всіх спеціалістів.',
    'customerSearch.title': 'Знайти спеціалістів',
    'customerSearch.useMyLocation': 'Використати мою локацію',

    'careHub.completedVisits': 'Завершені візити: {count}',
    'careHub.loadingMore': 'Завантажуємо ще…',
    'careHub.noCompletedFor': 'Поки немає завершених візитів для {serviceType}.',
    'careHub.noCompletedYet': 'Поки немає завершених візитів.',
    'careHub.noProcedureDetails': 'Деталі процедури ще не додані.',
    'careHub.procedurePhotos': 'Фото процедури',
    'careHub.procedureTypes': 'Типи процедур: {count}',
    'careHub.quickShare': 'Швидкий доступ',
    'careHub.quickShareBody': 'Створіть QR-код або посилання для одного з типів процедур.',
    'careHub.shareHistory': 'Поділитися історією',
    'careHub.title': 'Мій Care Hub',
    'careHub.titleAccent': 'Мій Care *Hub*',
    'careHub.detailNotes': 'Нотатки',
    'careHub.detailProducts': 'Продукти',
    'careHub.detailRecommendations': 'Рекомендації',

    'appointments.cancelled': 'Скасовані',
    'appointments.careProvider': 'Спеціаліст',
    'appointments.history': 'Історія',
    'appointments.loadOlder': 'Завантажити старіші записи',
    'appointments.loadingOlder': 'Завантажуємо старіші записи…',
    'appointments.noCancelled': 'Скасованих записів немає',
    'appointments.noFilterMatches': 'Жоден запис не відповідає вашим фільтрам.',
    'appointments.noHistory': 'Поки що нічого немає',
    'appointments.noUpcoming': 'Немає майбутніх записів',
    'appointments.rateThisVisit': 'Оцінити цей візит',
    'appointments.upcoming': 'Майбутні',
    'appointments.yourRating': 'Ваша оцінка',

    'providerAppointments.anyCustomer': 'Будь-який клієнт',
    'providerAppointments.noPending': 'Запитів на підтвердження немає — усе опрацьовано.',
    'providerAppointments.rateClient': 'Оцінити цього клієнта',

    'providerServices.addService': 'Додати послугу',
    'providerServices.addon': 'Доповнення',
    'providerServices.addonName': 'Назва доповнення',
    'providerServices.addonPlaceholder': 'наприклад, зняття гелю',
    'providerServices.deactivate': 'Деактивувати',
    'providerServices.deactivateAddon': 'Деактивувати доповнення',
    'providerServices.deactivateAddonDescription': 'Сама послуга залишиться доступною — забронювати не можна буде лише це доповнення. Його можна буде активувати знову.',
    'providerServices.deactivateAddonTitle': 'Деактивувати доповнення “{name}”?',
    'providerServices.deactivateService': 'Деактивувати послугу',
    'providerServices.deactivateServiceDescription': 'Клієнти не зможуть бронювати цю послугу для нових записів. Минулі бронювання залишаться в історії. Ви зможете активувати послугу знову.',
    'providerServices.deactivateServiceTitle': 'Деактивувати “{name}”?',
    'providerServices.durationMinutes': 'Тривалість (хвилини)',
    'providerServices.editAddon': 'Редагувати доповнення',
    'providerServices.extraMinutes': 'Додаткові хвилини',
    'providerServices.extraPrice': 'Додаткова ціна ({currency})',
    'providerServices.failedCreate': 'Не вдалося створити послугу',
    'providerServices.failedSaveAddon': 'Не вдалося зберегти доповнення',
    'providerServices.inactive': 'Неактивне',
    'providerServices.noAddons': 'Доповнень поки немає.',
    'providerServices.priceOptional': 'Ціна ({currency}, необов’язково)',
    'providerServices.reactivate': 'Активувати знову',
    'providerServices.reactivateService': 'Активувати послугу знову',
    'providerServices.renameDisabled': 'Перейменування не підтримується — минулі бронювання посилаються на цю назву. Деактивуйте її та створіть нову.',
    'chat.blockedTitle': 'Повідомлення недоступні',
    'chat.blocked': 'Ви більше не можете писати одне одному.',
    'pub.noSlotsMonth': 'Цього місяця немає вільних годин — гортайте стрілками інші місяці.',
    'pub.publicPage': 'Публічна сторінка записів',
    'pub.username': 'Ім’я користувача',
    'pub.usernameHelp': 'Ваше публічне посилання — діліться ним у соцмережах, щоб клієнти записувалися.',
    'pub.copy': 'Копіювати',
    'pub.copied': 'Скопійовано',
    'common.back': 'Назад',
    'pub.close': 'Закрити',
    'pub.bookWith': 'Запис до {name}',
    'pub.bookCta': 'Записатися',
    'pub.selectService': 'Оберіть послугу',
    'pub.chooseTime': 'Оберіть час',
    'pub.yourDetails': 'Ваші дані',
    'pub.pickDay': 'Оберіть день із вільними годинами.',
    'pub.continue': 'Далі',
    'pub.confirmBooking': 'Підтвердити запис',
    'pub.successTitle': 'Запит надіслано',
    'pub.successGuest': 'Ваш запит надіслано! Деталі запису надіслали на {email} — спеціаліст невдовзі підтвердить.',
    'pub.successCustomer': 'Заброньовано! {provider} невдовзі підтвердить ваш запис.',
    'pub.manageCancel': 'Керувати або скасувати запис',
    'pub.signedInAs': 'Запис від імені {name}',
    'pub.tooMany': 'Забагато спроб запису. Спробуйте пізніше.',
    'pub.bookingFailed': 'Не вдалося завершити запис. Спробуйте ще раз.',
    'pub.about': 'Про мене',
    'pub.services': 'Послуги',
    'pub.portfolio': 'Портфоліо',
    'pub.speaks': 'Мови',
    'pub.ratings': '{count} оцінок',
    'pub.atSalon': 'у {salon}',
    'pub.team': 'Команда',
    'pub.noTeam': 'Поки що немає учасників.',
    'pub.createYourOwn': 'Створіть власну сторінку записів на Bevelure →',
    'pub.agree': 'Я погоджуюся з',
    'pub.terms': 'Умовами',
    'pub.privacy': 'Політикою конфіденційності',
    'common.showMore': 'Показати більше',
    'common.showLess': 'Згорнути',
    'providerServices.descriptionOptional': 'Опис (необов’язково)',
    'providerServices.serviceDescriptionPlaceholder': 'Що входить, чого очікувати, підготовка та догляд після…',
    'providerServices.addonDescriptionPlaceholder': 'Що включає це доповнення…',
    'providerServices.serviceName': 'Назва послуги',
    'providerServices.servicePlaceholder': 'наприклад, манікюр',
    'providerServices.title': 'Мої послуги',

    'schedule.adding': 'Додаємо…',
    'schedule.addTimeOff': 'Додати неробочий час',
    'schedule.addTimeOffHelp': 'Заблокуйте дати для відпустки, лікарняних або перерв. Клієнти не зможуть бронювати в ці періоди.',
    'schedule.addTimeOffImpactHelp': 'Клієнти не зможуть створювати нові записи в цьому проміжку.',
    'schedule.addTimeOffTitle': 'Додати цей неробочий час?',
    'schedule.addWorkingHours': 'Додати робочі години',
    'schedule.dayOfWeek': 'День тижня',
    'schedule.dayOff': 'Вихідний',
    'schedule.end': 'Кінець',
    'schedule.errorEndAfterStart': 'Кінець має бути після початку',
    'schedule.errorEndAfterStartTime': 'Час кінця має бути після часу початку',
    'schedule.errorOverlap': 'Ці робочі години перетинаються з наявним правилом для цього дня',
    'schedule.errorPickStartEnd': 'Оберіть час початку й кінця',
    'schedule.failedAddRule': 'Не вдалося додати правило розкладу',
    'schedule.friShort': 'Пт',
    'schedule.friday': 'П’ятниця',
    'schedule.monShort': 'Пн',
    'schedule.monday': 'Понеділок',
    'schedule.noneScheduled': 'Нічого не заплановано',
    'schedule.pastTimeOff': 'Минулий неробочий час ({count})',
    'schedule.pickEnd': 'Оберіть кінець',
    'schedule.pickStart': 'Оберіть початок',
    'schedule.reasonPlaceholder': 'Причина (необов’язково) — відпустка, навчання тощо',
    'schedule.removeWorkingHoursDescription': 'буде видалено з вашого щотижневого розкладу. Нові бронювання в цьому проміжку будуть неможливі.',
    'schedule.removeWorkingHoursTitle': 'Видалити робочі години?',
    'schedule.satShort': 'Сб',
    'schedule.saturday': 'Субота',
    'schedule.start': 'Початок',
    'schedule.sunShort': 'Нд',
    'schedule.sunday': 'Неділя',
    'schedule.thuShort': 'Чт',
    'schedule.thursday': 'Четвер',
    'schedule.title': 'Розклад',
    'schedule.tueShort': 'Вт',
    'schedule.tuesday': 'Вівторок',
    'schedule.upcomingTimeOff': 'Майбутній неробочий час',
    'schedule.wedShort': 'Ср',
    'schedule.wednesday': 'Середа',
    'schedule.weeklySchedule': 'Щотижневий розклад',

    'providers.all': 'Усі',
    'providers.blocked': 'Заблоковані',
    'providers.blockedBody': 'Заблоковані спеціалісти не з’являтимуться в пошуку або ваших списках. Розблокувати можна будь-коли.',
    'providers.blockedOn': 'Заблоковано {date}',
    'providers.browseProviders': 'Переглянути спеціалістів',
    'providers.countSaved': 'Збережено: {count}',
    'providers.countVisited': 'Відвідано спеціалістів: {count}',
    'providers.emptyFavoritesBody': 'Натисніть серце в профілі спеціаліста, щоб зберегти його тут.',
    'providers.emptyVisitedBody': 'Спеціалісти, яких ви бронювали, з’являться тут після першого запису.',
    'providers.favorites': 'Обране',
    'providers.lastVisit': 'останній візит {when}',
    'providers.nextVisit': 'Наступний {when}',
    'providers.nextVisitSort': 'Наступний візит',
    'providers.noBlocked': 'Заблокованих спеціалістів немає',
    'providers.noFavorites': 'Обраного поки немає',
    'providers.noFilterMatches': 'Жоден спеціаліст не відповідає вашим фільтрам.',
    'providers.noProviders': 'Спеціалістів поки немає',
    'providers.overallRating': 'Загальний рейтинг',
    'providers.keepTyping': 'Показано {shown} з {total} — продовжуйте вводити для уточнення.',
    'providers.pickServiceForPrice': 'Оберіть послугу, щоб фільтрувати конкретну процедуру за ціною.',
    'providers.priceOfService': 'Ціна “{service}”.',
    'providers.searchPlaceholder': 'Пошук за ім’ям…',
    'providers.sortMostVisits': 'Найбільше візитів',
    'providers.title': 'Мої спеціалісти',
    'providers.unblock': 'Розблокувати',
    'providers.visits': 'Візитів: {count}',
    'providers.yearsShort': '{count} р.',
    'providers.youRated': 'Ваша оцінка {rating}',

    'providerCustomers.count': 'Клієнтів: {count}',
    'providerCustomers.last': 'останній візит {when}',
    'providerCustomers.noBlockedCustomers': 'Заблокованих клієнтів немає',
    'providerCustomers.noBlockedCustomersBody': 'Заблокуйте клієнта з його профілю, щоб він не міг бронювати нові записи з вами.',
    'providerCustomers.noCustomers': 'Клієнтів поки немає',
    'providerCustomers.noCustomersBody': 'Ваші клієнти з’являться тут після першого запису.',
    'providerCustomers.noSearchMatches': 'Жоден клієнт не відповідає пошуку.',

    'messages.attachImage': 'Прикріпити зображення',
    'messages.careProvider': 'Спеціаліст',
    'messages.customer': 'Клієнт',
    'messages.salonAdministrator': 'Адміністратор салону',
    'messages.emptyCustomer': 'Відкрийте профіль спеціаліста й натисніть Message, щоб почати чат.',
    'messages.emptyProvider': 'Відкрийте профіль клієнта й натисніть Message або дочекайтеся повідомлення.',
    'messages.failedUpload': 'Не вдалося завантажити зображення',
    'messages.loadOlder': 'Завантажити старіші повідомлення',
    'messages.loadingOlder': 'Завантажуємо старіші повідомлення…',
    'messages.noConversations': 'Розмов поки немає',
    'messages.noMessagesYet': 'Повідомлень поки немає. Напишіть першим!',
    'messages.noMessagesPreview': 'Повідомлень поки немає',
    'messages.photo': 'Фото',
    'messages.removeAttachment': 'Прибрати вкладення',
    'messages.reply': 'Відповісти',
    'messages.replyingTo': 'Відповідь для {name}',
    'messages.cancelReply': 'Скасувати відповідь',
    'messages.heart': 'Надіслати сердечко',
    'messages.unheart': 'Прибрати сердечко',
    'messages.messagingUnavailable': 'Повідомлення недоступні для цієї розмови.',
    'messages.title': 'Повідомлення',
    'messages.typeMessage': 'Напишіть повідомлення…',
    'messages.viewProfile': 'Переглянути профіль',

    'finance.customerCaption': 'Орієнтовно, на основі цін у ваших записах. Минулі місяці вже підраховані; поточний місяць розділяє завершене й майбутнє; майбутні місяці оцінюються за вже заброньованими записами.',
    'finance.customerEmpty': 'Коли ви забронюєте або завершите записи з ціною, щомісячні витрати з’являться тут.',
    'finance.customerTitle': 'Ваші витрати',
    'finance.done': 'завершено',
    'finance.explainNumbersAria': 'Що означають ці цифри?',
    'finance.hideBreakdown': 'Сховати деталізацію',
    'finance.hidePastMonths': 'Сховати минулі місяці: {count}',
    'finance.noActivity': 'Немає активності',
    'finance.nothingToShow': 'Поки що нічого показати',
    'finance.pendingConfirmation': 'Включає {amount}, що очікує підтвердження',
    'finance.providerEmpty': 'Коли у вас будуть записи з ціною, щомісячні доходи з’являться тут.',
    'finance.providerCaption': 'Орієнтовно, на основі зафіксованих цін у ваших записах. Минулі місяці вже підраховані; поточний місяць розділяє завершене й майбутнє; майбутні місяці оцінюються за вже заброньованими записами. Скасовані бронювання не враховуються.',
    'finance.providerTitle': 'Ваші доходи',
    'finance.showBreakdown': 'Показати деталізацію',
    'finance.showPastMonths': 'Показати минулі місяці: {count}',
    'finance.statusCurrent': 'Поточний',
    'finance.statusPast': 'Минулий',
    'finance.statusUpcoming': 'Майбутній',
    'finance.thisMonth': 'Цей місяць',
    'finance.upcoming': 'майбутнє',

    'appointmentNew.addons': 'Додатки',
    'appointmentNew.addonsHelp': 'Необов’язкові додатки — натисніть, щоб додати.',
    'appointmentNew.addressOptional': 'Адреса (необов’язково)',
    'appointmentNew.availableDaysHelp': 'Дні з доступними слотами позначені крапкою.',
    'appointmentNew.bookingAtSalon': 'Бронювання в салоні',
    'appointmentNew.confirmAppointment': 'Підтвердити запис',
    'appointmentNew.create': 'Створити',
    'appointmentNew.createError': 'Не вдалося створити запис',
    'appointmentNew.creating': 'Створення…',
    'appointmentNew.careProviderName': 'Ім’я спеціаліста',
    'appointmentNew.dateTime': 'Дата й час',
    'appointmentNew.email': 'Email',
    'appointmentNew.emailPhoneSocialHelp': 'Введіть щось одне: email, телефон або соціальне посилання спеціаліста. Ми спробуємо знайти його.',
    'appointmentNew.enterProviderContact': 'Введіть email, телефон або соціальне посилання спеціаліста',
    'appointmentNew.enterProviderName': 'Введіть ім’я спеціаліста',
    'appointmentNew.enterService': 'Введіть послугу (наприклад, манікюр)',
    'appointmentNew.externalProviderRecord': 'Додайте деталі, щоб зберегти цей візит у вашій історії.',
    'appointmentNew.externalProviderNotify': 'Додайте деталі, і ми повідомимо спеціаліста через {channel}.',
    'appointmentNew.findProvider': 'Знайдіть спеціаліста',
    'appointmentNew.lookupFailed': 'Пошук не вдався',
    'appointmentNew.newProvider': 'Новий спеціаліст',
    'appointmentNew.newProviderIntro': 'Ми не знайшли цього спеціаліста в Bevelure.',
    'appointmentNew.noMatchingProviders': 'Немає відповідних спеціалістів.',
    'appointmentNew.noServices': 'У цього спеціаліста немає доступних послуг',
    'appointmentNew.noSlots': 'Немає доступних слотів на найближчі 30 днів',
    'appointmentNew.or': 'або',
    'appointmentNew.orFindNew': 'або знайдіть нового',
    'appointmentNew.pickDateTime': 'Оберіть дату й час',
    'appointmentNew.pickDateTimeShort': 'Оберіть дату й час',
    'appointmentNew.pickDateWithSlots': 'Оберіть дату з доступними слотами вище.',
    'appointmentNew.phone': 'Телефон',
    'appointmentNew.priceOptional': 'Ціна (необов’язково)',
    'appointmentNew.providerFound': 'Спеціаліста знайдено!',
    'appointmentNew.providerFoundText': 'Ми знайшли {name} у системі. Будь ласка, оберіть послугу.',
    'appointmentNew.providerZone': 'Час показано в часовому поясі спеціаліста — {zone}.',
    'appointmentNew.selectService': 'Оберіть послугу',
    'appointmentNew.selectServiceHelp': 'Оберіть послугу для цього запису',
    'appointmentNew.shareAllDesc': 'Усі відповідні візити.',
    'appointmentNew.shareAllDescService': 'Усі ваші візити {service}.',
    'appointmentNew.shareAllTitle': 'Повна історія',
    'appointmentNew.shareConsent': 'Я розумію, що ділюся цією історією процедур із {provider}: нотатками, продуктами, рекомендаціями та фото результату/процедури. Мої приватні нотатки й фото ніколи не передаються.',
    'appointmentNew.shareDontDesc': 'Залишити історію приватною.',
    'appointmentNew.shareDontTitle': 'Не ділитися',
    'appointmentNew.shareHistory': 'Поділитися історією',
    'appointmentNew.shareHistoryHelp': 'Оберіть, що цей спеціаліст може бачити про ваші минулі візити.',
    'appointmentNew.shareLast5Desc': 'Ваші 5 останніх відповідних візитів.',
    'appointmentNew.shareLast5DescService': 'Ваші 5 останніх візитів {service}.',
    'appointmentNew.shareLast5Title': 'Останні 5 візитів',
    'appointmentNew.shareProcedureHint': 'Інші спеціалісти можуть називати ту саму послугу інакше — додайте ці назви, щоб включити такі візити.',
    'appointmentNew.shareProcedures': 'Які процедури поділитися',
    'appointmentNew.socialMedia': 'Соцмережа',
    'appointmentNew.stepOf': 'Крок {step} з {total}',
    'appointmentNew.thisProvider': 'цим спеціалістом',
    'appointmentNew.title': 'Створити запис',
    'appointmentNew.total': 'Разом',
    'appointmentNew.whereToGo': 'Куди йти',
    'appointmentNew.yourProviders': 'Ваші спеціалісти',

    'providerAppointmentNew.adminBookTeam': 'Як адмін салону ви можете бронювати для своєї команди.',
    'providerAppointmentNew.adminBookTeamOnly': 'Як адмін салону ви бронюєте від імені команди.',
    'providerAppointmentNew.bookFor': 'Забронювати для',
    'providerAppointmentNew.bookingFor': 'Бронювання для',
    'providerAppointmentNew.change': 'Змінити',
    'providerAppointmentNew.checkWorkingHours': 'Перевірте робочі години та вихідні спеціаліста.',
    'providerAppointmentNew.chooseOneService': 'Оберіть одну зі своїх послуг',
    'providerAppointmentNew.confirmDetails': 'Підтвердити деталі',
    'providerAppointmentNew.createAppointment': 'Створити запис',
    'providerAppointmentNew.customer': 'Клієнт',
    'providerAppointmentNew.customerPreselected': 'Клієнта обрано. Перейдіть далі, щоб вибрати послугу.',
    'providerAppointmentNew.enterOneContact': 'Введіть один контакт клієнта — email, телефон або соціальне посилання.',
    'providerAppointmentNew.inviteProviderFirst': 'Запросіть спеціаліста до салону перед створенням запису.',
    'providerAppointmentNew.matchInviteHelp': 'Ми спробуємо знайти акаунт Bevelure. Якщо клієнт ще не зареєстрований, ми надішлемо деталі й запрошення (для соціальних посилань сповіщення немає).',
    'providerAppointmentNew.myself': 'Я',
    'providerAppointmentNew.noActiveServices': 'У вас ще немає активних послуг.',
    'providerAppointmentNew.noBookableProviders': 'У салоні ще немає спеціалістів, яких можна бронювати. Запросіть їх у Моєму салоні.',
    'providerAppointmentNew.noMatchingCustomers': 'Немає відповідних клієнтів.',
    'providerAppointmentNew.noSlots': 'Немає доступних слотів на найближчі 30 днів.',
    'providerAppointmentNew.noTeamMembersMatch': 'Немає відповідних учасників команди.',
    'providerAppointmentNew.orEnterContact': 'або введіть контакт',
    'providerAppointmentNew.pickDateWithSlots': 'Оберіть дату з доступними слотами вище.',
    'providerAppointmentNew.pickTeamMember': 'Оберіть учасника команди, який виконає цей запис.',
    'providerAppointmentNew.pickTeamMemberError': 'Оберіть, для кого з команди цей запис.',
    'providerAppointmentNew.pleaseSelectService': 'Будь ласка, оберіть послугу',
    'providerAppointmentNew.providerFallback': 'Спеціаліст',
    'providerAppointmentNew.selected': 'Обрано',
    'providerAppointmentNew.teamMemberFallback': 'учасник команди',
    'providerAppointmentNew.title': 'Новий запис',
    'providerAppointmentNew.yourCustomers': 'Ваші клієнти',

    'providerProfile.about': 'Про спеціаліста',
    'providerProfile.addFavorite': 'Додати в обране',
    'providerProfile.addons': 'Додатки',
    'providerProfile.blockProvider': 'Заблокувати цього спеціаліста',
    'providerProfile.bookAppointment': 'Забронювати запис',
    'providerProfile.certificates': 'Сертифікати',
    'providerProfile.fromClients': 'від клієнтів',
    'providerProfile.message': 'Повідомлення',
    'providerProfile.noServicesListed': 'Послуг поки немає',
    'providerProfile.pastAppointments': 'Минулі записи',
    'providerProfile.rating': 'Рейтинг',
    'providerProfile.removeConfirm': 'Видалити',
    'providerProfile.removeDescription': 'Він втратить доступ до цього салону. Минулі записи залишаться в історії салону. Нові бронювання більше не будуть прив’язані до цього салону.',
    'providerProfile.removeFavorite': 'Прибрати з обраного',
    'providerProfile.removeFromSalon': 'Видалити із салону',
    'providerProfile.removeTitle': 'Видалити {name} із салону?',
    'providerProfile.services': 'Послуги',
    'providerProfile.title': 'Профіль спеціаліста',
    'providerProfile.unblockProvider': 'Розблокувати цього спеціаліста',
    'providerProfile.unblocking': 'Розблокування…',
    'providerProfile.unavailableTitle': 'Профіль недоступний',
    'providerProfile.unavailableBody': 'Цей профіль більше недоступний. Минулі записи та нотатки залишаються у вашій історії.',
    'providerProfile.visits': 'Візити',
    'providerProfile.worksAt': 'Працює в',
    'providerProfile.yearsExperience': 'Досвід: {count} р.',

    'customerProfile.allergies': 'Алергії',
    'customerProfile.allergiesNoDetails': 'Є алергії (деталі не вказані)',
    'customerProfile.blockCustomer': 'Заблокувати цього клієнта',
    'customerProfile.book': 'Забронювати',
    'customerProfile.careRoutine': 'Доглядова рутина',
    'customerProfile.goBack': 'Назад',
    'customerProfile.noKnownAllergies': 'Відомих алергій немає',
    'customerProfile.notFound': 'Клієнта не знайдено або немає спільних записів.',
    'customerProfile.unavailableTitle': 'Профіль недоступний',
    'customerProfile.unavailableBody': 'Цей профіль більше недоступний або ви більше не маєте доступу до нього. Минулі записи залишаються у вашій історії.',
    'customerProfile.ratings': 'Оцінок: {count}',
    'customerProfile.title': 'Клієнт',
    'customerProfile.unblockCustomer': 'Розблокувати цього клієнта',

    'externalCancel.alreadyCancelled': 'Цей запис уже скасовано.',
    'externalCancel.alreadyCompleted': 'Цей запис уже завершено — минулий візит не можна скасувати.',
    'externalCancel.cancelledBody': 'Ми повідомили іншу сторону. Слот знову доступний для інших.',
    'externalCancel.cancelledTitle': 'Запис скасовано',
    'externalCancel.cancelling': 'Скасування…',
    'externalCancel.confirmHint': 'Передумали? Просто закрийте сторінку — нічого не станеться, доки ви не натиснете кнопку вище.',
    'externalCancel.errorInvalid': 'Посилання для скасування недійсне або прострочене.',
    'externalCancel.errorMissing': 'У посиланні для скасування немає token.',
    'externalCancel.errorRetry': 'Не вдалося скасувати — спробуйте ще раз або зв’яжіться з іншою стороною напряму.',
    'externalCancel.intro': 'Ви збираєтеся скасувати:',
    'externalCancel.titlePrefix': 'Скасувати',
    'externalCancel.titleEmphasis': 'запис',

    'externalFill.addDetails': 'Додайте деталі процедури для цього запису.',
    'externalFill.errorInvalidTitle': 'Посилання недійсне',
    'externalFill.errorInvalidBody': 'Це посилання недійсне або прострочене. Попросіть клієнта надіслати його повторно.',
    'externalFill.lastUpdated': 'Останнє оновлення {date}',
    'externalFill.productsPlaceholder': 'Бренди, кольори, матеріали…',
    'externalFill.saveError': 'Не вдалося зберегти. Спробуйте ще раз.',

    'share.allergiesOnFile': 'Алергії вказані',
    'share.customerAllergyFallback': 'Клієнт повідомляє про алергії (без конкретних нотаток).',
    'share.expired': 'Це посилання прострочене або більше не дійсне.',
    'share.expires': 'Посилання діє до {date}',
    'share.failedLoad': 'Не вдалося завантажити спільну історію',
    'share.footer': 'Поділено через {brand}. Клієнт може відкликати це посилання будь-коли.',
    'share.fullHistory': 'Повна історія',
    'share.last5Visits': 'Останні 5 візитів',
    'share.linkUnavailable': 'Посилання недоступне',
    'share.noKnownAllergies': 'Відомих алергій не вказано.',
    'share.noSharedHistory': 'Не вдалося завантажити цю спільну історію.',
    'share.readOnly': 'Лише перегляд',
    'share.sharedTreatmentHistory': 'Спільна історія процедур',

    'appointmentDetail.add': 'Додати',
    'appointmentDetail.addPhoto': 'Додати фото',
    'appointmentDetail.addSelected': 'Додати ({count})',
    'appointmentDetail.addToPortfolio': 'Додати в портфоліо',
    'appointmentDetail.addToPortfolioBody': 'Оберіть фото, які будуть показані у вашому публічному профілі.',
    'appointmentDetail.addons': 'Додатки',
    'appointmentDetail.allergiesOnFile': 'Алергії вказані',
    'appointmentDetail.allergyFallback': 'Клієнт повідомляє про алергії (без конкретних нотаток).',
    'appointmentDetail.atSalon': 'у {salon}',
    'appointmentDetail.bookingRequestBody': 'Підтвердіть, щоб закріпити слот, або відхиліть, щоб клієнт міг обрати інший час.',
    'appointmentDetail.bookingRequestTitle': 'Запит на бронювання — очікує на вас',
    'appointmentDetail.byProvider': 'у {provider}',
    'appointmentDetail.cancelAppointment': 'Скасувати запис',
    'appointmentDetail.cancelAppointmentMessage': 'Слот звільниться, а інша сторона отримає сповіщення.',
    'appointmentDetail.cancelAppointmentTitle': 'Скасувати запис?',
    'appointmentDetail.cancelRequest': 'Скасувати запит',
    'appointmentDetail.cancelRequestMessage': 'Запит буде відкликано.',
    'appointmentDetail.cancelRequestTitle': 'Скасувати запит на бронювання?',
    'appointmentDetail.captionOptional': 'Підпис (необов’язково)',
    'appointmentDetail.category': 'Категорія',
    'appointmentDetail.categoryPlaceholder': 'наприклад, манікюр, Botox',
    'appointmentDetail.confirm': 'Підтвердити',
    'appointmentDetail.confirming': 'Підтвердження…',
    'appointmentDetail.customerAllergyInfo': 'Інформація про алергії клієнта',
    'appointmentDetail.customerAllergyInfoPrefix': 'Перегляньте нотатки клієнта про алергії в',
    'appointmentDetail.decline': 'Відхилити',
    'appointmentDetail.declineBookingMessage': 'Слот знову стане доступним.',
    'appointmentDetail.declineBookingTitle': 'Відхилити бронювання?',
    'appointmentDetail.declining': 'Відхилення…',
    'appointmentDetail.defaultProvider': 'спеціаліста',
    'appointmentDetail.detailsSaved': 'Деталі збережено',
    'appointmentDetail.failedConfirm': 'Не вдалося підтвердити',
    'appointmentDetail.failedDecline': 'Не вдалося відхилити',
    'appointmentDetail.fullHistory': 'Повна історія',
    'appointmentDetail.inProgressNow': 'Триває зараз',
    'appointmentDetail.keepIt': 'Залишити',
    'appointmentDetail.last5Visits': 'Останні 5 візитів',
    'appointmentDetail.minutes': '{count} хв',
    'appointmentDetail.myPersonalNotes': 'Мої особисті нотатки',
    'appointmentDetail.myPersonalNotesHelp': 'Це бачите лише ви. Зручно записати відчуття, реакції тощо.',
    'appointmentDetail.myPersonalNotesPlaceholder': 'Ваші приватні нотатки про цей запис…',
    'appointmentDetail.noCompletedShared': 'Завершених записів цього типу поки немає.',
    'appointmentDetail.noPhotosYet': 'Фото поки немає.',
    'appointmentDetail.noProcedureDetails': 'Деталі процедури не записані.',
    'appointmentDetail.notesSaved': 'Нотатки збережено',
    'appointmentDetail.photos': 'Фото',
    'appointmentDetail.private': 'Приватне',
    'appointmentDetail.privatePhotosHelp': 'їх бачите лише ви',
    'appointmentDetail.privatePortfolioHelp': 'Збережено в портфоліо, але приховано, доки ви не увімкнете показ.',
    'appointmentDetail.procedureNotes': 'Нотатки процедури',
    'appointmentDetail.procedureNotesPlaceholder': 'Що було зроблено під час процедури…',
    'appointmentDetail.procedurePhotosHelp': 'чеки, препарати, що наносилось',
    'appointmentDetail.productsUsed': 'Використані продукти',
    'appointmentDetail.providerOnlyNotes': 'Нотатки лише для спеціаліста',
    'appointmentDetail.providerOnlyNotesHelp': 'Видно лише спеціалістам, які працюють із цим записом, не клієнту.',
    'appointmentDetail.providerOnlyNotesPlaceholder': 'Внутрішній контекст запису, особливі зауваження, подальший супровід…',
    'appointmentDetail.public': 'Публічне',
    'appointmentDetail.publicPortfolioHelp': 'Видно клієнтам у вашому публічному профілі.',
    'appointmentDetail.rateCustomer': 'Оцінити клієнта',
    'appointmentDetail.rateProvider': 'Оцінити спеціаліста',
    'appointmentDetail.readOnly': 'Лише перегляд',
    'appointmentDetail.recommendationsPlaceholder': 'Рекомендації / догляд після візиту',
    'appointmentDetail.recordCount': 'Записів: {count}',
    'appointmentDetail.removePhoto': 'Видалити',
    'appointmentDetail.removePhotoMessage': 'Це фото буде видалено назавжди.',
    'appointmentDetail.removePhotoTitle': 'Видалити фото?',
    'appointmentDetail.resultPhotos': 'Фото результату',
    'appointmentDetail.saveDetails': 'Зберегти деталі',
    'appointmentDetail.saveNotes': 'Зберегти нотатки',
    'appointmentDetail.sharedHistoryTitle': 'Історія від {name}',
    'appointmentDetail.shortDescription': 'Короткий опис',
    'appointmentDetail.tapStar': 'Натисніть зірку, щоб надіслати оцінку.',
    'appointmentDetail.theirProfile': 'профілі клієнта',
    'appointmentDetail.title': 'Запис',
    'appointmentDetail.total': 'Разом',
    'appointmentDetail.waitingForProviderBody': 'Ви отримаєте сповіщення, коли запит приймуть або відхилять. Ви можете скасувати цей запит будь-коли.',
    'appointmentDetail.waitingForProviderTitle': 'Очікуємо підтвердження від {provider}',
    'appointmentDetail.yourTime': '{time} за вашим часом ({zone})',

    'auth.alreadyHaveAccount': 'Вже маєте акаунт?',
    'auth.backToBevelure': 'Назад до Bevelure',
    'auth.backToLogin': 'Назад до входу',
    'auth.checkInbox': 'Перевірте пошту',
    'auth.confirmEmail': 'Підтвердити *email*',
    'auth.confirmEmailLoading': 'Підтверджуємо email…',
    'auth.createAccount': 'Створити акаунт',
    'auth.createAccountTitle': 'Створити *акаунт*',
    'auth.email': 'Email',
    'auth.emailConfirmed': 'Email підтверджено',
    'auth.emailConfirmedBody': 'Дякуємо — ваш email підтверджено. Усе готово.',
    'auth.enterEmailPassword': 'Введіть email і пароль',
    'auth.forgotPassword': 'Забули *пароль*',
    'auth.forgotPasswordCta': 'Забули пароль?',
    'auth.forgotPasswordSubtitle': 'Ми надішлемо посилання, щоб створити новий.',
    'auth.goToLogin': 'Перейти до входу',
    'auth.invalidLogin': 'Невірний email або пароль',
    'auth.login': 'Увійти',
    'auth.loginButton': 'Увійти',
    'auth.loginSubtitle': 'Увійдіть, щоб продовжити свій beauty-шлях',
    'auth.loggingIn': 'Вхід…',
    'auth.newPassword': 'Новий пароль',
    'auth.newToBevelure': 'Вперше в Bevelure?',
    'auth.password': 'Пароль',
    'auth.passwordMismatch': 'Паролі не збігаються',
    'auth.passwordMin': 'Пароль має містити щонайменше 8 символів',
    'auth.passwordUpdated': 'Пароль оновлено',
    'auth.passwordUpdatedBody': 'Увійдіть із новим паролем. На інших пристроях також потрібно буде увійти знову.',
    'auth.rememberNow': 'Згадали пароль?',
    'auth.repeatPassword': 'Повторіть новий пароль',
    'auth.resetInvalid': 'Посилання для скидання недійсне або прострочене',
    'auth.resetLinkMissing': 'Посилання для скидання відсутнє — запросіть нове.',
    'auth.resetPassword': 'Скинути *пароль*',
    'auth.resetPasswordSubtitle': 'Оберіть надійний пароль — щонайменше 8 символів.',
    'auth.sendResetLink': 'Надіслати посилання',
    'auth.sending': 'Надсилання…',
    'auth.sentResetBody': 'Якщо акаунт для {email} існує, ми щойно надіслали посилання для скидання. Воно діє 1 годину.',
    'auth.signupChooseType': 'Оберіть тип акаунта, щоб почати',
    'auth.signupCustomerBody': 'Відстежуйте beauty-записи та процедури',
    'auth.signupCustomerTitle': 'Я клієнт',
    'auth.signupProviderBody': 'Керуйте клієнтами та записами',
    'auth.signupProviderTitle': 'Я спеціаліст',
    'auth.signupSalonBody': 'Зареєструйте бізнес і запросіть команду',
    'auth.signupSalonTitle': 'Ми салон / студія',
    'signup.acceptLegal': 'Підтвердьте, що вам уже виповнилося 18 років, і прийміть Умови та Політику конфіденційності.',
    'signup.aboutYou': 'Про вас',
    'signup.aboutYouSubtitle': 'Базова особиста інформація',
    'signup.accountSubtitle': 'Як ви входитимете в акаунт',
    'signup.allergies': 'Алергії',
    'signup.allergiesQuestion': 'У вас є алергічні реакції?',
    'signup.allergiesSubtitle': 'Це допоможе спеціалістам безпечніше піклуватися про вас. Ви зможете оновити це в профілі.',
    'signup.allergyPlaceholder': 'Опишіть ваші алергії...',
    'signup.birthDatePlaceholder': 'Оберіть дату народження',
    'signup.certificates': 'Сертифікати',
    'signup.certificatesFileHint': 'PDF, JPG, PNG — до 10 МБ кожен',
    'signup.certificatesLater': 'Нічого страшного — ви зможете завантажити їх пізніше з профілю.',
    'signup.certificatesQuestion': 'У вас є сертифікати, якими можна поділитися?',
    'signup.certificatesRequired': 'Будь ласка, дайте відповідь на питання про сертифікати.',
    'signup.certificatesSubtitle': 'Покажіть клієнтам вашу професійну кваліфікацію',
    'signup.certificatesUpload': 'Натисніть, щоб завантажити сертифікати',
    'signup.completeRegistration': 'Завершити реєстрацію',
    'signup.confirmPassword': 'Підтвердіть пароль',
    'signup.contact': 'Контакти',
    'signup.createSalon': 'Створити салон',
    'signup.creatingAccount': 'Створення акаунта…',
    'signup.creatingSalon': 'Створення салону…',
    'signup.customerTitle': 'Реєстрація *клієнта*',
    'signup.dateOfBirth': 'Дата народження',
    'signup.firstName': 'Ім’я',
    'signup.invalidEmail': 'Введіть коректну email-адресу.',
    'signup.invalidSalonEmail': 'Введіть коректний email салону або залиште поле порожнім.',
    'signup.languagesYouSpeak': 'Мови, якими ви говорите',
    'signup.lastName': 'Прізвище',
    'signup.legalPrefix': 'Я підтверджую, що мені виповнилося 18 років, і погоджуюся з',
    'signup.missingCity': 'Будь ласка, вкажіть місто.',
    'signup.missingCountry': 'Будь ласка, вкажіть країну.',
    'signup.missingAddress': 'Будь ласка, вкажіть адресу.',
    'signup.missingDateOfBirth': 'Будь ласка, вкажіть дату народження.',
    'signup.minimumAge': 'Щоб створити акаунт, вам має бути щонайменше 18 років.',
    'signup.missingEmail': 'Будь ласка, вкажіть email.',
    'signup.missingFirstName': 'Будь ласка, вкажіть ім’я.',
    'signup.missingLastName': 'Будь ласка, вкажіть прізвище.',
    'signup.missingPassword': 'Будь ласка, введіть пароль.',
    'signup.missingServiceType': 'Будь ласка, вкажіть тип послуги.',
    'signup.missingSex': 'Будь ласка, вкажіть стать.',
    'signup.missingYearsInService': 'Будь ласка, вкажіть роки досвіду.',
    'signup.passwordPlaceholder': 'Мінімум 8 символів',
    'signup.profession': 'Професія',
    'signup.professionSubtitle': 'Що ви пропонуєте',
    'signup.providerTitle': 'Реєстрація *спеціаліста*',
    'signup.repeatPasswordPlaceholder': 'Повторіть пароль',
    'signup.required': 'Обов’язково',
    'signup.salonAdminSubtitle': 'Акаунт адміністратора салону',
    'signup.salonContactSubtitle': 'Необов’язково — показується в профілі салону',
    'signup.salonMissingName': 'Будь ласка, дайте салону назву.',
    'signup.salonNamePlaceholder': 'напр., Velvet Beauty Studio',
    'signup.salonTitle': 'Реєстрація *салону*',
    'signup.selectOption': 'Будь ласка, оберіть варіант.',
    'signup.serviceTypePlaceholder': 'напр., майстер манікюру, косметолог',
    'signup.sexRequired': 'Будь ласка, оберіть стать',
    'signup.signupFailed': 'Не вдалося зареєструватися',
    'signup.stepOf2': 'Крок {step} з 2',
    'signup.whereYouAre': 'Де ви',
    'signup.whereYouAreSubtitle': 'Використовується, щоб знаходити локальних спеціалістів',
    'signup.whereYouWork': 'Де ви працюєте',
    'signup.whereYouWorkSubtitle': 'Допомагає клієнтам знайти вас',
    'signup.yearsInService': 'Роки досвіду',
    'signup.yearsPlaceholder': 'напр., 5',
    'signup.yourSalon': 'Ваш салон',
    'signup.yourSalonSubtitle': 'Це бачитимуть клієнти та ваша команда',

    'landing.nav.platform': 'Платформа',
    'landing.nav.features': 'Можливості',
    'landing.nav.story': 'Історія',
    'landing.nav.trust': 'Довіра',
    'landing.nav.startNow': 'Почати',
    'landing.nav.openMenu': 'Відкрити меню',
    'landing.cta.startJourney': 'Почати свій шлях',
    'landing.stores.appStoreEyebrow': 'Завантажити в',
    'landing.stores.appStoreName': 'App Store',
    'landing.stores.googlePlayEyebrow': 'Завантажити з',
    'landing.stores.googlePlayName': 'Google Play',
    'landing.appBanner.subtitle': 'Відкрити в застосунку Bevelure',
    'landing.appBanner.action': 'ВІДКРИТИ',
    'landing.appBanner.close': 'Закрити банер застосунку',
    'landing.panel.title': 'Beauty-історія',
    'landing.panel.visits': 'візити',
    'landing.panel.providers': 'спеціалісти',
    'landing.panel.rating': 'рейтинг',
    'landing.panel.row1Label': 'Алергії та догляд',
    'landing.panel.row1Meta': 'Надіслано ще до візиту',
    'landing.panel.row2Label': 'Процедура Hydrafacial',
    'landing.panel.row2Meta': 'Завершено · фото додано',
    'landing.panel.row3Label': 'Повідомлення від Майї',
    'landing.panel.row3Meta': 'Догляд після Hydrafacial · 1 год тому',
    'landing.hero.eyebrow': 'Ваша beauty-історія, нарешті ваша',
    'landing.hero.body': 'Один елегантний приватний запис, який належить вам і переходить із вами від спеціаліста до спеціаліста — ваша історія, якою ви легко поділитеся з наступним спеціалістом.',
    'landing.hero.tile1Title': 'Ваше',
    'landing.hero.tile1Body': 'запис, що подорожує з вами',
    'landing.hero.tile2Title': 'Готово',
    'landing.hero.tile2Body': 'контекст перед кожним клієнтом',
    'landing.hero.tile3Title': 'Синхронно',
    'landing.hero.tile3Body': 'уся команда, один календар',
    'landing.trustBar.item1': 'Історія, що належить вам',
    'landing.trustBar.item2': 'Контекст перед кожним клієнтом',
    'landing.trustBar.item3': 'Один календар для всієї команди',
    'landing.three.eyebrow': 'Один запис, три сторони',
    'landing.three.title': 'Той самий запис. Три погляди.',
    'landing.three.body': 'Beauty-догляд поєднує людей, які рідко бачать одні й ті самі нотатки. Bevelure дає єдине джерело правди — і кожен бачить саме те, що важливо для нього.',
    'landing.three.side1Tag': 'Для вас',
    'landing.three.side1Title': 'Вся ваша історія — зберігайте та діліться нею.',
    'landing.three.side1Desc': 'Кожен візит, продукт і фото в одному місці, яке завжди з вами.',
    'landing.three.side2Tag': 'Для вашого спеціаліста',
    'landing.three.side2Title': 'Усе важливе — ще до початку візиту.',
    'landing.three.side2Desc': 'Контекст, яким ви вирішили поділитися: алергії, попередні процедури, що спрацювало.',
    'landing.three.side3Tag': 'Для салону',
    'landing.three.side3Title': 'Кожен запис і кожен член команди — в одному місці, без метушні.',
    'landing.three.side3Desc': 'Команда, календар і бронювання під одним дахом — скоординовано, без хаосу.',
    'landing.audiences.eyebrow': 'Одна система для кожної сторони догляду',
    'landing.audiences.title': 'Що насправді отримує кожна сторона.',
    'landing.audiences.clientTag': 'Для клієнтів',
    'landing.audiences.clientTitle': 'Ваша історія, нарешті ваша.',
    'landing.audiences.clientLead': 'Кожна процедура в одному місці, яке подорожує з вами. Більше не треба починати з нуля з новим спеціалістом або згадувати, що спрацювало.',
    'landing.audiences.clientPoint1': 'Повний запис: візити, продукти, фото, ціни.',
    'landing.audiences.clientPoint2': 'Ваші алергії та чутливість шкіри доступні кожному спеціалісту.',
    'landing.audiences.clientPoint3': 'Поділіться останніми п’ятьма візитами або типом процедури — і відкличте доступ будь-коли.',
    'landing.audiences.clientPoint4': 'Дивіться, скільки ви реально витрачаєте на себе щомісяця.',
    'landing.audiences.clientCta': 'Почати свою історію',
    'landing.audiences.providerTag': 'Для спеціалістів',
    'landing.audiences.providerTitle': 'Знайте клієнта ще до початку прийому.',
    'landing.audiences.providerLead': 'Відкрийте запис — і контекст уже там: що робили минулого разу, на що була реакція, чим клієнт користується вдома. Менше здогадок, безпечніша робота, справжня клієнтська база.',
    'landing.audiences.providerPoint1': 'Переглядайте історію, якою клієнт вирішив поділитися, ще до його приходу.',
    'landing.audiences.providerPoint2': 'Ваші нотатки, продукти та фото результатів зберігаються між візитами.',
    'landing.audiences.providerPoint3': 'Один раз налаштуйте послуги, додаткові послуги, години роботи та ціни.',
    'landing.audiences.providerPoint4': 'Пишіть клієнтам, не розкриваючи свій номер.',
    'landing.audiences.providerCta': 'Відкрити акаунт спеціаліста',
    'landing.audiences.salonTag': 'Для салонів',
    'landing.audiences.salonTitle': 'Увесь салон синхронізовано.',
    'landing.audiences.salonLead': 'Об’єднайте команду, календар і кожне бронювання під одним дахом — з потрібним контролем і без втручання в роботу спеціалістів.',
    'landing.audiences.salonPoint1': 'Запрошуйте й керуйте командою за хвилини.',
    'landing.audiences.salonPoint2': 'Переглядайте всі бронювання салону за всіма спеціалістами.',
    'landing.audiences.salonPoint3': 'Координуйте без зайвого контролю — адміністратори не змінюють послуги спеціаліста.',
    'landing.audiences.salonPoint4': 'Без подвійних бронювань і плутанини між майстрами.',
    'landing.audiences.salonCta': 'Зареєструвати салон',
    'landing.features.eyebrow': 'Усе в одному місці',
    'landing.features.title': 'Те, що ви хотіли б мати записаним із самого початку.',
    'landing.features.body': 'Ваша історія — головна. Але Bevelure також тихо бере на себе бронювання, розмови, ціни, фото та візити, додані поза платформою.',
    'landing.features.item1Title': 'Запис, що справді ваш',
    'landing.features.item1Desc': 'Кожен візит, продукт, нотатка і фото результату в одній історії, яка рухається разом із вами.',
    'landing.features.item2Title': 'Бронювання без зайвого',
    'landing.features.item2Desc': 'Переглядайте реальні ціни й додаткові послуги, обирайте слот. Або додайте візит у салон, якого ще немає в Bevelure.',
    'landing.features.item3Title': 'Ви вирішуєте, хто що бачить',
    'landing.features.item3Desc': 'Поділіться останніми п’ятьма візитами або типом процедури з новим спеціалістом — і відкличте доступ коли захочете.',
    'landing.features.item4Title': 'До, після і тільки для вас',
    'landing.features.item4Desc': 'Фото результатів, фото процедури для спеціаліста і приватні фото лише для вас — нічого не змішується.',
    'landing.features.item5Title': 'Де б ви не бронювали',
    'landing.features.item5Desc': 'Спеціаліст у Маямі, ви в Берліні — час салону і ваш час поруч. Будь-яке місто, будь-яка валюта.',
    'landing.features.item6Title': 'Обговоріть деталі',
    'landing.features.item6Desc': 'Пишіть до і після візиту: уточнити деталь, запитати про догляд, надіслати фото — без обміну телефоном.',
    'landing.features.item7Title': 'Чесні ціни, що не змінюються',
    'landing.features.item7Desc': 'Реальні ціни фіксуються для кожного візиту у валюті спеціаліста.',
    'landing.features.item8Title': 'Приватні оцінки з двох сторін',
    'landing.features.item8Desc': 'Оцінюйте візит для себе — спеціалісти також можуть. Без публічного рейтингу, лише чесна нотатка.',
    'landing.founder.eyebrow': 'Історія засновниці',
    'landing.founder.title': 'Чому я вірю в Bevelure.',
    'landing.founder.p1': 'Я створила Bevelure, бо ніколи не могла точно згадати власні процедури: що робили, коли і які продукти використовували. Щоразу з новим спеціалістом питання були ті самі: *що робили минулого разу, які продукти були, чи була алергічна реакція*. Я намагалася згадати, але не могла дати чітку відповідь.',
    'landing.founder.p2': 'Зазвичай виходило щось на кшталт: *“Я пам’ятаю результат, але не точну назву процедури, продукти або чи щось подразнило шкіру.”* Це не дуже допомагало. Я хотіла мати все записаним: точні продукти й деталі кожного візиту.',
    'landing.founder.p3': 'Саме це і є Bevelure: ви зберігаєте все в одному місці й можете поділитися з будь-яким спеціалістом, якого оберете.',
    'landing.founder.p4': 'Мої beauty-записи теж були хаотично розкидані по календарю. Доводилося довго гортати, щоб згадати, що і коли я робила та коли час записуватися знову. Тепер усе в одному місці: ваша beauty-історія.',
    'landing.founder.p5': 'Я багато подорожую, тому часто записуюся до нових спеціалістів, щоб не переривати догляд. І завжди одна історія: на процедурі для обличчя мене питають, *який домашній догляд я використовую і які інгредієнти в кремах*. З Bevelure про це не треба думати: завантажте фото своєї домашньої рутини з етикетками й інгредієнтами та поділіться, коли потрібно.',
    'landing.founder.p6': 'Я думаю, багато людей потрапляють у такі самі ситуації. Я справді хочу їм допомогти. Саме тому я створила Bevelure.',
    'landing.founder.quote': '“Для кожної beauty-історії, яка заслуговує бути збереженою. З турботою.”',
    'landing.founder.signature': 'Євгенія Андрєєва, засновниця й CEO',
    'landing.trust.eyebrow': 'Рівень довіри',
    'landing.trust.title': 'Створено для того, що найважливіше — і що найважче відновити.',
    'landing.trust.body': 'Чутлива інформація, реальні записи, стосунки на роки. Зовні все здається простим, бо продумані межі непомітно тримають усе разом.',
    'landing.trust.item1Title': 'Приватність у фундаменті',
    'landing.trust.item1Desc': 'Ваші приватні нотатки, внутрішні нотатки спеціаліста і те, чим ви ділитеся, зберігаються окремо.',
    'landing.trust.item2Title': 'Просто залишайтеся на зв’язку',
    'landing.trust.item2Desc': 'Чат, підтвердження та делікатні нагадування допомагають усім залишатися в курсі.',
    'landing.trust.item3Title': 'Без публічного рейтингу',
    'landing.trust.item3Desc': 'Оцінюйте візит приватно, для власної пам’яті. Beauty-догляд не має перетворюватися на публічний рейтинг.',
    'landing.final.title': 'Ваша beauty-історія, нарешті в одному місці.',
    'landing.final.body': 'Ви бронюєте, виконуєте роботу чи керуєте салоном — усе живе в одному красивому приватному записі.',
    'landing.footer.tagline': 'Ваша beauty-історія в одному місці.',
    'landing.footer.contact': 'Контакти',
    'landing.footer.site': 'Сайт',

    'block.title': 'Заблокувати {name}?',
    'block.body': 'Ця людина не зможе бронювати або приймати нові записи з вами. Минулі записи й оцінки залишаться видимими для обох сторін.',
    'block.reasonLabel': 'Причина (бачите лише ви)',
    'block.reasonPlaceholder': 'Необов’язково — корисно, якщо ви переглядатимете блокування пізніше',
    'block.reportUser': 'Також поскаржитися на користувача',
    'block.reportHelp': 'Акаунт і вибрана причина будуть надіслані Bevelure на перевірку. Інший користувач не побачить вашу скаргу.',
    'block.reportReasonLabel': 'Причина скарги',
    'block.reportReasonPlaceholder': 'Виберіть причину',
    'block.reportReasonRequired': 'Виберіть причину перед надсиланням скарги.',
    'block.reportReasonSpam': 'Спам або шахрайство',
    'block.reportReasonHarassment': 'Переслідування або погрози',
    'block.reportReasonContent': 'Неприйнятний контент',
    'block.reportReasonSafety': 'Небезпечна поведінка',
    'block.reportReasonImpersonation': 'Видавання себе за іншу особу',
    'block.reportReasonOther': 'Інше',
    'block.blockAndReport': 'Заблокувати й поскаржитися',
    'block.cancelFutureOne': 'Також скасувати {count} майбутній запис між вами.',
    'block.cancelFutureMany': 'Також скасувати {count} майбутніх записів між вами.',
    'block.keepFutureHint': 'Якщо вимкнути це, записи залишаться в обох календарях — блокування лише зупиняє нові бронювання.',
    'block.failed': 'Не вдалося заблокувати',
    'block.blocking': 'Блокування…',
    'block.block': 'Заблокувати',

    'shareModal.titlePrefix': 'Поділитися',
    'shareModal.titleEmphasis': 'історією',
    'shareModal.subtitle': 'Створіть посилання тільки для перегляду для спеціаліста.',
    'shareModal.procedureTypes': 'Типи процедур',
    'shareModal.procedurePlaceholder': 'напр. чоловіча стрижка, манікюр…',
    'shareModal.procedureHelp': 'Оберіть зі своїх процедур або введіть власну. Видимими будуть лише завершені записи, що збігаються.',
    'shareModal.whatToShare': 'Що поширити',
    'shareModal.recipientEmail': 'Email отримувача',
    'shareModal.optional': 'необов’язково',
    'shareModal.emailHelp': 'Якщо вказано, посилання буде надіслано email. Інакше буде створено QR-код / посилання.',
    'shareModal.consent': 'Я розумію, що будь-хто з цим посиланням може переглянути історію процедур, якою я ділюся: нотатки, продукти, рекомендації та фото результату/процедури. Мої приватні нотатки й фото ніколи не поширюються.',
    'shareModal.createError': 'Не вдалося створити посилання. Спробуйте ще раз.',
    'shareModal.generateLink': 'Створити посилання',
    'shareModal.emailIt': 'Надіслати email',
    'shareModal.scanHint': 'Відскануйте камерою телефону, щоб відкрити поширену історію.',
    'shareModal.link': 'Посилання',
    'shareModal.copyLink': 'Копіювати посилання',
    'shareModal.procedure': 'Процедура',
    'shareModal.mode': 'Режим',
    'shareModal.expires': 'Діє до',
    'shareModal.sentTo': 'Надіслано',
    'shareModal.generateAnother': 'Створити ще одне',

    'contactSetup.title': 'Зробіть себе легше доступним для пошуку',
    'contactSetup.bodyPrefix': 'Додайте',
    'contactSetup.socialLink': 'соціальне посилання',
    'contactSetup.bodySuffix': 'щоб люди могли знаходити вас не лише за email.',
    'contactSetup.addContactInfo': 'Додати контакти',

    'errorBoundary.titlePrefix': 'Щось пішло',
    'errorBoundary.titleEmphasis': 'не так',
    'errorBoundary.body': 'Під час відображення сторінки сталася неочікувана помилка. Оновіть сторінку, щоб спробувати ще раз — якщо це повторюється, повідомте нам.',
    'errorBoundary.refresh': 'Оновити сторінку',
    'errorBoundary.dismiss': 'Закрити й продовжити',

    'confirm.defaultTitle': 'Ви впевнені?',
    'impact.futureBookingOne': '{count} майбутнє бронювання буде зачеплено.',
    'impact.futureBookingMany': '{count} майбутніх бронювань буде зачеплено.',
    'impact.cancelAffectedOn': 'Усі вони будуть скасовані, а клієнтів буде повідомлено.',
    'impact.cancelAffectedOff': 'Залиште вимкненим, щоб вони лишилися за розкладом — блокуються лише нові бронювання.',
    'impact.actionFailed': 'Дія не вдалася',
    'impact.working': 'Обробка…',

    'desktopHome.settings': 'Налаштування головної',
    'desktopHome.layoutTitle': 'Макет головної',
    'desktopHome.layout1': 'Розділено — Майбутні й Нещодавні біля календаря',
    'desktopHome.layout2': 'Компактне привітання зі списками',
    'desktopHome.layout3': 'Колонка статистики · календар · списки нижче',
    'desktopHome.layout4': 'Списки зі скролом на висоту календаря',
    'auth.tryAgain': 'Щось пішло не так — спробуйте ще раз',
    'auth.updatePassword': 'Оновити пароль',
    'auth.updating': 'Оновлення…',
    'auth.verificationInvalid': 'Посилання для підтвердження недійсне або прострочене.',
    'auth.verificationMissing': 'У посиланні для підтвердження немає токена.',
    'auth.verifyErrorTitle': 'Не вдалося підтвердити',
    'auth.verifyResendHint': 'Увійдіть в акаунт і натисніть Надіслати ще раз у банері підтвердження.',
    'auth.welcomeBack': 'Раді бачити *знову*',
    'notif.providerRequest.title': 'Нова заявка на запис',
    'notif.providerRequest.body': 'Клієнт хоче записатися. Підтвердьте або відхиліть.',
    'notif.providerScheduled.title': 'Новий запис',
    'notif.providerScheduled.body': 'Для вас призначено новий запис.',
    'notif.customerCreated.title': 'Запис призначено',
    'notif.customerCreated.body': '{provider} записав(ла) вас на {service}.',
    'notif.customerConfirmed.title': 'Запис підтверджено',
    'notif.customerConfirmed.body': '{provider} підтвердив(ла) ваш запис на {service}.',
    'notif.customerRejected.title': 'Заявку відхилено',
    'notif.customerRejected.body': '{provider} не зміг(ла) прийняти ваш запис на {service}. Оберіть інший час.',
    'notif.cancelled.title': 'Запис скасовано',
    'notif.cancelled.body': 'Ваш запис на {service} з {other} скасовано.',
    'notif.customerExpired.title': 'Заявка протермінована',
    'notif.customerExpired.body': 'Ваша заявка на {service} протермінована — не підтверджена вчасно. Оберіть інший час.',
    'notif.detailsUpdated.title': 'Деталі процедури оновлено',
    'notif.detailsUpdated.body': '{provider} додав(ла) нотатки або фото до вашого запису на {service}.',
    'notif.reminderCustomer.title': 'Запис через 48 годин',
    'notif.reminderCustomer.body': 'Ваш запис на {service} з {other} — приблизно через 48 годин.',
    'notif.reminderProvider.title': 'Запис через 48 годин',
    'notif.reminderProvider.body': '{service} з {other} — приблизно через 48 годин.',
    'notif.salonInvite.title': 'Запрошення в салон',
    'notif.salonInvite.body': '{salon} запрошує вас до своєї команди.',
    'notif.memberRemoved.title': 'Видалено із салону',
    'notif.memberRemoved.body': 'Адміністратор видалив вас із {salon}.',
    'notif.memberLeft.title': 'Спеціаліст залишив салон',
    'notif.memberLeft.body': '{provider} залишив(ла) команду.',
    'notif.inviteAccepted.title': 'Спеціаліст приєднався до салону',
    'notif.inviteAccepted.body': '{provider} прийняв(ла) запрошення і приєднався до команди.',
    'notif.inviteDeclined.title': 'Запрошення відхилено',
    'notif.inviteDeclined.body': '{provider} відхилив(ла) запрошення в салон.',
    'notif.salonBookingRequested.title': 'Нова заявка у вашому салоні',
    'notif.salonBookingRequested.body': '{customer} хоче записатися на {service} до {provider} — очікує підтвердження.',
    'notif.salonBooking.title': 'Новий запис у вашому салоні',
    'notif.salonBooking.body': 'У {provider} новий запис з клієнтом {customer}.',
    'notif.salonBookingCancelled.title': 'Запис у салоні скасовано',
    'notif.salonBookingCancelled.body': 'Запис {provider} з клієнтом {customer} скасовано.',
    'auth.yourPassword': 'Ваш пароль',

    'nav.navigation': 'Навігація',
    'nav.account': 'Акаунт',
    'nav.menu': 'Меню',
    'nav.home': 'Головна',
    'nav.search': 'Пошук',
    'nav.searchProviders': 'Пошук спеціалістів',
    'nav.favorites': 'Обране',
    'nav.messages': 'Повідомлення',
    'nav.myCareHub': 'Мій Care Hub',
    'nav.spending': 'Витрати',
    'nav.profile': 'Профіль',
    'nav.history': 'Історія',
    'nav.upcoming': 'Майбутні',
    'nav.customers': 'Клієнти',
    'nav.team': 'Команда',
    'nav.schedule': 'Розклад',
    'nav.services': 'Послуги',
    'nav.portfolio': 'Портфоліо',
    'nav.mySalon': 'Мій салон',
    'nav.earnings': 'Доходи',
    'nav.pending': 'Очікують',
    'nav.signOut': 'Вийти',
    'nav.terms': 'Умови',
    'nav.privacy': 'Конфіденційність',
    'nav.notifications': 'Сповіщення',
    'nav.salonInviteTitle': '{count} запрошенн{plural} до салону очікує',

    'notifications.allCaughtUp': 'Усе переглянуто',
    'notifications.allLoaded': 'Це всі сповіщення: {count}.',
    'notifications.empty': 'Сповіщень поки немає',
    'notifications.markAsRead': 'Позначити як прочитане',
    'notifications.title': 'Сповіщення',

    'salonTeam.inviteFromPrefix': 'Запросіть спеціалістів із',
    'salonTeam.manageInvitesPrefix': 'Керуйте запрошеннями з',
    'salonTeam.noProviders': 'Спеціалістів поки немає',
    'salonTeam.noSearchMatches': 'Жоден учасник команди не відповідає пошуку.',
    'salonTeam.providerCount': 'Спеціалістів: {count}',
    'salonTeam.searchPlaceholder': 'Пошук за ім’ям або послугою…',
    'salonTeam.sortName': 'Ім’я (A-Z)',
    'salonTeam.sortRecentlyJoined': 'Нещодавно приєдналися',

    'salon.accept': 'Прийняти',
    'salon.activeProviders': 'Активних спеціалістів: {count}',
    'salon.address': 'Адреса',
    'salon.addressPlaceholder': 'Почніть вводити адресу…',
    'salon.admin': 'Адмін',
    'salon.city': 'Місто',
    'salon.close': 'Закрити',
    'salon.country': 'Країна',
    'salon.decline': 'Відхилити',
    'salon.description': 'Опис',
    'salon.descriptionPlaceholder': 'Розкажіть клієнтам, що робить ваш салон особливим',
    'salon.desktopTitleEmphasis': 'салон',
    'salon.desktopTitlePrefix': 'Мій',
    'salon.editDetails': 'Редагувати деталі',
    'salon.invitation': 'Запрошення в салон',
    'salon.invitationSent': 'Запрошення надіслано.',
    'salon.inviteProvider': 'Запросити спеціаліста',
    'salon.inviteProviderHelp': 'У нього вже має бути акаунт спеціаліста Bevelure. Ми надішлемо сповіщення для прийняття.',
    'salon.invitedAs': 'Запрошено як {role}',
    'salon.leaveDescription': 'Ви більше не будете частиною цього салону. Нові бронювання після цього не будуть прив’язані до салону. Минулі візити залишаться в історії.',
    'salon.leaveSalon': 'Покинути салон',
    'salon.leaveTitle': 'Покинути {name}?',
    'salon.name': 'Назва салону',
    'salon.noPendingInvites': 'Немає запрошень в очікуванні. Активні учасники команди знаходяться в {team}.',
    'salon.notPartBody': 'Салони запрошують спеціалістів email-ом. Якщо салон запросить вас, запрошення з’явиться тут.',
    'salon.notPartTitle': 'Ви не є частиною салону',
    'salon.pendingInvites': 'Запрошення в очікуванні',
    'salon.phone': 'Телефон',
    'salon.phonePlaceholder': 'Номер телефону салону',
    'salon.postalCode': 'Поштовий індекс',
    'salon.providerEmail': 'Email спеціаліста',
    'salon.providerFallback': 'Спеціаліст',
    'salon.publicEmail': 'Публічний email',
    'salon.removeMember': 'Видалити учасника',
    'salon.revokeInvite': 'Відкликати запрошення',
    'salon.roleAdmin': 'адмін',
    'salon.roleMember': 'учасник',
    'salon.saveDetailsError': 'Не вдалося зберегти деталі салону',
    'salon.sendInvite': 'Надіслати запрошення',
    'salon.sending': 'Надсилання…',
    'salon.socialLink': 'Сайт / соцмережа',
    'salon.subtitle': 'Керуйте салоном і командою.',
    'salon.teamLink': 'Команді',
    'salon.title': 'Мій салон',
    'salon.uploadLogo': 'Завантажити логотип',
    'salon.view': 'Переглянути',
    'salon.viewProfile': 'Переглянути профіль салону',
    'salon.youWorkAt': 'Ви працюєте в',

    'teamBookings.all': 'Усі',
    'teamBookings.history': 'Історія',
    'teamBookings.noBookings': 'Тут поки немає бронювань.',
    'teamBookings.subtitle': 'Усі записи спеціалістів вашого салону.',
    'teamBookings.title': 'Бронювання команди',
    'teamBookings.titleEmphasis': 'команди',
    'teamBookings.titlePrefix': 'Бронювання',
    'teamBookings.upcoming': 'Майбутні',
    'salonAppointments.cancelReason': 'Скасовано адміністратором салону',

    'salonProfile.messageSalon': 'Написати салону',
    'salonProfile.noProviders': 'Спеціалістів поки не вказано.',
    'salonProfile.openingChat': 'Відкриваємо чат…',
    'salonProfile.ourTeam': 'Наша команда',
    'salonProfile.provider': 'спеціаліст',
    'salonProfile.providers': 'спеціалісти',
    'salonProfile.title': 'Салон',

    'portfolio.addMore': 'Додати ще',
    'portfolio.addPhoto': 'Додати фото',
    'portfolio.addToPortfolio': 'Додати до портфоліо',
    'portfolio.bulkApplyHint': 'Категорія та опис застосуються до всіх фото: {count}.',
    'portfolio.caption': 'Опис',
    'portfolio.captionOptional': 'Опис (необов’язково)',
    'portfolio.captionPlaceholder': 'Короткий опис під фото',
    'portfolio.category': 'Категорія',
    'portfolio.categoryCount': 'Категорій: {count}',
    'portfolio.categoryPlaceholder': 'напр. манікюр, ботокс, Hydrafacial',
    'portfolio.choosePhotos': 'Натисніть, щоб вибрати фото',
    'portfolio.deletePhotoMessage': 'Фото буде видалено з вашого публічного портфоліо.',
    'portfolio.deletePhotoTitle': 'Видалити фото?',
    'portfolio.desktopTitleEmphasis': 'портфоліо',
    'portfolio.desktopTitlePrefix': 'Моє',
    'portfolio.editTitle': 'Редагувати елемент портфоліо',
    'portfolio.fileHint': 'JPG, PNG до 10MB · можна вибрати кілька',
    'portfolio.hide': 'Сховати',
    'portfolio.noFiles': 'Немає файлів',
    'portfolio.noItemsBody': 'Покажіть свої роботи — клієнти бачать це у вашому публічному профілі. Завантажуйте напряму або додавайте фото із завершених візитів.',
    'portfolio.noItemsTitle': 'У портфоліо ще немає робіт',
    'portfolio.noItemsYet': 'Поки немає робіт',
    'portfolio.private': 'Приватне',
    'portfolio.privateDescription': 'Збережено в портфоліо, але приховано від клієнтів.',
    'portfolio.public': 'Публічне',
    'portfolio.publicDescription': 'Видно у вашому публічному профілі всім, хто його відкриє.',
    'portfolio.remove': 'Прибрати',
    'portfolio.shortDescription': 'Короткий опис',
    'portfolio.show': 'Показати',
    'portfolio.title': 'Портфоліо',
    'portfolio.uncategorized': 'Без категорії',
    'portfolio.upload': 'Завантажити',
    'portfolio.uploadCount': 'Завантажити ({count})',
    'portfolio.uploading': 'Завантаження…',

    'emailVerify.resendError': 'Не вдалося надіслати повторно — спробуйте ще раз за мить.',
    'emailVerify.sent': 'Лист надіслано — перевірте пошту.',
    'emailVerify.message': 'Підтвердьте email, щоб отримувати сповіщення про бронювання.',
    'emailVerify.sending': 'Надсилання…',
    'emailVerify.resend': 'Надіслати ще раз',

    'providerSetup.title': 'Завершіть налаштування профілю',
    'providerSetup.bodyPrefix': 'Клієнти не зможуть бронювати, доки ви не додасте',
    'providerSetup.oneService': 'принаймні одну послугу',
    'providerSetup.workingHours': 'робочі години',
    'providerSetup.addService': 'Додати послугу',
    'providerSetup.setHours': 'Налаштувати години',

    'profile.title': 'Профіль',
    'profile.preferences': 'Налаштування',
    'profile.language': 'Мова',
    'profile.pushNotifications': 'Push-сповіщення',
    'profile.languageAria': 'Мова інтерфейсу',
    'profile.providerLanguages': 'Мови, якими ви спілкуєтесь',
    'profile.providerLanguagesLabel': 'Мови',
    'profile.currencyLabel': 'Валюта',
    'profile.failedUpdate': 'Не вдалося оновити профіль',
    'profile.roleCustomer': 'Клієнт',
    'profile.roleCareProvider': 'Спеціаліст',
    'profile.roleSalonAdmin': 'Адміністратор салону',
    'profile.uploadPhoto': 'Завантажити фото',
    'profile.rating': 'Рейтинг',
    'profile.ratingSingular': 'оцінка',
    'profile.ratingPlural': 'оцінки',
    'profile.fromClients': 'від клієнтів',
    'profile.fromProviders': 'від спеціалістів',
    'profile.byService': 'За послугами',
    'profile.noRatingsCustomer': 'Оцінок поки немає. Спеціалісти зможуть оцінити вас після завершеного візиту.',
    'profile.noRatingsProvider': 'Оцінок поки немає. Клієнти зможуть оцінити вас після завершеного візиту.',
    'profile.personalInformation': 'Особиста інформація',
    'profile.dateOfBirth': 'Дата народження',
    'profile.sex': 'Стать',
    'profile.location': 'Локація',
    'profile.address': 'Адреса',
    'profile.addressPlaceholder': 'Почніть вводити адресу...',
    'profile.timeZone': 'Часовий пояс',
    'profile.timeZonePrefix': 'Часовий пояс: {zone}',
    'profile.timeZoneHelp': 'Ваші робочі години вказані в цьому поясі. Клієнти з інших місць також бачать свій час.',
    'profile.contact': 'Контакти',
    'profile.phone': 'Телефон',
    'profile.social': 'Соцмережа',
    'profile.socialMediaLink': 'Посилання на соцмережу',
    'profile.socialPlaceholder': 'https://instagram.com/your-handle',
    'profile.contactHelp': 'Щоб клієнти могли знайти й забронювати вас за телефоном або соцмережею. Телефон зберігається у міжнародному форматі — додайте код країни.',
    'profile.noContactDetails': 'Контактів поки немає.',
    'profile.allergies': 'Алергії',
    'profile.noAllergies': 'Немає алергій',
    'profile.hasAllergies': 'У мене є алергії',
    'profile.notes': 'Нотатки',
    'profile.allergyPlaceholder': 'Латекс, ароматизатори тощо.',
    'profile.allergiesNoDetails': 'Є алергії (без деталей)',
    'profile.noKnownAllergies': 'Відомих алергій немає',
    'profile.careRoutine': 'Домашній догляд',
    'profile.careRoutineHelp': 'Опишіть або сфотографуйте домашній догляд (продукти, кроки). Це бачать ваші спеціалісти, щоб мати контекст.',
    'profile.careRoutinePlaceholder': 'напр. Ранок: м’який очищувач, вітамін C, SPF. Вечір: ретинол 2x/тиждень...',
    'profile.addPhoto': 'Додати фото',
    'profile.removePhotoTitle': 'Видалити фото?',
    'profile.removePhotoBody': 'Це фото буде видалено назавжди.',
    'profile.removePhoto': 'Видалити',
    'profile.professionalDetails': 'Професійні деталі',
    'profile.yearExperience': '{service} · {count} рік досвіду',
    'profile.yearsExperience': '{service} · {count} років досвіду',
    'profile.bio': 'Біо',
    'profile.bioPlaceholder': 'Короткий опис, який видно у вашому публічному профілі...',
    'profile.about': 'Про себе',
    'profile.noBio': 'Біо поки немає — додайте його, щоб клієнтам було легше вам довіряти.',
    'profile.currencyHelp': 'Використовується для всіх ваших майбутніх послуг і add-ons. Минулі візити зберігають валюту бронювання.',
    'profile.certificates': 'Сертифікати',
    'profile.addCertificate': 'Додати',
    'profile.certificatesHelp': 'Показуються у вашому публічному профілі. Зображення або PDF.',
    'profile.noCertificates': 'Сертифікатів поки немає.',
    'profile.removeCertificateTitle': 'Видалити сертифікат?',
    'profile.removeCertificateBody': 'Цей сертифікат буде видалено назавжди.',
    'profile.removeCertificate': 'Видалити',
    'profile.password': 'Пароль',
    'profile.changePassword': 'Змінити',
    'profile.currentPassword': 'Поточний пароль',
    'profile.newPassword': 'Новий пароль',
    'profile.confirmNewPassword': 'Підтвердьте новий пароль',
    'profile.newPasswordMin': 'Новий пароль має містити щонайменше 8 символів',
    'profile.newPasswordsMismatch': 'Нові паролі не збігаються',
    'profile.passwordUpdated': 'Пароль оновлено. На інших пристроях потрібно буде увійти знову.',
    'profile.passwordChangeFailed': 'Не вдалося змінити пароль',
    'profile.newPasswordPlaceholder': 'Щонайменше 8 символів',
    'profile.confirmNewPasswordPlaceholder': 'Повторіть новий пароль',
    'profile.updatePassword': 'Оновити пароль',
    'profile.deleteAccountTitle': 'Видалити акаунт',
    'profile.deleteAccountBody': 'Видалення акаунта назавжди видалить ваш профіль і пов’язані з акаунтом персональні дані. Майбутні та поточні записи буде скасовано. Цю дію неможливо скасувати.',
    'profile.deleteAccount': 'Видалити акаунт',
    'profile.deletingAccount': 'Видалення…',
    'profile.deleteAccountConfirmTitle': 'Видалити акаунт?',
    'profile.deleteAccountImpactNone': 'Жоден майбутній або поточний запис не буде скасовано.',
    'profile.deleteAccountImpactOne': '1 майбутній або поточний запис буде скасовано автоматично.',
    'profile.deleteAccountImpactMany': '{count} майбутніх або поточних записів буде скасовано автоматично.',
    'profile.deleteAccountConfirmBody': 'Ваш акаунт і пов’язані з ним персональні дані буде видалено назавжди. Цю дію неможливо скасувати.',
    'profile.deleteAccountConfirm': 'Видалити акаунт',
    'profile.deleteAccountError': 'Не вдалося видалити акаунт',
    'profile.switchCurrencyTitle': 'Змінити валюту на {currency}?',
    'profile.serviceSingular': 'послуга',
    'profile.servicePlural': 'послуг',
    'profile.currencyWarningBody': 'У вас є {count} активних {services} з цінами в {from}. Перехід на {to} не конвертує ці числа — цифри залишаться тими самими, але відображатимуться з новим символом валюти. Перевірте й оновіть ціни на сторінці My Services після зміни.',
    'profile.currencyWarningPast': 'Минулі візити залишаються у валюті бронювання — вони не зміняться.',
    'profile.switchToCurrency': 'Змінити на {currency}',
  },
  ru: {
    ...en,
    'common.loading': 'Загрузка...',
    'common.loadingEllipsis': 'Загрузка…',
    'common.loadingMore': 'Загружаем еще…',
    'common.loadMore': 'Загрузить еще',
    'common.save': 'Сохранить',
    'common.saveChanges': 'Сохранить изменения',
    'common.saving': 'Сохранение…',
    'common.saved': 'Сохранено',
    'common.edit': 'Редактировать',
    'common.cancel': 'Отмена',
    'common.discard': 'Сбросить',
    'common.delete': 'Удалить',
    'common.unsavedChanges': 'У вас есть несохраненные изменения',
    'common.enabled': 'Включены',
    'common.disabled': 'Выключены',
    'common.custom': 'Свое значение',
    'common.and': 'и',
    'common.all': 'Все',
    'common.any': 'Любой',
    'common.add': 'Добавить',
    'common.clear': 'Очистить',
    'common.clearFilters': 'Очистить фильтры',
    'common.close': 'Закрыть',
    'common.confirm': 'Подтвердить',
    'common.decrease': 'Уменьшить',
    'common.dismiss': 'Закрыть',
    'common.done': 'Готово',
    'common.emailChannel': 'электронной почте',
    'common.filters': 'Фильтры',
    'common.from': 'С',
    'common.homeAria': 'Главная Bevelure',
    'common.increase': 'Увеличить',
    'common.km': 'км',
    'common.max': 'Макс.',
    'common.min': 'Мин.',
    'common.minutesShort': '{count} мин',
    'common.next': 'Далее',
    'common.noMatches': 'Ничего не найдено',
    'common.no': 'Нет',
    'common.pageOf': 'Страница {page} из {total}',
    'common.prev': 'Назад',
    'common.price': 'Цена',
    'common.remove': 'Удалить',
    'common.scrollRight': 'Прокрутить вправо',
    'common.search': 'Поиск',
    'common.sort': 'Сортировка',
    'common.sortBy': 'Сортировка: {label}',
    'common.to': 'До',
    'common.unknown': 'Неизвестно',
    'common.user': 'Пользователь',
    'common.viewAll': 'Смотреть все',
    'common.viewFullDetails': 'Смотреть детали',
    'common.viewHistory': 'Смотреть историю',
    'common.viewProfile': 'Смотреть профиль',
    'common.yes': 'Да',

    'imageCrop.adjustLogo': 'Настроить логотип',
    'imageCrop.adjustPhoto': 'Настроить фото',
    'imageCrop.description': 'Переместите и увеличьте изображение так, чтобы нужная часть была внутри круга.',
    'imageCrop.dragHint': 'Перетаскивайте изображение, чтобы изменить позицию.',
    'imageCrop.squareDescription': 'Переместите и увеличьте изображение так, чтобы нужная часть была внутри квадрата.',
    'imageCrop.useImage': 'Использовать изображение',
    'imageCrop.zoom': 'Масштаб',

    'greeting.afternoon': 'Добрый день',
    'greeting.evening': 'Добрый вечер',
    'greeting.morning': 'Доброе утро',
    'greeting.night': 'Доброй ночи',

    'status.awaitingProvider': 'Ожидает специалиста',
    'status.cancelled': 'Отменено',
    'status.completed': 'Завершено',
    'status.confirmed': 'Подтверждено',
    'status.inProgress': 'В процессе',
    'status.pending': 'Ожидает',

    'date.daysAgo': '{count} дн назад',
    'date.afternoon': 'День',
    'date.evening': 'Вечер',
    'date.morning': 'Утро',
    'date.noon': 'Полдень',
    'date.selectDate': 'Выберите дату',
    'date.selectDateTime': 'Выберите дату и время',
    'date.time': 'Время',
    'date.today': 'Сегодня',
    'date.tomorrow': 'Завтра',
    'date.yesterday': 'Вчера',

    'field.addCustomValue': 'Добавить “{value}”',
    'field.addLanguage': 'Добавить язык…',
    'field.anyDate': 'Любая дата',
    'field.anyLanguage': 'Любой язык',
    'field.anyProvider': 'Любой специалист',
    'field.anyService': 'Любая услуга',
    'field.age': 'Возраст',
    'field.ageMaxAria': 'Максимальный возраст',
    'field.ageMinAria': 'Минимальный возраст',
    'field.ageUpTo': 'До {age}',
    'field.cityPlaceholder': 'Майами',
    'field.city': 'Город',
    'field.firstNamePlaceholder': 'Анна',
    'field.hidePassword': 'Скрыть пароль',
    'field.invalidPhone': 'Введите корректный номер телефона для выбранной страны.',
    'field.language': 'Язык',
    'field.languageHelp': 'Выберите из списка или введите свой язык и нажмите Enter.',
    'field.lastNamePlaceholder': 'Иванова',
    'field.noCountriesFound': 'Страны не найдены',
    'field.phoneNumber': 'Номер телефона',
    'field.priceMaxAria': 'Максимальная цена',
    'field.priceMinAria': 'Минимальная цена',
    'field.providerNamePlaceholder': 'Алекс Морган',
    'field.rating': 'Рейтинг',
    'field.searchCountry': 'Поиск страны…',
    'field.searchCurrency': 'Поиск валюты…',
    'field.selectCountry': 'Выберите страну',
    'field.service': 'Услуга',
    'field.serviceExamplePlaceholder': 'например, маникюр',
    'field.shareProcedurePlaceholder': 'например, мужская стрижка, уход за лицом…',
    'field.showPassword': 'Показать пароль',
    'field.socialPlaceholder': '@имя или URL профиля',
    'field.startTypingAddress': 'Начните вводить адрес…',
    'field.startTypingCity': 'Начните вводить город…',
    'field.serviceType': 'Тип услуги',
    'field.sex': 'Пол',
    'field.useCustomLanguage': 'Использовать “{value}”',

    'sex.female': 'Женщина',
    'sex.male': 'Мужчина',
    'sex.other': 'Другое',
    'sex.preferNotToSay': 'Предпочитаю не указывать',

    'rating.any': 'Любой',
    'sort.distance': 'Расстояние',
    'sort.name': 'Имя',
    'sort.rating': 'Рейтинг',

    'customerHome.bookingConfirmedBody': 'Сохранено в вашу историю. Откройте запись, чтобы увидеть все детали.',
    'customerHome.bookingPendingBody': 'Мы сообщим, когда специалист подтвердит. Вы можете отменить в любой момент.',
    'customerHome.bookingRequestSent': 'Запрос на бронирование отправлен',
    'customerHome.appointmentBooked': 'Запись забронирована',
    'customerHome.inProgressSubtitle': 'Ваш визит сейчас идет',
    'customerHome.lastVisit': 'Последний визит',
    'customerHome.nextAppointment': 'Ваш следующий визит {when}',
    'customerHome.noAppointmentsThisDay': 'На этот день нет записей',
    'customerHome.noUpcoming': 'Нет будущих записей',
    'customerHome.nothingHereYet': 'Пока ничего нет',
    'customerHome.recent': 'Недавние',
    'customerHome.upcoming': 'Будущие',

    'providerHome.allProviders': 'Все специалисты',
    'providerHome.appointmentToday': 'Запись сегодня',
    'providerHome.appointmentsToday': 'Записи сегодня',
    'providerHome.awaitingYou': 'Ожидает вас',
    'providerHome.pendingRequests': 'Запросов на бронирование ожидает: {count}',
    'providerHome.pendingRequestsBody': 'Подтвердите или отклоните, чтобы клиенты знали, чего ожидать.',
    'providerHome.reviewConfirm': 'Проверить и подтвердить',
    'providerHome.searchTeam': 'Поиск по команде…',

    'customerSearch.anyCity': 'Любой город',
    'customerSearch.detectingLocation': 'Определяем вашу локацию…',
    'customerSearch.findingProviders': 'Ищем специалистов…',
    'customerSearch.inCity': ' в {city}',
    'customerSearch.locationCity': 'Показываем специалистов в {city}.',
    'customerSearch.locationNearest': 'Показываем специалистов рядом с вами{city}.',
    'customerSearch.noProvidersFound': 'Специалисты не найдены',
    'customerSearch.priceHintAny': 'Укажите тип услуги выше, чтобы фильтровать конкретную процедуру по цене.',
    'customerSearch.priceHintService': 'Цена услуг, подходящих под “{serviceType}”.',
    'customerSearch.searchPlaceholder': 'Поиск по имени…',
    'customerSearch.servicePlaceholder': 'Любой (например, маникюр, барбер…)',
    'customerSearch.showingAll': 'Показываем всех специалистов.',
    'customerSearch.title': 'Найти специалистов',
    'customerSearch.useMyLocation': 'Использовать мою локацию',

    'careHub.completedVisits': 'Завершенные визиты: {count}',
    'careHub.loadingMore': 'Загружаем еще…',
    'careHub.noCompletedFor': 'Пока нет завершенных визитов для {serviceType}.',
    'careHub.noCompletedYet': 'Пока нет завершенных визитов.',
    'careHub.noProcedureDetails': 'Детали процедуры пока не добавлены.',
    'careHub.procedurePhotos': 'Фото процедуры',
    'careHub.procedureTypes': 'Типы процедур: {count}',
    'careHub.quickShare': 'Быстрый доступ',
    'careHub.quickShareBody': 'Создайте QR-код или ссылку для одного из типов процедур.',
    'careHub.shareHistory': 'Поделиться историей',
    'careHub.title': 'Мой Care Hub',
    'careHub.titleAccent': 'Мой Care *Hub*',
    'careHub.detailNotes': 'Заметки',
    'careHub.detailProducts': 'Продукты',
    'careHub.detailRecommendations': 'Рекомендации',

    'appointments.cancelled': 'Отмененные',
    'appointments.careProvider': 'Специалист',
    'appointments.history': 'История',
    'appointments.loadOlder': 'Загрузить старые записи',
    'appointments.loadingOlder': 'Загружаем старые записи…',
    'appointments.noCancelled': 'Отмененных записей нет',
    'appointments.noFilterMatches': 'Ни одна запись не подходит под ваши фильтры.',
    'appointments.noHistory': 'Пока ничего нет',
    'appointments.noUpcoming': 'Нет будущих записей',
    'appointments.rateThisVisit': 'Оценить визит',
    'appointments.upcoming': 'Будущие',
    'appointments.yourRating': 'Ваша оценка',

    'providerAppointments.anyCustomer': 'Любой клиент',
    'providerAppointments.noPending': 'Запросов на подтверждение нет — все обработано.',
    'providerAppointments.rateClient': 'Оценить этого клиента',

    'providerServices.addService': 'Добавить услугу',
    'providerServices.addon': 'Дополнение',
    'providerServices.addonName': 'Название дополнения',
    'providerServices.addonPlaceholder': 'например, снятие геля',
    'providerServices.deactivate': 'Деактивировать',
    'providerServices.deactivateAddon': 'Деактивировать дополнение',
    'providerServices.deactivateAddonDescription': 'Сама услуга останется доступной — нельзя будет забронировать только это дополнение. Его можно будет активировать снова.',
    'providerServices.deactivateAddonTitle': 'Деактивировать дополнение “{name}”?',
    'providerServices.deactivateService': 'Деактивировать услугу',
    'providerServices.deactivateServiceDescription': 'Клиенты не смогут бронировать эту услугу для новых записей. Прошлые бронирования останутся в истории. Вы сможете активировать услугу снова.',
    'providerServices.deactivateServiceTitle': 'Деактивировать “{name}”?',
    'providerServices.durationMinutes': 'Длительность (минуты)',
    'providerServices.editAddon': 'Редактировать дополнение',
    'providerServices.extraMinutes': 'Дополнительные минуты',
    'providerServices.extraPrice': 'Дополнительная цена ({currency})',
    'providerServices.failedCreate': 'Не удалось создать услугу',
    'providerServices.failedSaveAddon': 'Не удалось сохранить дополнение',
    'providerServices.inactive': 'Неактивно',
    'providerServices.noAddons': 'Дополнений пока нет.',
    'providerServices.priceOptional': 'Цена ({currency}, необязательно)',
    'providerServices.reactivate': 'Активировать снова',
    'providerServices.reactivateService': 'Активировать услугу снова',
    'providerServices.renameDisabled': 'Переименование не поддерживается — прошлые бронирования ссылаются на это название. Деактивируйте ее и создайте новую.',
    'chat.blockedTitle': 'Сообщения недоступны',
    'chat.blocked': 'Вы больше не можете писать друг другу.',
    'pub.noSlotsMonth': 'В этом месяце нет свободных окон — пролистайте стрелками другие месяцы.',
    'pub.publicPage': 'Публичная страница записи',
    'pub.username': 'Имя пользователя',
    'pub.usernameHelp': 'Ваша публичная ссылка — делитесь ею в соцсетях, чтобы клиенты записывались.',
    'pub.copy': 'Копировать',
    'pub.copied': 'Скопировано',
    'common.back': 'Назад',
    'pub.close': 'Закрыть',
    'pub.bookWith': 'Запись к {name}',
    'pub.bookCta': 'Записаться',
    'pub.selectService': 'Выберите услугу',
    'pub.chooseTime': 'Выберите время',
    'pub.yourDetails': 'Ваши данные',
    'pub.pickDay': 'Выберите день со свободными окнами.',
    'pub.continue': 'Далее',
    'pub.confirmBooking': 'Подтвердить запись',
    'pub.successTitle': 'Запрос отправлен',
    'pub.successGuest': 'Ваш запрос отправлен! Детали записи отправили на {email} — специалист скоро подтвердит.',
    'pub.successCustomer': 'Забронировано! {provider} скоро подтвердит вашу запись.',
    'pub.manageCancel': 'Управлять или отменить запись',
    'pub.signedInAs': 'Запись от имени {name}',
    'pub.tooMany': 'Слишком много попыток записи. Попробуйте позже.',
    'pub.bookingFailed': 'Не удалось завершить запись. Попробуйте ещё раз.',
    'pub.about': 'Обо мне',
    'pub.services': 'Услуги',
    'pub.portfolio': 'Портфолио',
    'pub.speaks': 'Языки',
    'pub.ratings': '{count} оценок',
    'pub.atSalon': 'в {salon}',
    'pub.team': 'Команда',
    'pub.noTeam': 'Пока нет участников.',
    'pub.createYourOwn': 'Создайте свою страницу записи на Bevelure →',
    'pub.agree': 'Я согласен(а) с',
    'pub.terms': 'Условиями',
    'pub.privacy': 'Политикой конфиденциальности',
    'common.showMore': 'Показать больше',
    'common.showLess': 'Свернуть',
    'providerServices.descriptionOptional': 'Описание (необязательно)',
    'providerServices.serviceDescriptionPlaceholder': 'Что входит, чего ожидать, подготовка и уход после…',
    'providerServices.addonDescriptionPlaceholder': 'Что включает это дополнение…',
    'providerServices.serviceName': 'Название услуги',
    'providerServices.servicePlaceholder': 'например, маникюр',
    'providerServices.title': 'Мои услуги',

    'schedule.adding': 'Добавляем…',
    'schedule.addTimeOff': 'Добавить нерабочее время',
    'schedule.addTimeOffHelp': 'Заблокируйте даты для отпуска, больничных или перерывов. Клиенты не смогут бронировать в эти периоды.',
    'schedule.addTimeOffImpactHelp': 'Клиенты не смогут создавать новые записи в этом промежутке.',
    'schedule.addTimeOffTitle': 'Добавить это нерабочее время?',
    'schedule.addWorkingHours': 'Добавить рабочие часы',
    'schedule.dayOfWeek': 'День недели',
    'schedule.dayOff': 'Выходной',
    'schedule.end': 'Конец',
    'schedule.errorEndAfterStart': 'Конец должен быть после начала',
    'schedule.errorEndAfterStartTime': 'Время окончания должно быть после времени начала',
    'schedule.errorOverlap': 'Эти рабочие часы пересекаются с существующим правилом для этого дня',
    'schedule.errorPickStartEnd': 'Выберите время начала и окончания',
    'schedule.failedAddRule': 'Не удалось добавить правило расписания',
    'schedule.friShort': 'Пт',
    'schedule.friday': 'Пятница',
    'schedule.monShort': 'Пн',
    'schedule.monday': 'Понедельник',
    'schedule.noneScheduled': 'Ничего не запланировано',
    'schedule.pastTimeOff': 'Прошлое нерабочее время ({count})',
    'schedule.pickEnd': 'Выберите конец',
    'schedule.pickStart': 'Выберите начало',
    'schedule.reasonPlaceholder': 'Причина (необязательно) — отпуск, обучение и т.д.',
    'schedule.removeWorkingHoursDescription': 'будет удалено из вашего еженедельного расписания. Новые бронирования в этом промежутке будут невозможны.',
    'schedule.removeWorkingHoursTitle': 'Удалить рабочие часы?',
    'schedule.satShort': 'Сб',
    'schedule.saturday': 'Суббота',
    'schedule.start': 'Начало',
    'schedule.sunShort': 'Вс',
    'schedule.sunday': 'Воскресенье',
    'schedule.thuShort': 'Чт',
    'schedule.thursday': 'Четверг',
    'schedule.title': 'Расписание',
    'schedule.tueShort': 'Вт',
    'schedule.tuesday': 'Вторник',
    'schedule.upcomingTimeOff': 'Будущее нерабочее время',
    'schedule.wedShort': 'Ср',
    'schedule.wednesday': 'Среда',
    'schedule.weeklySchedule': 'Еженедельное расписание',

    'providers.all': 'Все',
    'providers.blocked': 'Заблокированные',
    'providers.blockedBody': 'Заблокированные специалисты не будут появляться в поиске и ваших списках. Разблокировать можно в любой момент.',
    'providers.blockedOn': 'Заблокировано {date}',
    'providers.browseProviders': 'Смотреть специалистов',
    'providers.countSaved': 'Сохранено: {count}',
    'providers.countVisited': 'Посещенных специалистов: {count}',
    'providers.emptyFavoritesBody': 'Нажмите сердечко в профиле специалиста, чтобы сохранить его здесь.',
    'providers.emptyVisitedBody': 'Специалисты, которых вы бронировали, появятся здесь после первой записи.',
    'providers.favorites': 'Избранное',
    'providers.lastVisit': 'последний визит {when}',
    'providers.nextVisit': 'Следующий {when}',
    'providers.nextVisitSort': 'Следующий визит',
    'providers.noBlocked': 'Заблокированных специалистов нет',
    'providers.noFavorites': 'Избранного пока нет',
    'providers.noFilterMatches': 'Ни один специалист не подходит под ваши фильтры.',
    'providers.noProviders': 'Специалистов пока нет',
    'providers.overallRating': 'Общий рейтинг',
    'providers.keepTyping': 'Показано {shown} из {total} — продолжайте вводить, чтобы уточнить.',
    'providers.pickServiceForPrice': 'Выберите услугу, чтобы фильтровать конкретную процедуру по цене.',
    'providers.priceOfService': 'Цена “{service}”.',
    'providers.searchPlaceholder': 'Поиск по имени…',
    'providers.sortMostVisits': 'Больше всего визитов',
    'providers.title': 'Мои специалисты',
    'providers.unblock': 'Разблокировать',
    'providers.visits': 'Визитов: {count}',
    'providers.yearsShort': '{count} г.',
    'providers.youRated': 'Ваша оценка {rating}',

    'providerCustomers.count': 'Клиентов: {count}',
    'providerCustomers.last': 'последний визит {when}',
    'providerCustomers.noBlockedCustomers': 'Заблокированных клиентов нет',
    'providerCustomers.noBlockedCustomersBody': 'Заблокируйте клиента из его профиля, чтобы он не мог бронировать новые записи с вами.',
    'providerCustomers.noCustomers': 'Клиентов пока нет',
    'providerCustomers.noCustomersBody': 'Ваши клиенты появятся здесь после первой записи.',
    'providerCustomers.noSearchMatches': 'Ни один клиент не подходит под поиск.',

    'messages.attachImage': 'Прикрепить изображение',
    'messages.careProvider': 'Специалист',
    'messages.customer': 'Клиент',
    'messages.salonAdministrator': 'Администратор салона',
    'messages.emptyCustomer': 'Откройте профиль специалиста и нажмите Message, чтобы начать чат.',
    'messages.emptyProvider': 'Откройте профиль клиента и нажмите Message или дождитесь сообщения.',
    'messages.failedUpload': 'Не удалось загрузить изображение',
    'messages.loadOlder': 'Загрузить старые сообщения',
    'messages.loadingOlder': 'Загружаем старые сообщения…',
    'messages.noConversations': 'Разговоров пока нет',
    'messages.noMessagesYet': 'Сообщений пока нет. Напишите первым!',
    'messages.noMessagesPreview': 'Сообщений пока нет',
    'messages.photo': 'Фото',
    'messages.removeAttachment': 'Убрать вложение',
    'messages.reply': 'Ответить',
    'messages.replyingTo': 'Ответ для {name}',
    'messages.cancelReply': 'Отменить ответ',
    'messages.heart': 'Отправить сердечко',
    'messages.unheart': 'Убрать сердечко',
    'messages.messagingUnavailable': 'Сообщения недоступны для этой переписки.',
    'messages.title': 'Сообщения',
    'messages.typeMessage': 'Введите сообщение…',
    'messages.viewProfile': 'Смотреть профиль',

    'finance.customerCaption': 'Ориентировочно, на основе цен в ваших записях. Прошлые месяцы уже учтены; текущий месяц разделяет завершенное и будущее; будущие месяцы оцениваются по уже забронированным записям.',
    'finance.customerEmpty': 'Когда вы забронируете или завершите записи с ценой, ежемесячные расходы появятся здесь.',
    'finance.customerTitle': 'Ваши расходы',
    'finance.done': 'завершено',
    'finance.explainNumbersAria': 'Что означают эти цифры?',
    'finance.hideBreakdown': 'Скрыть детализацию',
    'finance.hidePastMonths': 'Скрыть прошлые месяцы: {count}',
    'finance.noActivity': 'Нет активности',
    'finance.nothingToShow': 'Пока нечего показать',
    'finance.pendingConfirmation': 'Включает {amount}, ожидающие подтверждения',
    'finance.providerEmpty': 'Когда у вас будут записи с ценой, ежемесячные доходы появятся здесь.',
    'finance.providerCaption': 'Ориентировочно, на основе зафиксированных цен в ваших записях. Прошлые месяцы уже учтены; текущий месяц разделяет завершенное и будущее; будущие месяцы оцениваются по уже забронированным записям. Отмененные бронирования не учитываются.',
    'finance.providerTitle': 'Ваши доходы',
    'finance.showBreakdown': 'Показать детализацию',
    'finance.showPastMonths': 'Показать прошлые месяцы: {count}',
    'finance.statusCurrent': 'Текущий',
    'finance.statusPast': 'Прошлый',
    'finance.statusUpcoming': 'Будущий',
    'finance.thisMonth': 'Этот месяц',
    'finance.upcoming': 'будущее',

    'appointmentNew.addons': 'Дополнения',
    'appointmentNew.addonsHelp': 'Необязательные дополнения — нажмите, чтобы добавить.',
    'appointmentNew.addressOptional': 'Адрес (необязательно)',
    'appointmentNew.availableDaysHelp': 'Дни с доступными слотами отмечены точкой.',
    'appointmentNew.bookingAtSalon': 'Бронирование в салоне',
    'appointmentNew.confirmAppointment': 'Подтвердить запись',
    'appointmentNew.create': 'Создать',
    'appointmentNew.createError': 'Не удалось создать запись',
    'appointmentNew.creating': 'Создание…',
    'appointmentNew.careProviderName': 'Имя специалиста',
    'appointmentNew.dateTime': 'Дата и время',
    'appointmentNew.email': 'Email',
    'appointmentNew.emailPhoneSocialHelp': 'Введите что-то одно: email, телефон или социальную ссылку специалиста. Мы попробуем найти его.',
    'appointmentNew.enterProviderContact': 'Введите email, телефон или социальную ссылку специалиста',
    'appointmentNew.enterProviderName': 'Введите имя специалиста',
    'appointmentNew.enterService': 'Введите услугу (например, маникюр)',
    'appointmentNew.externalProviderRecord': 'Добавьте детали, чтобы сохранить этот визит в вашей истории.',
    'appointmentNew.externalProviderNotify': 'Добавьте детали, и мы уведомим специалиста через {channel}.',
    'appointmentNew.findProvider': 'Найдите специалиста',
    'appointmentNew.lookupFailed': 'Поиск не удался',
    'appointmentNew.newProvider': 'Новый специалист',
    'appointmentNew.newProviderIntro': 'Мы не нашли этого специалиста в Bevelure.',
    'appointmentNew.noMatchingProviders': 'Подходящих специалистов нет.',
    'appointmentNew.noServices': 'У этого специалиста нет доступных услуг',
    'appointmentNew.noSlots': 'Нет доступных слотов на ближайшие 30 дней',
    'appointmentNew.or': 'или',
    'appointmentNew.orFindNew': 'или найдите нового',
    'appointmentNew.pickDateTime': 'Выберите дату и время',
    'appointmentNew.pickDateTimeShort': 'Выберите дату и время',
    'appointmentNew.pickDateWithSlots': 'Выберите дату с доступными слотами выше.',
    'appointmentNew.phone': 'Телефон',
    'appointmentNew.priceOptional': 'Цена (необязательно)',
    'appointmentNew.providerFound': 'Специалист найден!',
    'appointmentNew.providerFoundText': 'Мы нашли {name} в системе. Пожалуйста, выберите услугу.',
    'appointmentNew.providerZone': 'Время показано в часовом поясе специалиста — {zone}.',
    'appointmentNew.selectService': 'Выберите услугу',
    'appointmentNew.selectServiceHelp': 'Выберите услугу для этой записи',
    'appointmentNew.shareAllDesc': 'Все подходящие визиты.',
    'appointmentNew.shareAllDescService': 'Все ваши визиты {service}.',
    'appointmentNew.shareAllTitle': 'Полная история',
    'appointmentNew.shareConsent': 'Я понимаю, что делюсь этой историей процедур с {provider}: заметками, продуктами, рекомендациями и фото результата/процедуры. Мои приватные заметки и фото никогда не передаются.',
    'appointmentNew.shareDontDesc': 'Оставить историю приватной.',
    'appointmentNew.shareDontTitle': 'Не делиться',
    'appointmentNew.shareHistory': 'Поделиться историей',
    'appointmentNew.shareHistoryHelp': 'Выберите, что этот специалист сможет видеть о ваших прошлых визитах.',
    'appointmentNew.shareLast5Desc': 'Ваши 5 последних подходящих визитов.',
    'appointmentNew.shareLast5DescService': 'Ваши 5 последних визитов {service}.',
    'appointmentNew.shareLast5Title': 'Последние 5 визитов',
    'appointmentNew.shareProcedureHint': 'Другие специалисты могут называть ту же услугу иначе — добавьте эти названия, чтобы включить такие визиты.',
    'appointmentNew.shareProcedures': 'Какими процедурами поделиться',
    'appointmentNew.socialMedia': 'Соцсеть',
    'appointmentNew.stepOf': 'Шаг {step} из {total}',
    'appointmentNew.thisProvider': 'этим специалистом',
    'appointmentNew.title': 'Создать запись',
    'appointmentNew.total': 'Итого',
    'appointmentNew.whereToGo': 'Куда идти',
    'appointmentNew.yourProviders': 'Ваши специалисты',

    'providerAppointmentNew.adminBookTeam': 'Как админ салона вы можете бронировать для команды.',
    'providerAppointmentNew.adminBookTeamOnly': 'Как админ салона вы бронируете от имени команды.',
    'providerAppointmentNew.bookFor': 'Забронировать для',
    'providerAppointmentNew.bookingFor': 'Бронирование для',
    'providerAppointmentNew.change': 'Изменить',
    'providerAppointmentNew.checkWorkingHours': 'Проверьте рабочие часы и выходные специалиста.',
    'providerAppointmentNew.chooseOneService': 'Выберите одну из своих услуг',
    'providerAppointmentNew.confirmDetails': 'Подтвердить детали',
    'providerAppointmentNew.createAppointment': 'Создать запись',
    'providerAppointmentNew.customer': 'Клиент',
    'providerAppointmentNew.customerPreselected': 'Клиент выбран. Перейдите дальше, чтобы выбрать услугу.',
    'providerAppointmentNew.enterOneContact': 'Введите один контакт клиента — email, телефон или ссылку на соцсеть.',
    'providerAppointmentNew.inviteProviderFirst': 'Пригласите специалиста в салон перед созданием записи.',
    'providerAppointmentNew.matchInviteHelp': 'Мы попробуем найти существующий аккаунт Bevelure. Если клиент еще не зарегистрирован, отправим детали и приглашение (для соцссылок уведомления нет).',
    'providerAppointmentNew.myself': 'Я',
    'providerAppointmentNew.noActiveServices': 'У вас пока нет активных услуг.',
    'providerAppointmentNew.noBookableProviders': 'В салоне пока нет специалистов, которых можно бронировать. Пригласите их в Моем салоне.',
    'providerAppointmentNew.noMatchingCustomers': 'Нет подходящих клиентов.',
    'providerAppointmentNew.noSlots': 'Нет доступных слотов на ближайшие 30 дней.',
    'providerAppointmentNew.noTeamMembersMatch': 'Нет подходящих участников команды.',
    'providerAppointmentNew.orEnterContact': 'или введите контакт',
    'providerAppointmentNew.pickDateWithSlots': 'Выберите дату с доступными слотами выше.',
    'providerAppointmentNew.pickTeamMember': 'Выберите участника команды, который выполнит эту запись.',
    'providerAppointmentNew.pickTeamMemberError': 'Выберите, для кого из команды эта запись.',
    'providerAppointmentNew.pleaseSelectService': 'Пожалуйста, выберите услугу',
    'providerAppointmentNew.providerFallback': 'Специалист',
    'providerAppointmentNew.selected': 'Выбрано',
    'providerAppointmentNew.teamMemberFallback': 'участник команды',
    'providerAppointmentNew.title': 'Новая запись',
    'providerAppointmentNew.yourCustomers': 'Ваши клиенты',

    'providerProfile.about': 'О специалисте',
    'providerProfile.addFavorite': 'Добавить в избранное',
    'providerProfile.addons': 'Дополнения',
    'providerProfile.blockProvider': 'Заблокировать этого специалиста',
    'providerProfile.bookAppointment': 'Забронировать запись',
    'providerProfile.certificates': 'Сертификаты',
    'providerProfile.fromClients': 'от клиентов',
    'providerProfile.message': 'Сообщение',
    'providerProfile.noServicesListed': 'Услуг пока нет',
    'providerProfile.pastAppointments': 'Прошлые записи',
    'providerProfile.rating': 'Рейтинг',
    'providerProfile.removeConfirm': 'Удалить',
    'providerProfile.removeDescription': 'Он потеряет доступ к этому салону. Прошлые записи останутся в истории салона. Новые бронирования больше не будут привязаны к этому салону.',
    'providerProfile.removeFavorite': 'Убрать из избранного',
    'providerProfile.removeFromSalon': 'Удалить из салона',
    'providerProfile.removeTitle': 'Удалить {name} из салона?',
    'providerProfile.services': 'Услуги',
    'providerProfile.title': 'Профиль специалиста',
    'providerProfile.unblockProvider': 'Разблокировать этого специалиста',
    'providerProfile.unblocking': 'Разблокировка…',
    'providerProfile.unavailableTitle': 'Профиль недоступен',
    'providerProfile.unavailableBody': 'Этот профиль больше недоступен. Прошлые записи и данные остаются в вашей истории.',
    'providerProfile.visits': 'Визиты',
    'providerProfile.worksAt': 'Работает в',
    'providerProfile.yearsExperience': 'Опыт: {count} г.',

    'customerProfile.allergies': 'Аллергии',
    'customerProfile.allergiesNoDetails': 'Есть аллергии (детали не указаны)',
    'customerProfile.blockCustomer': 'Заблокировать этого клиента',
    'customerProfile.book': 'Забронировать',
    'customerProfile.careRoutine': 'Домашний уход',
    'customerProfile.goBack': 'Назад',
    'customerProfile.noKnownAllergies': 'Известных аллергий нет',
    'customerProfile.notFound': 'Клиент не найден или нет общих записей.',
    'customerProfile.unavailableTitle': 'Профиль недоступен',
    'customerProfile.unavailableBody': 'Этот профиль больше недоступен или у вас больше нет доступа к нему. Прошлые записи остаются в вашей истории.',
    'customerProfile.ratings': 'Оценок: {count}',
    'customerProfile.title': 'Клиент',
    'customerProfile.unblockCustomer': 'Разблокировать этого клиента',

    'externalCancel.alreadyCancelled': 'Эта запись уже отменена.',
    'externalCancel.alreadyCompleted': 'Эта запись уже завершена — прошлый визит нельзя отменить.',
    'externalCancel.cancelledBody': 'Мы уведомили другую сторону. Слот снова доступен для других.',
    'externalCancel.cancelledTitle': 'Запись отменена',
    'externalCancel.cancelling': 'Отмена…',
    'externalCancel.confirmHint': 'Передумали? Просто закройте страницу — ничего не произойдет, пока вы не нажмете кнопку выше.',
    'externalCancel.errorInvalid': 'Ссылка для отмены недействительна или истекла.',
    'externalCancel.errorMissing': 'В ссылке для отмены нет token.',
    'externalCancel.errorRetry': 'Не удалось отменить — попробуйте еще раз или свяжитесь с другой стороной напрямую.',
    'externalCancel.intro': 'Вы собираетесь отменить:',
    'externalCancel.titlePrefix': 'Отменить',
    'externalCancel.titleEmphasis': 'запись',

    'externalFill.addDetails': 'Добавьте детали процедуры для этой записи.',
    'externalFill.errorInvalidTitle': 'Ссылка недействительна',
    'externalFill.errorInvalidBody': 'Эта ссылка недействительна или истекла. Попросите клиента отправить ее повторно.',
    'externalFill.lastUpdated': 'Последнее обновление {date}',
    'externalFill.productsPlaceholder': 'Бренды, цвета, материалы…',
    'externalFill.saveError': 'Не удалось сохранить. Попробуйте еще раз.',

    'share.allergiesOnFile': 'Аллергии указаны',
    'share.customerAllergyFallback': 'Клиент сообщает об аллергиях (без конкретных заметок).',
    'share.expired': 'Эта ссылка истекла или больше недействительна.',
    'share.expires': 'Ссылка действует до {date}',
    'share.failedLoad': 'Не удалось загрузить общую историю',
    'share.footer': 'Поделено через {brand}. Клиент может отозвать эту ссылку в любой момент.',
    'share.fullHistory': 'Полная история',
    'share.last5Visits': 'Последние 5 визитов',
    'share.linkUnavailable': 'Ссылка недоступна',
    'share.noKnownAllergies': 'Известных аллергий не указано.',
    'share.noSharedHistory': 'Не удалось загрузить эту общую историю.',
    'share.readOnly': 'Только просмотр',
    'share.sharedTreatmentHistory': 'Общая история процедур',

    'appointmentDetail.add': 'Добавить',
    'appointmentDetail.addPhoto': 'Добавить фото',
    'appointmentDetail.addSelected': 'Добавить ({count})',
    'appointmentDetail.addToPortfolio': 'Добавить в портфолио',
    'appointmentDetail.addToPortfolioBody': 'Выберите фото, которые будут показаны в публичном профиле.',
    'appointmentDetail.addons': 'Дополнения',
    'appointmentDetail.allergiesOnFile': 'Аллергии указаны',
    'appointmentDetail.allergyFallback': 'Клиент сообщает об аллергиях (без конкретных заметок).',
    'appointmentDetail.atSalon': 'в {salon}',
    'appointmentDetail.bookingRequestBody': 'Подтвердите, чтобы закрепить слот, или отклоните, чтобы клиент мог выбрать другое время.',
    'appointmentDetail.bookingRequestTitle': 'Запрос на бронирование — ожидает вас',
    'appointmentDetail.byProvider': 'у {provider}',
    'appointmentDetail.cancelAppointment': 'Отменить запись',
    'appointmentDetail.cancelAppointmentMessage': 'Слот освободится, а другая сторона получит уведомление.',
    'appointmentDetail.cancelAppointmentTitle': 'Отменить запись?',
    'appointmentDetail.cancelRequest': 'Отменить запрос',
    'appointmentDetail.cancelRequestMessage': 'Запрос будет отозван.',
    'appointmentDetail.cancelRequestTitle': 'Отменить запрос на бронирование?',
    'appointmentDetail.captionOptional': 'Подпись (необязательно)',
    'appointmentDetail.category': 'Категория',
    'appointmentDetail.categoryPlaceholder': 'например, маникюр, Botox',
    'appointmentDetail.confirm': 'Подтвердить',
    'appointmentDetail.confirming': 'Подтверждение…',
    'appointmentDetail.customerAllergyInfo': 'Информация об аллергиях клиента',
    'appointmentDetail.customerAllergyInfoPrefix': 'Смотрите заметки клиента об аллергиях в',
    'appointmentDetail.decline': 'Отклонить',
    'appointmentDetail.declineBookingMessage': 'Слот снова станет доступным.',
    'appointmentDetail.declineBookingTitle': 'Отклонить бронирование?',
    'appointmentDetail.declining': 'Отклонение…',
    'appointmentDetail.defaultProvider': 'специалиста',
    'appointmentDetail.detailsSaved': 'Детали сохранены',
    'appointmentDetail.failedConfirm': 'Не удалось подтвердить',
    'appointmentDetail.failedDecline': 'Не удалось отклонить',
    'appointmentDetail.fullHistory': 'Полная история',
    'appointmentDetail.inProgressNow': 'Идет сейчас',
    'appointmentDetail.keepIt': 'Оставить',
    'appointmentDetail.last5Visits': 'Последние 5 визитов',
    'appointmentDetail.minutes': '{count} мин',
    'appointmentDetail.myPersonalNotes': 'Мои личные заметки',
    'appointmentDetail.myPersonalNotesHelp': 'Это видите только вы. Удобно записать ощущения, реакции и другие детали.',
    'appointmentDetail.myPersonalNotesPlaceholder': 'Ваши приватные заметки об этой записи…',
    'appointmentDetail.noCompletedShared': 'Завершенных записей этого типа пока нет.',
    'appointmentDetail.noPhotosYet': 'Фото пока нет.',
    'appointmentDetail.noProcedureDetails': 'Детали процедуры не записаны.',
    'appointmentDetail.notesSaved': 'Заметки сохранены',
    'appointmentDetail.photos': 'Фото',
    'appointmentDetail.private': 'Приватно',
    'appointmentDetail.privatePhotosHelp': 'их видите только вы',
    'appointmentDetail.privatePortfolioHelp': 'Сохранено в портфолио, но скрыто, пока вы не включите показ.',
    'appointmentDetail.procedureNotes': 'Заметки процедуры',
    'appointmentDetail.procedureNotesPlaceholder': 'Что было сделано во время процедуры…',
    'appointmentDetail.procedurePhotosHelp': 'чеки, препараты, что наносилось',
    'appointmentDetail.productsUsed': 'Использованные продукты',
    'appointmentDetail.providerOnlyNotes': 'Заметки только для специалиста',
    'appointmentDetail.providerOnlyNotesHelp': 'Видно только специалистам, которые работают с этой записью, не клиенту.',
    'appointmentDetail.providerOnlyNotesPlaceholder': 'Внутренний контекст записи, особые замечания, последующее сопровождение…',
    'appointmentDetail.public': 'Публично',
    'appointmentDetail.publicPortfolioHelp': 'Видно клиентам в вашем публичном профиле.',
    'appointmentDetail.rateCustomer': 'Оценить клиента',
    'appointmentDetail.rateProvider': 'Оценить специалиста',
    'appointmentDetail.readOnly': 'Только просмотр',
    'appointmentDetail.recommendationsPlaceholder': 'Рекомендации / уход после визита',
    'appointmentDetail.recordCount': 'Записей: {count}',
    'appointmentDetail.removePhoto': 'Удалить',
    'appointmentDetail.removePhotoMessage': 'Это фото будет удалено навсегда.',
    'appointmentDetail.removePhotoTitle': 'Удалить фото?',
    'appointmentDetail.resultPhotos': 'Фото результата',
    'appointmentDetail.saveDetails': 'Сохранить детали',
    'appointmentDetail.saveNotes': 'Сохранить заметки',
    'appointmentDetail.sharedHistoryTitle': 'История от {name}',
    'appointmentDetail.shortDescription': 'Короткое описание',
    'appointmentDetail.tapStar': 'Нажмите на звезду, чтобы отправить оценку.',
    'appointmentDetail.theirProfile': 'профиле клиента',
    'appointmentDetail.title': 'Запись',
    'appointmentDetail.total': 'Итого',
    'appointmentDetail.waitingForProviderBody': 'Вы получите уведомление, когда запрос примут или отклонят. Вы можете отменить этот запрос в любой момент.',
    'appointmentDetail.waitingForProviderTitle': 'Ожидаем подтверждение от {provider}',
    'appointmentDetail.yourTime': '{time} по вашему времени ({zone})',

    'auth.alreadyHaveAccount': 'Уже есть аккаунт?',
    'auth.backToBevelure': 'Назад к Bevelure',
    'auth.backToLogin': 'Назад ко входу',
    'auth.checkInbox': 'Проверьте почту',
    'auth.confirmEmail': 'Подтвердить *email*',
    'auth.confirmEmailLoading': 'Подтверждаем email…',
    'auth.createAccount': 'Создать аккаунт',
    'auth.createAccountTitle': 'Создать *аккаунт*',
    'auth.email': 'Email',
    'auth.emailConfirmed': 'Email подтвержден',
    'auth.emailConfirmedBody': 'Спасибо — ваш email подтвержден. Все готово.',
    'auth.enterEmailPassword': 'Введите email и пароль',
    'auth.forgotPassword': 'Забыли *пароль*',
    'auth.forgotPasswordCta': 'Забыли пароль?',
    'auth.forgotPasswordSubtitle': 'Мы отправим ссылку, чтобы создать новый.',
    'auth.goToLogin': 'Перейти ко входу',
    'auth.invalidLogin': 'Неверный email или пароль',
    'auth.login': 'Войти',
    'auth.loginButton': 'Войти',
    'auth.loginSubtitle': 'Войдите, чтобы продолжить свой beauty-путь',
    'auth.loggingIn': 'Вход…',
    'auth.newPassword': 'Новый пароль',
    'auth.newToBevelure': 'Впервые в Bevelure?',
    'auth.password': 'Пароль',
    'auth.passwordMismatch': 'Пароли не совпадают',
    'auth.passwordMin': 'Пароль должен быть не короче 8 символов',
    'auth.passwordUpdated': 'Пароль обновлен',
    'auth.passwordUpdatedBody': 'Войдите с новым паролем. На других устройствах тоже нужно будет войти заново.',
    'auth.rememberNow': 'Вспомнили пароль?',
    'auth.repeatPassword': 'Повторите новый пароль',
    'auth.resetInvalid': 'Ссылка для сброса недействительна или истекла',
    'auth.resetLinkMissing': 'Ссылка для сброса отсутствует — запросите новую.',
    'auth.resetPassword': 'Сбросить *пароль*',
    'auth.resetPasswordSubtitle': 'Выберите надежный пароль — минимум 8 символов.',
    'auth.sendResetLink': 'Отправить ссылку',
    'auth.sending': 'Отправка…',
    'auth.sentResetBody': 'Если аккаунт для {email} существует, мы только что отправили ссылку для сброса. Она действует 1 час.',
    'auth.signupChooseType': 'Выберите тип аккаунта, чтобы начать',
    'auth.signupCustomerBody': 'Отслеживайте beauty-записи и процедуры',
    'auth.signupCustomerTitle': 'Я клиент',
    'auth.signupProviderBody': 'Управляйте клиентами и записями',
    'auth.signupProviderTitle': 'Я специалист',
    'auth.signupSalonBody': 'Зарегистрируйте бизнес и пригласите команду',
    'auth.signupSalonTitle': 'Мы салон / студия',
    'signup.acceptLegal': 'Подтвердите, что вам уже исполнилось 18 лет, и примите Условия и Политику приватности.',
    'signup.aboutYou': 'О вас',
    'signup.aboutYouSubtitle': 'Базовая личная информация',
    'signup.accountSubtitle': 'Как вы будете входить в аккаунт',
    'signup.allergies': 'Аллергии',
    'signup.allergiesQuestion': 'У вас есть аллергические реакции?',
    'signup.allergiesSubtitle': 'Это помогает специалистам безопаснее заботиться о вас. Вы сможете обновить это в профиле.',
    'signup.allergyPlaceholder': 'Опишите ваши аллергии...',
    'signup.birthDatePlaceholder': 'Выберите дату рождения',
    'signup.certificates': 'Сертификаты',
    'signup.certificatesFileHint': 'PDF, JPG, PNG — до 10 МБ каждый',
    'signup.certificatesLater': 'Ничего страшного — вы сможете загрузить их позже из профиля.',
    'signup.certificatesQuestion': 'У вас есть сертификаты, которыми можно поделиться?',
    'signup.certificatesRequired': 'Пожалуйста, ответьте на вопрос о сертификатах.',
    'signup.certificatesSubtitle': 'Покажите клиентам вашу профессиональную квалификацию',
    'signup.certificatesUpload': 'Нажмите, чтобы загрузить сертификаты',
    'signup.completeRegistration': 'Завершить регистрацию',
    'signup.confirmPassword': 'Подтвердите пароль',
    'signup.contact': 'Контакты',
    'signup.createSalon': 'Создать салон',
    'signup.creatingAccount': 'Создание аккаунта…',
    'signup.creatingSalon': 'Создание салона…',
    'signup.customerTitle': 'Регистрация *клиента*',
    'signup.dateOfBirth': 'Дата рождения',
    'signup.firstName': 'Имя',
    'signup.invalidEmail': 'Введите корректный email.',
    'signup.invalidSalonEmail': 'Введите корректный email салона или оставьте поле пустым.',
    'signup.languagesYouSpeak': 'Языки, на которых вы говорите',
    'signup.lastName': 'Фамилия',
    'signup.legalPrefix': 'Я подтверждаю, что мне уже исполнилось 18 лет, и соглашаюсь с',
    'signup.missingCity': 'Пожалуйста, укажите город.',
    'signup.missingCountry': 'Пожалуйста, укажите страну.',
    'signup.missingAddress': 'Пожалуйста, укажите адрес.',
    'signup.missingDateOfBirth': 'Пожалуйста, укажите дату рождения.',
    'signup.minimumAge': 'Чтобы создать аккаунт, вам должно быть не меньше 18 лет.',
    'signup.missingEmail': 'Пожалуйста, укажите email.',
    'signup.missingFirstName': 'Пожалуйста, укажите имя.',
    'signup.missingLastName': 'Пожалуйста, укажите фамилию.',
    'signup.missingPassword': 'Пожалуйста, введите пароль.',
    'signup.missingServiceType': 'Пожалуйста, укажите тип услуги.',
    'signup.missingSex': 'Пожалуйста, укажите пол.',
    'signup.missingYearsInService': 'Пожалуйста, укажите годы опыта.',
    'signup.passwordPlaceholder': 'Минимум 8 символов',
    'signup.profession': 'Профессия',
    'signup.professionSubtitle': 'Что вы предлагаете',
    'signup.providerTitle': 'Регистрация *специалиста*',
    'signup.repeatPasswordPlaceholder': 'Повторите пароль',
    'signup.required': 'Обязательно',
    'signup.salonAdminSubtitle': 'Аккаунт администратора салона',
    'signup.salonContactSubtitle': 'Необязательно — показывается в профиле салона',
    'signup.salonMissingName': 'Пожалуйста, дайте салону название.',
    'signup.salonNamePlaceholder': 'например, Velvet Beauty Studio',
    'signup.salonTitle': 'Регистрация *салона*',
    'signup.selectOption': 'Пожалуйста, выберите вариант.',
    'signup.serviceTypePlaceholder': 'например, мастер маникюра, косметолог',
    'signup.sexRequired': 'Пожалуйста, выберите пол',
    'signup.signupFailed': 'Не удалось зарегистрироваться',
    'signup.stepOf2': 'Шаг {step} из 2',
    'signup.whereYouAre': 'Где вы',
    'signup.whereYouAreSubtitle': 'Используется, чтобы находить локальных специалистов',
    'signup.whereYouWork': 'Где вы работаете',
    'signup.whereYouWorkSubtitle': 'Помогает клиентам найти вас',
    'signup.yearsInService': 'Годы опыта',
    'signup.yearsPlaceholder': 'например, 5',
    'signup.yourSalon': 'Ваш салон',
    'signup.yourSalonSubtitle': 'Это увидят клиенты и ваша команда',

    'landing.nav.platform': 'Платформа',
    'landing.nav.features': 'Возможности',
    'landing.nav.story': 'История',
    'landing.nav.trust': 'Доверие',
    'landing.nav.startNow': 'Начать',
    'landing.nav.openMenu': 'Открыть меню',
    'landing.cta.startJourney': 'Начать свой путь',
    'landing.stores.appStoreEyebrow': 'Загрузить в',
    'landing.stores.appStoreName': 'App Store',
    'landing.stores.googlePlayEyebrow': 'Доступно в',
    'landing.stores.googlePlayName': 'Google Play',
    'landing.appBanner.subtitle': 'Открыть в приложении Bevelure',
    'landing.appBanner.action': 'ОТКРЫТЬ',
    'landing.appBanner.close': 'Закрыть баннер приложения',
    'landing.panel.title': 'Beauty-история',
    'landing.panel.visits': 'визиты',
    'landing.panel.providers': 'специалисты',
    'landing.panel.rating': 'рейтинг',
    'landing.panel.row1Label': 'Аллергии и уходовая рутина',
    'landing.panel.row1Meta': 'Доступно до визита',
    'landing.panel.row2Label': 'Процедура Hydrafacial',
    'landing.panel.row2Meta': 'Завершено · фото добавлены',
    'landing.panel.row3Label': 'Сообщение от Майи',
    'landing.panel.row3Meta': 'Уход после Hydrafacial · 1 ч назад',
    'landing.hero.eyebrow': 'Ваша beauty-история, наконец ваша',
    'landing.hero.body': 'Одна элегантная приватная запись, которая принадлежит вам и следует за вами от специалиста к специалисту — ваша история, готовая к тому, чтобы поделиться ею с тем, к кому вы пойдете дальше.',
    'landing.hero.tile1Title': 'Ваше',
    'landing.hero.tile1Body': 'запись, которая путешествует с вами',
    'landing.hero.tile2Title': 'Готово',
    'landing.hero.tile2Body': 'контекст перед каждым клиентом',
    'landing.hero.tile3Title': 'Синхронно',
    'landing.hero.tile3Body': 'вся команда, один календарь',
    'landing.trustBar.item1': 'История, которая принадлежит вам',
    'landing.trustBar.item2': 'Контекст перед каждым клиентом',
    'landing.trustBar.item3': 'Один календарь для всей команды',
    'landing.three.eyebrow': 'Одна запись, три стороны',
    'landing.three.title': 'Та же запись. Три взгляда.',
    'landing.three.body': 'Beauty-уход соединяет людей, которые редко видят одни и те же заметки. Bevelure дает им один источник правды — и каждому показывает именно ту часть, которая важна ему.',
    'landing.three.side1Tag': 'Для вас',
    'landing.three.side1Title': 'Вся ваша история — чтобы хранить и делиться.',
    'landing.three.side1Desc': 'Каждый визит, продукт и фото в одном месте, которое всегда с вами.',
    'landing.three.side2Tag': 'Для вашего специалиста',
    'landing.three.side2Title': 'Все важное — еще до начала визита.',
    'landing.three.side2Desc': 'Контекст, которым вы решили поделиться: аллергии, прошлые процедуры, что сработало.',
    'landing.three.side3Tag': 'Для салона',
    'landing.three.side3Title': 'Каждая запись и каждый член команды — в одном месте, без суеты.',
    'landing.three.side3Desc': 'Команда, календарь и бронирования под одной крышей — скоординировано, без хаоса.',
    'landing.audiences.eyebrow': 'Одна система для каждой стороны ухода',
    'landing.audiences.title': 'Что на самом деле получает каждая сторона.',
    'landing.audiences.clientTag': 'Для клиентов',
    'landing.audiences.clientTitle': 'Ваша история, наконец ваша.',
    'landing.audiences.clientLead': 'Каждая процедура в одном месте, которое путешествует с вами. Больше не нужно начинать с нуля с новым специалистом или пытаться вспомнить, что сработало.',
    'landing.audiences.clientPoint1': 'Полная запись: визиты, продукты, фото, цены.',
    'landing.audiences.clientPoint2': 'Ваши аллергии и чувствительность кожи доступны каждому специалисту.',
    'landing.audiences.clientPoint3': 'Делитесь последними пятью визитами или типом процедуры — и отзывайте доступ в любой момент.',
    'landing.audiences.clientPoint4': 'Смотрите, сколько вы реально тратите на себя месяц за месяцем.',
    'landing.audiences.clientCta': 'Начать свою историю',
    'landing.audiences.providerTag': 'Для специалистов',
    'landing.audiences.providerTitle': 'Знайте клиента еще до начала приема.',
    'landing.audiences.providerLead': 'Откройте запись — и контекст уже там: что делали в прошлый раз, на что была реакция, чем клиент пользуется дома. Меньше догадок, безопаснее работа, настоящая клиентская база.',
    'landing.audiences.providerPoint1': 'Просматривайте историю, которой клиент решил поделиться, еще до его прихода.',
    'landing.audiences.providerPoint2': 'Ваши заметки, продукты и фото результатов сохраняются между визитами.',
    'landing.audiences.providerPoint3': 'Один раз настройте услуги, доп. услуги, часы работы и цены.',
    'landing.audiences.providerPoint4': 'Пишите клиентам, не раскрывая свой номер.',
    'landing.audiences.providerCta': 'Открыть аккаунт специалиста',
    'landing.audiences.salonTag': 'Для салонов',
    'landing.audiences.salonTitle': 'Весь салон синхронизирован.',
    'landing.audiences.salonLead': 'Объедините команду, календарь и каждое бронирование под одной крышей — с нужным контролем и без вмешательства в работу специалистов.',
    'landing.audiences.salonPoint1': 'Приглашайте и управляйте командой за минуты.',
    'landing.audiences.salonPoint2': 'Просматривайте все бронирования салона по всем специалистам.',
    'landing.audiences.salonPoint3': 'Координируйте без лишнего контроля — администраторы не меняют услуги специалиста.',
    'landing.audiences.salonPoint4': 'Без двойных бронирований и путаницы между мастерами.',
    'landing.audiences.salonCta': 'Зарегистрировать салон',
    'landing.features.eyebrow': 'Все в одном месте',
    'landing.features.title': 'То, что вы хотели бы иметь записанным с самого начала.',
    'landing.features.body': 'Ваша история — главное. Но Bevelure также спокойно берет на себя бронирования, разговоры, цены, фото и визиты, добавленные вне платформы.',
    'landing.features.item1Title': 'Запись, которая действительно ваша',
    'landing.features.item1Desc': 'Каждый визит, продукт, заметка и фото результата в одной истории, которая движется вместе с вами.',
    'landing.features.item2Title': 'Бронирование без лишнего',
    'landing.features.item2Desc': 'Смотрите реальные цены и доп. услуги, выбирайте слот. Или добавьте визит в салон, которого еще нет в Bevelure.',
    'landing.features.item3Title': 'Вы решаете, кто что видит',
    'landing.features.item3Desc': 'Поделитесь последними пятью визитами или типом процедуры с новым специалистом — и отзовите доступ, когда захотите.',
    'landing.features.item4Title': 'До, после и только для вас',
    'landing.features.item4Desc': 'Фото результатов, фото процедуры для специалиста и приватные фото только для вас — ничего не смешивается.',
    'landing.features.item5Title': 'Где бы вы ни бронировали',
    'landing.features.item5Desc': 'Специалист в Майами, вы в Берлине — время салона и ваше время рядом. Любой город, любая валюта.',
    'landing.features.item6Title': 'Обсудите детали',
    'landing.features.item6Desc': 'Пишите до и после визита: уточнить деталь, спросить про уход, отправить фото — без обмена телефоном.',
    'landing.features.item7Title': 'Честные цены, которые не меняются',
    'landing.features.item7Desc': 'Реальные цены фиксируются для каждого визита в валюте специалиста.',
    'landing.features.item8Title': 'Приватные оценки с двух сторон',
    'landing.features.item8Desc': 'Оценивайте визит для себя — специалисты тоже могут. Без публичного рейтинга, просто честная заметка.',
    'landing.founder.eyebrow': 'История основательницы',
    'landing.founder.title': 'Почему я верю в Bevelure.',
    'landing.founder.p1': 'Я создала Bevelure, потому что никогда не могла точно вспомнить свои процедуры: что делали, когда и какие продукты использовали. Каждый раз с новым специалистом вопросы были одни и те же: *что делали в прошлый раз, какие продукты использовали, была ли аллергическая реакция*. Я пыталась вспомнить, но не могла дать четкий ответ.',
    'landing.founder.p2': 'Обычно получалось что-то вроде: *“Я помню результат, но не точное название процедуры, продукты или раздражало ли что-то кожу.”* Это не очень помогало. Мне всегда хотелось иметь все записанным: точные продукты и детали каждого визита.',
    'landing.founder.p3': 'Именно это и есть Bevelure: вы храните запись обо всем в одном месте и можете поделиться ею с любым специалистом, которого выберете.',
    'landing.founder.p4': 'Мои beauty-записи тоже были хаотично разбросаны по календарю. Приходилось долго листать, чтобы вспомнить, что и когда я делала и когда снова пора записываться. Теперь все в одном месте: ваша beauty-история.',
    'landing.founder.p5': 'Я много путешествую, поэтому часто записываюсь к новым специалистам, чтобы не прерывать уход. И всегда одна история: на процедуре для лица меня спрашивают, *какой домашний уход я использую и какие ингредиенты в кремах*. С Bevelure об этом не нужно думать: загрузите фото своей домашней рутины с этикетками и ингредиентами — и поделитесь, когда нужно.',
    'landing.founder.p6': 'Я думаю, многие люди оказываются в таких же ситуациях. Я действительно хочу им помочь. Именно поэтому я создала Bevelure.',
    'landing.founder.quote': '“Для каждой beauty-истории, которая заслуживает быть сохраненной. С заботой.”',
    'landing.founder.signature': 'Евгения Андреева, основательница и CEO',
    'landing.trust.eyebrow': 'Уровень доверия',
    'landing.trust.title': 'Создано для самого важного — и того, что сложнее всего восстановить.',
    'landing.trust.body': 'Чувствительная информация, реальные записи, отношения на годы. Снаружи все ощущается легко, потому что важные границы тихо держат все вместе.',
    'landing.trust.item1Title': 'Приватность в основе',
    'landing.trust.item1Desc': 'Ваши приватные заметки, внутренние заметки специалиста и то, чем вы делитесь, хранятся отдельно.',
    'landing.trust.item2Title': 'Просто оставайтесь на связи',
    'landing.trust.item2Desc': 'Чат, подтверждения и деликатные напоминания помогают всем оставаться в курсе.',
    'landing.trust.item3Title': 'Без публичного рейтинга',
    'landing.trust.item3Desc': 'Оценивайте визит приватно, для собственной памяти. Beauty-уход не должен превращаться в публичный рейтинг.',
    'landing.final.title': 'Ваша beauty-история, наконец в одном месте.',
    'landing.final.body': 'Вы бронируете, выполняете работу или управляете салоном — все живет в одной красивой приватной записи.',
    'landing.footer.tagline': 'Ваша beauty-история в одном месте.',
    'landing.footer.contact': 'Контакты',
    'landing.footer.site': 'Сайт',

    'block.title': 'Заблокировать {name}?',
    'block.body': 'Этот человек не сможет бронировать или принимать новые записи с вами. Прошлые записи и оценки останутся видимыми для обеих сторон.',
    'block.reasonLabel': 'Причина (видите только вы)',
    'block.reasonPlaceholder': 'Необязательно — полезно, если вы будете пересматривать блокировки позже',
    'block.reportUser': 'Также пожаловаться на пользователя',
    'block.reportHelp': 'Аккаунт и выбранная причина будут отправлены Bevelure на проверку. Другой пользователь не увидит вашу жалобу.',
    'block.reportReasonLabel': 'Причина жалобы',
    'block.reportReasonPlaceholder': 'Выберите причину',
    'block.reportReasonRequired': 'Выберите причину перед отправкой жалобы.',
    'block.reportReasonSpam': 'Спам или мошенничество',
    'block.reportReasonHarassment': 'Преследование или угрозы',
    'block.reportReasonContent': 'Неприемлемый контент',
    'block.reportReasonSafety': 'Опасное поведение',
    'block.reportReasonImpersonation': 'Выдаёт себя за другого человека',
    'block.reportReasonOther': 'Другое',
    'block.blockAndReport': 'Заблокировать и пожаловаться',
    'block.cancelFutureOne': 'Также отменить {count} будущую запись между вами.',
    'block.cancelFutureMany': 'Также отменить {count} будущих записей между вами.',
    'block.keepFutureHint': 'Если выключить это, записи останутся в обоих календарях — блокировка остановит только новые бронирования.',
    'block.failed': 'Не удалось заблокировать',
    'block.blocking': 'Блокировка…',
    'block.block': 'Заблокировать',

    'shareModal.titlePrefix': 'Поделиться',
    'shareModal.titleEmphasis': 'историей',
    'shareModal.subtitle': 'Создайте ссылку только для просмотра для специалиста.',
    'shareModal.procedureTypes': 'Типы процедур',
    'shareModal.procedurePlaceholder': 'например, мужская стрижка, маникюр…',
    'shareModal.procedureHelp': 'Выберите из своих процедур или введите свою. Видимыми будут только завершенные записи, которые совпадают.',
    'shareModal.whatToShare': 'Чем поделиться',
    'shareModal.recipientEmail': 'Email получателя',
    'shareModal.optional': 'необязательно',
    'shareModal.emailHelp': 'Если указано, ссылка будет отправлена по email. Иначе будет создан QR-код / ссылка.',
    'shareModal.consent': 'Я понимаю, что любой человек с этой ссылкой сможет увидеть историю процедур, которой я делюсь: заметки, продукты, рекомендации и фото результата/процедуры. Мои приватные заметки и фото никогда не передаются.',
    'shareModal.createError': 'Не удалось создать ссылку. Попробуйте снова.',
    'shareModal.generateLink': 'Создать ссылку',
    'shareModal.emailIt': 'Отправить email',
    'shareModal.scanHint': 'Отсканируйте камерой телефона, чтобы открыть общую историю.',
    'shareModal.link': 'Ссылка',
    'shareModal.copyLink': 'Копировать ссылку',
    'shareModal.procedure': 'Процедура',
    'shareModal.mode': 'Режим',
    'shareModal.expires': 'Действует до',
    'shareModal.sentTo': 'Отправлено',
    'shareModal.generateAnother': 'Создать еще одну',

    'contactSetup.title': 'Сделайте себя проще для поиска',
    'contactSetup.bodyPrefix': 'Добавьте',
    'contactSetup.socialLink': 'социальную ссылку',
    'contactSetup.bodySuffix': 'чтобы люди могли находить вас не только по email.',
    'contactSetup.addContactInfo': 'Добавить контакты',

    'errorBoundary.titlePrefix': 'Что-то пошло',
    'errorBoundary.titleEmphasis': 'не так',
    'errorBoundary.body': 'При отображении страницы произошла неожиданная ошибка. Обновите страницу, чтобы попробовать снова — если это повторяется, сообщите нам.',
    'errorBoundary.refresh': 'Обновить страницу',
    'errorBoundary.dismiss': 'Закрыть и продолжить',

    'confirm.defaultTitle': 'Вы уверены?',
    'impact.futureBookingOne': '{count} будущее бронирование будет затронуто.',
    'impact.futureBookingMany': '{count} будущих бронирований будет затронуто.',
    'impact.cancelAffectedOn': 'Все они будут отменены, а клиенты получат уведомление.',
    'impact.cancelAffectedOff': 'Оставьте выключенным, чтобы они остались по расписанию — блокируются только новые бронирования.',
    'impact.actionFailed': 'Действие не удалось',
    'impact.working': 'Обработка…',

    'desktopHome.settings': 'Настройки главной',
    'desktopHome.layoutTitle': 'Макет главной',
    'desktopHome.layout1': 'Разделено — Предстоящие и Недавние рядом с календарем',
    'desktopHome.layout2': 'Компактное приветствие со списками',
    'desktopHome.layout3': 'Колонка статистики · календарь · списки ниже',
    'desktopHome.layout4': 'Списки со скроллом на высоту календаря',
    'auth.tryAgain': 'Что-то пошло не так — попробуйте еще раз',
    'auth.updatePassword': 'Обновить пароль',
    'auth.updating': 'Обновление…',
    'auth.verificationInvalid': 'Ссылка подтверждения недействительна или истекла.',
    'auth.verificationMissing': 'В ссылке подтверждения нет токена.',
    'auth.verifyErrorTitle': 'Не удалось подтвердить',
    'auth.verifyResendHint': 'Войдите в аккаунт и нажмите Отправить снова в баннере подтверждения.',
    'auth.welcomeBack': 'Рады видеть *снова*',
    'notif.providerRequest.title': 'Новая заявка на запись',
    'notif.providerRequest.body': 'Клиент хочет записаться. Подтвердите или отклоните.',
    'notif.providerScheduled.title': 'Новая запись',
    'notif.providerScheduled.body': 'Для вас назначена новая запись.',
    'notif.customerCreated.title': 'Запись назначена',
    'notif.customerCreated.body': '{provider} записал(а) вас на {service}.',
    'notif.customerConfirmed.title': 'Запись подтверждена',
    'notif.customerConfirmed.body': '{provider} подтвердил(а) вашу запись на {service}.',
    'notif.customerRejected.title': 'Заявка отклонена',
    'notif.customerRejected.body': '{provider} не смог(ла) принять вашу запись на {service}. Выберите другое время.',
    'notif.cancelled.title': 'Запись отменена',
    'notif.cancelled.body': 'Ваша запись на {service} с {other} отменена.',
    'notif.customerExpired.title': 'Заявка истекла',
    'notif.customerExpired.body': 'Ваша заявка на {service} истекла — не подтверждена вовремя. Выберите другое время.',
    'notif.detailsUpdated.title': 'Детали процедуры обновлены',
    'notif.detailsUpdated.body': '{provider} добавил(а) заметки или фото к вашей записи на {service}.',
    'notif.reminderCustomer.title': 'Запись через 48 часов',
    'notif.reminderCustomer.body': 'Ваша запись на {service} с {other} — примерно через 48 часов.',
    'notif.reminderProvider.title': 'Запись через 48 часов',
    'notif.reminderProvider.body': '{service} с {other} — примерно через 48 часов.',
    'notif.salonInvite.title': 'Приглашение в салон',
    'notif.salonInvite.body': '{salon} приглашает вас в свою команду.',
    'notif.memberRemoved.title': 'Удалены из салона',
    'notif.memberRemoved.body': 'Администратор удалил вас из {salon}.',
    'notif.memberLeft.title': 'Специалист покинул салон',
    'notif.memberLeft.body': '{provider} покинул(а) команду.',
    'notif.inviteAccepted.title': 'Специалист присоединился к салону',
    'notif.inviteAccepted.body': '{provider} принял(а) приглашение и присоединился к команде.',
    'notif.inviteDeclined.title': 'Приглашение отклонено',
    'notif.inviteDeclined.body': '{provider} отклонил(а) приглашение в салон.',
    'notif.salonBookingRequested.title': 'Новая заявка в вашем салоне',
    'notif.salonBookingRequested.body': '{customer} хочет записаться на {service} к {provider} — ожидает подтверждения.',
    'notif.salonBooking.title': 'Новая запись в вашем салоне',
    'notif.salonBooking.body': 'У {provider} новая запись с клиентом {customer}.',
    'notif.salonBookingCancelled.title': 'Запись в салоне отменена',
    'notif.salonBookingCancelled.body': 'Запись {provider} с клиентом {customer} отменена.',
    'auth.yourPassword': 'Ваш пароль',

    'nav.navigation': 'Навигация',
    'nav.account': 'Аккаунт',
    'nav.menu': 'Меню',
    'nav.home': 'Главная',
    'nav.search': 'Поиск',
    'nav.searchProviders': 'Поиск специалистов',
    'nav.favorites': 'Избранное',
    'nav.messages': 'Сообщения',
    'nav.myCareHub': 'Мой Care Hub',
    'nav.spending': 'Расходы',
    'nav.profile': 'Профиль',
    'nav.history': 'История',
    'nav.upcoming': 'Будущие',
    'nav.customers': 'Клиенты',
    'nav.team': 'Команда',
    'nav.schedule': 'Расписание',
    'nav.services': 'Услуги',
    'nav.portfolio': 'Портфолио',
    'nav.mySalon': 'Мой салон',
    'nav.earnings': 'Доходы',
    'nav.pending': 'Ожидают',
    'nav.signOut': 'Выйти',
    'nav.terms': 'Условия',
    'nav.privacy': 'Приватность',
    'nav.notifications': 'Уведомления',
    'nav.salonInviteTitle': '{count} приглашени{plural} в салон ожидает',

    'notifications.allCaughtUp': 'Все просмотрено',
    'notifications.allLoaded': 'Это все уведомления: {count}.',
    'notifications.empty': 'Уведомлений пока нет',
    'notifications.markAsRead': 'Отметить прочитанным',
    'notifications.title': 'Уведомления',

    'salonTeam.inviteFromPrefix': 'Пригласите специалистов из',
    'salonTeam.manageInvitesPrefix': 'Управляйте приглашениями из',
    'salonTeam.noProviders': 'Специалистов пока нет',
    'salonTeam.noSearchMatches': 'Ни один участник команды не подходит под поиск.',
    'salonTeam.providerCount': 'Специалистов: {count}',
    'salonTeam.searchPlaceholder': 'Поиск по имени или услуге…',
    'salonTeam.sortName': 'Имя (A-Z)',
    'salonTeam.sortRecentlyJoined': 'Недавно присоединились',

    'salon.accept': 'Принять',
    'salon.activeProviders': 'Активных специалистов: {count}',
    'salon.address': 'Адрес',
    'salon.addressPlaceholder': 'Начните вводить адрес…',
    'salon.admin': 'Админ',
    'salon.city': 'Город',
    'salon.close': 'Закрыть',
    'salon.country': 'Страна',
    'salon.decline': 'Отклонить',
    'salon.description': 'Описание',
    'salon.descriptionPlaceholder': 'Расскажите клиентам, что делает ваш салон особенным',
    'salon.desktopTitleEmphasis': 'салон',
    'salon.desktopTitlePrefix': 'Мой',
    'salon.editDetails': 'Редактировать детали',
    'salon.invitation': 'Приглашение в салон',
    'salon.invitationSent': 'Приглашение отправлено.',
    'salon.inviteProvider': 'Пригласить специалиста',
    'salon.inviteProviderHelp': 'У него уже должен быть аккаунт специалиста Bevelure. Мы отправим уведомление для принятия.',
    'salon.invitedAs': 'Приглашен как {role}',
    'salon.leaveDescription': 'Вы перестанете быть частью этого салона. Новые бронирования после этого не будут привязаны к салону. Прошлые записи останутся в истории.',
    'salon.leaveSalon': 'Покинуть салон',
    'salon.leaveTitle': 'Покинуть {name}?',
    'salon.name': 'Название салона',
    'salon.noPendingInvites': 'Нет приглашений в ожидании. Активные участники команды находятся в {team}.',
    'salon.notPartBody': 'Салоны приглашают специалистов по email. Если салон пригласит вас, приглашение появится здесь.',
    'salon.notPartTitle': 'Вы не состоите в салоне',
    'salon.pendingInvites': 'Приглашения в ожидании',
    'salon.phone': 'Телефон',
    'salon.phonePlaceholder': 'Номер телефона салона',
    'salon.postalCode': 'Почтовый индекс',
    'salon.providerEmail': 'Email специалиста',
    'salon.providerFallback': 'Специалист',
    'salon.publicEmail': 'Публичный email',
    'salon.removeMember': 'Удалить участника',
    'salon.revokeInvite': 'Отозвать приглашение',
    'salon.roleAdmin': 'админ',
    'salon.roleMember': 'участник',
    'salon.saveDetailsError': 'Не удалось сохранить детали салона',
    'salon.sendInvite': 'Отправить приглашение',
    'salon.sending': 'Отправка…',
    'salon.socialLink': 'Сайт / соцсеть',
    'salon.subtitle': 'Управляйте салоном и командой.',
    'salon.teamLink': 'Команде',
    'salon.title': 'Мой салон',
    'salon.uploadLogo': 'Загрузить логотип',
    'salon.view': 'Смотреть',
    'salon.viewProfile': 'Смотреть профиль салона',
    'salon.youWorkAt': 'Вы работаете в',

    'teamBookings.all': 'Все',
    'teamBookings.history': 'История',
    'teamBookings.noBookings': 'Здесь пока нет бронирований.',
    'teamBookings.subtitle': 'Все записи специалистов вашего салона.',
    'teamBookings.title': 'Бронирования команды',
    'teamBookings.titleEmphasis': 'команды',
    'teamBookings.titlePrefix': 'Бронирования',
    'teamBookings.upcoming': 'Будущие',
    'salonAppointments.cancelReason': 'Отменено администратором салона',

    'salonProfile.messageSalon': 'Написать салону',
    'salonProfile.noProviders': 'Специалисты пока не указаны.',
    'salonProfile.openingChat': 'Открываем чат…',
    'salonProfile.ourTeam': 'Наша команда',
    'salonProfile.provider': 'специалист',
    'salonProfile.providers': 'специалисты',
    'salonProfile.title': 'Салон',

    'portfolio.addMore': 'Добавить еще',
    'portfolio.addPhoto': 'Добавить фото',
    'portfolio.addToPortfolio': 'Добавить в портфолио',
    'portfolio.bulkApplyHint': 'Категория и описание применятся ко всем фото: {count}.',
    'portfolio.caption': 'Описание',
    'portfolio.captionOptional': 'Описание (необязательно)',
    'portfolio.captionPlaceholder': 'Короткое описание под фото',
    'portfolio.category': 'Категория',
    'portfolio.categoryCount': 'Категорий: {count}',
    'portfolio.categoryPlaceholder': 'например, маникюр, ботокс, Hydrafacial',
    'portfolio.choosePhotos': 'Нажмите, чтобы выбрать фото',
    'portfolio.deletePhotoMessage': 'Фото будет удалено из вашего публичного портфолио.',
    'portfolio.deletePhotoTitle': 'Удалить фото?',
    'portfolio.desktopTitleEmphasis': 'портфолио',
    'portfolio.desktopTitlePrefix': 'Мое',
    'portfolio.editTitle': 'Редактировать элемент портфолио',
    'portfolio.fileHint': 'JPG, PNG до 10MB · можно выбрать несколько',
    'portfolio.hide': 'Скрыть',
    'portfolio.noFiles': 'Нет файлов',
    'portfolio.noItemsBody': 'Покажите свои работы — клиенты видят это в вашем публичном профиле. Загружайте напрямую или добавляйте фото из завершенных визитов.',
    'portfolio.noItemsTitle': 'В портфолио пока нет работ',
    'portfolio.noItemsYet': 'Пока нет работ',
    'portfolio.private': 'Приватное',
    'portfolio.privateDescription': 'Сохранено в портфолио, но скрыто от клиентов.',
    'portfolio.public': 'Публичное',
    'portfolio.publicDescription': 'Видно в вашем публичном профиле всем, кто его откроет.',
    'portfolio.remove': 'Убрать',
    'portfolio.shortDescription': 'Короткое описание',
    'portfolio.show': 'Показать',
    'portfolio.title': 'Портфолио',
    'portfolio.uncategorized': 'Без категории',
    'portfolio.upload': 'Загрузить',
    'portfolio.uploadCount': 'Загрузить ({count})',
    'portfolio.uploading': 'Загрузка…',

    'emailVerify.resendError': 'Не удалось отправить повторно — попробуйте чуть позже.',
    'emailVerify.sent': 'Письмо отправлено — проверьте почту.',
    'emailVerify.message': 'Подтвердите email, чтобы получать уведомления о бронированиях.',
    'emailVerify.sending': 'Отправка…',
    'emailVerify.resend': 'Отправить снова',

    'providerSetup.title': 'Завершите настройку профиля',
    'providerSetup.bodyPrefix': 'Клиенты не смогут бронировать, пока вы не добавите',
    'providerSetup.oneService': 'хотя бы одну услугу',
    'providerSetup.workingHours': 'рабочие часы',
    'providerSetup.addService': 'Добавить услугу',
    'providerSetup.setHours': 'Настроить часы',

    'profile.title': 'Профиль',
    'profile.preferences': 'Настройки',
    'profile.language': 'Язык',
    'profile.pushNotifications': 'Push-уведомления',
    'profile.languageAria': 'Язык интерфейса',
    'profile.providerLanguages': 'Языки, на которых вы говорите',
    'profile.providerLanguagesLabel': 'Языки',
    'profile.currencyLabel': 'Валюта',
    'profile.failedUpdate': 'Не удалось обновить профиль',
    'profile.roleCustomer': 'Клиент',
    'profile.roleCareProvider': 'Специалист',
    'profile.roleSalonAdmin': 'Администратор салона',
    'profile.uploadPhoto': 'Загрузить фото',
    'profile.rating': 'Рейтинг',
    'profile.ratingSingular': 'оценка',
    'profile.ratingPlural': 'оценки',
    'profile.fromClients': 'от клиентов',
    'profile.fromProviders': 'от специалистов',
    'profile.byService': 'По услугам',
    'profile.noRatingsCustomer': 'Оценок пока нет. Специалисты смогут оценить вас после завершенного визита.',
    'profile.noRatingsProvider': 'Оценок пока нет. Клиенты смогут оценить вас после завершенного визита.',
    'profile.personalInformation': 'Личная информация',
    'profile.dateOfBirth': 'Дата рождения',
    'profile.sex': 'Пол',
    'profile.location': 'Локация',
    'profile.address': 'Адрес',
    'profile.addressPlaceholder': 'Начните вводить адрес...',
    'profile.timeZone': 'Часовой пояс',
    'profile.timeZonePrefix': 'Часовой пояс: {zone}',
    'profile.timeZoneHelp': 'Ваши рабочие часы указаны в этом поясе. Клиенты из других мест также видят свое время.',
    'profile.contact': 'Контакты',
    'profile.phone': 'Телефон',
    'profile.social': 'Соцсеть',
    'profile.socialMediaLink': 'Ссылка на соцсеть',
    'profile.socialPlaceholder': 'https://instagram.com/your-handle',
    'profile.contactHelp': 'Чтобы клиенты могли найти и забронировать вас по телефону или ссылке на соцсеть. Телефон сохраняется в международном формате — добавьте код страны.',
    'profile.noContactDetails': 'Контактов пока нет.',
    'profile.allergies': 'Аллергии',
    'profile.noAllergies': 'Нет аллергий',
    'profile.hasAllergies': 'У меня есть аллергии',
    'profile.notes': 'Заметки',
    'profile.allergyPlaceholder': 'Латекс, отдушки и т.д.',
    'profile.allergiesNoDetails': 'Есть аллергии (без деталей)',
    'profile.noKnownAllergies': 'Известных аллергий нет',
    'profile.careRoutine': 'Домашний уход',
    'profile.careRoutineHelp': 'Опишите или сфотографируйте домашний уход (продукты, шаги). Это видят ваши специалисты, чтобы у них был контекст.',
    'profile.careRoutinePlaceholder': 'например: утро — мягкий гель, витамин C, SPF. Вечер — ретинол 2 раза в неделю...',
    'profile.addPhoto': 'Добавить фото',
    'profile.removePhotoTitle': 'Удалить фото?',
    'profile.removePhotoBody': 'Это фото будет удалено навсегда.',
    'profile.removePhoto': 'Удалить',
    'profile.professionalDetails': 'Профессиональные детали',
    'profile.yearExperience': '{service} · {count} год опыта',
    'profile.yearsExperience': '{service} · {count} лет опыта',
    'profile.bio': 'Био',
    'profile.bioPlaceholder': 'Короткое описание для публичного профиля...',
    'profile.about': 'О себе',
    'profile.noBio': 'Био пока нет — добавьте его, чтобы клиентам было легче вам доверять.',
    'profile.currencyHelp': 'Используется для всех ваших будущих услуг и add-ons. Прошлые визиты сохраняют валюту бронирования.',
    'profile.certificates': 'Сертификаты',
    'profile.addCertificate': 'Добавить',
    'profile.certificatesHelp': 'Показываются в вашем публичном профиле. Изображения или PDF.',
    'profile.noCertificates': 'Сертификатов пока нет.',
    'profile.removeCertificateTitle': 'Удалить сертификат?',
    'profile.removeCertificateBody': 'Этот сертификат будет удален навсегда.',
    'profile.removeCertificate': 'Удалить',
    'profile.password': 'Пароль',
    'profile.changePassword': 'Изменить',
    'profile.currentPassword': 'Текущий пароль',
    'profile.newPassword': 'Новый пароль',
    'profile.confirmNewPassword': 'Подтвердите новый пароль',
    'profile.newPasswordMin': 'Новый пароль должен быть не короче 8 символов',
    'profile.newPasswordsMismatch': 'Новые пароли не совпадают',
    'profile.passwordUpdated': 'Пароль обновлен. На других устройствах нужно будет войти снова.',
    'profile.passwordChangeFailed': 'Не удалось изменить пароль',
    'profile.newPasswordPlaceholder': 'Минимум 8 символов',
    'profile.confirmNewPasswordPlaceholder': 'Повторите новый пароль',
    'profile.updatePassword': 'Обновить пароль',
    'profile.deleteAccountTitle': 'Удалить аккаунт',
    'profile.deleteAccountBody': 'При удалении аккаунта ваш профиль и связанные с ним персональные данные будут удалены навсегда. Будущие и текущие записи будут отменены. Это действие нельзя отменить.',
    'profile.deleteAccount': 'Удалить аккаунт',
    'profile.deletingAccount': 'Удаление…',
    'profile.deleteAccountConfirmTitle': 'Удалить аккаунт?',
    'profile.deleteAccountImpactNone': 'Будущих или текущих записей для отмены нет.',
    'profile.deleteAccountImpactOne': '1 будущая или текущая запись будет отменена автоматически.',
    'profile.deleteAccountImpactMany': '{count} будущих или текущих записей будет отменено автоматически.',
    'profile.deleteAccountConfirmBody': 'Ваш аккаунт и связанные с ним персональные данные будут удалены навсегда. Это действие нельзя отменить.',
    'profile.deleteAccountConfirm': 'Удалить аккаунт',
    'profile.deleteAccountError': 'Не удалось удалить аккаунт',
    'profile.switchCurrencyTitle': 'Сменить валюту на {currency}?',
    'profile.serviceSingular': 'услуга',
    'profile.servicePlural': 'услуг',
    'profile.currencyWarningBody': 'У вас есть {count} активных {services} с ценами в {from}. Переход на {to} не конвертирует эти числа — цифры останутся теми же, но будут отображаться с новым символом валюты. Проверьте и обновите цены на странице My Services после смены.',
    'profile.currencyWarningPast': 'Прошлые визиты остаются в валюте бронирования — они не изменятся.',
    'profile.switchToCurrency': 'Сменить на {currency}',
  },
};

export function normalizeLocale(value?: string | null): AppLocale | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase().replace('_', '-');
  if (normalized === 'ua') return 'uk';
  const base = normalized.split('-')[0];
  return SUPPORTED_LOCALES.includes(base as AppLocale) ? (base as AppLocale) : null;
}

function detectBrowserLocale(storageKey: string): AppLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const saved = normalizeLocale(window.localStorage.getItem(storageKey));
  if (saved) return saved;
  for (const candidate of window.navigator.languages ?? [window.navigator.language]) {
    const locale = normalizeLocale(candidate);
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}

function interpolate(message: string, values?: Record<string, string | number>) {
  if (!values) return message;
  return Object.entries(values).reduce(
    (next, [key, value]) => next.replaceAll(`{${key}}`, String(value)),
    message
  );
}

interface I18nContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  publicLocale: AppLocale;
  setPublicLocale: (locale: AppLocale) => void;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  publicLocale: DEFAULT_LOCALE,
  setPublicLocale: () => {},
  t: (key, values) => interpolate(en[key] ?? key, values),
});

function isPublicLocalePath(pathname: string | null) {
  if (!pathname) return false;
  return (
    pathname === '/' ||
    pathname === '/landing-v1' ||
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password' ||
    pathname === '/verify-email' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/signup' ||
    pathname.startsWith('/signup/') ||
    pathname.startsWith('/p/') ||
    pathname.startsWith('/s/') ||
    pathname.startsWith('/external/') ||
    pathname.startsWith('/share/')
  );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [accountLocale, setAccountLocale] = useState<AppLocale>(DEFAULT_LOCALE);
  const [publicLocale, setPublicLocaleState] = useState<AppLocale>(DEFAULT_LOCALE);
  const userLocale = normalizeLocale(user?.language);
  const locale = mounted
    ? (isPublicLocalePath(pathname) ? publicLocale : accountLocale)
    : DEFAULT_LOCALE;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setMounted(true);
    setAccountLocale(detectBrowserLocale(ACCOUNT_STORAGE_KEY));
    setPublicLocaleState(detectBrowserLocale(PUBLIC_STORAGE_KEY));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !mounted) return;
    if (userLocale) {
      window.localStorage.setItem(ACCOUNT_STORAGE_KEY, userLocale);
      setAccountLocale(userLocale);
      window.dispatchEvent(new CustomEvent(ACCOUNT_LOCALE_CHANGE_EVENT, { detail: userLocale }));
    }
  }, [mounted, userLocale]);

  const setLocale = useCallback((next: AppLocale) => {
    setAccountLocale(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ACCOUNT_STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent(ACCOUNT_LOCALE_CHANGE_EVENT, { detail: next }));
    }
  }, []);

  const setPublicLocale = useCallback((next: AppLocale) => {
    setPublicLocaleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PUBLIC_STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent(PUBLIC_LOCALE_CHANGE_EVENT, { detail: next }));
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function onStorage(e: StorageEvent) {
      if (e.key === ACCOUNT_STORAGE_KEY) {
        const next = normalizeLocale(e.newValue);
        if (next) setAccountLocale(next);
      }
      if (e.key === PUBLIC_STORAGE_KEY) {
        const next = normalizeLocale(e.newValue);
        if (next) setPublicLocaleState(next);
      }
    }

    function onAccountLocaleChange(e: Event) {
      const next = normalizeLocale((e as CustomEvent<string>).detail);
      if (next) setAccountLocale(next);
    }

    function onPublicLocaleChange(e: Event) {
      const next = normalizeLocale((e as CustomEvent<string>).detail);
      if (next) setPublicLocaleState(next);
    }

    window.addEventListener('storage', onStorage);
    window.addEventListener(ACCOUNT_LOCALE_CHANGE_EVENT, onAccountLocaleChange);
    window.addEventListener(PUBLIC_LOCALE_CHANGE_EVENT, onPublicLocaleChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ACCOUNT_LOCALE_CHANGE_EVENT, onAccountLocaleChange);
      window.removeEventListener(PUBLIC_LOCALE_CHANGE_EVENT, onPublicLocaleChange);
    };
  }, []);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale,
    publicLocale,
    setPublicLocale,
    t: (key, values) => interpolate(dictionaries[locale][key] ?? dictionaries.en[key] ?? key, values),
  }), [locale, publicLocale, setLocale, setPublicLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

/**
 * Renders a translated heading with an editorial accent: wrap the accent
 * segment in *asterisks* inside the translation value (e.g. 'Welcome *back*'),
 * and it renders in `accentClassName` (italic + brand colour). Each language
 * can place the marker on whatever word reads best; strings with no marker
 * render plainly. Keeps the two-tone heading style working across locales.
 */
export function AccentText({ text, accentClassName }: { text: string; accentClassName: string }) {
  const m = text.match(/^([\s\S]*?)\*([\s\S]+?)\*([\s\S]*)$/);
  if (!m) return <>{text}</>;
  return (
    <>
      {m[1]}
      <span className={accentClassName}>{m[2]}</span>
      {m[3]}
    </>
  );
}

export function LanguageDropdown({
  value,
  onChange,
  className = '',
  align = 'right',
  compact = false,
}: {
  value: AppLocale | string;
  onChange: (locale: AppLocale) => void;
  className?: string;
  align?: 'left' | 'right';
  compact?: boolean;
}) {
  const { t } = useI18n();
  const locale = normalizeLocale(value) ?? DEFAULT_LOCALE;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = LOCALE_OPTIONS.find((option) => option.code === locale) ?? LOCALE_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(next: AppLocale) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div ref={ref} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((next) => !next)}
        aria-label={t('profile.languageAria')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex h-9 items-center justify-between gap-2 rounded-full border border-[#E8DDD5] bg-white/80 px-3 text-xs font-semibold text-ink-900 shadow-[0_8px_22px_rgba(33,27,25,0.06)] outline-none transition-colors hover:border-stone-300 focus:border-customer-400 focus:ring-2 focus:ring-customer-100 ${
          compact ? 'min-w-[5.25rem] uppercase' : 'min-w-[10rem]'
        }`}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <Globe2 size={14} className="flex-shrink-0 text-champagne-500" />
          <span className="truncate">{compact ? selected.code : selected.nativeLabel}</span>
        </span>
        <ChevronsUpDown size={14} className="flex-shrink-0 text-stone-400" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={t('profile.languageAria')}
          className={`absolute top-full z-50 mt-2 w-48 overflow-hidden rounded-2xl border border-[#E8DDD5] bg-[#FFFCF8] py-1 shadow-[0_18px_48px_rgba(33,27,25,0.14)] ${
            align === 'left' ? 'left-0' : 'right-0'
          }`}
        >
          {LOCALE_OPTIONS.map((option) => {
            const active = option.code === locale;
            return (
              <button
                key={option.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => pick(option.code)}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#F6EEE7] ${
                  active ? 'font-semibold text-customer-700' : 'text-stone-700'
                }`}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="w-7 text-xs font-bold uppercase tracking-wide text-champagne-500">{option.code}</span>
                  <span>
                    <span className="block">{option.nativeLabel}</span>
                    <span className="block text-[11px] font-normal text-stone-400">{option.label}</span>
                  </span>
                </span>
                {active && <Check size={15} className="text-customer-700" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PublicLanguageSwitcher({
  className = '',
  align = 'right',
  showLabel = true,
}: {
  className?: string;
  align?: 'left' | 'right';
  showLabel?: boolean;
}) {
  const { publicLocale, setPublicLocale, t } = useI18n();

  return (
    <div className={`inline-flex items-center gap-2 text-xs text-stone-500 ${className}`}>
      {showLabel && <span className="font-medium">{t('profile.language')}</span>}
      <LanguageDropdown value={publicLocale} onChange={setPublicLocale} align={align} compact />
    </div>
  );
}
